// Centralized translations for the application.
// Structure: translations[language_code][namespace].[key]

export type SupportedLanguageCode =
  | 'en'
  | 'da'
  | 'sv'
  | 'de'
  | 'nb'
  | 'fr'
  | 'fi'
  | 'es'
  | 'fo'
  | 'is'
  | 'pl';

export const supported_languages: Array<{ code: SupportedLanguageCode; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'da', label: 'Dansk' },
  { code: 'sv', label: 'Svenska' },
  { code: 'de', label: 'Deutsch' },
  { code: 'nb', label: 'Norsk' },
  { code: 'fr', label: 'Français' },
  { code: 'fi', label: 'Suomi' },
  { code: 'es', label: 'Español' },
  { code: 'fo', label: 'Føroyskt' },
  { code: 'is', label: 'Íslenska' },
  { code: 'pl', label: 'Polski' }
];

export type Translations = Record<string, Record<string, any>>;

export const translations: Translations = {
  en: {
    auth: {
      forgot_title: 'Forgot your password?',
      forgot_link: 'Forgot your password?',
      forgot_confirmation: 'If an account exists for that email, we sent a reset link.',
      email_label: 'Email',
      send_reset_link: 'Send reset link',
      reset_title: 'Set a new password',
      new_password_label: 'New password',
      confirm_password_label: 'Confirm password',
      set_new_password: 'Set new password',
      password_mismatch: 'Passwords do not match',
      password_policy_msg: 'At least 12 characters, include uppercase, lowercase and a number.',
      invalid_token: 'Invalid or expired reset link',
      reset_success: 'Your password has been updated. Redirecting to login...',
      reset_failed: 'Failed to reset password. Please try again.',
      login_failed: 'Login failed. Please try again.',
      beta_banner: 'AntNest is in beta. Have an invite code?',
      beta_banner_link: 'Sign up here',
      or: 'or',
      login_google: 'Log in with Google',
      login_facebook: 'Log in with Facebook',
      no_account: 'Don\'t have an account?',
      create_one: 'Create one',
      err_code_required: 'A beta access code is required.',
      err_agree: 'Please agree to the Terms and Privacy Policy.',
      err_fields: 'Please fill in all the fields.',
      err_code_invalid: 'This beta code is not valid or has already been used.',
      err_signup_failed: 'The account could not be created. Please try again.',
      signup_title: 'Create your account',
      beta_code: 'Beta access code',
      beta_code_ph: 'Enter your invite code',
      full_name: 'Your name',
      full_name_ph: 'e.g. Anna',
      username: 'Username',
      show_password: 'Show password',
      hide_password: 'Hide password',
      rule_length: 'At least 12 characters',
      rule_upper: 'An uppercase letter',
      rule_lower: 'A lowercase letter',
      rule_digit: 'A number',
      password_match: 'Passwords match',
      family_name: 'Family name',
      family_name_ph: 'e.g. The Hansen Family',
      family_default: 'My family',
      agree_before: 'I agree to the',
      terms: 'Terms',
      and: 'and',
      privacy: 'Privacy Policy',
      creating_account: 'Creating account…',
      create_account: 'Create account',
      signup_google: 'Sign up with Google',
      signup_facebook: 'Sign up with Facebook',
      choose_language: 'Choose language',
      oauth_failed: 'Sign-in did not finish. Please try again.',
      processing: 'Signing you in…',
      complete_setup: 'Almost done',
      your_name: 'Your name',
      complete_setup_btn: 'Create family and continue',
      login_apple: 'Log in with Apple',
      signup_apple: 'Sign up with Apple',
    },
    rooms: {
      kitchen: 'Kitchen',
      living_room: 'Living Room',
      dining_room: 'Dining Room',
      bedroom: 'Bedroom',
      bathroom: 'Bathroom',
      toilet: 'Toilet',
      hallway: 'Hallway',
      entryway: 'Entryway',
      laundry_room: 'Laundry Room',
      garage: 'Garage',
      office: 'Office',
      playroom: 'Playroom',
      nursery: 'Nursery',
      patio: 'Patio/Balcony',
      garden: 'Garden',
      storage: 'Storage',
      basement: 'Basement',
      attic: 'Attic',
      child_bedroom: 'Child\'s room',
    },
    onboarding: {
      title: "Let's get started",
      family_name: 'Family name',
      adults: 'Adults',
      children: 'Children',
      member_name: 'Name',
      username: 'Username',
      pin_placeholder: 'PIN (4 digits)',
      role_admin: 'Admin',
      role_member: 'Member',
      next: 'Next',
      back: 'Back',
      confirm: 'Looks good',
      processing: 'Saving…',
      step_rooms_title: 'Set up your home',
      step_rooms_help: 'Click on rooms below to add them to your house. If you have a room or space that is not in the list, you can add it manually.\nYou can always edit, add and remove rooms in your admin section.',
      custom_room_placeholder: 'Custom room name',
      add_custom: 'Add custom',
      remove: 'Remove',
      step_review_title: 'Review',
      review_family: 'Family',
      review_members: 'Members',
      review_rooms: 'Rooms',
      error_generic: 'Something went wrong while saving',
      // Step 1 helper/validation
      step_members_help: 'Add your family members here.\nGive each family member a name and a username.\nEach family member must have a PIN code so they can log in.',
      validation_incomplete: 'Some fields are not filled out correctly',
      adult_label: 'Adult',
      child_label: 'Child',
      privacy_note: 'This information is only used to set up your family. We do not, and never will, share or sell your data or personal information to anyone.',
      child_age: 'Age',
      child_room_name: '{{name}}\'s room',
      child_room_name_s: '{{name}}\'s room',
      hint_members: 'You can always add or change family members later under Admin → Members.',
      hint_rooms: 'Each child gets their own room, so their own tasks are in the right place. You can always change rooms later under Admin → Rooms.',
      step_tasks_title: 'Tasks to get you started',
      step_tasks_help: 'We\'ve picked a few tasks that suit your rooms and your children\'s ages. Untick the ones you don\'t want — you can change everything later.',
      starters_unavailable: 'The suggestions could not be loaded. You can skip this step and add tasks later.',
      tasks_for_child: '{{name}}\'s own tasks',
      tasks_shared: 'For everyone (anyone can take them)',
      whole_home: 'Whole home',
      hint_tasks: 'There are many more standard tasks under Admin → Tasks, and you can make your own there too.',
      skip_step: 'Skip this step',
      step_rewards_title: 'Rewards to aim for',
      step_rewards_help: 'Children stay motivated when the first reward is within reach. These are set so the first one can be reached in a few days.',
      rewards_for_child: 'For {{name}}',
      rewards_family: 'For the whole family (everyone\'s stars count)',
      hint_rewards: 'You can always add your own rewards, or change these, under Admin → Rewards.',
      review_starters: 'To get you started',
      review_tasks_one: '{{n}} task',
      review_tasks_other: '{{n}} tasks',
      review_rewards_one: '{{n}} reward',
      review_rewards_other: '{{n}} rewards',
      create_family: 'Create our family',
      done_title: 'Your family is ready!',
      done_made: 'We made {{tasks}} tasks and {{rewards}} rewards for you.',
      done_try: 'Try the first task now: switch to a child with "{{switch}}" at the top, and tap "{{start}}" on a task.',
      done_admin: 'When a task is done, approve it under "{{approve}}" — then the stars are added.',
      go_home: 'Go to the family\'s home',
      go_admin: 'Go to Admin',
    },
    common: {
      save: 'Save',
      take_photo: 'Take photo',
      cancel: 'Cancel',
      ok: 'OK',
      loading: 'Loading...',
      all: 'All',
      remove_item: 'Remove item',
      remove_image: 'Remove Image',
      error_generic: 'Something went wrong. Please try again.',
      you: 'You',
      stars_one: '{{n}} star',
      stars_other: '{{n}} stars',
      update_ready: 'A new version of AntNest is ready.',
      update_now: 'Update',
    },
    header: {
      stats: 'Stats',
      app_title: 'AntNest',
      home: 'Home',
      tasks: 'Tasks',
      calendar: 'Calendar',
      rewards: 'Rewards',
      settings: 'Settings',
      profile: 'Profile',
      admin: 'Admin',
      logout: 'Logout',
      points: 'points',
      settings_tooltip: 'Settings',
      menu_open: 'Open menu',
      menu_close: 'Close menu',
      nav_menu: 'Menu',
      tab_bar_label: 'Main sections',
      switch_user: 'Switch user',
      master_admin: 'Master admin',
      change_picture: 'Change my picture',
      someone_else: 'Someone else\'s turn',
      for_grownups: 'For grown-ups',
      for_grownups_hint: 'Settings, approvals, log out',
      my_page: 'My page',
    },
    stats: {
      title: 'Stats',
      tab_me: 'Me',
      tab_family: 'Family',
      tab_tasks: 'Tasks',
      period_week: 'Week',
      period_month: 'Month',
      period_year: 'Year',
      period_all: 'All time',
      prev: 'Previous',
      next: 'Next',
      all_time: 'All time',
      tasks_done: 'tasks done',
      points: 'points',
      rewards: 'rewards',
      chart_points: 'Points over time',
      where_helped: 'Where I helped',
      no_room: 'No room',
      my_top_tasks: 'My most-done tasks',
      together: 'Together',
      who_helped: 'Who helped',
      everyone_over_time: 'Everyone over time',
      repeating_title: 'Tasks',
      per_task_title: 'Each task',
      times_done_axis: 'Times done',
      times: '{n}×',
      last_done: 'last: {date}',
      empty: 'Nothing here yet for this period.',
      family_hidden: 'Your family shows this only to grown-ups.',
      week_short: 'Wk {n}',
      enc_start: 'A fresh start — every task counts! 💪',
      enc_first: 'Your first task — well done! 🎉',
      enc_done: 'You finished {n} tasks — great job! ⭐',
      enc_more: 'More points than the period before — keep it up! 🚀',
      visibility_title: 'Stats: what children see',
      visibility_help: 'Choose how the Family tab looks for children. Grown-up admins always see everything.',
      visibility_team: 'Team view — everyone\'s share, no ranking (recommended)',
      visibility_full: 'Full comparison — ranked by points',
      visibility_admins: 'Hidden — only grown-ups see the family comparison',
      visibility_saved: 'Saved',
      team_view_note: 'Everyone\'s share of the family total',
    },
    calendar: {
      title: 'Calendar',
      coming_soon: 'Calendar is being built. Basic views and events will appear here.',
      view_day: 'Day',
      view_week: 'Week',
      view_month: 'Month',
      week_label: 'Week',
      no_events_for_day: 'No events',
      today_label: 'today',
      prev_day: 'Previous day',
      next_day: 'Next day',
      prev_week: 'Previous week',
      next_week: 'Next week',
      prev_month: 'Previous month',
      next_month: 'Next month',
      go_to_today: 'Today',
      not_allowed_edit_event: 'You can only change events you created yourself.',
      filters_title: 'Filter by member',
      single_view_button: 'Single view',
      family_view_button: 'Family view',
      no_events_for_range: 'No events in this range',
      no_events: 'No events in this period.',
      add_event: 'Add event',
      edit_event: 'Edit Event',
      event_form_title: 'Title',
      event_form_description: 'Description (optional)',
      event_form_location: 'Location (optional)',
      event_form_start: 'Start',
      event_form_end: 'End',
      event_form_all_day: 'All-day',
      event_form_attendees: 'Attendees',
      event_form_save: 'Save',
      event_form_cancel: 'Cancel',
      event_form_delete: 'Delete',
      delete_confirm_title: 'Delete Event',
      delete_confirm_message: 'Are you sure you want to delete this event?',
      success_created: 'Event created',
      success_updated: 'Event updated',
      success_deleted: 'Event deleted',
      error_generic: 'Something went wrong',
      validation_title_required: 'Title is required',
      validation_end_after_start: 'End must be after start',
      event_form_image: 'Event image (optional)',
      remove_image: 'Remove image',
      create_calendar_event: 'Create calendar event',
      event_modal_title_create: 'Create calendar event',
      event_modal_title_edit_prefix: 'Edit'
      ,
      reminder_label: 'Reminder',
      reminder_none: 'No reminder',
      reminder_10m: '10 minutes before',
      reminder_30m: '30 minutes before',
      reminder_1h: '1 hour before',
      reminder_12h: '12 hours before',
      reminder_1d: '1 day before',
      nothing_planned: 'Nothing planned yet',
      nothing_this_month: 'Nothing planned this month',
      today_day: 'Today, {{day}}',
      tomorrow_day: 'Tomorrow, {{day}}',
      at_time: 'At {{time}}',
      all_day_label: 'All day',
      with_names: 'with {{names}}',
      task_label: 'Task',
      everyone: 'Everyone',
      back: 'Back',
      due_today: 'Finish today',
      due_today_at: 'Finish today by {{time}}',
      due_tomorrow: 'Finish by tomorrow',
      due_tomorrow_at: 'Finish tomorrow by {{time}}',
      due_later: 'Finish by this day',
      due_later_at: 'Finish by {{time}}',
      overdue_since: 'Overdue since {{day}}',
      planned: 'Planned',
    },
    settings: {
      title: 'Settings',
      subtitle: 'Manage your account and application preferences',
      dark_mode_section: 'Dark Mode',
      current_theme: 'Current Theme',
      following_system: 'Following system preference',
      manual_selection: 'Manual selection',
      follow_system_label: 'Follow system preference',
      auto_switch_hint: 'Automatically switch between light and dark mode based on your operating system settings',
      enable_dark_mode: 'Enable dark mode',
      manual_toggle_hint: 'Manually toggle between light and dark themes',
      light_mode: 'Light Mode',
      dark_mode: 'Dark Mode',
      more_settings_title: 'More Settings Coming Soon',
      more_settings_desc: 'Additional customization options will be available here, including notification preferences, privacy settings, and account management features.',
      language_section: 'Language',
      language_hint: 'Choose your preferred language for the interface',
      back: 'Back',
      profile_parent: 'Parent. Edit name, picture and password',
      profile_member: 'Edit name, picture and PIN',
      look: 'Look',
      auto: 'Auto',
      tell_me_when: 'Tell me when',
      notif_this_device: 'Notifications on this device',
      notif_blocked_parent: 'Blocked in this browser. Allow notifications for this site in the browser\'s settings.',
      n_review_ready: 'A task needs approval',
      n_task_approved: 'My task is approved',
      n_reward_ready: 'A reward is ready to get',
      n_due_soon: 'A task of mine is due soon',
      n_overdue: 'A task of mine is late',
      n_new_task: 'A new task is free to take',
      n_event: 'Calendar reminders',
      daily_summary: 'Daily summary',
      daily_summary_time: 'Time for the daily summary',
      quiet_hours: 'Quiet hours',
      quiet_hours_hint: 'No notifications between these times',
      quiet_from: 'Quiet from',
      quiet_to: 'Quiet until',
      this_device: 'This device',
      change_pin: 'Change my PIN',
      switch_person: 'Switch person',
      refresh_session: 'Refresh this device',
      session_refreshed: 'This device is up to date.',
      locked_until: 'Locked until {{time}}',
      unlock: 'Unlock',
      unlocked: '@{{name}} can log in again.',
      log_out_device: 'Log out of this device',
      logout_confirm_title: 'Log out of this device?',
      logout_confirm_text: 'The whole {{family}} family is logged out here. A grown-up has to log in again before anyone can use AntNest on this device.',
      logout_confirm_yes: 'Log out',
      cancel: 'Cancel',
      n_reward_claimed: 'A reward is claimed',
    },
    rewards: {
      title: 'Rewards',
      subtitle: 'Track your progress and claim your rewards',
      your_points: 'Your Points',
      family_points: 'Family Points',
      claimed_rewards: 'Claimed Rewards',
      your_coins: 'Your Coins',
      individual_rewards: 'Individual Rewards',
      no_individual_rewards: 'No individual rewards available for you yet.',
      family_rewards: 'Family Rewards',
      no_family_rewards: 'No family rewards available yet.',
      goal_reached: 'Goal reached!',
      complete_suffix: 'complete',
      target_whole_family: 'Whole Family',
      claim_reward_button: 'Claim Reward!',
      family_claim_parent_only: 'Only a parent can claim family rewards',
      claimed_rewards_section: 'Claimed Rewards',
      no_claimed_rewards: 'No rewards claimed yet. Keep working on your tasks!',
      claimed_on: 'Claimed on',
      unknown_date: 'Unknown date',
      unknown_member: 'Unknown Member',
      reward_header_alt: 'Reward header',
      show_more: 'Show more',
      show_fewer: 'Show fewer',
      points_abbrev: 'pts',
      need_more_prefix: 'Need',
      rewards_to_claim_heading: 'Rewards to claim',
      more_stars_for_prefix: '{{count}} more stars for',
      progress_counts_from: 'Progress counts points earned on or after {{date}}.',
      lifetime_total_hint: 'All-time total (separate from reward progress windows).',
      your_stars_label: 'your stars',
      ready_to_get: 'Ready to get',
      keep_saving: 'Keep saving for',
      n_more: '{{n}} more',
      get_it: 'Get it',
      grownup_gets: 'A grown-up gets this one',
      got_one: '{{n}} reward you got',
      got_other: '{{n}} rewards you got',
      see_all: 'See all',
      counting_since: 'Counting stars since {{date}}',
      nothing_yet: 'No rewards yet. Ask a grown-up to add some!',
      claim_failed: 'Could not get the reward. Please try again.',
    },
    dashboard: {
      welcome_back: 'Welcome back',
      subheading: "Here's what's happening in your family today",
      leaderboard_title: 'Family Leaderboard',
      family_member: 'Family Member',
      tasks_title: 'Tasks',
      available_tasks: 'Available Tasks',
      no_available_tasks: 'No tasks available at the moment!',
      my_active_tasks: 'My Active Tasks',
      no_my_tasks: "You don't have any active tasks!",
      recently_completed_tasks: 'Recently Completed Tasks',
      completed_tasks: 'Completed Tasks',
      no_completed_tasks: 'No completed tasks yet!',
      manage_all_tasks: 'Manage All Tasks',
      my_rewards_title: 'My Rewards',
      my_points: 'My Points',
      available_rewards: 'Available Rewards',
      claimed_rewards: 'Claimed Rewards',
      next_reward_goal: 'Next Reward Goal',
      all_rewards_claimed: 'All rewards claimed! Great job!',
      reward_ready: 'Ready to claim!',
      showing_rewards_help: 'Showing: Your personal rewards and family-wide rewards',
      claim_now: 'Claim Now!',
      locked: 'Locked',
      go_to_rewards: 'Go to Rewards',
      my_tasks_primary_title: 'My tasks',
      my_tasks_home_hint: 'These are only tasks assigned to you. To pick new tasks, open Tasks.',
      due_overdue: 'Overdue',
      due_today: 'Due today',
      due_no_deadline: 'No due date',
      tasks_heading_possessive: '{{name}}’s tasks',
      missions_banner: 'You have {{count}} tasks ready today. Let’s go!',
      missions_banner_empty: 'No tasks waiting right now — nice work!',
      available_tasks_banner_one: 'You can earn more points! There is 1 task available.',
      available_tasks_banner_other: 'You can earn more points! There are {{count}} tasks available.',
      find_tasks_cta: 'Find a task',
      missions_heading: "Today's tasks",
      mission_cta: "Let's go!",
      hi_name: 'Hi {{name}}',
      goal_of: '{{have}} of {{need}} stars',
      goal_get_it: 'Get it',
      goal_none: 'No rewards to aim for yet. Ask a grown-up to add one!',
      today: 'Today',
      no_tasks_now: 'No tasks right now',
      waiting_one: '{{n}} star when a grown-up says yes',
      waiting_other: '{{n}} stars when a grown-up says yes',
      family_goal: 'Family goal',
      family_goal_more: 'Everyone\'s stars count. {{n}} to go for {{reward}}.',
      family_goal_ready: '{{reward}} is ready! A grown-up can get it for the family.',
      due_tomorrow: 'Tomorrow',
      due_in_days_one: 'In {{n}} day',
      due_in_days_other: 'In {{n}} days',
      more_stars_title: 'Want more stars?',
      more_stars_hint: 'Anyone can take these. Pick one when you feel like it!',
      see_all_can_take: 'See all {{n}}',
      take_failed: 'Could not take the task',
    },
    task_board: {
      title: 'Task Board',
      subtitle: 'Manage and track all family tasks',
      available_tasks: 'Available Tasks',
      no_available_tasks: 'No tasks available at the moment!',
      my_active_tasks: 'My Active Tasks',
      no_my_tasks: "You don't have any active tasks!",
      view: 'View',
      complete: 'Complete',
      awaiting_verification: 'Waiting for Approval',
      awaiting_verification_pill: 'Waiting for approval',
      tasks_assigned_to_others: 'Tasks Assigned to Others',
      no_tasks_assigned_to_others: 'No tasks are assigned to other users!',
      view_task: 'View Task',
      completed_tasks: 'Completed Tasks',
      no_completed_tasks: 'No completed tasks yet!',
      take_task: 'I\'ll take it',
      take_over_task: 'Take Over Task',
      unclaim_task: 'Give it back',
      your_tasks: 'Your tasks',
      can_take: 'You can take these',
      waiting_heading: 'Waiting for a grown-up',
      others_tasks: 'Other people\'s tasks',
      done_one: '{{n}} task done',
      done_other: '{{n}} tasks done',
      moved_to_yours: '{{task}} is now in Your tasks',
      nothing_to_take: 'No tasks to take right now',
      no_your_tasks: 'You have no tasks right now.',
    }
    ,
    login: {
      title: 'AntNest',
      email_label: 'Email',
      password_label: 'Password',
      logging_in: 'Logging in...',
      login_button: 'Login',
      demo_account: 'Demo Account',
      demo_email: 'Email: admin@family.com',
      demo_password: 'Password: admin'
    },
    member_chooser: {
      title: 'Choose Member',
      sign_out_device: 'Sign out of this device',
      sign_out_device_hint:
        'Clears this browser’s family login. Use this if you’re stuck or want to sign in as guardian with Google/email again.',
      who_title: 'Who are you?',
      hi_name: 'Hi {{name}}!',
      enter_pin: 'Enter your PIN',
      log_in: 'Log in',
      use_password: 'Use password instead',
      use_pin: 'Use PIN instead',
      password_label: 'Password',
      delete_digit: 'Delete',
      back: 'Back',
      locked_until: 'Too many tries. Try again at {{time}}.',
      locked_later: 'Too many tries. Try again a bit later.',
      wrong_pin: 'That didn\'t work. Try again.',
    },
    user_profile: {
      user_not_found: 'User not found.',
      back_to_home: 'Back to Home',
      title: 'User Profile',
      profile_information: 'Profile Information',
      name: 'Name',
      email: 'Email',
      email_not_set: 'No email set',
      role: 'Role',
      admin: 'Admin',
      family_member: 'Family Member',
      points: 'Points',
      coins: 'Coins',
      edit_profile: 'Edit Profile',
      save_changes: 'Save Changes',
      cancel: 'Cancel',
      email_optional_label: 'Email (optional)',
      new_password_optional_label: 'New Password (optional)',
      name_placeholder: 'Enter your name',
      email_enter_placeholder: 'Enter email address',
      email_add_placeholder: 'Add email',
      username_for_member_login: 'Username (for member login)',
      pin_label: 'PIN (4–8 digits)',
      avatar: 'Avatar',
      avatar_hint: 'Click a preset or upload your own.',
      upload: 'Upload',
      avatar_updated: 'Avatar updated',
      avatar_update_failed: 'Failed to update avatar',
      linked_accounts: 'Linked social accounts',
      no_linked_accounts: 'No linked accounts.',
      unlink: 'Unlink',
      unlink_failed: 'Failed to unlink',
      unlink_confirm: 'Unlink {{provider}}?',
      account_unlinked: 'Account unlinked.',
      link_to_google: 'Link to Google',
      link_to_facebook: 'Link to Facebook',
      change_password: 'Change password',
      current_password: 'Current password',
      new_password: 'New password',
      password_changed: 'Password changed successfully',
      password_change_failed: 'Failed to change password',
      save_new_password: 'Save new password',
      pin_validation: 'PIN must be 4–8 digits (numbers only)',
      current_pin_label: 'Current PIN (only needed if you already have one)',
      current_pin_required: 'Enter your current PIN to change it.',
      current_pin_incorrect: 'Your current PIN is incorrect.',
      email_managed_by_login: 'Your email comes from how you sign in and can\'t be changed here.',
      username_validation: 'Username must be at least 2 characters',
      profile_updated: 'Profile updated',
      google_link_success: 'Google account linked successfully',
      facebook_link_success: 'Facebook account linked successfully',
      picture_type_bad: 'Please choose a PNG, JPG or WebP picture.',
      picture_too_big: 'That picture is too big (max 1 MB).',
      choose_picture: 'Choose this picture',
      or_pick_one: 'Or pick one of these',
      tap_picture: 'Tap the picture to choose a new one.',
      link_to_apple: 'Link to Apple',
    },
    admin: {
      header_title: 'Admin Panel',
      header_subtitle: "Manage your family's tasks, rewards, and members",
      tab_tasks: 'Tasks',
      tab_rooms: 'Rooms',
      tab_members: 'Members',
      tab_pending: 'Pending',
      tab_rewards: 'Rewards',
      tab_tools: 'Tools'
    },
    checklist_editor: {
      back_to_admin: 'Back to Admin Panel',
      edit_checklist_for: 'Edit Checklist',
      add_new_item: 'Add New Checklist Item',
      add_item: 'Add Item',
      current_items: 'Current Checklist Items',
      no_items: 'No checklist items yet. Add some above!',
      cancel: 'Cancel',
      save: 'Save'
    },
    task_details: {
      loading: 'Loading...',
      title: 'Task Details',
      room: 'Room',
      points: 'Points',
      status: 'Status',
      status_completed: 'Completed',
      status_active: 'Active',
      assigned_to: 'Assigned To',
      due_date: 'Due Date',
      tools_required: 'Tools Required',
      checklist: 'Checklist',
      task_completed: 'Task Completed',
      completed_by: 'Completed by',
      message: 'Message',
      submit_for_verification: 'Submit for Verification',
      close: 'Close',
      preview_title: 'Preview task',
      preview_room_placeholder: 'Suggested when you use this task',
      preview_checklist_hint: 'Steps (preview only — check off after you add the task)',
      view: 'View'
    },
    task_flow: {
      start_task: 'Let\'s go',
      tools_ready_title: 'Have you got everything?',
      tools_ready_subtitle: 'You will need these tools. Gather them before you continue.',
      ready_to_start_title: 'Ready?',
      ready_to_start_body: 'Are you ready to start the task?',
      tools_ready_no: 'Not yet',
      tools_ready_yes: "Yes, let's go",
      finish_step: 'I did it',
      final_put_tools_message:
        'Well done! Please put all tools back where they belong. Then tap the button below to complete the task.',
      complete_task_button: 'All done',
      no_checklist_warning: 'This task has no steps yet. Ask a parent to add checklist steps before you can start.',
      view_checklist_readonly_hint: 'You can check off steps after you tap Start task.',
      progress_label: 'Progress',
      step_counter_prefix: 'Step',
      not_assigned: 'Not assigned yet',
      mission_label: 'Mission',
      celebrate_heading: 'Awesome job!',
      celebrate_subtitle: 'You finished {{title}}',
      celebrate_note: 'Great work — keep it up!',
      celebrate_button: 'Back to home',
      step_of: 'Step {{n}} of {{total}}',
      back_step: 'Go back one step',
      tidy_title: 'Almost there!',
      tidy_subtitle: 'Put these back where they live',
      tidy_no_tools: 'Is everything finished? Then tap All done.',
      back_to_steps: 'Go back to the steps',
      celebrate_stars_title: '{{n}} stars are on the way',
      celebrate_stars_body: 'You get them when a grown-up says yes',
      see_details: 'See details',
      step_tools: 'You need:',
      where_room: 'Where: {{room}}',
      celebrate_stars_got_title: 'You got {{n}} stars',
      celebrate_stars_got_body: 'Approved and added to your stars',
      tidy_put_all_back: 'Put all these things back where they belong',
    },
    reward_celebration: {
      congratulations: 'Congratulations!',
      you_have_a_reward: 'You have a reward!',
      check_rewards_prompt: 'Check your rewards section to claim it!'
    }
    ,
    notifications: {
      task_verified_heading: 'Well done!',
      task_verified_message: '{{task}} was verified by {{admin}}. You have been awarded {{points}} points!',
      task_verified_message_zero_points: '{{task}} was verified by {{admin}}.',
      dismiss_ok: 'OK',
      dismiss_aria: 'Dismiss',
      admin_pending_verification_one: '1 task is waiting for your verification.',
      admin_pending_verification_other: '{{count}} tasks are waiting for your verification.',
      approved_said_yes: '{{admin}} said yes!',
      approved_for: 'for {{task}}',
      approved_note: '{{admin}} says: {{note}}',
      approved_now_you_have: 'Now you have {{n}} stars',
      approved_reward_ready: '{{reward}} is ready!',
      approved_more_to_goal: '{{n}} more stars to {{reward}}',
      approved_see_reward: 'See my reward',
      approved_not_now: 'Not now',
      approved_ok: 'Yay!',
    }
    ,
    admin_panel: {
      header_title: 'Admin Panel',
      header_subtitle: "Manage your family's tasks, rewards, and members",
      tab_tasks: 'Tasks',
      tab_rooms: 'Rooms',
      tab_members: 'Members',
      tab_pending: 'Pending',
      tab_rewards: 'Rewards',
      tab_tools: 'Tools',
      task_management: 'Task Management',
      create_new_task: 'Create New Task',
      active_tasks: 'Active Tasks',
      active_tasks_help: 'Tasks that are currently in progress or available',
      edit: 'Edit',
      remove: 'Remove',
      mark_as_incomplete: 'Mark as incomplete',
      confirm_mark_incomplete_title: 'Are you sure that you want to mark this task as not completed',
      cancel: 'Cancel',
      confirm_yes_im_sure: "Yes I'm sure",
      no_active_tasks: 'No active tasks found.',
      completed_tasks: 'Completed Tasks',
      completed_tasks_help: 'Tasks that have been finished and verified',
      no_completed_tasks: 'No completed tasks found.',
      show_more_completed_tasks: 'Show more',
      show_less_completed_tasks: 'Show less',
      room_management: 'Room Management',
      add_new_room: 'Add New Room',
      member_management: 'Member Management',
      add_new_member: 'Add New Member',
      pending_verification: 'Pending Task Verification',
      verify_this_task: 'Verify this task',
      self_verify_not_allowed_hint: 'Another admin must verify this task when more than one admin exists.',
      points_not_awarded_section: 'Verified — missing points',
      points_not_awarded_help:
        'These tasks were marked verified before points were saved correctly. Use “Award points” once to credit the member who completed the task.',
      award_missing_points: 'Award points',
      no_tasks_needing_points: 'All verified tasks have points recorded.',
      verify_success: 'Task verified successfully. Points have been awarded.',
      verify_repaired_points_success:
        'Missing points have been awarded to the member who completed this task.',
      verify_already_done: 'This task is already verified and points are recorded.',
      verify_misattribution_credit_confirm:
        'The system shows this task as already paid out. If the member’s balance never increased, OK to apply a one-time credit now? (Do not use if points were already received.)',
      verify_misattribution_success: 'One-time credit applied. Ask the member to refresh or sign in again to see updated points.',
      verify_misattribution_already_done: 'A one-time misattribution credit was already applied for this task.',
      verify_already_done_hint:
        'If the member still has no points, click Award points again and confirm the one-time credit, or clear points_awarded_on_verification for this task in the database and retry.',
      no_pending_verify: 'No tasks waiting for verification.',
      reward_management: 'Reward Management',
      create_new_reward: 'Create New Reward',
      claimed: 'Claimed',
      available: 'Available',
      tool_management: 'Tool Management',
      create_new_tool: 'Create New Tool',
      no_tools_msg: 'No tools created yet. Create your first tool to get started!',
      // Modal titles
      task_modal_title_create: 'Create New Task',
      task_modal_title_edit_prefix: 'Edit Task',
      reward_modal_title_create: 'Create New Reward',
      reward_modal_title_edit_prefix: 'Edit Reward',
      tool_modal_title_create: 'Create New Tool',
      tool_modal_title_edit_prefix: 'Edit Tool',
      room_modal_title_add: 'Add New Room',
      room_modal_title_edit: 'Edit Room',
      delete_room_title: 'Delete Room',
      delete_room_btn: 'Delete',
      delete_btn: 'Delete',
      delete_tool_title: 'Delete tool',
      delete_reward_title: 'Delete reward',
      delete_confirm_prefix: 'Are you sure you want to delete',
      delete_room_prompt_prefix: 'Are you sure you want to permanently delete the room',
      delete_room_blocked_message: 'This room cannot be deleted because one or more tasks are using it. Please delete or reassign those tasks first.',
      go_to_tasks_btn: 'View Tasks',
      member_modal_title_add: 'Add New Family Member',
      member_edit_title: 'Edit Family Member',
      // General buttons
      cancel_btn: 'Cancel',
      save_changes_btn: 'Save Changes',
      create_task_btn: 'Create Task',
      create_reward_btn: 'Create Reward',
      create_tool_btn: 'Create Tool',
      add_room_btn: 'Add Room',
      add_member_btn: 'Add Member',
      remove_image_btn: 'Remove Image',
      remove_item_btn: 'Remove Item',
      remove_task_btn: 'Remove Task',
      // Task modal content
      task_title_label: 'Task Title',
      task_title_placeholder: 'Enter task title',
      description_label: 'Description (Optional)',
      description_placeholder: 'Enter task description',
      room_label: 'Room',
      no_room_option: 'No Room',
      assignment_label: 'Task Assignment',
      available_anyone_option: 'Available to Anyone',
      assign_to_member_prefix: 'Assign to',
      assignment_hint_assigned: 'This task will only be visible to the assigned member',
      assignment_hint_anyone: 'This task will be available for any family member to claim',
      points_label: 'Points to Award',
      points_placeholder: 'Points',
      due_date_label: 'Due Date',
      recurrence_label: 'Recreate Every X Days (0 = no recurrence)',
      recreate_on_validation_label: 'Recreate on validation',
      recreate_on_validation_help: 'Immediately recreate after admin validation',
      days_to_complete_label: 'Number of days to complete task',
      days_to_complete_placeholder: 'e.g., 7',
      days_to_complete_help: 'If set, due date will be creation date + this many days.',
      auto_available_label: 'Automatically Available',
      header_image_label: 'Header Image (Optional)',
      current_header_image_label: 'Current header image',
      checklist_label: 'Checklist Items',
      checklist_item_placeholder: 'Enter checklist item text',
      add_checklist_item_btn: 'Add Checklist Item',
      images_optional_label: 'Images (optional):',
      camera_title: 'Camera',
      required_tools_label: 'Required Tools',
      no_tools_available_msg: 'No tools available. Create tools first in the Tools tab.',
      // Remove task modal
      remove_task_title: 'Remove Task',
      remove_task_prompt_prefix: 'Are you sure you want to remove the task',
      irreversible_note: 'This action cannot be undone.',
      // Reward modal content
      reward_title_label: 'Reward Title',
      reward_title_placeholder: 'Enter reward title',
      reward_desc_label: 'Description',
      reward_desc_placeholder: 'Enter reward description',
      points_required_label: 'Points Required',
      reward_progress_start_label: 'Points count from (date)',
      reward_progress_start_hint: 'Only points earned from the start of this day (your time) count toward this reward. Lifetime totals elsewhere are unchanged.',
      target_type_label: 'Target Type',
      target_type_individual: 'Individual',
      target_type_family: 'Family',
      assign_member_label: 'Assign to Family Member',
      select_member_option: 'Select a family member',
      current_header_image_id_label: 'Current header image ID',
      // Tool modal content
      tool_name_label: 'Tool Name',
      tool_name_placeholder: 'Enter tool name',
      tool_image_label: 'Tool Image (Optional)',
      current_tool_image_label: 'Current tool image',
      // Room modal content
      room_name_label_required: 'Room Name *',
      room_name_placeholder: 'Enter room name',
      // Member modal content
      member_name_label_required: 'Name *',
      member_name_label: 'Name',
      member_name_placeholder: 'Enter member name',
      member_pin_label_optional: 'PIN (optional)',
      member_pin_hint_optional: 'Leave blank to keep current PIN. PIN must be 4–8 digits.',
      member_is_admin_label: 'Admin access',
      member_update_success: 'Member updated',
      member_update_failed: 'Failed to update member',
      member_password_label_required: 'Password *',
      member_password_placeholder: 'Enter password',
      member_email_label_optional: 'Email (Optional)',
      member_email_placeholder: 'Enter email address',
      admin_access_label: 'Admin Access',
      member_type_label: 'Member Type',
      member_type_adult: 'Adult',
      member_type_child: 'Child',
      err_room_name: 'Please give the room a name.',
      err_room_create: 'The room couldn\'t be created. Please try again.',
      err_room_update: 'The room couldn\'t be saved. Please try again.',
      err_generic_missing: 'Something is missing. Please close this and try again.',
      err_task_title_points: 'Please give the task a title and a number of stars.',
      err_checklist_empty: 'Please add at least one step with text.',
      err_task_create: 'The task couldn\'t be created. Please try again.',
      err_task_update: 'The task couldn\'t be saved. Please try again.',
      err_task_remove: 'The task couldn\'t be removed. Please try again.',
      err_template_use: 'A task couldn\'t be made from this template. Please try again.',
      err_save_template: 'The template couldn\'t be saved. Please try again.',
      saved_template: 'Saved as a template.',
      err_header_image: 'The picture couldn\'t be saved. Please try again.',
      err_reward_title: 'Please give the reward a title.',
      err_reward_points: 'Please enter how many stars the reward needs.',
      err_reward_member: 'Please choose who the reward is for.',
      err_reward_create: 'The reward couldn\'t be created. Please try again.',
      err_reward_update: 'The reward couldn\'t be saved. Please try again.',
      reward_updated: 'Reward saved.',
      err_tool_name: 'Please give the tool a name.',
      err_tool_create: 'The tool couldn\'t be created. Please try again.',
      err_tool_update: 'The tool couldn\'t be saved. Please try again.',
      err_member_required: 'A new member needs a name, a username and a PIN.',
      err_pin_format: 'The PIN must be 4–8 digits (numbers only).',
      err_username_short: 'The username must be at least 2 characters.',
      err_member_create: 'The member couldn\'t be added. Please try again.',
      verify_misattribution_title: 'Give the stars now?',
      verify_misattribution_yes: 'Yes, give them',
      status_assigned: 'For {{name}}',
      status_open: 'Anyone can take it',
      comes_back_on: 'Comes back {{date}}',
      anyone_can_take: 'Anyone can take the next one (otherwise it goes back to the same person)',
      repeat_hint: 'With "repeat every" set, the next one is due that many days after approval and shows up "days to complete" days before it\'s due.',
      step_tools_label: 'Tools for this step',
      step_tools_hint: 'Choose the task\'s tools further down first. Then you can pick the ones this step needs.',
      room_picture_label: 'Picture of the room (optional)',
      room_picture_hint: 'A photo helps children see which room a task is in.',
      room_picture_add: 'Add picture',
      room_picture_change: 'Change picture',
      room_picture_failed: 'The room was saved, but the picture could not be uploaded. Please try again.',
      step_drag: 'Drag to move this step',
      step_move_up: 'Move step up',
      step_move_down: 'Move step down',
      task_saving: 'Saving task…',
      task_saved: 'Task saved',
      try_again: 'Try again',
      deadline_question: 'Deadline',
      deadline_none: 'No deadline',
      deadline_within: 'Must be done within',
      days_unit: 'days',
      repeat_question: 'When it\'s approved…',
      repeat_none: 'it\'s finished – it doesn\'t come back',
      repeat_now: 'it comes back right away',
      repeat_after: 'it comes back after',
      next_question: 'Who gets the next one?',
      next_same: '{{name}} again',
      next_anyone: 'Anyone can take it',
      summary_title: 'What happens:',
      due_this_time_label: 'Deadline this time (optional)',
      due_this_time_help: 'Leave it empty and the deadline above is used.',
      sum_now_person: '{{name}} gets the task now.',
      sum_now_anyone: 'Anyone in the family can take it now.',
      sum_within_one: 'It must be done within {{n}} day.',
      sum_within_other: 'It must be done within {{n}} days.',
      sum_end: 'When you approve it, it\'s finished.',
      sum_back_now_same: 'When you approve it, it comes straight back to {{name}}.',
      sum_back_now_anyone: 'When you approve it, it comes straight back for anyone to take.',
      sum_back_after_same_one: 'When you approve it, it comes back to {{name}} after {{n}} day.',
      sum_back_after_same_other: 'When you approve it, it comes back to {{name}} after {{n}} days.',
      sum_back_after_anyone_one: 'When you approve it, it comes back after {{n}} day for anyone to take.',
      sum_back_after_anyone_other: 'When you approve it, it comes back after {{n}} days for anyone to take.',
      sum_then_within_one: 'Then there is {{n}} day to do it.',
      sum_then_within_other: 'Then there are {{n}} days to do it.',
      repeat_next: 'the next task is',
      next_ready_after: 'Ready after',
      hours_unit: 'hours',
      waits_for: 'Waiting for: {{task}}',
      then_next: 'Then: {{task}}',
      sum_next: 'When you approve it, "{{task}}" comes next.',
      sum_next_wait_one: 'It\'s ready {{n}} hour after this one was finished.',
      sum_next_wait_other: 'It\'s ready {{n}} hours after this one was finished.',
      sum_next_waits: 'Until then, the children can\'t see "{{task}}".',
      reward_saving: 'Saving reward…',
      reward_saved: 'Reward saved',
      assignment_hint_each: 'Each person gets their own copy: in their own calendar, approved on its own, with their own stars.',
      err_choose_person: 'Choose at least one person.',
      names_and: '{{first}} and {{last}}',
      sum_now_each: '{{names}} each get their own copy now.',
      sum_back_now_each: 'When you approve someone\'s copy, theirs comes straight back.',
      sum_back_after_each_one: 'When you approve someone\'s copy, theirs comes back after {{n}} day.',
      sum_back_after_each_other: 'When you approve someone\'s copy, theirs comes back after {{n}} days.',
      shared_ready: 'Ready',
      remove_shared_note: 'It is removed for everyone it was given to. Copies already finished stay in the history.',
      repeat_plan: 'it comes back on a fixed plan',
      plan_weekly: 'every week on',
      plan_every: 'every',
      plan_every_unit: 'days',
      plan_from: 'starting',
      sum_plan_person: '{{name}} gets it.',
      sum_plan_anyone: 'Anyone in the family can take it.',
      sum_plan_each: '{{names}} each get their own copy.',
      sum_plan_weekly: 'It shows up every {{days}}.',
      sum_plan_every_one: 'It shows up every day, starting {{date}}.',
      sum_plan_every_other: 'It shows up every {{n}} days, starting {{date}}.',
      sum_plan_back: 'When you approve it, it comes back on the next planned day.',
      plan_week_every_one: 'every week',
      plan_week_every_other: 'every {{n}} weeks',
      plan_on: 'on',
      plan_monthly: 'every month',
      plan_month_every_one: 'every month',
      plan_month_every_other: 'every {{n}} months',
      plan_the: 'on the',
      plan_which: 'which',
      plan_weekday: 'weekday',
      plan_nth_1: 'first',
      plan_nth_2: 'second',
      plan_nth_3: 'third',
      plan_nth_4: 'fourth',
      plan_nth_last: 'last',
      plan_on_date: 'on day',
      plan_date_unit: 'of the month',
      sum_plan_weekly_every: 'It shows up every {{n}} weeks, on {{days}}.',
      sum_plan_every_open_one: 'It shows up every day.',
      sum_plan_every_open_other: 'It shows up every {{n}} days.',
      sum_plan_month_day_one: 'It shows up on day {{day}} of every month.',
      sum_plan_month_day_other: 'It shows up on day {{day}} every {{n}} months.',
      sum_plan_month_nth_one: 'It shows up on the {{nth}} {{weekday}} of every month.',
      sum_plan_month_nth_other: 'It shows up on the {{nth}} {{weekday}} every {{n}} months.',
      step_timer_label: 'Timer (optional)',
      step_timer_minutes: 'Timer minutes',
      step_timer_seconds: 'Timer seconds',
      step_timer_min: 'min',
      step_timer_sec: 'sec',
      step_timer_clear: 'No timer',
      step_timer_hint: 'E.g. "Plank – 0:45". The person doing the task starts it and hears a sound when time is up.',
    },
    parent: {
      to_approve_one: '{{n}} task to approve',
      to_approve_other: '{{n}} tasks to approve',
      finished: '{{name}} finished {{task}}',
      review: 'Review',
      nothing_to_approve: 'Nothing to approve right now',
      family: 'Family',
      open_one: '{{n}} task to do',
      open_other: '{{n}} tasks to do',
      open_none: 'No tasks to do',
      set_up: 'Set up',
      tasks: 'Tasks',
      rewards: 'Rewards',
      members: 'Family',
      rooms_tools: 'Rooms and tools',
      lock: 'Lock',
      settings: 'Settings',
      more_waiting: '+{{n}} more',
      your_tasks: 'Your own tasks',
    },
    approve: {
      title: 'Approve tasks',
      approve: 'Approve',
      not_done: 'Not done yet',
      note_so_neat: 'So neat!',
      note_well_done: 'Well done!',
      note_thank_you: 'Thank you!',
      reason_look_again: 'Almost there — have another look',
      reason_steps_missing: 'Some steps aren\'t done yet',
      reason_do_together: 'Let\'s do it together',
      pick_reason: 'What should {{name}} hear?',
      cancel: 'Cancel',
      steps_done_one: 'The step is done',
      steps_done_other: 'All {{n}} steps done',
      approved_toast: '{{task}} approved. {{name}} got {{stars}}.',
      undo: 'Undo',
      sent_back_toast: '{{task}} went back to {{name}}.',
      today_at: 'Today {{time}}',
      yesterday_at: 'Yesterday {{time}}',
      from_grownup: 'A grown-up says: {{reason}}',
      back: 'Back',
      self_blocked: 'Another grown-up must approve your own task.',
    },
    my_settings: {
      title: 'My settings',
      language: 'Language',
      same_as_device: 'Same as this device',
      look: 'Light or dark',
      light: 'Light',
      dark: 'Dark',
      notifications: 'Notifications',
      on: 'On',
      off: 'Off',
      notif_turn_on: 'Turn on for this device',
      notif_turned_on: 'Notifications are on.',
      notif_turned_off: 'Notifications are off.',
      notif_parent_off: 'A grown-up has turned your notifications off.',
      notif_parent_on: 'A grown-up wants your notifications on.',
      notif_unsupported: 'This device can\'t show notifications from AntNest.',
      notif_blocked: 'Notifications are blocked in this browser. Ask a grown-up to allow them in the browser\'s settings.',
      timer_sound: 'Timer sound',
      timer_sound_help: 'The sound when a step\'s timer reaches zero. Tap to hear it.',
    },
    parent_notifications: {
      title: 'Children\'s notifications',
      help: 'Choose who decides. "Always on" means the child can\'t turn them off; "Off" means nothing is sent to the child.',
      child: 'The child decides',
      on: 'Always on',
      off: 'Off',
      saved: 'Saved.',
    },
    handover: {
      title: 'Rewards to hand over',
      help: 'These were claimed. Give them to the child, then tap Handed over.',
      got: '{{name}} got {{reward}}',
      handed_over: 'Handed over',
      done_toast: '{{reward}} handed over to {{name}}.',
    },
    library: {
      access_denied: 'Only grown-ups can open this.',
      title: 'Task library',
      intro: 'Start from a standard task or a template you saved from a task. Only your family sees "My templates".',
      age: 'Age',
      age_group: 'Age group',
      age_all: 'All ages',
      age_2_3: '2–3 years',
      age_4_5: '4–5 years',
      age_6_7: '6–7 years',
      age_8_9: '8–9 years',
      age_10_12: '10–12 years',
      age_13_plus: '13+ years',
      standard_title: 'Standard tasks',
      none_standard: 'No standard tasks for this language and age.',
      badge_standard: 'Standard',
      my_title: 'My templates',
      my_intro: 'Saved with "Save as template" in the task form. Only your family can see these.',
      none_my: 'No saved templates yet. Make a task and use "Save as template", or change the age filter.',
      badge_my: 'My template',
      use: 'Use',
      save_as_template: 'Save as template',
      days: 'Days',
      stars: 'Stars',
      tool_name: 'Tool name',
      tool_name_ph: 'e.g. Vacuum cleaner',
      username_required: 'Username (required)',
      username_ph: 'Choose a username',
      pin_label: 'PIN (4–8 digits)',
      pin_ph: 'Enter a PIN',
      adults: 'Grown-ups',
      show: 'Show',
      hide: 'Hide',
      added_to_tasks: '"{{title}}" was added to your tasks',
      chain_title: '{{n}} tasks that follow each other',
      chain_wait_one: 'next one ready after {{n}} hour',
      chain_wait_other: 'next one ready after {{n}} hours',
      chain_loops: 'Then it starts over.',
    },
    deletion: {
      delete_member: 'Delete member',
      delete_member_title: 'Delete {{name}}?',
      delete_member_text: '{{name}} is removed from the family straight away and signed out on every device.',
      delete_member_lost: 'All of {{name}}\'s stars, statistics, tasks and rewards will be deleted.',
      undo_30: 'You can undo this for 30 days, until {{date}}. After that, everything is deleted for good and can\'t be recovered.',
      delete_member_yes: 'Yes, delete',
      member_deleted_toast: '{{name}} has been deleted. You can restore them for 30 days.',
      recently_deleted: 'Recently deleted',
      recently_deleted_hint: 'You can restore these members until the date shown. After that, they and all their data are deleted for good.',
      gone_on: 'Deleted for good on {{date}}',
      restore: 'Restore',
      member_restored_toast: '{{name}} is back in the family. Notifications need to be turned on again.',
      family_section: 'Delete family',
      delete_family_row: 'Delete the whole family',
      delete_family_hint: 'Everyone and everything in the family. Can be undone for 30 days.',
      delete_family_title: 'Delete the whole family?',
      delete_family_text: 'Everyone in {{family}} is signed out at once and can\'t use AntNest until the family is restored.',
      delete_family_lost: 'Everything will be deleted: all members, stars, statistics, tasks, rewards, events and pictures.',
      type_family_name: 'To confirm, type the family name: {{family}}',
      delete_family_yes: 'Delete the family',
      pending_title: '{{family}} is scheduled for deletion',
      pending_text: 'The family and all its data will be deleted for good on {{date}}. Until then, you can restore everything just as it was.',
      days_left: 'Days left: {{days}}',
      pending_not_admin: 'Only a grown-up with admin access can restore the family.',
      restore_family: 'Restore the family',
      family_restored_toast: 'Welcome back! The family has been restored. Notifications need to be turned on again.',
      log_out: 'Log out',
      cancel: 'Cancel',
    },
    pictures: {
      remove_picture: 'Remove picture',
      removed: 'Picture removed. You have the basic ant again.',
      unreadable: 'This picture couldn\'t be opened. Try another picture, or take a new photo.',
      rotate: 'Rotate',
      preparing: 'Preparing the picture…',
    },
    billing: {
      title: 'Subscription',
      trial_title: 'Free trial: {{days}} days left',
      trial_text: 'Your free trial ends on {{date}}. Choose a plan before then to keep using AntNest.',
      expired_title: 'Your free trial has ended',
      expired_text: 'Choose a plan to keep using AntNest. Your family\'s tasks, stars and rewards are all still here.',
      active_title_monthly: 'You pay monthly',
      active_title_yearly: 'You pay yearly',
      active_text: 'Your subscription renews on {{date}}.',
      grace_title: 'A payment didn\'t go through',
      grace_text: 'Please update your payment details by {{date}} to keep using AntNest.',
      cancelling_title: 'Your subscription is cancelled',
      cancelling_text: 'You can keep using AntNest until {{date}}. Choose a plan to continue after that.',
      exempt_title: 'AntNest is free for your family',
      exempt_text: 'You don\'t need to pay anything.',
      plans_title: 'Choose a plan',
      plans_intro: 'One subscription covers the whole family, on every device.',
      plan_monthly: 'Monthly',
      plan_yearly: 'Yearly',
      price_monthly: '{{price}} a month',
      price_yearly: '{{price}} a year',
      price_soon: 'Price coming soon',
      includes_family: 'The whole family, on every device',
      includes_tasks: 'All ready-made tasks and checklists',
      includes_cancel: 'Cancel any time',
      choose_monthly: 'Choose monthly',
      choose_yearly: 'Choose yearly',
      coming_soon: 'Payment opens soon. Until then, AntNest is free.',
      manage_title: 'Manage your subscription',
      manage_text: 'Change your plan, update your card, see receipts or cancel.',
      manage_button: 'Manage subscription',
      banner_trial: 'Your free trial ends in {{days}} days.',
      banner_trial_last_day: 'Your free trial ends today.',
      banner_payment: 'A payment didn\'t go through. Update your card by {{date}}.',
      banner_cancelling: 'Your subscription ends on {{date}}.',
      banner_action: 'See plans',
      paywall_title: 'AntNest is taking a break',
      paywall_text: 'Ask a grown-up to choose a plan. All your stars and tasks are safe and waiting for you.',
      manage_failed: 'The subscription page could not be opened. Please try again in a moment.',
    },
    pocket: {
      title: 'Pocket money',
      intro: 'Pocket money is worked out from the stars each child earns from approved tasks in a month. AntNest doesn\'t move any money: you pay as usual and mark it as paid.',
      no_children: 'Add a child to the family to give pocket money.',
      currency: 'Currency',
      for_child: 'Pocket money for {{name}}',
      how_question: 'How is it earned?',
      per_ten_label: '{{currency}} for every 10 stars',
      fixed_label: '{{currency}} a month, once {{name}} has earned at least',
      threshold_label: 'stars',
      threshold_help: '0 stars = always given (a fixed allowance).',
      bonus_label: 'Bonus:',
      bonus_every_label: '{{currency}} extra for every',
      bonus_stars_label: 'stars more',
      cap_question: 'Monthly cap — choose one',
      cap_none: 'No cap',
      cap_at: 'At most',
      cap_unit: '{{currency}} a month',
      cap_required: 'Choose a cap or no cap before saving.',
      example_title: 'For example, in a month with:',
      example_line_one: '{{n}} star → {{amount}}',
      example_line_other: '{{n}} stars → {{amount}}',
      capped_short: 'cap reached',
      typical_dk: 'Typical in Denmark per month: under 100 kr at 6–10, 50–200 kr at 11–14, 200–500 kr at 15–17.',
      save: 'Save',
      saved: 'Pocket money for {{name}} saved',
      save_failed: 'Could not save. Please try again.',
      my_month: 'Pocket money in {{month}}: {{amount}}',
      my_next_one: '{{n}} more star gives {{amount}} more',
      my_next_other: '{{n}} more stars give {{amount}} more',
      my_capped: 'You have reached this month\'s maximum. Well done!',
      my_last_paid: '{{month}}: {{amount}} — paid ✓',
      my_last_unpaid: '{{month}}: {{amount}} — waiting to be paid',
      to_pay_title: 'Pocket money to pay out',
      to_pay_help: 'Pay as usual (cash, bank or a payment app), then mark it as paid.',
      mark_paid: 'Mark as paid',
      task_counts: 'Gives pocket money',
      task_counts_help: 'Untick for everyday tasks such as making the bed, so only bigger jobs count towards pocket money.',
      typical_intro: 'Typical in {{country}} per month:',
      typical_band: '{{ages}} years: {{amount}}',
      typical_under: 'under {{amount}}',
      typical_about: 'about {{amount}}',
      typical_source: 'source: {{source}}',
    },
    getting_started: {
      title: 'Get started',
      progress: '{{done}} of {{all}} done',
      hide: 'Hide',
      item_approve: 'Approve the first finished task',
      item_own_task: 'Change a task, or make your own',
      item_home_screen: 'Put AntNest on your home screen',
      item_calendar: 'See the tasks in the calendar',
      home_screen_how: 'iPhone/iPad: tap the Share button in Safari and choose "Add to Home Screen". Android: tap ⋮ in Chrome and choose "Install app" or "Add to Home screen". Do it on the children\'s devices too.',
      home_screen_done: 'I have done it',
      pocket_title: 'Pocket money for the stars?',
      pocket_text: 'Now that the children have got going, their stars can also give pocket money — a fixed amount, an amount per 10 stars, and a monthly cap you choose yourselves.',
      pocket_setup: 'Set it up',
      pocket_no: 'No thanks',
    },
    editor: {
      close: 'Close',
      unsaved_title: 'Save your changes?',
      unsaved_body: 'You have changed something. Do you want to save it before closing?',
      save: 'Save',
      discard: 'Don\'t save',
      keep_editing: 'Keep editing',
    },
    timer: {
      start: 'Start timer',
      pause: 'Pause',
      resume: 'Continue',
      restart: 'Start over',
      times_up: 'Time\'s up!',
      sound_bell: 'Bell',
      sound_chime: 'Chime',
      sound_beeps: 'Alarm beeps',
      sound_marimba: 'Marimba',
      sound_fanfare: 'Fanfare',
      volume_note: 'The sound follows your phone\'s volume – turn it up so you hear it.',
    },
    approval_setting: {
      title: 'Approving grown-ups\' tasks',
      label: 'Grown-ups\' tasks must be approved by another grown-up',
      help_off: 'Off: when a grown-up finishes a task, it is approved at once and the stars are theirs. Children\'s tasks are always approved by a grown-up.',
      help_on: 'On: a grown-up\'s finished task waits until another grown-up approves it (if the family has only one admin, they can approve their own).',
      setup_help: 'Leave it unticked and grown-ups\' tasks are approved as soon as they are done. You can change this later in Settings.',
      saved: 'Saved.',
    },
  },
  da: {
    auth: {
      forgot_title: 'Glemt din adgangskode?',
      forgot_link: 'Glemt din adgangskode?',
      forgot_confirmation: 'Hvis der findes en konto med den e-mail, har vi sendt et nulstillingslink.',
      email_label: 'E-mail',
      send_reset_link: 'Send nulstillingslink',
      reset_title: 'Vælg en ny adgangskode',
      new_password_label: 'Ny adgangskode',
      confirm_password_label: 'Bekræft adgangskode',
      set_new_password: 'Gem ny adgangskode',
      password_mismatch: 'Adgangskoderne er ikke ens',
      password_policy_msg: 'Mindst 12 tegn, inkluder store/små bogstaver og et tal.',
      invalid_token: 'Ugyldigt eller udløbet link',
      reset_success: 'Din adgangskode er opdateret. Omdirigerer til login...',
      reset_failed: 'Kunne ikke nulstille adgangskoden. Prøv igen.',
      login_failed: 'Login mislykkedes. Prøv igen.',
      beta_banner: 'AntNest er i beta. Har du en invitationskode?',
      beta_banner_link: 'Opret en konto her',
      or: 'eller',
      login_google: 'Log ind med Google',
      login_facebook: 'Log ind med Facebook',
      no_account: 'Har du ikke en konto?',
      create_one: 'Opret en',
      err_code_required: 'Du skal bruge en beta-kode.',
      err_agree: 'Du skal acceptere vilkårene og privatlivspolitikken.',
      err_fields: 'Udfyld venligst alle felter.',
      err_code_invalid: 'Denne beta-kode er ugyldig eller allerede brugt.',
      err_signup_failed: 'Kontoen kunne ikke oprettes. Prøv igen.',
      signup_title: 'Opret din konto',
      beta_code: 'Beta-kode',
      beta_code_ph: 'Skriv din invitationskode',
      full_name: 'Dit navn',
      full_name_ph: 'f.eks. Anna',
      username: 'Brugernavn',
      show_password: 'Vis adgangskode',
      hide_password: 'Skjul adgangskode',
      rule_length: 'Mindst 12 tegn',
      rule_upper: 'Et stort bogstav',
      rule_lower: 'Et lille bogstav',
      rule_digit: 'Et tal',
      password_match: 'Adgangskoderne er ens',
      family_name: 'Familiens navn',
      family_name_ph: 'f.eks. Familien Hansen',
      family_default: 'Min familie',
      agree_before: 'Jeg accepterer',
      terms: 'vilkårene',
      and: 'og',
      privacy: 'privatlivspolitikken',
      creating_account: 'Opretter konto…',
      create_account: 'Opret konto',
      signup_google: 'Opret konto med Google',
      signup_facebook: 'Opret konto med Facebook',
      choose_language: 'Vælg sprog',
      oauth_failed: 'Login blev ikke gennemført. Prøv igen.',
      processing: 'Logger dig ind…',
      complete_setup: 'Næsten færdig',
      your_name: 'Dit navn',
      complete_setup_btn: 'Opret familie og fortsæt',
      login_apple: 'Log ind med Apple',
      signup_apple: 'Opret konto med Apple',
    },
    rooms: {
      kitchen: 'Køkken',
      living_room: 'Stue',
      dining_room: 'Spisestue',
      bedroom: 'Soveværelse',
      bathroom: 'Badeværelse',
      toilet: 'Toilet',
      hallway: 'Gang',
      entryway: 'Entré',
      laundry_room: 'Vaskerum',
      garage: 'Garage',
      office: 'Kontor',
      playroom: 'Legeværelse',
      nursery: 'Børneværelse',
      patio: 'Altan/Terrasse',
      garden: 'Have',
      storage: 'Opbevaring',
      basement: 'Kælder',
      attic: 'Loft',
      child_bedroom: 'Børneværelse',
    },
    onboarding: {
      title: 'Lad os komme i gang',
      family_name: 'Familienavn',
      adults: 'Voksne',
      children: 'Børn',
      member_name: 'Navn',
      username: 'Brugernavn',
      pin_placeholder: 'PIN (4 cifre)',
      role_admin: 'Admin',
      role_member: 'Medlem',
      next: 'Næste',
      back: 'Tilbage',
      confirm: 'Ser godt ud',
      processing: 'Gemmer…',
      step_rooms_title: 'Opsæt dit hjem',
      step_rooms_help: 'Klik på rum nedenfor for at tilføje dem til dit hjem. Hvis du har et rum eller område, der ikke er på listen, kan du tilføje det manuelt.\nDu kan altid redigere, tilføje og fjerne rum i din adminsektion.',
      custom_room_placeholder: 'Brugerdefineret rumnavn',
      add_custom: 'Tilføj brugerdefineret',
      remove: 'Fjern',
      step_review_title: 'Gennemse',
      review_family: 'Familie',
      review_members: 'Medlemmer',
      review_rooms: 'Rum',
      error_generic: 'Noget gik galt under gemning',
      // Step 1 helper/validation
      step_members_help: 'Tilføj dine familiemedlemmer her.\nGiv hvert familiemedlem et navn og et brugernavn.\nHvert familiemedlem skal have en PIN-kode, så de kan logge ind.',
      validation_incomplete: 'Nogle felter er ikke udfyldt korrekt',
      adult_label: 'Voksen',
      child_label: 'Barn',
      privacy_note: 'Oplysningerne bruges kun til at sætte jeres familie op. Vi deler eller sælger ikke – og vil aldrig dele eller sælge – jeres data eller personlige oplysninger til nogen.',
      child_age: 'Alder',
      child_room_name: '{{name}}s værelse',
      child_room_name_s: '{{name}}\' værelse',
      hint_members: 'Du kan altid tilføje eller ændre familiemedlemmer senere under Admin → Medlemmer.',
      hint_rooms: 'Hvert barn får sit eget værelse, så deres egne opgaver kommer det rigtige sted. Du kan altid ændre rummene senere under Admin → Rum.',
      step_tasks_title: 'Opgaver til at komme i gang',
      step_tasks_help: 'Vi har valgt nogle få opgaver, der passer til jeres rum og børnenes alder. Fjern fluebenet ved dem, I ikke vil have – I kan ændre det hele senere.',
      starters_unavailable: 'Forslagene kunne ikke hentes. Du kan springe over og tilføje opgaver senere.',
      tasks_for_child: '{{name}}s egne opgaver',
      tasks_shared: 'For alle (alle kan tage dem)',
      whole_home: 'Hele hjemmet',
      hint_tasks: 'Der er mange flere standardopgaver under Admin → Opgaver, og der kan I også lave jeres egne.',
      skip_step: 'Spring over',
      step_rewards_title: 'Belønninger at spare op til',
      step_rewards_help: 'Børn holder motivationen, når den første belønning er inden for rækkevidde. Disse er sat, så den første kan nås på få dage.',
      rewards_for_child: 'Til {{name}}',
      rewards_family: 'For hele familien (alles stjerner tæller)',
      hint_rewards: 'I kan altid tilføje jeres egne belønninger eller ændre disse under Admin → Belønninger.',
      review_starters: 'Til at komme i gang',
      review_tasks_one: '{{n}} opgave',
      review_tasks_other: '{{n}} opgaver',
      review_rewards_one: '{{n}} belønning',
      review_rewards_other: '{{n}} belønninger',
      create_family: 'Opret vores familie',
      done_title: 'Jeres familie er klar!',
      done_made: 'Vi har lavet {{tasks}} opgaver og {{rewards}} belønninger til jer.',
      done_try: 'Prøv den første opgave nu: skift til et barn med "{{switch}}" øverst, og tryk "{{start}}" på en opgave.',
      done_admin: 'Når en opgave er lavet, godkender du den under "{{approve}}" – så lægges stjernerne til.',
      go_home: 'Gå til familiens forside',
      go_admin: 'Gå til Admin',
    },
    common: {
      save: 'Gem',
      take_photo: 'Tag foto',
      cancel: 'Annuller',
      ok: 'OK',
      loading: 'Indlæser...',
      all: 'Alle',
      stars_one: '{{n}} stjerne',
      stars_other: '{{n}} stjerner',
      update_ready: 'En ny version af AntNest er klar.',
      update_now: 'Opdater',
      remove_image: 'Fjern billede',
      remove_item: 'Fjern punkt',
      error_generic: 'Noget gik galt. Prøv igen.',
    },
    header: {
      stats: 'Statistik',
      app_title: 'AntNest',
      home: 'Hjem',
      tasks: 'Opgaver',
      calendar: 'Kalender',
      rewards: 'Belønninger',
      settings: 'Indstillinger',
      profile: 'Profil',
      admin: 'Admin',
      logout: 'Log ud',
      points: 'point',
      settings_tooltip: 'Indstillinger',
      menu_open: 'Åbn menu',
      menu_close: 'Luk menu',
      nav_menu: 'Menu',
      tab_bar_label: 'Hovedsektioner',
      switch_user: 'Skift bruger',
      master_admin: 'Master admin',
      change_picture: 'Skift mit billede',
      someone_else: 'En andens tur',
      for_grownups: 'For voksne',
      for_grownups_hint: 'Indstillinger, godkendelser, log ud',
      my_page: 'Min side',
    },
    stats: {
      title: 'Statistik',
      tab_me: 'Mig',
      tab_family: 'Familie',
      tab_tasks: 'Opgaver',
      period_week: 'Uge',
      period_month: 'Måned',
      period_year: 'År',
      period_all: 'Altid',
      prev: 'Forrige',
      next: 'Næste',
      all_time: 'Altid',
      tasks_done: 'opgaver løst',
      points: 'point',
      rewards: 'belønninger',
      chart_points: 'Point over tid',
      where_helped: 'Hvor jeg hjalp',
      no_room: 'Intet rum',
      my_top_tasks: 'Mine hyppigste opgaver',
      together: 'Sammen',
      who_helped: 'Hvem hjalp',
      everyone_over_time: 'Alle over tid',
      repeating_title: 'Opgaver',
      per_task_title: 'Hver opgave',
      times_done_axis: 'Antal gange løst',
      times: '{n}×',
      last_done: 'sidst: {date}',
      empty: 'Intet her endnu i denne periode.',
      family_hidden: 'Jeres familie viser kun dette for voksne.',
      week_short: 'Uge {n}',
      enc_start: 'En frisk start — hver opgave tæller! 💪',
      enc_first: 'Din første opgave — godt gået! 🎉',
      enc_done: 'Du har løst {n} opgaver — flot! ⭐',
      enc_more: 'Flere point end perioden før — bliv ved! 🚀',
      visibility_title: 'Statistik: hvad børn kan se',
      visibility_help: 'Vælg hvordan fanen Familie ser ud for børn. Voksne administratorer ser altid alt.',
      visibility_team: 'Holdvisning — alles andel, ingen rangering (anbefalet)',
      visibility_full: 'Fuld sammenligning — rangeret efter point',
      visibility_admins: 'Skjult — kun voksne ser familiesammenligningen',
      visibility_saved: 'Gemt',
      team_view_note: 'Alles andel af familiens samlede point',
    },
    calendar: {
      title: 'Kalender',
      coming_soon: 'Kalender er under opbygning. Grundlæggende visninger og events kommer her.',
      view_day: 'Dag',
      view_week: 'Uge',
      view_month: 'Måned',
      week_label: 'Uge',
      no_events_for_day: 'Ingen begivenheder',
      today_label: 'i dag',
      prev_day: 'Forrige dag',
      next_day: 'Næste dag',
      prev_week: 'Forrige uge',
      next_week: 'Næste uge',
      prev_month: 'Forrige måned',
      next_month: 'Næste måned',
      go_to_today: 'I dag',
      not_allowed_edit_event: 'Du kan kun ændre begivenheder, du selv har oprettet.',
      filters_title: 'Filtrér efter medlem',
      single_view_button: 'Enkeltvisning',
      family_view_button: 'Familievisning',
      no_events_for_range: 'Ingen begivenheder i dette interval',
      no_events: 'Ingen begivenheder i denne periode.',
      add_event: 'Tilføj begivenhed',
      edit_event: 'Redigér begivenhed',
      event_form_title: 'Titel',
      event_form_description: 'Beskrivelse (valgfri)',
      event_form_location: 'Sted (valgfri)',
      event_form_start: 'Start',
      event_form_end: 'Slut',
      event_form_all_day: 'Hele dagen',
      event_form_attendees: 'Deltagere',
      event_form_save: 'Gem',
      event_form_cancel: 'Annuller',
      event_form_delete: 'Slet',
      delete_confirm_title: 'Slet begivenhed',
      delete_confirm_message: 'Er du sikker på, at du vil slette denne begivenhed?',
      success_created: 'Begivenhed oprettet',
      success_updated: 'Begivenhed opdateret',
      success_deleted: 'Begivenhed slettet',
      error_generic: 'Noget gik galt',
      validation_title_required: 'Titel er påkrævet',
      validation_end_after_start: 'Slut skal være efter start',
      create_calendar_event: 'Opret kalenderbegivenhed',
      event_modal_title_create: 'Opret kalenderbegivenhed',
      event_modal_title_edit_prefix: 'Rediger'
      ,
      reminder_label: 'Påmindelse',
      reminder_none: 'Ingen påmindelse',
      reminder_10m: '10 minutter før',
      reminder_30m: '30 minutter før',
      reminder_1h: '1 time før',
      reminder_12h: '12 timer før',
      reminder_1d: '1 dag før',
      nothing_planned: 'Intet planlagt endnu',
      nothing_this_month: 'Intet planlagt denne måned',
      today_day: 'I dag, {{day}}',
      tomorrow_day: 'I morgen, {{day}}',
      at_time: 'Kl. {{time}}',
      all_day_label: 'Hele dagen',
      with_names: 'med {{names}}',
      task_label: 'Opgave',
      everyone: 'Alle',
      back: 'Tilbage',
      due_today: 'Skal laves i dag',
      due_today_at: 'Skal laves i dag inden {{time}}',
      due_tomorrow: 'Skal laves senest i morgen',
      due_tomorrow_at: 'Skal laves i morgen inden {{time}}',
      due_later: 'Skal laves senest denne dag',
      due_later_at: 'Skal laves inden {{time}}',
      overdue_since: 'Forsinket siden {{day}}',
      event_form_image: 'Billede til begivenhed (valgfrit)',
      remove_image: 'Fjern billede',
      planned: 'Planlagt',
    },
    settings: {
      title: 'Indstillinger',
      subtitle: 'Administrer din konto og applikationspræferencer',
      dark_mode_section: 'Mørk tilstand',
      current_theme: 'Nuværende tema',
      following_system: 'Følger systempræference',
      manual_selection: 'Manuelt valg',
      follow_system_label: 'Følg systempræference',
      auto_switch_hint: 'Skift automatisk mellem lys og mørk tilstand baseret på dine systemindstillinger',
      enable_dark_mode: 'Aktivér mørk tilstand',
      manual_toggle_hint: 'Skift manuelt mellem lyse og mørke temaer',
      light_mode: 'Lys tilstand',
      dark_mode: 'Mørk tilstand',
      more_settings_title: 'Flere indstillinger kommer snart',
      more_settings_desc: 'Yderligere tilpasningsmuligheder vil være tilgængelige her, herunder notifikationspræferencer, privatlivsindstillinger og kontoadministration.',
      language_section: 'Sprog',
      language_hint: 'Vælg dit foretrukne sprog til grænsefladen',
      back: 'Tilbage',
      profile_parent: 'Forælder. Ret navn, billede og adgangskode',
      profile_member: 'Ret navn, billede og PIN',
      look: 'Udseende',
      auto: 'Auto',
      tell_me_when: 'Giv mig besked, når',
      notif_this_device: 'Notifikationer på denne enhed',
      notif_blocked_parent: 'Blokeret i denne browser. Tillad notifikationer for siden i browserens indstillinger.',
      n_review_ready: 'En opgave skal godkendes',
      n_task_approved: 'Min opgave er godkendt',
      n_reward_ready: 'En belønning er klar',
      n_due_soon: 'En af mine opgaver skal snart være færdig',
      n_overdue: 'En af mine opgaver er forsinket',
      n_new_task: 'En ny opgave er ledig',
      n_event: 'Påmindelser fra kalenderen',
      daily_summary: 'Daglig oversigt',
      daily_summary_time: 'Tidspunkt for den daglige oversigt',
      quiet_hours: 'Stille timer',
      quiet_hours_hint: 'Ingen notifikationer mellem disse tidspunkter',
      quiet_from: 'Stille fra',
      quiet_to: 'Stille til',
      this_device: 'Denne enhed',
      change_pin: 'Skift min PIN',
      switch_person: 'Skift person',
      refresh_session: 'Opdater denne enhed',
      session_refreshed: 'Denne enhed er opdateret.',
      locked_until: 'Låst til {{time}}',
      unlock: 'Lås op',
      unlocked: '@{{name}} kan logge ind igen.',
      log_out_device: 'Log ud af denne enhed',
      logout_confirm_title: 'Log ud af denne enhed?',
      logout_confirm_text: 'Hele familien {{family}} bliver logget ud her. En voksen skal logge ind igen, før nogen kan bruge AntNest på denne enhed.',
      logout_confirm_yes: 'Log ud',
      cancel: 'Annuller',
      n_reward_claimed: 'En belønning bliver hentet',
    },
    rewards: {
      title: 'Belønninger',
      subtitle: 'Følg din fremgang og indløs dine belønninger',
      your_points: 'Dine point',
      family_points: 'Familiens point',
      claimed_rewards: 'Indløste belønninger',
      your_coins: 'Dine mønter',
      individual_rewards: 'Individuelle belønninger',
      no_individual_rewards: 'Ingen individuelle belønninger tilgængelige endnu.',
      family_rewards: 'Familiebelønninger',
      no_family_rewards: 'Ingen familiebelønninger tilgængelige endnu.',
      goal_reached: 'Målet er nået!',
      complete_suffix: 'færdigt',
      target_whole_family: 'Hele familien',
      claim_reward_button: 'Indløs belønning!',
      claimed_rewards_section: 'Indløste belønninger',
      no_claimed_rewards: 'Ingen belønninger indløst endnu. Fortsæt det gode arbejde!',
      claimed_on: 'Indløst den',
      unknown_date: 'Ukendt dato',
      unknown_member: 'Ukendt medlem',
      reward_header_alt: 'Belønningsheader',
      show_more: 'Vis mere',
      show_fewer: 'Vis færre',
      points_abbrev: 'pts',
      progress_counts_from: 'Fremskridt tæller point tjent den {{date}} eller senere.',
      lifetime_total_hint: 'Samlet saldo (ikke kun mod en bestemt belønning).',
      your_stars_label: 'dine stjerner',
      ready_to_get: 'Klar til at hente',
      keep_saving: 'Spar videre til',
      n_more: '{{n}} mere',
      get_it: 'Hent den',
      grownup_gets: 'En voksen henter denne',
      got_one: '{{n}} belønning, du har fået',
      got_other: '{{n}} belønninger, du har fået',
      see_all: 'Se alle',
      counting_since: 'Tæller stjerner fra {{date}}',
      nothing_yet: 'Ingen belønninger endnu. Spørg en voksen om at lave nogle!',
      claim_failed: 'Belønningen kunne ikke hentes. Prøv igen.',
    },
    dashboard: {
      welcome_back: 'Velkommen tilbage',
      subheading: 'Her er, hvad der sker i din familie i dag',
      leaderboard_title: 'Familie Rangliste',
      family_member: 'Familiemedlem',
      tasks_title: 'Opgaver',
      available_tasks: 'Tilgængelige opgaver',
      no_available_tasks: 'Ingen opgaver tilgængelige i øjeblikket!',
      my_active_tasks: 'Mine aktive opgaver',
      no_my_tasks: 'Du har ingen aktive opgaver!',
      recently_completed_tasks: 'Senest fuldførte opgaver',
      completed_tasks: 'Fuldførte opgaver',
      no_completed_tasks: 'Ingen fuldførte opgaver endnu!',
      manage_all_tasks: 'Administrer alle opgaver',
      my_rewards_title: 'Mine belønninger',
      my_points: 'Mine point',
      available_rewards: 'Tilgængelige belønninger',
      claimed_rewards: 'Indløste belønninger',
      next_reward_goal: 'Næste belønningsmål',
      all_rewards_claimed: 'Alle belønninger indløst! Godt gået!',
      showing_rewards_help: 'Viser: Dine personlige og familie-belønninger',
      claim_now: 'Indløs nu!',
      locked: 'Låst',
      go_to_rewards: 'Gå til belønninger',
      my_tasks_primary_title: 'Mine opgaver',
      my_tasks_home_hint: 'Her ser du kun opgaver tildelt dig. For at vælge nye opgaver, åbn Opgaver.',
      due_overdue: 'Forfalden',
      due_today: 'Forfalder i dag',
      due_no_deadline: 'Ingen dato',
      hi_name: 'Hej {{name}}',
      goal_of: '{{have}} af {{need}} stjerner',
      goal_get_it: 'Hent den',
      goal_none: 'Der er ingen belønninger at gå efter endnu. Spørg en voksen om at lave en!',
      today: 'I dag',
      no_tasks_now: 'Ingen opgaver lige nu',
      waiting_one: '{{n}} stjerne, når en voksen siger ja',
      waiting_other: '{{n}} stjerner, når en voksen siger ja',
      family_goal: 'Familiens mål',
      family_goal_more: 'Alles stjerner tæller. {{n}} mere til {{reward}}.',
      family_goal_ready: '{{reward}} er klar! En voksen kan hente den til familien.',
      find_tasks_cta: 'Find en opgave',
      due_tomorrow: 'I morgen',
      due_in_days_one: 'Om {{n}} dag',
      due_in_days_other: 'Om {{n}} dage',
      more_stars_title: 'Vil du have flere stjerner?',
      more_stars_hint: 'Dem her må alle tage. Vælg en, når du har lyst!',
      see_all_can_take: 'Se alle {{n}}',
      take_failed: 'Kunne ikke tage opgaven',
    },
    task_board: {
      title: 'Opgavetavle',
      subtitle: 'Administrer og spor alle familieopgaver',
      available_tasks: 'Tilgængelige opgaver',
      no_available_tasks: 'Ingen opgaver tilgængelige i øjeblikket!',
      my_active_tasks: 'Mine aktive opgaver',
      no_my_tasks: 'Du har ingen aktive opgaver!',
      view: 'Vis',
      complete: 'Fuldfør',
      tasks_assigned_to_others: 'Opgaver tildelt andre',
      no_tasks_assigned_to_others: 'Ingen opgaver er tildelt andre brugere!',
      view_task: 'Vis opgave',
      completed_tasks: 'Fuldførte opgaver',
      no_completed_tasks: 'Ingen fuldførte opgaver endnu!',
      take_task: 'Den tager jeg',
      take_over_task: 'Overtag opgave',
      unclaim_task: 'Giv den tilbage',
      your_tasks: 'Dine opgaver',
      can_take: 'Dem kan du tage',
      waiting_heading: 'Venter på en voksen',
      others_tasks: 'Andres opgaver',
      done_one: '{{n}} opgave løst',
      done_other: '{{n}} opgaver løst',
      moved_to_yours: '{{task}} er nu under Dine opgaver',
      nothing_to_take: 'Ingen opgaver at tage lige nu',
      no_your_tasks: 'Du har ingen opgaver lige nu.',
    }
    ,
    login: {
      title: 'AntNest',
      email_label: 'Email',
      password_label: 'Adgangskode',
      logging_in: 'Logger ind...',
      login_button: 'Log ind',
      demo_account: 'Demo-konto',
      demo_email: 'Email: admin@family.com',
      demo_password: 'Adgangskode: admin'
    },
    user_profile: {
      user_not_found: 'Bruger ikke fundet.',
      back_to_home: 'Tilbage til hjem',
      title: 'Brugerprofil',
      profile_information: 'Profiloplysninger',
      name: 'Navn',
      email: 'Email',
      email_not_set: 'Ingen email angivet',
      role: 'Rolle',
      admin: 'Admin',
      family_member: 'Familiemedlem',
      points: 'Point',
      coins: 'Mønter',
      edit_profile: 'Rediger profil',
      save_changes: 'Gem ændringer',
      cancel: 'Annuller',
      email_optional_label: 'Email (valgfrit)',
      new_password_optional_label: 'Ny adgangskode (valgfrit)',
      name_placeholder: 'Indtast dit navn',
      email_enter_placeholder: 'Indtast emailadresse',
      email_add_placeholder: 'Tilføj email',
      username_for_member_login: 'Brugernavn (til medlemslogin)',
      pin_label: 'PIN (4–8 cifre)',
      avatar: 'Avatar',
      avatar_hint: 'Vælg en forudindstilling eller upload din egen.',
      upload: 'Upload',
      avatar_updated: 'Avatar opdateret',
      avatar_update_failed: 'Kunne ikke opdatere avatar',
      linked_accounts: 'Tilknyttede sociale konti',
      no_linked_accounts: 'Ingen tilknyttede konti.',
      unlink: 'Fjern',
      unlink_failed: 'Kunne ikke fjerne',
      unlink_confirm: 'Fjern {{provider}}?',
      link_to_google: 'Knyt til Google',
      change_password: 'Skift adgangskode',
      current_password: 'Nuværende adgangskode',
      new_password: 'Ny adgangskode',
      password_changed: 'Adgangskode ændret',
      password_change_failed: 'Kunne ikke ændre adgangskode',
      save_new_password: 'Gem ny adgangskode',
      pin_validation: 'PIN skal være 4–8 cifre (kun tal)',
      current_pin_label: 'Nuværende PIN (kun hvis du allerede har en)',
      current_pin_required: 'Indtast din nuværende PIN for at ændre den.',
      current_pin_incorrect: 'Din nuværende PIN er forkert.',
      email_managed_by_login: 'Din e-mail kommer fra den måde, du logger ind på, og kan ikke ændres her.',
      username_validation: 'Brugernavn skal være mindst 2 tegn',
      profile_updated: 'Profil opdateret',
      google_link_success: 'Google-konto tilknyttet',
      picture_type_bad: 'Vælg et billede i PNG, JPG eller WebP.',
      picture_too_big: 'Billedet er for stort (højst 1 MB).',
      choose_picture: 'Vælg dette billede',
      or_pick_one: 'Eller vælg et af disse',
      tap_picture: 'Tryk på billedet for at vælge et nyt.',
      facebook_link_success: 'Facebook-konto tilknyttet',
      link_to_facebook: 'Knyt til Facebook',
      account_unlinked: 'Kontoen er ikke længere tilknyttet.',
      link_to_apple: 'Knyt til Apple',
    },
    admin: {
      header_title: 'Adminpanel',
      header_subtitle: 'Administrer familiens opgaver, belønninger og medlemmer',
      tab_tasks: 'Opgaver',
      tab_rooms: 'Rum',
      tab_members: 'Medlemmer',
      tab_pending: 'Afventer',
      tab_rewards: 'Belønninger',
      tab_tools: 'Værktøjer'
    },
    checklist_editor: {
      back_to_admin: 'Tilbage til adminpanelet',
      edit_checklist_for: 'Rediger checkliste',
      add_new_item: 'Tilføj ny checklistepost',
      add_item: 'Tilføj',
      current_items: 'Nuværende checklisteposter',
      no_items: 'Ingen checklisteposter endnu. Tilføj nogle ovenfor!',
      cancel: 'Annuller',
      save: 'Gem'
    },
    task_details: {
      loading: 'Indlæser...',
      title: 'Opgavedetaljer',
      room: 'Rum',
      points: 'Point',
      status: 'Status',
      status_completed: 'Fuldført',
      status_active: 'Aktiv',
      assigned_to: 'Tildelt til',
      due_date: 'Forfaldsdato',
      tools_required: 'Påkrævede værktøjer',
      checklist: 'Tjekliste',
      task_completed: 'Opgave fuldført',
      completed_by: 'Fuldført af',
      message: 'Besked',
      submit_for_verification: 'Send til verifikation',
      close: 'Luk',
      preview_title: 'Forhåndsvisning af opgave',
      preview_room_placeholder: 'Foreslået når du tilføjer opgaven',
      preview_checklist_hint: 'Trin (kun forhåndsvisning — du kan afkrydse efter du har tilføjet opgaven)',
      view: 'Vis'
    },
    task_flow: {
      start_task: 'Start',
      tools_ready_title: 'Har du alt, du skal bruge?',
      tools_ready_subtitle: 'Du skal bruge disse værktøjer. Saml dem, før du fortsætter.',
      ready_to_start_title: 'Klar?',
      ready_to_start_body: 'Er du klar til at starte opgaven?',
      tools_ready_no: 'Ikke endnu',
      tools_ready_yes: 'Ja, lad os starte',
      finish_step: 'Jeg gjorde det',
      final_put_tools_message:
        'Godt gået! Læg alle værktøjer tilbage, hvor de hører til. Tryk derefter på knappen nedenfor for at fuldføre opgaven.',
      complete_task_button: 'Helt færdig',
      no_checklist_warning: 'Denne opgave har ingen trin endnu. Bed en voksen om at tilføje en tjekliste, før du kan starte.',
      view_checklist_readonly_hint: 'Du kan afkrydse trin, efter du har trykket på Start opgaven.',
      progress_label: 'Fremskridt',
      step_counter_prefix: 'Trin',
      not_assigned: 'Ikke tildelt endnu',
      step_of: 'Trin {{n}} af {{total}}',
      back_step: 'Gå et trin tilbage',
      tidy_title: 'Næsten færdig!',
      tidy_subtitle: 'Læg tingene tilbage, hvor de hører til',
      tidy_no_tools: 'Er alt færdigt? Så tryk på Helt færdig.',
      back_to_steps: 'Tilbage til trinene',
      celebrate_stars_title: '{{n}} stjerner er på vej',
      celebrate_stars_body: 'Du får dem, når en voksen siger ja',
      celebrate_button: 'Tilbage til forsiden',
      see_details: 'Se detaljer',
      step_tools: 'Du skal bruge:',
      where_room: 'Hvor: {{room}}',
      celebrate_heading: 'Flot klaret!',
      celebrate_subtitle: 'Du er færdig med {{title}}',
      celebrate_stars_got_title: 'Du fik {{n}} stjerner',
      celebrate_stars_got_body: 'Godkendt og lagt til dine stjerner',
      tidy_put_all_back: 'Læg alle disse ting tilbage, hvor de hører til',
    },
    reward_celebration: {
      congratulations: 'Tillykke!',
      you_have_a_reward: 'Du har en belønning!',
      check_rewards_prompt: 'Tjek dine belønninger for at indløse den!'
    }
    ,
    admin_panel: {
      header_title: 'Adminpanel',
      header_subtitle: 'Administrer familiens opgaver, belønninger og medlemmer',
      tab_tasks: 'Opgaver',
      tab_rooms: 'Rum',
      tab_members: 'Medlemmer',
      tab_pending: 'Afventer',
      tab_rewards: 'Belønninger',
      tab_tools: 'Værktøjer',
      task_management: 'Opgaveadministration',
      create_new_task: 'Opret ny opgave',
      active_tasks: 'Aktive opgaver',
      active_tasks_help: 'Opgaver der er i gang eller tilgængelige',
      edit: 'Rediger',
      remove: 'Fjern',
      mark_as_incomplete: 'Markér som ufuldført',
      confirm_mark_incomplete_title: 'Er du sikker på, at du vil markere denne opgave som ikke fuldført',
      cancel: 'Annuller',
      confirm_yes_im_sure: 'Ja, jeg er sikker',
      no_active_tasks: 'Ingen aktive opgaver fundet.',
      completed_tasks: 'Fuldførte opgaver',
      completed_tasks_help: 'Opgaver der er fuldført og godkendt',
      no_completed_tasks: 'Ingen fuldførte opgaver fundet.',
      room_management: 'Rumadministration',
      add_new_room: 'Tilføj nyt rum',
      member_management: 'Medlemsadministration',
      add_new_member: 'Tilføj nyt medlem',
      pending_verification: 'Afventende opgavegodkendelse',
      verify_this_task: 'Godkend denne opgave',
      self_verify_not_allowed_hint:
        'En anden administrator skal godkende denne opgave, når der er mere end én administrator.',
      points_not_awarded_section: 'Godkendt — mangler point',
      points_not_awarded_help:
        'Disse opgaver blev markeret som godkendt, før point blev gemt korrekt. Brug «Tildel point» én gang for at tildele dem til den, der udførte opgaven.',
      award_missing_points: 'Tildel point',
      no_tasks_needing_points: 'Alle godkendte opgaver har registrerede point.',
      verify_success: 'Opgaven er godkendt. Point er tildelt.',
      verify_repaired_points_success:
        'Manglende point er tildelt det medlem, der udførte opgaven.',
      verify_already_done: 'Opgaven er allerede godkendt, og point er registreret.',
      verify_misattribution_credit_confirm:
        'Systemet viser at udbetaling allerede er registreret. Hvis medlemmets saldo aldrig steg: Vil du tildele et engangsfradrag nu? (Ikke hvis point allerede er modtaget.)',
      verify_misattribution_success: 'Engangskredit er anvendt. Bed medlemmet om at opdatere eller logge ind igen.',
      verify_misattribution_already_done: 'Engangs-kredit for fejlregistrering er allerede brugt for denne opgave.',
      verify_already_done_hint:
        'Har medlemmet stadig ingen point: prøv «Tildel point» igen og bekræft engangskredit, eller nulstil points_awarded_on_verification i databasen.',
      no_pending_verify: 'Ingen opgaver afventer godkendelse.',
      reward_management: 'Belønningsadministration',
      create_new_reward: 'Opret ny belønning',
      claimed: 'Indløst',
      available: 'Tilgængelig',
      tool_management: 'Værktøjsadministration',
      create_new_tool: 'Opret nyt værktøj',
      no_tools_msg: 'Ingen værktøjer oprettet endnu. Opret dit første værktøj for at komme i gang!',
      task_modal_title_create: 'Opret ny opgave',
      task_modal_title_edit_prefix: 'Rediger opgave',
      task_title_label: 'Opgavetitel',
      task_title_placeholder: 'Indtast opgavetitel',
      description_label: 'Beskrivelse (valgfrit)',
      description_placeholder: 'Indtast opgavebeskrivelse',
      room_label: 'Rum',
      no_room_option: 'Intet rum',
      assignment_label: 'Opgavetildeling',
      available_anyone_option: 'Tilgængelig for alle',
      assign_to_member_prefix: 'Tildel til',
      assignment_hint_assigned: 'Denne opgave er kun synlig for det tildelte medlem',
      assignment_hint_anyone: 'Denne opgave er tilgængelig for alle familiemedlemmer',
      points_label: 'Point at tildele',
      points_placeholder: 'Point',
      due_date_label: 'Forfaldsdato',
      recurrence_label: 'Gentag hver X dag (0 = ingen gentagelse)',
      auto_available_label: 'Automatisk tilgængelig',
      header_image_label: 'Headerbillede (valgfrit)',
      current_header_image_label: 'Nuværende headerbillede',
      checklist_label: 'Tjeklistepunkter',
      checklist_item_placeholder: 'Indtast tjeklistepunkt',
      images_optional_label: 'Billeder (valgfrit):',
      required_tools_label: 'Påkrævede værktøjer',
      no_tools_available_msg: 'Ingen værktøjer tilgængelige. Opret værktøjer i fanen Værktøjer.',
      cancel_btn: 'Annuller',
      create_task_btn: 'Opret opgave',
      save_changes_btn: 'Gem ændringer',
      remove_task_title: 'Fjern opgave',
      remove_task_prompt_prefix: 'Er du sikker på, at du vil fjerne opgaven',
      irreversible_note: 'Denne handling kan ikke fortrydes.',
      remove_task_btn: 'Fjern opgave',
      reward_modal_title_create: 'Opret ny belønning',
      reward_modal_title_edit_prefix: 'Rediger belønning',
      reward_title_label: 'Belønningstitel',
      reward_title_placeholder: 'Indtast belønningstitel',
      reward_desc_label: 'Beskrivelse',
      reward_desc_placeholder: 'Indtast belønningsbeskrivelse',
      points_required_label: 'Påkrævede point',
      reward_progress_start_label: 'Point tælles fra (dato)',
      reward_progress_start_hint: 'Kun point optjent fra denne dags start (din tid) tæller mod belønningen.',
      target_type_label: 'Måltype',
      target_type_individual: 'Individuel',
      target_type_family: 'Familie',
      assign_member_label: 'Tildel familiemedlem',
      select_member_option: 'Vælg et familiemedlem',
      current_header_image_id_label: 'Nuværende headerbillede-ID',
      create_reward_btn: 'Opret belønning',
      tool_modal_title_create: 'Opret nyt værktøj',
      tool_modal_title_edit_prefix: 'Rediger værktøj',
      tool_name_label: 'Værktøjsnavn',
      tool_name_placeholder: 'Indtast værktøjsnavn',
      tool_image_label: 'Værktøjsbillede (valgfrit)',
      current_tool_image_label: 'Nuværende værktøjsbillede',
      create_tool_btn: 'Opret værktøj',
      room_modal_title_add: 'Tilføj nyt rum',
      room_modal_title_edit: 'Rediger rum',
      delete_room_title: 'Slet rum',
      delete_room_btn: 'Slet',
      delete_room_prompt_prefix: 'Er du sikker på, at du vil slette rummet permanent',
      delete_room_blocked_message: 'Dette rum kan ikke slettes, fordi en eller flere opgaver bruger det. Slet eller omfordel disse opgaver først.',
      go_to_tasks_btn: 'Vis opgaver',
      room_name_label_required: 'Rumnavn *',
      room_name_placeholder: 'Indtast rumnavn',
      add_room_btn: 'Tilføj rum',
      member_modal_title_add: 'Tilføj nyt familiemedlem',
      member_name_label_required: 'Navn *',
      member_name_placeholder: 'Indtast medlemsnavn',
      member_password_label_required: 'Adgangskode *',
      member_password_placeholder: 'Indtast adgangskode',
      member_email_label_optional: 'Email (valgfrit)',
      member_email_placeholder: 'Indtast emailadresse',
      admin_access_label: 'Administratoradgang',
      member_type_label: 'Medlemstype',
      member_type_adult: 'Voksen',
      member_type_child: 'Barn',
      err_room_name: 'Giv rummet et navn.',
      err_room_create: 'Rummet kunne ikke oprettes. Prøv igen.',
      err_room_update: 'Rummet kunne ikke gemmes. Prøv igen.',
      err_generic_missing: 'Der mangler noget. Luk og prøv igen.',
      err_task_title_points: 'Giv opgaven en titel og et antal stjerner.',
      err_checklist_empty: 'Tilføj mindst ét trin med tekst.',
      err_task_create: 'Opgaven kunne ikke oprettes. Prøv igen.',
      err_task_update: 'Opgaven kunne ikke gemmes. Prøv igen.',
      err_task_remove: 'Opgaven kunne ikke fjernes. Prøv igen.',
      err_template_use: 'Der kunne ikke laves en opgave ud fra skabelonen. Prøv igen.',
      err_save_template: 'Skabelonen kunne ikke gemmes. Prøv igen.',
      saved_template: 'Gemt som skabelon.',
      err_header_image: 'Billedet kunne ikke gemmes. Prøv igen.',
      err_reward_title: 'Giv belønningen en titel.',
      err_reward_points: 'Skriv hvor mange stjerner belønningen kræver.',
      err_reward_member: 'Vælg hvem belønningen er til.',
      err_reward_create: 'Belønningen kunne ikke oprettes. Prøv igen.',
      err_reward_update: 'Belønningen kunne ikke gemmes. Prøv igen.',
      reward_updated: 'Belønningen er gemt.',
      err_tool_name: 'Giv redskabet et navn.',
      err_tool_create: 'Redskabet kunne ikke oprettes. Prøv igen.',
      err_tool_update: 'Redskabet kunne ikke gemmes. Prøv igen.',
      err_member_required: 'Et nyt medlem skal have navn, brugernavn og PIN.',
      err_pin_format: 'PIN skal være 4–8 cifre (kun tal).',
      err_username_short: 'Brugernavnet skal være mindst 2 tegn.',
      err_member_create: 'Medlemmet kunne ikke tilføjes. Prøv igen.',
      verify_misattribution_title: 'Giv stjernerne nu?',
      verify_misattribution_yes: 'Ja, giv dem',
      status_assigned: 'Til {{name}}',
      status_open: 'Alle kan tage den',
      comes_back_on: 'Kommer igen {{date}}',
      anyone_can_take: 'Alle kan tage den næste (ellers går den til samme person igen)',
      repeat_hint: 'Med "gentag hver" er den næste klar så mange dage efter godkendelsen og dukker op "dage til at fuldføre" dage før.',
      step_tools_label: 'Værktøjer til dette trin',
      step_tools_hint: 'Vælg først opgavens værktøjer længere nede. Så kan du vælge dem, dette trin skal bruge.',
      room_picture_label: 'Billede af rummet (valgfrit)',
      room_picture_hint: 'Et billede hjælper børnene med at se, hvilket rum en opgave er i.',
      room_picture_add: 'Tilføj billede',
      room_picture_change: 'Skift billede',
      room_picture_failed: 'Rummet er gemt, men billedet kunne ikke uploades. Prøv igen.',
      step_drag: 'Træk for at flytte trinnet',
      step_move_up: 'Flyt trinnet op',
      step_move_down: 'Flyt trinnet ned',
      delete_btn: 'Slet',
      show_more_completed_tasks: 'Vis flere',
      add_member_btn: 'Tilføj medlem',
      member_edit_title: 'Rediger familiemedlem',
      member_name_label: 'Navn',
      member_pin_label_optional: 'PIN (valgfri)',
      member_pin_hint_optional: 'Lad feltet stå tomt for at beholde den nuværende PIN. PIN skal være 4–8 cifre.',
      member_is_admin_label: 'Administratoradgang',
      member_update_failed: 'Kunne ikke opdatere medlemmet',
      delete_reward_title: 'Slet belønning',
      delete_confirm_prefix: 'Er du sikker på, at du vil slette',
      recreate_on_validation_label: 'Opret igen ved godkendelse',
      recreate_on_validation_help: 'Opret opgaven igen, så snart en voksen har godkendt den',
      days_to_complete_label: 'Antal dage til at fuldføre opgaven',
      days_to_complete_placeholder: 'f.eks. 7',
      days_to_complete_help: 'Hvis udfyldt, bliver forfaldsdatoen oprettelsesdatoen + dette antal dage.',
      add_checklist_item_btn: 'Tilføj trin',
      camera_title: 'Kamera',
      show_less_completed_tasks: 'Vis færre',
      delete_tool_title: 'Slet værktøj',
      task_saving: 'Gemmer opgaven…',
      task_saved: 'Opgaven er gemt',
      try_again: 'Prøv igen',
      deadline_question: 'Tidsfrist',
      deadline_none: 'Ingen frist',
      deadline_within: 'Skal laves inden for',
      days_unit: 'dage',
      repeat_question: 'Når den er godkendt…',
      repeat_none: 'er den færdig – den kommer ikke igen',
      repeat_now: 'kommer den igen med det samme',
      repeat_after: 'kommer den igen efter',
      next_question: 'Hvem får den næste?',
      next_same: '{{name}} igen',
      next_anyone: 'Alle kan tage den',
      summary_title: 'Sådan bliver det:',
      due_this_time_label: 'Frist denne gang (valgfri)',
      due_this_time_help: 'Lad den stå tom, så bruges tidsfristen ovenfor.',
      sum_now_person: '{{name}} får opgaven nu.',
      sum_now_anyone: 'Alle i familien kan tage den nu.',
      sum_within_one: 'Den skal være lavet inden for {{n}} dag.',
      sum_within_other: 'Den skal være lavet inden for {{n}} dage.',
      sum_end: 'Når du godkender den, er den færdig.',
      sum_back_now_same: 'Når du godkender den, kommer den igen til {{name}} med det samme.',
      sum_back_now_anyone: 'Når du godkender den, kommer den igen med det samme, og alle kan tage den.',
      sum_back_after_same_one: 'Når du godkender den, kommer den igen til {{name}} om {{n}} dag.',
      sum_back_after_same_other: 'Når du godkender den, kommer den igen til {{name}} om {{n}} dage.',
      sum_back_after_anyone_one: 'Når du godkender den, kommer den igen om {{n}} dag, og alle kan tage den.',
      sum_back_after_anyone_other: 'Når du godkender den, kommer den igen om {{n}} dage, og alle kan tage den.',
      sum_then_within_one: 'Så er der {{n}} dag til at lave den.',
      sum_then_within_other: 'Så er der {{n}} dage til at lave den.',
      repeat_next: 'er næste opgave',
      next_ready_after: 'Klar efter',
      hours_unit: 'timer',
      waits_for: 'Venter på: {{task}}',
      then_next: 'Derefter: {{task}}',
      sum_next: 'Når du godkender den, kommer "{{task}}" bagefter.',
      sum_next_wait_one: 'Den er klar {{n}} time efter, at denne blev lavet.',
      sum_next_wait_other: 'Den er klar {{n}} timer efter, at denne blev lavet.',
      sum_next_waits: 'Indtil da kan børnene ikke se "{{task}}".',
      reward_saving: 'Gemmer belønningen…',
      reward_saved: 'Belønningen er gemt',
      assignment_hint_each: 'Hver får sin egen opgave: i sin egen kalender, godkendt for sig og med sine egne stjerner.',
      err_choose_person: 'Vælg mindst én person.',
      names_and: '{{first}} og {{last}}',
      sum_now_each: '{{names}} får hver sin opgave nu.',
      sum_back_now_each: 'Når du godkender en persons opgave, kommer hans eller hendes igen med det samme.',
      sum_back_after_each_one: 'Når du godkender en persons opgave, kommer hans eller hendes igen om {{n}} dag.',
      sum_back_after_each_other: 'Når du godkender en persons opgave, kommer hans eller hendes igen om {{n}} dage.',
      shared_ready: 'Klar',
      remove_shared_note: 'Den fjernes for alle, den er givet til. Opgaver, der allerede er lavet, bliver i historikken.',
      repeat_plan: 'kommer den igen efter en fast plan',
      plan_weekly: 'hver uge på',
      plan_every: 'hver',
      plan_every_unit: '. dag',
      plan_from: 'fra',
      sum_plan_person: '{{name}} får opgaven.',
      sum_plan_anyone: 'Alle i familien kan tage den.',
      sum_plan_each: '{{names}} får hver sin opgave.',
      sum_plan_weekly: 'Den kommer frem hver {{days}}.',
      sum_plan_every_one: 'Den kommer frem hver dag fra {{date}}.',
      sum_plan_every_other: 'Den kommer frem hver {{n}}. dag fra {{date}}.',
      sum_plan_back: 'Når du godkender den, kommer den igen på næste planlagte dag.',
      plan_week_every_one: 'hver uge',
      plan_week_every_other: 'hver {{n}}. uge',
      plan_on: 'på',
      plan_monthly: 'hver måned',
      plan_month_every_one: 'hver måned',
      plan_month_every_other: 'hver {{n}}. måned',
      plan_the: 'den',
      plan_which: 'hvilken',
      plan_weekday: 'ugedag',
      plan_nth_1: 'første',
      plan_nth_2: 'anden',
      plan_nth_3: 'tredje',
      plan_nth_4: 'fjerde',
      plan_nth_last: 'sidste',
      plan_on_date: 'den',
      plan_date_unit: '. i måneden',
      sum_plan_weekly_every: 'Den kommer frem hver {{n}}. uge, på {{days}}.',
      sum_plan_every_open_one: 'Den kommer frem hver dag.',
      sum_plan_every_open_other: 'Den kommer frem hver {{n}}. dag.',
      sum_plan_month_day_one: 'Den kommer frem den {{day}}. i hver måned.',
      sum_plan_month_day_other: 'Den kommer frem den {{day}}. hver {{n}}. måned.',
      sum_plan_month_nth_one: 'Den kommer frem den {{nth}} {{weekday}} i hver måned.',
      sum_plan_month_nth_other: 'Den kommer frem den {{nth}} {{weekday}} hver {{n}}. måned.',
      step_timer_label: 'Timer (valgfri)',
      step_timer_minutes: 'Timer, minutter',
      step_timer_seconds: 'Timer, sekunder',
      step_timer_min: 'min',
      step_timer_sec: 'sek',
      step_timer_clear: 'Ingen timer',
      step_timer_hint: 'Fx "Planke – 0:45". Den, der laver opgaven, starter den og hører en lyd, når tiden er gået.',
    },
    notifications: {
      approved_said_yes: '{{admin}} sagde ja!',
      approved_for: 'for {{task}}',
      approved_note: '{{admin}} siger: {{note}}',
      approved_now_you_have: 'Nu har du {{n}} stjerner',
      approved_reward_ready: '{{reward}} er klar!',
      approved_more_to_goal: '{{n}} stjerner mere til {{reward}}',
      approved_see_reward: 'Se min belønning',
      approved_not_now: 'Ikke nu',
      approved_ok: 'Juhu!',
      admin_pending_verification_one: '1 opgave venter på din godkendelse.',
      admin_pending_verification_other: '{{count}} opgaver venter på din godkendelse.',
    },
    member_chooser: {
      who_title: 'Hvem er du?',
      hi_name: 'Hej {{name}}!',
      enter_pin: 'Skriv din PIN-kode',
      log_in: 'Log ind',
      use_password: 'Brug adgangskode i stedet',
      use_pin: 'Brug PIN-kode i stedet',
      password_label: 'Adgangskode',
      delete_digit: 'Slet',
      back: 'Tilbage',
      locked_until: 'For mange forsøg. Prøv igen kl. {{time}}.',
      locked_later: 'For mange forsøg. Prøv igen lidt senere.',
      wrong_pin: 'Det virkede ikke. Prøv igen.',
      sign_out_device_hint: 'Rydder familielogin i denne browser. Brug det, hvis du sidder fast eller vil logge ind som voksen med Google/e-mail igen.',
      sign_out_device: 'Log ud af denne enhed',
    },
    parent: {
      to_approve_one: '{{n}} opgave at godkende',
      to_approve_other: '{{n}} opgaver at godkende',
      finished: '{{name}} er færdig med {{task}}',
      review: 'Gennemgå',
      nothing_to_approve: 'Intet at godkende lige nu',
      family: 'Familie',
      open_one: '{{n}} opgave at lave',
      open_other: '{{n}} opgaver at lave',
      open_none: 'Ingen opgaver at lave',
      set_up: 'Opsætning',
      tasks: 'Opgaver',
      rewards: 'Belønninger',
      members: 'Familie',
      rooms_tools: 'Rum og redskaber',
      lock: 'Lås',
      settings: 'Indstillinger',
      more_waiting: '+{{n}} mere',
      your_tasks: 'Dine egne opgaver',
    },
    approve: {
      title: 'Godkend opgaver',
      approve: 'Godkend',
      not_done: 'Ikke færdig endnu',
      note_so_neat: 'Så pænt!',
      note_well_done: 'Godt gået!',
      note_thank_you: 'Tak!',
      reason_look_again: 'Næsten — kig lige en gang til',
      reason_steps_missing: 'Nogle trin mangler stadig',
      reason_do_together: 'Lad os gøre det sammen',
      pick_reason: 'Hvad skal {{name}} have at vide?',
      cancel: 'Annuller',
      steps_done_one: 'Trinnet er klaret',
      steps_done_other: 'Alle {{n}} trin klaret',
      approved_toast: '{{task}} er godkendt. {{name}} fik {{stars}}.',
      undo: 'Fortryd',
      sent_back_toast: '{{task}} er sendt tilbage til {{name}}.',
      today_at: 'I dag {{time}}',
      yesterday_at: 'I går {{time}}',
      from_grownup: 'En voksen siger: {{reason}}',
      back: 'Tilbage',
      self_blocked: 'En anden voksen skal godkende din egen opgave.',
    },
    my_settings: {
      title: 'Mine indstillinger',
      language: 'Sprog',
      same_as_device: 'Som denne enhed',
      look: 'Lys eller mørk',
      light: 'Lys',
      dark: 'Mørk',
      notifications: 'Notifikationer',
      on: 'Til',
      off: 'Fra',
      notif_turn_on: 'Slå til på denne enhed',
      notif_turned_on: 'Notifikationer er slået til.',
      notif_turned_off: 'Notifikationer er slået fra.',
      notif_parent_off: 'En voksen har slået dine notifikationer fra.',
      notif_parent_on: 'En voksen vil gerne have, at dine notifikationer er slået til.',
      notif_unsupported: 'Denne enhed kan ikke vise notifikationer fra AntNest.',
      notif_blocked: 'Notifikationer er blokeret i denne browser. Bed en voksen om at tillade dem i browserens indstillinger.',
      timer_sound: 'Timerlyd',
      timer_sound_help: 'Lyden, når en timer på et trin når nul. Tryk for at høre den.',
    },
    parent_notifications: {
      title: 'Børnenes notifikationer',
      help: 'Vælg hvem der bestemmer. "Altid til" betyder, at barnet ikke kan slå dem fra; "Fra" betyder, at der ikke sendes noget til barnet.',
      child: 'Barnet bestemmer',
      on: 'Altid til',
      off: 'Fra',
      saved: 'Gemt.',
    },
    handover: {
      title: 'Belønninger der skal gives',
      help: 'Disse er blevet hentet. Giv dem til barnet, og tryk så på Givet.',
      got: '{{name}} har fået {{reward}}',
      handed_over: 'Givet',
      done_toast: '{{reward}} er givet til {{name}}.',
    },
    library: {
      access_denied: 'Kun voksne kan åbne dette.',
      title: 'Opgavebibliotek',
      intro: 'Start fra en standardopgave eller en skabelon, du har gemt fra en opgave. Kun din familie ser "Mine skabeloner".',
      age: 'Alder',
      age_group: 'Aldersgruppe',
      age_all: 'Alle aldre',
      age_2_3: '2–3 år',
      age_4_5: '4–5 år',
      age_6_7: '6–7 år',
      age_8_9: '8–9 år',
      age_10_12: '10–12 år',
      age_13_plus: '13+ år',
      standard_title: 'Standardopgaver',
      none_standard: 'Ingen standardopgaver for dette sprog og denne alder.',
      badge_standard: 'Standard',
      my_title: 'Mine skabeloner',
      my_intro: 'Gemt med "Gem som skabelon" i opgaveformularen. Kun din familie kan se dem.',
      none_my: 'Ingen gemte skabeloner endnu. Lav en opgave og brug "Gem som skabelon", eller skift aldersfilter.',
      badge_my: 'Min skabelon',
      use: 'Brug',
      save_as_template: 'Gem som skabelon',
      days: 'Dage',
      stars: 'Stjerner',
      tool_name: 'Redskabets navn',
      tool_name_ph: 'f.eks. Støvsuger',
      username_required: 'Brugernavn (skal udfyldes)',
      username_ph: 'Vælg et brugernavn',
      pin_label: 'PIN (4–8 cifre)',
      pin_ph: 'Skriv en PIN',
      adults: 'Voksne',
      show: 'Vis',
      hide: 'Skjul',
      added_to_tasks: '»{{title}}« er tilføjet til jeres opgaver',
      chain_title: '{{n}} opgaver, der følger efter hinanden',
      chain_wait_one: 'den næste er klar efter {{n}} time',
      chain_wait_other: 'den næste er klar efter {{n}} timer',
      chain_loops: 'Så starter den forfra.',
    },
    deletion: {
      delete_member: 'Slet medlem',
      delete_member_title: 'Slet {{name}}?',
      delete_member_text: '{{name}} fjernes fra familien med det samme og logges ud på alle enheder.',
      delete_member_lost: 'Alle {{name}}s stjerner, statistikker, opgaver og belønninger bliver slettet.',
      undo_30: 'Du kan fortryde i 30 dage, indtil {{date}}. Derefter slettes alt for altid og kan ikke gendannes.',
      delete_member_yes: 'Ja, slet',
      member_deleted_toast: '{{name}} er slettet. Du kan gendanne i 30 dage.',
      recently_deleted: 'Slettet for nylig',
      recently_deleted_hint: 'Du kan gendanne disse medlemmer indtil den viste dato. Derefter slettes de og alle deres data for altid.',
      gone_on: 'Slettes for altid {{date}}',
      restore: 'Gendan',
      member_restored_toast: '{{name}} er tilbage i familien. Notifikationer skal slås til igen.',
      family_section: 'Slet familie',
      delete_family_row: 'Slet hele familien',
      delete_family_hint: 'Alle og alt i familien. Kan fortrydes i 30 dage.',
      delete_family_title: 'Slet hele familien?',
      delete_family_text: 'Alle i {{family}} logges ud med det samme og kan ikke bruge AntNest, før familien er gendannet.',
      delete_family_lost: 'Alt bliver slettet: alle medlemmer, stjerner, statistikker, opgaver, belønninger, begivenheder og billeder.',
      type_family_name: 'Skriv familiens navn for at bekræfte: {{family}}',
      delete_family_yes: 'Slet familien',
      pending_title: '{{family}} er sat til sletning',
      pending_text: 'Familien og alle dens data slettes for altid {{date}}. Indtil da kan du gendanne alt, præcis som det var.',
      days_left: 'Dage tilbage: {{days}}',
      pending_not_admin: 'Kun en voksen med administratoradgang kan gendanne familien.',
      restore_family: 'Gendan familien',
      family_restored_toast: 'Velkommen tilbage! Familien er gendannet. Notifikationer skal slås til igen.',
      log_out: 'Log ud',
      cancel: 'Annuller',
    },
    pictures: {
      remove_picture: 'Fjern billede',
      removed: 'Billedet er fjernet. Du har den almindelige myre igen.',
      unreadable: 'Billedet kunne ikke åbnes. Prøv et andet billede, eller tag et nyt foto.',
      rotate: 'Drej',
      preparing: 'Gør billedet klar…',
    },
    billing: {
      title: 'Abonnement',
      trial_title: 'Gratis prøveperiode – dage tilbage: {{days}}',
      trial_text: 'Din gratis prøveperiode slutter den {{date}}. Vælg en plan inden da for at blive ved med at bruge AntNest.',
      expired_title: 'Din gratis prøveperiode er slut',
      expired_text: 'Vælg en plan for at blive ved med at bruge AntNest. Familiens opgaver, stjerner og belønninger er her stadig.',
      active_title_monthly: 'Du betaler månedligt',
      active_title_yearly: 'Du betaler årligt',
      active_text: 'Dit abonnement fornyes den {{date}}.',
      grace_title: 'En betaling gik ikke igennem',
      grace_text: 'Opdater dine betalingsoplysninger senest den {{date}} for at blive ved med at bruge AntNest.',
      cancelling_title: 'Dit abonnement er opsagt',
      cancelling_text: 'Du kan bruge AntNest indtil den {{date}}. Vælg en plan for at fortsætte derefter.',
      exempt_title: 'AntNest er gratis for jeres familie',
      exempt_text: 'Du skal ikke betale noget.',
      plans_title: 'Vælg en plan',
      plans_intro: 'Ét abonnement dækker hele familien på alle enheder.',
      plan_monthly: 'Månedlig',
      plan_yearly: 'Årlig',
      price_monthly: '{{price}} om måneden',
      price_yearly: '{{price}} om året',
      price_soon: 'Prisen kommer snart',
      includes_family: 'Hele familien på alle enheder',
      includes_tasks: 'Alle færdige opgaver og checklister',
      includes_cancel: 'Opsig når som helst',
      choose_monthly: 'Vælg månedlig',
      choose_yearly: 'Vælg årlig',
      coming_soon: 'Betaling åbner snart. Indtil da er AntNest gratis.',
      manage_title: 'Administrer dit abonnement',
      manage_text: 'Skift plan, opdater dit kort, se kvitteringer eller opsig.',
      manage_button: 'Administrer abonnement',
      banner_trial: 'Dage til din gratis prøveperiode slutter: {{days}}.',
      banner_trial_last_day: 'Din gratis prøveperiode slutter i dag.',
      banner_payment: 'En betaling gik ikke igennem. Opdater dit kort senest den {{date}}.',
      banner_cancelling: 'Dit abonnement slutter den {{date}}.',
      banner_action: 'Se planer',
      paywall_title: 'AntNest holder en pause',
      paywall_text: 'Spørg en voksen om at vælge en plan. Alle dine stjerner og opgaver er gemt og venter på dig.',
      manage_failed: 'Abonnementssiden kunne ikke åbnes. Prøv igen om lidt.',
    },
    pocket: {
      title: 'Lommepenge',
      intro: 'Lommepenge regnes ud fra de stjerner, hvert barn får for godkendte opgaver i løbet af en måned. AntNest flytter ingen penge: I betaler, som I plejer, og markerer det som udbetalt.',
      no_children: 'Tilføj et barn til familien for at give lommepenge.',
      currency: 'Valuta',
      for_child: 'Lommepenge til {{name}}',
      how_question: 'Hvordan tjenes de?',
      per_ten_label: '{{currency}} for hver 10 stjerner',
      fixed_label: '{{currency}} om måneden, når {{name}} har fået mindst',
      threshold_label: 'stjerner',
      threshold_help: '0 stjerner = gives altid (faste lommepenge).',
      bonus_label: 'Bonus:',
      bonus_every_label: '{{currency}} ekstra for hver',
      bonus_stars_label: 'stjerner mere',
      cap_question: 'Grænse om måneden — vælg en',
      cap_none: 'Ingen grænse',
      cap_at: 'Højst',
      cap_unit: '{{currency}} om måneden',
      cap_required: 'Vælg en grænse eller ingen grænse, før du gemmer.',
      example_title: 'For eksempel i en måned med:',
      example_line_one: '{{n}} stjerne → {{amount}}',
      example_line_other: '{{n}} stjerner → {{amount}}',
      capped_short: 'grænsen nået',
      typical_dk: 'Typisk i Danmark om måneden: under 100 kr for 6–10 år, 50–200 kr for 11–14 år og 200–500 kr for 15–17 år.',
      save: 'Gem',
      saved: 'Lommepenge til {{name}} er gemt',
      save_failed: 'Kunne ikke gemme. Prøv igen.',
      my_month: 'Lommepenge i {{month}}: {{amount}}',
      my_next_one: '{{n}} stjerne mere giver {{amount}} mere',
      my_next_other: '{{n}} stjerner mere giver {{amount}} mere',
      my_capped: 'Du har nået månedens højeste beløb. Flot!',
      my_last_paid: '{{month}}: {{amount}} — udbetalt ✓',
      my_last_unpaid: '{{month}}: {{amount}} — venter på at blive udbetalt',
      to_pay_title: 'Lommepenge til udbetaling',
      to_pay_help: 'Betal som I plejer (kontant, bank eller MobilePay), og markér det så som udbetalt.',
      mark_paid: 'Markér som udbetalt',
      task_counts: 'Giver lommepenge',
      task_counts_help: 'Fjern fluebenet ved hverdagsopgaver som at rede seng, så kun større opgaver tæller med i lommepengene.',
      typical_intro: 'Typisk i {{country}} om måneden:',
      typical_band: '{{ages}} år: {{amount}}',
      typical_under: 'under {{amount}}',
      typical_about: 'ca. {{amount}}',
      typical_source: 'kilde: {{source}}',
    },
    getting_started: {
      title: 'Kom godt i gang',
      progress: '{{done}} af {{all}} klaret',
      hide: 'Skjul',
      item_approve: 'Godkend den første færdige opgave',
      item_own_task: 'Tilpas en opgave, eller lav jeres egen',
      item_home_screen: 'Læg AntNest på hjemmeskærmen',
      item_calendar: 'Se opgaverne i kalenderen',
      home_screen_how: 'iPhone/iPad: Tryk på Del-knappen i Safari, og vælg "Føj til hjemmeskærm". Android: Tryk på ⋮ i Chrome, og vælg "Installer app" eller "Føj til startskærm". Gør det også på børnenes enheder.',
      home_screen_done: 'Det har jeg gjort',
      pocket_title: 'Lommepenge for stjernerne?',
      pocket_text: 'Nu hvor børnene er kommet i gang, kan stjernerne også give lommepenge – et fast beløb, et beløb pr. 10 stjerner og en grænse om måneden, som I selv bestemmer.',
      pocket_setup: 'Sæt det op',
      pocket_no: 'Nej tak',
    },
    editor: {
      close: 'Luk',
      unsaved_title: 'Gem dine ændringer?',
      unsaved_body: 'Du har ændret noget. Vil du gemme det, før du lukker?',
      save: 'Gem',
      discard: 'Gem ikke',
      keep_editing: 'Fortsæt med at redigere',
    },
    timer: {
      start: 'Start timer',
      pause: 'Pause',
      resume: 'Fortsæt',
      restart: 'Start forfra',
      times_up: 'Tiden er gået!',
      sound_bell: 'Klokke',
      sound_chime: 'Klokkespil',
      sound_beeps: 'Vækkeur',
      sound_marimba: 'Marimba',
      sound_fanfare: 'Fanfare',
      volume_note: 'Lyden følger telefonens lydstyrke – skru op, så du kan høre den.',
    },
    approval_setting: {
      title: 'Godkendelse af voksnes opgaver',
      label: 'Voksnes opgaver skal godkendes af en anden voksen',
      help_off: 'Fra: Når en voksen bliver færdig med en opgave, er den godkendt med det samme, og stjernerne er deres. Børns opgaver godkendes altid af en voksen.',
      help_on: 'Til: En voksens færdige opgave venter, til en anden voksen godkender den (har familien kun én administrator, kan vedkommende godkende sine egne).',
      setup_help: 'Lad den være tom, så bliver voksnes opgaver godkendt, så snart de er færdige. Du kan ændre det senere under Indstillinger.',
      saved: 'Gemt.',
    },
  },
  sv: {
    auth: {
      forgot_title: 'Glömt lösenordet?',
      forgot_link: 'Glömt lösenordet?',
      forgot_confirmation: 'Om ett konto finns för e‑posten har vi skickat en återställningslänk.',
      email_label: 'E‑post',
      send_reset_link: 'Skicka återställningslänk',
      reset_title: 'Skapa ett nytt lösenord',
      new_password_label: 'Nytt lösenord',
      confirm_password_label: 'Bekräfta lösenord',
      set_new_password: 'Spara nytt lösenord',
      password_mismatch: 'Lösenorden matchar inte',
      password_policy_msg: 'Minst 12 tecken, innehåll versal, gemen och en siffra.',
      invalid_token: 'Ogiltig eller utgången länk',
      reset_success: 'Ditt lösenord har uppdaterats. Omdirigerar till inloggning...',
      reset_failed: 'Kunde inte återställa lösenord. Försök igen.',
      login_failed: 'Inloggningen misslyckades. Försök igen.',
      beta_banner: 'AntNest är i beta. Har du en inbjudningskod?',
      beta_banner_link: 'Skapa ett konto här',
      or: 'eller',
      login_google: 'Logga in med Google',
      login_facebook: 'Logga in med Facebook',
      no_account: 'Har du inget konto?',
      create_one: 'Skapa ett',
      err_code_required: 'Du behöver en betakod.',
      err_agree: 'Du behöver godkänna villkoren och integritetspolicyn.',
      err_fields: 'Fyll i alla fält.',
      err_code_invalid: 'Den här betakoden är ogiltig eller redan använd.',
      err_signup_failed: 'Kontot kunde inte skapas. Försök igen.',
      signup_title: 'Skapa ditt konto',
      beta_code: 'Betakod',
      beta_code_ph: 'Skriv din inbjudningskod',
      full_name: 'Ditt namn',
      full_name_ph: 't.ex. Anna',
      username: 'Användarnamn',
      show_password: 'Visa lösenord',
      hide_password: 'Dölj lösenord',
      rule_length: 'Minst 12 tecken',
      rule_upper: 'En stor bokstav',
      rule_lower: 'En liten bokstav',
      rule_digit: 'En siffra',
      password_match: 'Lösenorden matchar',
      family_name: 'Familjens namn',
      family_name_ph: 't.ex. Familjen Andersson',
      family_default: 'Min familj',
      agree_before: 'Jag godkänner',
      terms: 'villkoren',
      and: 'och',
      privacy: 'integritetspolicyn',
      creating_account: 'Skapar konto…',
      create_account: 'Skapa konto',
      signup_google: 'Skapa konto med Google',
      signup_facebook: 'Skapa konto med Facebook',
      choose_language: 'Välj språk',
      oauth_failed: 'Inloggningen blev inte klar. Försök igen.',
      processing: 'Loggar in dig…',
      complete_setup: 'Nästan klart',
      your_name: 'Ditt namn',
      complete_setup_btn: 'Skapa familj och fortsätt',
      login_apple: 'Logga in med Apple',
      signup_apple: 'Skapa konto med Apple',
    },
    rooms: {
      kitchen: 'Kök',
      living_room: 'Vardagsrum',
      dining_room: 'Matsal',
      bedroom: 'Sovrum',
      bathroom: 'Badrum',
      toilet: 'Toalett',
      hallway: 'Hall',
      entryway: 'Entré',
      laundry_room: 'Tvättstuga',
      garage: 'Garage',
      office: 'Kontor',
      playroom: 'Lekrum',
      nursery: 'Barnkammare',
      patio: 'Altan/Balkong',
      garden: 'Trädgård',
      storage: 'Förråd',
      basement: 'Källare',
      attic: 'Vindsutrymme',
      child_bedroom: 'Barnrum',
    },
    onboarding: {
      title: 'Kom igång',
      family_name: 'Familjenamn',
      adults: 'Vuxna',
      children: 'Barn',
      member_name: 'Namn',
      username: 'Användarnamn',
      pin_placeholder: 'PIN (4 siffror)',
      role_admin: 'Admin',
      role_member: 'Medlem',
      next: 'Nästa',
      back: 'Tillbaka',
      confirm: 'Ser bra ut',
      processing: 'Sparar…',
      step_rooms_title: 'Ställ in ditt hem',
      step_rooms_help: 'Klicka på rum nedan för att lägga till dem i ditt hem. Om du har ett rum eller utrymme som inte finns i listan kan du lägga till det manuellt.\nDu kan alltid redigera, lägga till och ta bort rum i din adminsektion.',
      custom_room_placeholder: 'Anpassat rumsnamn',
      add_custom: 'Lägg till anpassat',
      remove: 'Ta bort',
      step_review_title: 'Granska',
      review_family: 'Familj',
      review_members: 'Medlemmar',
      review_rooms: 'Rum',
      error_generic: 'Något gick fel vid sparandet',
      step_members_help: 'Lägg till dina familjemedlemmar här.\nGe varje familjemedlem ett namn och ett användarnamn.\nVarje familjemedlem måste ha en PIN-kod så att de kan logga in.',
      validation_incomplete: 'Vissa fält är inte korrekt ifyllda',
      adult_label: 'Vuxen',
      child_label: 'Barn',
      privacy_note: 'Uppgifterna används bara för att ställa in er familj. Vi delar eller säljer inte – och kommer aldrig att dela eller sälja – era data eller personuppgifter till någon.',
      child_age: 'Ålder',
      child_room_name: '{{name}}s rum',
      child_room_name_s: '{{name}}\' rum',
      hint_members: 'Du kan alltid lägga till eller ändra familjemedlemmar senare under Admin → Medlemmar.',
      hint_rooms: 'Varje barn får ett eget rum, så att deras egna uppgifter hamnar på rätt ställe. Du kan alltid ändra rummen senare under Admin → Rum.',
      step_tasks_title: 'Uppgifter för att komma igång',
      step_tasks_help: 'Vi har valt några uppgifter som passar era rum och barnens ålder. Bocka ur dem ni inte vill ha – ni kan ändra allt senare.',
      starters_unavailable: 'Förslagen kunde inte hämtas. Du kan hoppa över och lägga till uppgifter senare.',
      tasks_for_child: '{{name}}s egna uppgifter',
      tasks_shared: 'För alla (alla kan ta dem)',
      whole_home: 'Hela hemmet',
      hint_tasks: 'Det finns många fler standarduppgifter under Admin → Uppgifter, och där kan ni också göra egna.',
      skip_step: 'Hoppa över',
      step_rewards_title: 'Belöningar att spara till',
      step_rewards_help: 'Barn håller motivationen uppe när den första belöningen är inom räckhåll. De här är satta så att den första kan nås på några dagar.',
      rewards_for_child: 'Till {{name}}',
      rewards_family: 'För hela familjen (allas stjärnor räknas)',
      hint_rewards: 'Ni kan alltid lägga till egna belöningar eller ändra dessa under Admin → Belöningar.',
      review_starters: 'För att komma igång',
      review_tasks_one: '{{n}} uppgift',
      review_tasks_other: '{{n}} uppgifter',
      review_rewards_one: '{{n}} belöning',
      review_rewards_other: '{{n}} belöningar',
      create_family: 'Skapa vår familj',
      done_title: 'Er familj är redo!',
      done_made: 'Vi har gjort {{tasks}} uppgifter och {{rewards}} belöningar åt er.',
      done_try: 'Prova den första uppgiften nu: byt till ett barn med "{{switch}}" högst upp och tryck "{{start}}" på en uppgift.',
      done_admin: 'När en uppgift är gjord godkänner du den under "{{approve}}" – då läggs stjärnorna till.',
      go_home: 'Gå till familjens startsida',
      go_admin: 'Gå till Admin',
    },
    common: {
      save: 'Spara',
      take_photo: 'Ta foto',
      cancel: 'Avbryt',
      loading: 'Laddar...',
      all: 'Alla',
      stars_one: '{{n}} stjärna',
      stars_other: '{{n}} stjärnor',
      update_ready: 'En ny version av AntNest är klar.',
      update_now: 'Uppdatera',
      remove_image: 'Ta bort bild',
      remove_item: 'Ta bort punkt',
      ok: 'OK',
      error_generic: 'Något gick fel. Försök igen.',
    },
    header: {
      stats: 'Statistik',
      app_title: 'AntNest',
      home: 'Hem',
      tasks: 'Uppgifter',
      calendar: 'Kalender',
      rewards: 'Belöningar',
      settings: 'Inställningar',
      profile: 'Profil',
      admin: 'Admin',
      logout: 'Logga ut',
      points: 'poäng',
      settings_tooltip: 'Inställningar',
      change_picture: 'Byt min bild',
      someone_else: 'Någon annans tur',
      for_grownups: 'För vuxna',
      for_grownups_hint: 'Inställningar, godkännanden, logga ut',
      my_page: 'Min sida',
      menu_close: 'Stäng meny',
      menu_open: 'Öppna meny',
      nav_menu: 'Meny',
      master_admin: 'Huvudadmin',
      switch_user: 'Byt användare',
      tab_bar_label: 'Huvudavsnitt',
    },
    stats: {
      title: 'Statistik',
      tab_me: 'Jag',
      tab_family: 'Familj',
      tab_tasks: 'Uppgifter',
      period_week: 'Vecka',
      period_month: 'Månad',
      period_year: 'År',
      period_all: 'Hela tiden',
      prev: 'Föregående',
      next: 'Nästa',
      all_time: 'Hela tiden',
      tasks_done: 'uppgifter klara',
      points: 'poäng',
      rewards: 'belöningar',
      chart_points: 'Poäng över tid',
      where_helped: 'Där jag hjälpte till',
      no_room: 'Inget rum',
      my_top_tasks: 'Mina vanligaste uppgifter',
      together: 'Tillsammans',
      who_helped: 'Vem hjälpte till',
      everyone_over_time: 'Alla över tid',
      repeating_title: 'Uppgifter',
      per_task_title: 'Varje uppgift',
      times_done_axis: 'Antal gånger klar',
      times: '{n}×',
      last_done: 'senast: {date}',
      empty: 'Inget här ännu för den här perioden.',
      family_hidden: 'Er familj visar detta bara för vuxna.',
      week_short: 'V. {n}',
      enc_start: 'En nystart — varje uppgift räknas! 💪',
      enc_first: 'Din första uppgift — bra jobbat! 🎉',
      enc_done: 'Du har klarat {n} uppgifter — snyggt! ⭐',
      enc_more: 'Fler poäng än perioden innan — fortsätt så! 🚀',
      visibility_title: 'Statistik: vad barn ser',
      visibility_help: 'Välj hur fliken Familj ser ut för barn. Vuxna administratörer ser alltid allt.',
      visibility_team: 'Lagvy — allas andel, ingen rangordning (rekommenderas)',
      visibility_full: 'Full jämförelse — rangordnad efter poäng',
      visibility_admins: 'Dold — bara vuxna ser familjejämförelsen',
      visibility_saved: 'Sparat',
      team_view_note: 'Allas andel av familjens totala poäng',
    },
    calendar: {
      title: 'Kalender',
      coming_soon: 'Kalendern byggs. Grundläggande vyer och händelser kommer här.',
      view_day: 'Dag',
      view_week: 'Vecka',
      view_month: 'Månad',
      week_label: 'Vecka',
      no_events_for_day: 'Inga händelser',
      today_label: 'idag',
      prev_day: 'Föregående dag',
      next_day: 'Nästa dag',
      prev_week: 'Föregående vecka',
      next_week: 'Nästa vecka',
      prev_month: 'Föregående månad',
      next_month: 'Nästa månad',
      go_to_today: 'Idag',
      not_allowed_edit_event: 'Du kan bara ändra händelser som du själv har skapat.',
      filters_title: 'Filtrera per medlem',
      single_view_button: 'Enkel vy',
      family_view_button: 'Familjevy',
      no_events_for_range: 'Inga händelser i detta intervall',
      no_events: 'Inga händelser under perioden.',
      add_event: 'Lägg till händelse',
      edit_event: 'Redigera händelse',
      event_form_title: 'Titel',
      event_form_description: 'Beskrivning (valfritt)',
      event_form_location: 'Plats (valfritt)',
      event_form_start: 'Start',
      event_form_end: 'Slut',
      event_form_all_day: 'Hela dagen',
      event_form_attendees: 'Deltagare',
      event_form_save: 'Spara',
      event_form_cancel: 'Avbryt',
      event_form_delete: 'Ta bort',
      delete_confirm_title: 'Ta bort händelse',
      delete_confirm_message: 'Är du säker på att du vill ta bort denna händelse?',
      success_created: 'Händelse skapad',
      success_updated: 'Händelse uppdaterad',
      success_deleted: 'Händelse borttagen',
      error_generic: 'Något gick fel',
      validation_title_required: 'Titel krävs',
      validation_end_after_start: 'Slut måste vara efter start',
      create_calendar_event: 'Skapa kalenderhändelse',
      event_modal_title_create: 'Skapa kalenderhändelse',
      event_modal_title_edit_prefix: 'Redigera'
      ,
      reminder_label: 'Påminnelse',
      reminder_none: 'Ingen påminnelse',
      reminder_10m: '10 minuter före',
      reminder_30m: '30 minuter före',
      reminder_1h: '1 timme före',
      reminder_12h: '12 timmar före',
      reminder_1d: '1 dag före',
      nothing_planned: 'Inget planerat än',
      nothing_this_month: 'Inget planerat den här månaden',
      today_day: 'Idag, {{day}}',
      tomorrow_day: 'I morgon, {{day}}',
      at_time: 'Kl. {{time}}',
      all_day_label: 'Hela dagen',
      with_names: 'med {{names}}',
      task_label: 'Uppgift',
      everyone: 'Alla',
      back: 'Tillbaka',
      due_today: 'Ska göras i dag',
      due_today_at: 'Ska göras i dag före {{time}}',
      due_tomorrow: 'Ska göras senast i morgon',
      due_tomorrow_at: 'Ska göras i morgon före {{time}}',
      due_later: 'Ska göras senast denna dag',
      due_later_at: 'Ska göras före {{time}}',
      overdue_since: 'Försenad sedan {{day}}',
      event_form_image: 'Bild för händelsen (valfritt)',
      remove_image: 'Ta bort bild',
      planned: 'Planerad',
    },
    settings: {
      title: 'Inställningar',
      subtitle: 'Hantera ditt konto och dina appinställningar',
      dark_mode_section: 'Mörkt läge',
      current_theme: 'Aktuellt tema',
      following_system: 'Följer systeminställning',
      manual_selection: 'Manuellt val',
      follow_system_label: 'Följ systeminställning',
      auto_switch_hint: 'Växla automatiskt mellan ljust och mörkt läge baserat på systeminställning',
      enable_dark_mode: 'Aktivera mörkt läge',
      manual_toggle_hint: 'Växla manuellt mellan ljusa och mörka teman',
      light_mode: 'Ljust läge',
      dark_mode: 'Mörkt läge',
      more_settings_title: 'Fler inställningar kommer snart',
      more_settings_desc: 'Fler anpassningsalternativ kommer snart, inklusive aviseringar, sekretess och konto.',
      language_section: 'Språk',
      language_hint: 'Välj ditt föredragna språk för gränssnittet',
      back: 'Tillbaka',
      profile_parent: 'Förälder. Ändra namn, bild och lösenord',
      profile_member: 'Ändra namn, bild och PIN',
      look: 'Utseende',
      auto: 'Auto',
      tell_me_when: 'Säg till när',
      notif_this_device: 'Aviseringar på den här enheten',
      notif_blocked_parent: 'Blockerat i den här webbläsaren. Tillåt aviseringar för sidan i webbläsarens inställningar.',
      n_review_ready: 'En uppgift behöver godkännas',
      n_task_approved: 'Min uppgift är godkänd',
      n_reward_ready: 'En belöning är redo',
      n_due_soon: 'En av mina uppgifter ska snart vara klar',
      n_overdue: 'En av mina uppgifter är försenad',
      n_new_task: 'En ny uppgift är ledig',
      n_event: 'Påminnelser från kalendern',
      daily_summary: 'Daglig sammanfattning',
      daily_summary_time: 'Tid för den dagliga sammanfattningen',
      quiet_hours: 'Tysta timmar',
      quiet_hours_hint: 'Inga aviseringar mellan dessa tider',
      quiet_from: 'Tyst från',
      quiet_to: 'Tyst till',
      this_device: 'Den här enheten',
      change_pin: 'Byt min PIN',
      switch_person: 'Byt person',
      refresh_session: 'Uppdatera den här enheten',
      session_refreshed: 'Den här enheten är uppdaterad.',
      locked_until: 'Låst till {{time}}',
      unlock: 'Lås upp',
      unlocked: '@{{name}} kan logga in igen.',
      log_out_device: 'Logga ut från den här enheten',
      logout_confirm_title: 'Logga ut från den här enheten?',
      logout_confirm_text: 'Hela familjen {{family}} loggas ut här. En vuxen måste logga in igen innan någon kan använda AntNest på den här enheten.',
      logout_confirm_yes: 'Logga ut',
      cancel: 'Avbryt',
      n_reward_claimed: 'En belöning hämtas',
    },
    rewards: {
      title: 'Belöningar',
      subtitle: 'Följ dina framsteg och hämta dina belöningar',
      your_points: 'Dina poäng',
      family_points: 'Familjens poäng',
      claimed_rewards: 'Hämtade belöningar',
      your_coins: 'Dina mynt',
      individual_rewards: 'Individuella belöningar',
      no_individual_rewards: 'Inga individuella belöningar tillgängliga ännu.',
      family_rewards: 'Familjebelöningar',
      no_family_rewards: 'Inga familjebelöningar tillgängliga ännu.',
      goal_reached: 'Målet uppnått!',
      complete_suffix: 'klart',
      target_whole_family: 'Hela familjen',
      claim_reward_button: 'Hämta belöning!',
      claimed_rewards_section: 'Hämtade belöningar',
      no_claimed_rewards: 'Inga belöningar hämtade ännu. Fortsätt arbeta på dina uppgifter!',
      claimed_on: 'Hämtad den',
      unknown_date: 'Okänt datum',
      unknown_member: 'Okänd medlem',
      reward_header_alt: 'Belöningshuvud',
      show_more: 'Visa mer',
      show_fewer: 'Visa färre',
      points_abbrev: 'pts',
      progress_counts_from: 'Framsteg räknar poäng intjänade den {{date}} eller senare.',
      lifetime_total_hint: 'Totalt saldo (skilt från varje belönings tidsfönster).',
      your_stars_label: 'dina stjärnor',
      ready_to_get: 'Redo att hämta',
      keep_saving: 'Fortsätt spara till',
      n_more: '{{n}} till',
      get_it: 'Hämta den',
      grownup_gets: 'En vuxen hämtar den här',
      got_one: '{{n}} belöning du har fått',
      got_other: '{{n}} belöningar du har fått',
      see_all: 'Se alla',
      counting_since: 'Räknar stjärnor sedan {{date}}',
      nothing_yet: 'Inga belöningar än. Be en vuxen lägga till några!',
      claim_failed: 'Belöningen kunde inte hämtas. Försök igen.',
    },
    dashboard: {
      welcome_back: 'Välkommen tillbaka',
      subheading: 'Här är vad som händer i din familj idag',
      leaderboard_title: 'Familjens topplista',
      family_member: 'Familjemedlem',
      tasks_title: 'Uppgifter',
      available_tasks: 'Tillgängliga uppgifter',
      no_available_tasks: 'Inga uppgifter tillgängliga just nu!',
      my_active_tasks: 'Mina aktiva uppgifter',
      no_my_tasks: 'Du har inga aktiva uppgifter!',
      recently_completed_tasks: 'Nyligen slutförda uppgifter',
      completed_tasks: 'Slutförda uppgifter',
      no_completed_tasks: 'Inga slutförda uppgifter ännu!',
      manage_all_tasks: 'Hantera alla uppgifter',
      my_rewards_title: 'Mina belöningar',
      my_points: 'Mina poäng',
      available_rewards: 'Tillgängliga belöningar',
      claimed_rewards: 'Hämtade belöningar',
      next_reward_goal: 'Nästa belöningsmål',
      all_rewards_claimed: 'Alla belöningar hämtade! Bra jobbat!',
      showing_rewards_help: 'Visar: Dina personliga och familjegemensamma belöningar',
      claim_now: 'Hämta nu!',
      locked: 'Låst',
      go_to_rewards: 'Gå till belöningar',
      my_tasks_primary_title: 'Mina uppgifter',
      my_tasks_home_hint: 'Här visas bara uppgifter som är tilldelade dig. För att välja nya uppgifter, öppna Uppgifter.',
      due_overdue: 'Försenad',
      due_today: 'Ska göras idag',
      due_no_deadline: 'Ingen deadline',
      hi_name: 'Hej {{name}}',
      goal_of: '{{have}} av {{need}} stjärnor',
      goal_get_it: 'Hämta den',
      goal_none: 'Inga belöningar att sikta på än. Be en vuxen lägga till en!',
      today: 'Idag',
      no_tasks_now: 'Inga uppgifter just nu',
      waiting_one: '{{n}} stjärna när en vuxen säger ja',
      waiting_other: '{{n}} stjärnor när en vuxen säger ja',
      family_goal: 'Familjens mål',
      family_goal_more: 'Allas stjärnor räknas. {{n}} kvar till {{reward}}.',
      family_goal_ready: '{{reward}} är redo! En vuxen kan hämta den åt familjen.',
      find_tasks_cta: 'Hitta en uppgift',
      due_tomorrow: 'I morgon',
      due_in_days_one: 'Om {{n}} dag',
      due_in_days_other: 'Om {{n}} dagar',
      more_stars_title: 'Vill du ha fler stjärnor?',
      more_stars_hint: 'De här får alla ta. Välj en när du har lust!',
      see_all_can_take: 'Se alla {{n}}',
      take_failed: 'Kunde inte ta uppgiften',
    },
    task_board: {
      title: 'Uppgiftstavla',
      subtitle: 'Hantera och följ alla familjeuppgifter',
      available_tasks: 'Tillgängliga uppgifter',
      no_available_tasks: 'Ingen uppgifter tillgängliga just nu!',
      my_active_tasks: 'Mina aktiva uppgifter',
      no_my_tasks: 'Du har ingen aktiva uppgifter!',
      view: 'Visa',
      complete: 'Slutför',
      tasks_assigned_to_others: 'Uppgifter tilldelade andra',
      no_tasks_assigned_to_others: 'Ingen uppgifter är tilldelade andre användare!',
      view_task: 'Visa uppgift',
      completed_tasks: 'Slutförda uppgifter',
      no_completed_tasks: 'Inga slutförda uppgifter ännu!',
      take_task: 'Jag tar den',
      take_over_task: 'Ta över uppgift',
      unclaim_task: 'Lämna tillbaka',
      your_tasks: 'Dina uppgifter',
      can_take: 'De här kan du ta',
      waiting_heading: 'Väntar på en vuxen',
      others_tasks: 'Andras uppgifter',
      done_one: '{{n}} uppgift klar',
      done_other: '{{n}} uppgifter klara',
      moved_to_yours: '{{task}} finns nu under Dina uppgifter',
      nothing_to_take: 'Inga uppgifter att ta just nu',
      no_your_tasks: 'Du har inga uppgifter just nu.',
    }
    ,
    login: {
      title: 'AntNest',
      email_label: 'E-post',
      password_label: 'Lösenord',
      logging_in: 'Loggar in...',
      login_button: 'Logga in',
      demo_account: 'Demokonto',
      demo_email: 'E-post: admin@family.com',
      demo_password: 'Lösenord: admin'
    },
    user_profile: {
      user_not_found: 'Användaren hittades inte.',
      back_to_home: 'Tillbaka till hem',
      title: 'Användarprofil',
      profile_information: 'Profilinformation',
      name: 'Namn',
      email: 'E-post',
      email_not_set: 'Ingen e-post angiven',
      role: 'Roll',
      admin: 'Admin',
      family_member: 'Familjemedlem',
      points: 'Poäng',
      coins: 'Mynt',
      edit_profile: 'Redigera profil',
      save_changes: 'Spara ändringar',
      cancel: 'Avbryt',
      email_optional_label: 'E-post (valfritt)',
      new_password_optional_label: 'Nytt lösenord (valfritt)',
      name_placeholder: 'Ange ditt namn',
      email_enter_placeholder: 'Ange e-postadress',
      email_add_placeholder: 'Lägg till e-post',
      username_for_member_login: 'Användarnamn (för medlemsinloggning)',
      pin_label: 'PIN-kod (4–8 siffror)',
      avatar: 'Avatar',
      avatar_hint: 'Välj en förinställning eller ladda upp din egen.',
      upload: 'Ladda upp',
      avatar_updated: 'Avatar uppdaterad',
      avatar_update_failed: 'Kunde inte uppdatera avatar',
      linked_accounts: 'Länkade konton',
      no_linked_accounts: 'Inga länkade konton.',
      unlink: 'Koppla från',
      unlink_failed: 'Misslyckades att koppla från',
      unlink_confirm: 'Koppla från {{provider}}?',
      link_to_google: 'Länka till Google',
      change_password: 'Byt lösenord',
      current_password: 'Nuvarande lösenord',
      new_password: 'Nytt lösenord',
      password_changed: 'Lösenordet har ändrats',
      password_change_failed: 'Misslyckades att byta lösenord',
      save_new_password: 'Spara nytt lösenord',
      pin_validation: 'PIN-kod måste vara 4–8 siffror',
      current_pin_label: 'Nuvarande PIN-kod (bara om du redan har en)',
      current_pin_required: 'Ange din nuvarande PIN-kod för att ändra den.',
      current_pin_incorrect: 'Din nuvarande PIN-kod är fel.',
      email_managed_by_login: 'Din e-postadress kommer från hur du loggar in och kan inte ändras här.',
      username_validation: 'Användarnamnet måste vara minst 2 tecken',
      profile_updated: 'Profil uppdaterad',
      google_link_success: 'Google-konto länkat',
      picture_type_bad: 'Välj en bild i PNG, JPG eller WebP.',
      picture_too_big: 'Bilden är för stor (max 1 MB).',
      choose_picture: 'Välj den här bilden',
      or_pick_one: 'Eller välj en av dessa',
      tap_picture: 'Tryck på bilden för att välja en ny.',
      facebook_link_success: 'Facebook-konto länkat',
      link_to_facebook: 'Länka till Facebook',
      account_unlinked: 'Kontot är inte längre länkat.',
      link_to_apple: 'Länka till Apple',
    },
    admin: {
      header_title: 'Adminpanel',
      header_subtitle: 'Hantera familjens uppgifter, belöningar och medlemmer',
      tab_tasks: 'Uppgifter',
      tab_rooms: 'Rum',
      tab_members: 'Medlemmar',
      tab_pending: 'Väntande',
      tab_rewards: 'Belöningar',
      tab_tools: 'Verktyg'
    },
    checklist_editor: {
      back_to_admin: 'Tillbaka till adminpanelen',
      edit_checklist_for: 'Redigera checklista',
      add_new_item: 'Lägg till ny checklistepunkt',
      add_item: 'Lägg till',
      current_items: 'Aktuella checklistepunkter',
      no_items: 'Inga checklistepunkter ännu. Lägg till ovan!',
      cancel: 'Avbryt',
      save: 'Spara'
    },
    task_details: {
      loading: 'Laddar...',
      title: 'Uppgiftsdetaljer',
      room: 'Rum',
      points: 'Poäng',
      status: 'Status',
      status_completed: 'Slutförd',
      status_active: 'Aktiv',
      assigned_to: 'Tildelt till',
      due_date: 'Förfallodatum',
      tools_required: 'Nödvändiga verktyg',
      checklist: 'Checklista',
      task_completed: 'Uppgift slutförd',
      completed_by: 'Slutförd av',
      message: 'Meddelande',
      submit_for_verification: 'Skicka för verifiering',
      close: 'Stäng',
      preview_room_placeholder: 'Föreslås när du lägger till uppgiften',
      preview_title: 'Förhandsvisa uppgift',
      preview_checklist_hint: 'Steg (endast förhandsvisning — bocka av efter att du lagt till uppgiften)',
      view: 'Visa',
    },
    task_flow: {
      start_task: 'Nu kör vi',
      tools_ready_title: 'Har du allt du behöver?',
      tools_ready_subtitle: 'Du behöver dessa verktyg. Samla dem innan du fortsätter.',
      ready_to_start_title: 'Redo?',
      ready_to_start_body: 'Är du redo att börja uppgiften?',
      tools_ready_no: 'Inte än',
      tools_ready_yes: 'Ja, kör',
      finish_step: 'Jag gjorde det',
      final_put_tools_message:
        'Bra jobbat! Lägg tillbaka alla verktyg där de hör hemma. Tryck sedan på knappen nedan för att slutföra uppgiften.',
      complete_task_button: 'Klart',
      no_checklist_warning: 'Den här uppgiften har inga steg än. Be en vuxen lägga till checkliststeg innan du kan starta.',
      view_checklist_readonly_hint: 'Du kan bocka av steg efter att du tryckt på Starta uppgiften.',
      progress_label: 'Framsteg',
      step_counter_prefix: 'Steg',
      not_assigned: 'Inte tilldelad än',
      step_of: 'Steg {{n}} av {{total}}',
      back_step: 'Gå tillbaka ett steg',
      tidy_title: 'Nästan klart!',
      tidy_subtitle: 'Lägg tillbaka sakerna där de hör hemma',
      tidy_no_tools: 'Är allt klart? Tryck då på Klart.',
      back_to_steps: 'Tillbaka till stegen',
      celebrate_stars_title: '{{n}} stjärnor är på väg',
      celebrate_stars_body: 'Du får dem när en vuxen säger ja',
      celebrate_button: 'Tillbaka till start',
      see_details: 'Se detaljer',
      step_tools: 'Du behöver:',
      where_room: 'Var: {{room}}',
      celebrate_heading: 'Bra jobbat!',
      celebrate_subtitle: 'Du är klar med {{title}}',
      celebrate_stars_got_title: 'Du fick {{n}} stjärnor',
      celebrate_stars_got_body: 'Godkänt och tillagt bland dina stjärnor',
      tidy_put_all_back: 'Lägg tillbaka alla de här sakerna där de hör hemma',
    },
    reward_celebration: {
      congratulations: 'Grattis!',
      you_have_a_reward: 'Du har en belöning!',
      check_rewards_prompt: 'Gå till belöningssidan för att hämta den!'
    }
    ,
    admin_panel: {
      header_title: 'Adminpanel',
      header_subtitle: 'Administrer familiens uppgifter, belöninger och medlemmer',
      tab_tasks: 'Uppgifter',
      tab_rooms: 'Rum',
      tab_members: 'Medlemmar',
      tab_pending: 'Väntande',
      tab_rewards: 'Belöningar',
      tab_tools: 'Verktyg',
      task_management: 'Uppgiftshantering',
      create_new_task: 'Skapa ny uppgift',
      active_tasks: 'Aktive uppgifter',
      active_tasks_help: 'Uppgifter som är pågående eller tillgängliga',
      edit: 'Redigera',
      remove: 'Ta bort',
      mark_as_incomplete: 'Markera som ej slutförd',
      confirm_mark_incomplete_title: 'Är du säker på att du vill markera denna uppgift som inte slutförd',
      cancel: 'Avbryt',
      confirm_yes_im_sure: 'Ja, jag är säker',
      no_active_tasks: 'Ingen aktive uppgifter hittades.',
      completed_tasks: 'Slutförda uppgifter',
      completed_tasks_help: 'Uppgifter som har slutförts och verifierats',
      no_completed_tasks: 'Ingen slutförda uppgifter hittades.',
      room_management: 'Rumsadministration',
      add_new_room: 'Lägg till nytt rum',
      member_management: 'Medlemshantering',
      add_new_member: 'Lägg till ny medlem',
      pending_verification: 'Väntande uppgiftsverifiering',
      verify_this_task: 'Verifiera denna uppgift',
      self_verify_not_allowed_hint:
        'En annan administratör måste verifiera denna uppgift när det finns mer än en administratör.',
      points_not_awarded_section: 'Verifierad — poäng saknas',
      points_not_awarded_help:
        'Dessa uppgifter markerades som verifierade innan poäng sparades korrekt. Använd «Tilldela poäng» en gång för att kreditera medlemmen som slutförde uppgiften.',
      award_missing_points: 'Tilldela poäng',
      no_tasks_needing_points: 'Alla verifierade uppgifter har registrerade poäng.',
      verify_success: 'Uppgiften är verifierad. Poäng har tilldelats.',
      verify_repaired_points_success:
        'Saknade poäng har tilldelats medlemmen som slutförde uppgiften.',
      verify_already_done: 'Uppgiften är redan verifierad och poäng är registrerade.',
      verify_misattribution_credit_confirm:
        'Systemet visar att utbetalning redan är registrerad. Om medlemmets saldo aldrig ökade: vill du kreditera en gång nu? (Inte om poäng redan mottagits.)',
      verify_misattribution_success: 'Engångskredit tillämpad. Be medlemmet uppdatera eller logga in igen.',
      verify_misattribution_already_done: 'Engångskredit för felaktig registrering har redan använts för denna uppgift.',
      verify_already_done_hint:
        'Om medlemmet fortfarande saknar poäng: försök «Tilldela poäng» igen och bekräfta, eller rensa points_awarded_on_verification i databasen.',
      no_pending_verify: 'Inga uppgifter väntar på verifiering.',
      reward_management: 'Belöningar',
      create_new_reward: 'Skapa ny belöning',
      claimed: 'Inlöst',
      available: 'Tillgänglig',
      tool_management: 'Verktyg',
      create_new_tool: 'Skapa nytt verktyg',
      no_tools_msg: 'Inga verktyg skapade än. Skapa ditt första verktyg för att komma igång!',
      task_modal_title_create: 'Skapa ny uppgift',
      task_modal_title_edit_prefix: 'Redigera uppgift',
      task_title_label: 'Uppgiftens titel',
      task_title_placeholder: 'Skriv uppgiftens titel',
      description_label: 'Beskrivning (valfri)',
      description_placeholder: 'Skriv en beskrivning av uppgiften',
      room_label: 'Rum',
      no_room_option: 'Inget rum',
      assignment_label: 'Vem gör uppgiften',
      available_anyone_option: 'Tillgänglig för alla',
      assign_to_member_prefix: 'Ge till',
      assignment_hint_assigned: 'Uppgiften syns bara för den familjemedlem som fått den',
      assignment_hint_anyone: 'Alla i familjen kan ta den här uppgiften',
      points_label: 'Poäng',
      points_placeholder: 'Poäng',
      due_date_label: 'Förfallodatum',
      recurrence_label: 'Upprepa var X:e dag (0 = ingen upprepning)',
      auto_available_label: 'Alla kan ta den',
      header_image_label: 'Omslagsbild (valfri)',
      current_header_image_label: 'Nuvarande omslagsbild',
      checklist_label: 'Steg',
      checklist_item_placeholder: 'Skriv ett steg',
      images_optional_label: 'Bilder (valfria):',
      required_tools_label: 'Verktyg som behövs',
      no_tools_available_msg: 'Inga verktyg än. Skapa verktyg under fliken Verktyg först.',
      cancel_btn: 'Avbryt',
      create_task_btn: 'Skapa uppgift',
      save_changes_btn: 'Spara ändringar',
      remove_task_title: 'Ta bort uppgift',
      remove_task_prompt_prefix: 'Är du säker på att du vill ta bort uppgiften',
      irreversible_note: 'Det går inte att ångra.',
      remove_task_btn: 'Ta bort uppgift',
      reward_modal_title_create: 'Skapa ny belöning',
      reward_modal_title_edit_prefix: 'Redigera belöning',
      reward_title_label: 'Belöningens titel',
      reward_title_placeholder: 'Skriv belöningens titel',
      reward_desc_label: 'Beskrivning',
      reward_desc_placeholder: 'Skriv en beskrivning av belöningen',
      points_required_label: 'Poäng som behövs',
      reward_progress_start_label: 'Poäng räknas från (datum)',
      reward_progress_start_hint: 'Bara poäng som tjänats från början av den här dagen (din tid) räknas mot belöningen. Totalsummor på andra ställen ändras inte.',
      target_type_label: 'Belöning för',
      target_type_individual: 'En person',
      target_type_family: 'Familjen',
      assign_member_label: 'Ge till familjemedlem',
      select_member_option: 'Välj en familjemedlem',
      current_header_image_id_label: 'Nuvarande omslagsbilds-ID',
      create_reward_btn: 'Skapa belöning',
      tool_modal_title_create: 'Skapa nytt verktyg',
      tool_modal_title_edit_prefix: 'Redigera verktyg',
      tool_name_label: 'Verktygets namn',
      tool_name_placeholder: 'Skriv verktygets namn',
      tool_image_label: 'Bild på verktyget (valfri)',
      current_tool_image_label: 'Nuvarande bild på verktyget',
      create_tool_btn: 'Skapa verktyg',
      room_modal_title_add: 'Lägg till nytt rum',
      room_modal_title_edit: 'Redigera rum',
      room_name_label_required: 'Rummets namn *',
      room_name_placeholder: 'Skriv rummets namn',
      add_room_btn: 'Lägg till rum',
      member_modal_title_add: 'Lägg till ny familjemedlem',
      member_name_label_required: 'Namn *',
      member_name_placeholder: 'Skriv medlemmens namn',
      member_password_label_required: 'Lösenord *',
      member_password_placeholder: 'Skriv lösenord',
      member_email_label_optional: 'E-post (valfri)',
      member_email_placeholder: 'Skriv e-postadress',
      admin_access_label: 'Vuxenbehörighet',
      member_type_label: 'Typ av medlem',
      member_type_adult: 'Vuxen',
      member_type_child: 'Barn',
      err_room_name: 'Ge rummet ett namn.',
      err_room_create: 'Rummet kunde inte skapas. Försök igen.',
      err_room_update: 'Rummet kunde inte sparas. Försök igen.',
      err_generic_missing: 'Något saknas. Stäng och försök igen.',
      err_task_title_points: 'Ge uppgiften en titel och ett antal stjärnor.',
      err_checklist_empty: 'Lägg till minst ett steg med text.',
      err_task_create: 'Uppgiften kunde inte skapas. Försök igen.',
      err_task_update: 'Uppgiften kunde inte sparas. Försök igen.',
      err_task_remove: 'Uppgiften kunde inte tas bort. Försök igen.',
      err_template_use: 'Det gick inte att skapa en uppgift från mallen. Försök igen.',
      err_save_template: 'Mallen kunde inte sparas. Försök igen.',
      saved_template: 'Sparad som mall.',
      err_header_image: 'Bilden kunde inte sparas. Försök igen.',
      err_reward_title: 'Ge belöningen en titel.',
      err_reward_points: 'Skriv hur många stjärnor belöningen kräver.',
      err_reward_member: 'Välj vem belöningen är till.',
      err_reward_create: 'Belöningen kunde inte skapas. Försök igen.',
      err_reward_update: 'Belöningen kunde inte sparas. Försök igen.',
      reward_updated: 'Belöningen är sparad.',
      err_tool_name: 'Ge verktyget ett namn.',
      err_tool_create: 'Verktyget kunde inte skapas. Försök igen.',
      err_tool_update: 'Verktyget kunde inte sparas. Försök igen.',
      err_member_required: 'En ny medlem behöver namn, användarnamn och PIN.',
      err_pin_format: 'PIN måste vara 4–8 siffror (bara siffror).',
      err_username_short: 'Användarnamnet måste vara minst 2 tecken.',
      err_member_create: 'Medlemmen kunde inte läggas till. Försök igen.',
      verify_misattribution_title: 'Ge stjärnorna nu?',
      verify_misattribution_yes: 'Ja, ge dem',
      status_assigned: 'Till {{name}}',
      status_open: 'Vem som helst kan ta den',
      comes_back_on: 'Kommer tillbaka {{date}}',
      anyone_can_take: 'Vem som helst kan ta nästa (annars går den till samma person igen)',
      repeat_hint: 'Med "upprepa var" ska nästa vara klar så många dagar efter godkännandet och dyker upp "dagar att slutföra" dagar innan.',
      step_tools_label: 'Verktyg för det här steget',
      step_tools_hint: 'Välj först uppgiftens verktyg längre ner. Sedan kan du välja de som det här steget behöver.',
      room_picture_label: 'Bild av rummet (valfritt)',
      room_picture_hint: 'En bild hjälper barnen att se vilket rum en uppgift gäller.',
      room_picture_add: 'Lägg till bild',
      room_picture_change: 'Byt bild',
      room_picture_failed: 'Rummet sparades, men bilden kunde inte laddas upp. Försök igen.',
      step_drag: 'Dra för att flytta steget',
      step_move_up: 'Flytta steget upp',
      step_move_down: 'Flytta steget ner',
      delete_btn: 'Ta bort',
      show_more_completed_tasks: 'Visa fler',
      add_member_btn: 'Lägg till medlem',
      member_edit_title: 'Redigera familjemedlem',
      member_name_label: 'Namn',
      member_pin_label_optional: 'PIN (valfritt)',
      member_pin_hint_optional: 'Lämna tomt för att behålla nuvarande PIN. PIN måste vara 4–8 siffror.',
      member_is_admin_label: 'Administratörsbehörighet',
      member_update_failed: 'Det gick inte att uppdatera medlemmen',
      delete_reward_title: 'Ta bort belöning',
      delete_confirm_prefix: 'Är du säker på att du vill ta bort',
      recreate_on_validation_label: 'Skapa igen vid godkännande',
      recreate_on_validation_help: 'Skapa uppgiften igen direkt när en vuxen har godkänt den',
      days_to_complete_label: 'Antal dagar för att slutföra uppgiften',
      days_to_complete_placeholder: 't.ex. 7',
      days_to_complete_help: 'Om ifyllt blir förfallodatumet skapandedatumet + så här många dagar.',
      add_checklist_item_btn: 'Lägg till steg',
      camera_title: 'Kamera',
      show_less_completed_tasks: 'Visa färre',
      delete_tool_title: 'Ta bort verktyg',
      delete_room_blocked_message: 'Det här rummet kan inte tas bort eftersom en eller flera uppgifter använder det. Ta bort eller flytta de uppgifterna först.',
      delete_room_btn: 'Ta bort',
      delete_room_title: 'Ta bort rum',
      delete_room_prompt_prefix: 'Är du säker på att du vill ta bort rummet permanent',
      task_saving: 'Sparar uppgiften…',
      task_saved: 'Uppgiften är sparad',
      try_again: 'Försök igen',
      deadline_question: 'Tidsfrist',
      deadline_none: 'Ingen tidsfrist',
      deadline_within: 'Ska göras inom',
      days_unit: 'dagar',
      repeat_question: 'När den är godkänd…',
      repeat_none: 'är den klar – den kommer inte tillbaka',
      repeat_now: 'kommer den tillbaka direkt',
      repeat_after: 'kommer den tillbaka efter',
      next_question: 'Vem får nästa?',
      next_same: '{{name}} igen',
      next_anyone: 'Alla kan ta den',
      summary_title: 'Så här blir det:',
      due_this_time_label: 'Tidsfrist den här gången (valfri)',
      due_this_time_help: 'Lämna tomt så används tidsfristen ovan.',
      sum_now_person: '{{name}} får uppgiften nu.',
      sum_now_anyone: 'Alla i familjen kan ta den nu.',
      sum_within_one: 'Den ska vara gjord inom {{n}} dag.',
      sum_within_other: 'Den ska vara gjord inom {{n}} dagar.',
      sum_end: 'När du godkänner den är den klar.',
      sum_back_now_same: 'När du godkänner den kommer den direkt tillbaka till {{name}}.',
      sum_back_now_anyone: 'När du godkänner den kommer den direkt tillbaka och alla kan ta den.',
      sum_back_after_same_one: 'När du godkänner den kommer den tillbaka till {{name}} om {{n}} dag.',
      sum_back_after_same_other: 'När du godkänner den kommer den tillbaka till {{name}} om {{n}} dagar.',
      sum_back_after_anyone_one: 'När du godkänner den kommer den tillbaka om {{n}} dag och alla kan ta den.',
      sum_back_after_anyone_other: 'När du godkänner den kommer den tillbaka om {{n}} dagar och alla kan ta den.',
      sum_then_within_one: 'Då finns det {{n}} dag att göra den på.',
      sum_then_within_other: 'Då finns det {{n}} dagar att göra den på.',
      repeat_next: 'är nästa uppgift',
      next_ready_after: 'Klar efter',
      hours_unit: 'timmar',
      waits_for: 'Väntar på: {{task}}',
      then_next: 'Sedan: {{task}}',
      sum_next: 'När du godkänner den kommer "{{task}}" efteråt.',
      sum_next_wait_one: 'Den är klar {{n}} timme efter att den här blev gjord.',
      sum_next_wait_other: 'Den är klar {{n}} timmar efter att den här blev gjord.',
      sum_next_waits: 'Till dess kan barnen inte se "{{task}}".',
      reward_saving: 'Sparar belöningen…',
      reward_saved: 'Belöningen är sparad',
      assignment_hint_each: 'Var och en får sin egen uppgift: i sin egen kalender, godkänd för sig och med sina egna stjärnor.',
      err_choose_person: 'Välj minst en person.',
      names_and: '{{first}} och {{last}}',
      sum_now_each: '{{names}} får var sin uppgift nu.',
      sum_back_now_each: 'När du godkänner någons uppgift kommer hans eller hennes tillbaka direkt.',
      sum_back_after_each_one: 'När du godkänner någons uppgift kommer hans eller hennes tillbaka om {{n}} dag.',
      sum_back_after_each_other: 'När du godkänner någons uppgift kommer hans eller hennes tillbaka om {{n}} dagar.',
      shared_ready: 'Klar',
      remove_shared_note: 'Den tas bort för alla den gavs till. Uppgifter som redan är gjorda finns kvar i historiken.',
      repeat_plan: 'kommer den tillbaka enligt ett fast schema',
      plan_weekly: 'varje vecka på',
      plan_every: 'var',
      plan_every_unit: ':e dag',
      plan_from: 'från',
      sum_plan_person: '{{name}} får uppgiften.',
      sum_plan_anyone: 'Alla i familjen kan ta den.',
      sum_plan_each: '{{names}} får var sin uppgift.',
      sum_plan_weekly: 'Den dyker upp varje {{days}}.',
      sum_plan_every_one: 'Den dyker upp varje dag från {{date}}.',
      sum_plan_every_other: 'Den dyker upp var {{n}}:e dag från {{date}}.',
      sum_plan_back: 'När du godkänner den kommer den tillbaka nästa planerade dag.',
      plan_week_every_one: 'varje vecka',
      plan_week_every_other: 'var {{n}}:e vecka',
      plan_on: 'på',
      plan_monthly: 'varje månad',
      plan_month_every_one: 'varje månad',
      plan_month_every_other: 'var {{n}}:e månad',
      plan_the: 'den',
      plan_which: 'vilken',
      plan_weekday: 'veckodag',
      plan_nth_1: 'första',
      plan_nth_2: 'andra',
      plan_nth_3: 'tredje',
      plan_nth_4: 'fjärde',
      plan_nth_last: 'sista',
      plan_on_date: 'den',
      plan_date_unit: 'i månaden',
      sum_plan_weekly_every: 'Den dyker upp var {{n}}:e vecka, på {{days}}.',
      sum_plan_every_open_one: 'Den dyker upp varje dag.',
      sum_plan_every_open_other: 'Den dyker upp var {{n}}:e dag.',
      sum_plan_month_day_one: 'Den dyker upp den {{day}} varje månad.',
      sum_plan_month_day_other: 'Den dyker upp den {{day}} var {{n}}:e månad.',
      sum_plan_month_nth_one: 'Den dyker upp den {{nth}} {{weekday}} i varje månad.',
      sum_plan_month_nth_other: 'Den dyker upp den {{nth}} {{weekday}} var {{n}}:e månad.',
      step_timer_label: 'Timer (valfri)',
      step_timer_minutes: 'Timer, minuter',
      step_timer_seconds: 'Timer, sekunder',
      step_timer_min: 'min',
      step_timer_sec: 'sek',
      step_timer_clear: 'Ingen timer',
      step_timer_hint: 'T.ex. "Plankan – 0:45". Den som gör uppgiften startar den och hör ett ljud när tiden är ute.',
    },
    notifications: {
      approved_said_yes: '{{admin}} sa ja!',
      approved_for: 'för {{task}}',
      approved_note: '{{admin}} säger: {{note}}',
      approved_now_you_have: 'Nu har du {{n}} stjärnor',
      approved_reward_ready: '{{reward}} är redo!',
      approved_more_to_goal: '{{n}} stjärnor kvar till {{reward}}',
      approved_see_reward: 'Se min belöning',
      approved_not_now: 'Inte nu',
      approved_ok: 'Hurra!',
      admin_pending_verification_one: '1 uppgift väntar på ditt godkännande.',
      admin_pending_verification_other: '{{count}} uppgifter väntar på ditt godkännande.',
    },
    member_chooser: {
      who_title: 'Vem är du?',
      hi_name: 'Hej {{name}}!',
      enter_pin: 'Skriv din PIN-kod',
      log_in: 'Logga in',
      use_password: 'Använd lösenord i stället',
      use_pin: 'Använd PIN-kod i stället',
      password_label: 'Lösenord',
      delete_digit: 'Radera',
      back: 'Tillbaka',
      locked_until: 'För många försök. Försök igen kl. {{time}}.',
      locked_later: 'För många försök. Försök igen lite senare.',
      wrong_pin: 'Det fungerade inte. Försök igen.',
      sign_out_device_hint: 'Rensar familjeinloggningen i den här webbläsaren. Använd det om du har fastnat eller vill logga in som vuxen med Google/e-post igen.',
      sign_out_device: 'Logga ut från den här enheten',
    },
    parent: {
      to_approve_one: '{{n}} uppgift att godkänna',
      to_approve_other: '{{n}} uppgifter att godkänna',
      finished: '{{name}} är klar med {{task}}',
      review: 'Granska',
      nothing_to_approve: 'Inget att godkänna just nu',
      family: 'Familj',
      open_one: '{{n}} uppgift att göra',
      open_other: '{{n}} uppgifter att göra',
      open_none: 'Inga uppgifter att göra',
      set_up: 'Inställning',
      tasks: 'Uppgifter',
      rewards: 'Belöningar',
      members: 'Familj',
      rooms_tools: 'Rum och redskap',
      lock: 'Lås',
      settings: 'Inställningar',
      more_waiting: '+{{n}} till',
      your_tasks: 'Dina egna uppgifter',
    },
    approve: {
      title: 'Godkänn uppgifter',
      approve: 'Godkänn',
      not_done: 'Inte klar än',
      note_so_neat: 'Så fint!',
      note_well_done: 'Bra jobbat!',
      note_thank_you: 'Tack!',
      reason_look_again: 'Nästan — titta en gång till',
      reason_steps_missing: 'Några steg är inte klara än',
      reason_do_together: 'Vi gör det tillsammans',
      pick_reason: 'Vad ska {{name}} få höra?',
      cancel: 'Avbryt',
      steps_done_one: 'Steget är klart',
      steps_done_other: 'Alla {{n}} steg klara',
      approved_toast: '{{task}} är godkänd. {{name}} fick {{stars}}.',
      undo: 'Ångra',
      sent_back_toast: '{{task}} skickades tillbaka till {{name}}.',
      today_at: 'Idag {{time}}',
      yesterday_at: 'Igår {{time}}',
      from_grownup: 'En vuxen säger: {{reason}}',
      back: 'Tillbaka',
      self_blocked: 'En annan vuxen måste godkänna din egen uppgift.',
    },
    my_settings: {
      title: 'Mina inställningar',
      language: 'Språk',
      same_as_device: 'Som den här enheten',
      look: 'Ljust eller mörkt',
      light: 'Ljust',
      dark: 'Mörkt',
      notifications: 'Aviseringar',
      on: 'På',
      off: 'Av',
      notif_turn_on: 'Slå på för den här enheten',
      notif_turned_on: 'Aviseringar är på.',
      notif_turned_off: 'Aviseringar är av.',
      notif_parent_off: 'En vuxen har stängt av dina aviseringar.',
      notif_parent_on: 'En vuxen vill att dina aviseringar är på.',
      notif_unsupported: 'Den här enheten kan inte visa aviseringar från AntNest.',
      notif_blocked: 'Aviseringar är blockerade i den här webbläsaren. Be en vuxen att tillåta dem i webbläsarens inställningar.',
      timer_sound: 'Timerljud',
      timer_sound_help: 'Ljudet när timern på ett steg når noll. Tryck för att höra det.',
    },
    parent_notifications: {
      title: 'Barnens aviseringar',
      help: 'Välj vem som bestämmer. "Alltid på" betyder att barnet inte kan stänga av dem; "Av" betyder att inget skickas till barnet.',
      child: 'Barnet bestämmer',
      on: 'Alltid på',
      off: 'Av',
      saved: 'Sparat.',
    },
    handover: {
      title: 'Belöningar att lämna över',
      help: 'De här har hämtats. Ge dem till barnet och tryck sedan på Överlämnad.',
      got: '{{name}} fick {{reward}}',
      handed_over: 'Överlämnad',
      done_toast: '{{reward}} är överlämnad till {{name}}.',
    },
    library: {
      access_denied: 'Bara vuxna kan öppna detta.',
      title: 'Uppgiftsbibliotek',
      intro: 'Börja från en standarduppgift eller en mall du sparat från en uppgift. Bara din familj ser "Mina mallar".',
      age: 'Ålder',
      age_group: 'Åldersgrupp',
      age_all: 'Alla åldrar',
      age_2_3: '2–3 år',
      age_4_5: '4–5 år',
      age_6_7: '6–7 år',
      age_8_9: '8–9 år',
      age_10_12: '10–12 år',
      age_13_plus: '13+ år',
      standard_title: 'Standarduppgifter',
      none_standard: 'Inga standarduppgifter för det här språket och åldern.',
      badge_standard: 'Standard',
      my_title: 'Mina mallar',
      my_intro: 'Sparade med "Spara som mall" i uppgiftsformuläret. Bara din familj kan se dem.',
      none_my: 'Inga sparade mallar än. Skapa en uppgift och använd "Spara som mall", eller ändra åldersfiltret.',
      badge_my: 'Min mall',
      use: 'Använd',
      save_as_template: 'Spara som mall',
      days: 'Dagar',
      stars: 'Stjärnor',
      tool_name: 'Verktygets namn',
      tool_name_ph: 't.ex. Dammsugare',
      username_required: 'Användarnamn (obligatoriskt)',
      username_ph: 'Välj ett användarnamn',
      pin_label: 'PIN (4–8 siffror)',
      pin_ph: 'Skriv en PIN',
      adults: 'Vuxna',
      show: 'Visa',
      hide: 'Dölj',
      added_to_tasks: '"{{title}}" har lagts till bland era uppgifter',
      chain_title: '{{n}} uppgifter som följer på varandra',
      chain_wait_one: 'nästa är klar efter {{n}} timme',
      chain_wait_other: 'nästa är klar efter {{n}} timmar',
      chain_loops: 'Sedan börjar den om.',
    },
    deletion: {
      delete_member: 'Radera medlem',
      delete_member_title: 'Radera {{name}}?',
      delete_member_text: '{{name}} tas bort från familjen direkt och loggas ut på alla enheter.',
      delete_member_lost: 'Alla {{name}}s stjärnor, statistik, uppgifter och belöningar raderas.',
      undo_30: 'Du kan ångra detta i 30 dagar, till {{date}}. Därefter raderas allt för gott och kan inte återställas.',
      delete_member_yes: 'Ja, radera',
      member_deleted_toast: '{{name}} har raderats. Du kan återställa i 30 dagar.',
      recently_deleted: 'Nyligen raderade',
      recently_deleted_hint: 'Du kan återställa dessa medlemmar fram till datumet som visas. Därefter raderas de och all deras data för gott.',
      gone_on: 'Raderas för gott {{date}}',
      restore: 'Återställ',
      member_restored_toast: '{{name}} är tillbaka i familjen. Aviseringar behöver slås på igen.',
      family_section: 'Radera familj',
      delete_family_row: 'Radera hela familjen',
      delete_family_hint: 'Alla och allt i familjen. Kan ångras i 30 dagar.',
      delete_family_title: 'Radera hela familjen?',
      delete_family_text: 'Alla i {{family}} loggas ut direkt och kan inte använda AntNest förrän familjen har återställts.',
      delete_family_lost: 'Allt raderas: alla medlemmar, stjärnor, statistik, uppgifter, belöningar, händelser och bilder.',
      type_family_name: 'Skriv familjens namn för att bekräfta: {{family}}',
      delete_family_yes: 'Radera familjen',
      pending_title: '{{family}} är schemalagd för radering',
      pending_text: 'Familjen och all dess data raderas för gott {{date}}. Fram till dess kan du återställa allt precis som det var.',
      days_left: 'Dagar kvar: {{days}}',
      pending_not_admin: 'Bara en vuxen med administratörsbehörighet kan återställa familjen.',
      restore_family: 'Återställ familjen',
      family_restored_toast: 'Välkommen tillbaka! Familjen har återställts. Aviseringar behöver slås på igen.',
      log_out: 'Logga ut',
      cancel: 'Avbryt',
    },
    pictures: {
      remove_picture: 'Ta bort bild',
      removed: 'Bilden är borttagen. Du har den vanliga myran igen.',
      unreadable: 'Bilden kunde inte öppnas. Prova en annan bild eller ta ett nytt foto.',
      rotate: 'Rotera',
      preparing: 'Förbereder bilden…',
    },
    billing: {
      title: 'Prenumeration',
      trial_title: 'Gratis provperiod – dagar kvar: {{days}}',
      trial_text: 'Din gratis provperiod slutar {{date}}. Välj en plan innan dess för att fortsätta använda AntNest.',
      expired_title: 'Din gratis provperiod är slut',
      expired_text: 'Välj en plan för att fortsätta använda AntNest. Familjens uppgifter, stjärnor och belöningar finns kvar.',
      active_title_monthly: 'Du betalar per månad',
      active_title_yearly: 'Du betalar per år',
      active_text: 'Din prenumeration förnyas {{date}}.',
      grace_title: 'En betalning gick inte igenom',
      grace_text: 'Uppdatera dina betalningsuppgifter senast {{date}} för att fortsätta använda AntNest.',
      cancelling_title: 'Din prenumeration är uppsagd',
      cancelling_text: 'Du kan använda AntNest till och med {{date}}. Välj en plan för att fortsätta efter det.',
      exempt_title: 'AntNest är gratis för er familj',
      exempt_text: 'Du behöver inte betala något.',
      plans_title: 'Välj en plan',
      plans_intro: 'En prenumeration räcker för hela familjen, på alla enheter.',
      plan_monthly: 'Månadsvis',
      plan_yearly: 'Årsvis',
      price_monthly: '{{price}} i månaden',
      price_yearly: '{{price}} per år',
      price_soon: 'Priset kommer snart',
      includes_family: 'Hela familjen, på alla enheter',
      includes_tasks: 'Alla färdiga uppgifter och checklistor',
      includes_cancel: 'Säg upp när du vill',
      choose_monthly: 'Välj månadsvis',
      choose_yearly: 'Välj årsvis',
      coming_soon: 'Betalning öppnar snart. Fram till dess är AntNest gratis.',
      manage_title: 'Hantera din prenumeration',
      manage_text: 'Byt plan, uppdatera ditt kort, se kvitton eller säg upp.',
      manage_button: 'Hantera prenumeration',
      banner_trial: 'Dagar kvar av din gratis provperiod: {{days}}.',
      banner_trial_last_day: 'Din gratis provperiod slutar i dag.',
      banner_payment: 'En betalning gick inte igenom. Uppdatera ditt kort senast {{date}}.',
      banner_cancelling: 'Din prenumeration slutar {{date}}.',
      banner_action: 'Se planer',
      paywall_title: 'AntNest tar en paus',
      paywall_text: 'Be en vuxen att välja en plan. Alla dina stjärnor och uppgifter finns kvar och väntar på dig.',
      manage_failed: 'Prenumerationssidan kunde inte öppnas. Försök igen om en stund.',
    },
    pocket: {
      title: 'Veckopeng',
      intro: 'Veckopeng räknas ut från stjärnorna varje barn får för godkända uppgifter under en månad. AntNest flyttar inga pengar: ni betalar som vanligt och markerar det som utbetalt.',
      no_children: 'Lägg till ett barn i familjen för att ge veckopeng.',
      currency: 'Valuta',
      for_child: 'Veckopeng till {{name}}',
      how_question: 'Hur tjänas de?',
      per_ten_label: '{{currency}} för var 10:e stjärna',
      fixed_label: '{{currency}} i månaden, när {{name}} har fått minst',
      threshold_label: 'stjärnor',
      threshold_help: '0 stjärnor = ges alltid (fast veckopeng).',
      bonus_label: 'Bonus:',
      bonus_every_label: '{{currency}} extra för var',
      bonus_stars_label: 'stjärnor till',
      cap_question: 'Gräns per månad — välj en',
      cap_none: 'Ingen gräns',
      cap_at: 'Högst',
      cap_unit: '{{currency}} i månaden',
      cap_required: 'Välj en gräns eller ingen gräns innan du sparar.',
      example_title: 'Till exempel en månad med:',
      example_line_one: '{{n}} stjärna → {{amount}}',
      example_line_other: '{{n}} stjärnor → {{amount}}',
      capped_short: 'gränsen nådd',
      typical_dk: 'Vanligt i Danmark per månad: under 100 DKK för 6–10 år, 50–200 DKK för 11–14 år, 200–500 DKK för 15–17 år.',
      save: 'Spara',
      saved: 'Veckopeng till {{name}} har sparats',
      save_failed: 'Kunde inte spara. Försök igen.',
      my_month: 'Veckopeng i {{month}}: {{amount}}',
      my_next_one: '{{n}} stjärna till ger {{amount}} mer',
      my_next_other: '{{n}} stjärnor till ger {{amount}} mer',
      my_capped: 'Du har nått månadens högsta belopp. Bra jobbat!',
      my_last_paid: '{{month}}: {{amount}} — utbetalt ✓',
      my_last_unpaid: '{{month}}: {{amount}} — väntar på utbetalning',
      to_pay_title: 'Veckopeng att betala ut',
      to_pay_help: 'Betala som vanligt (kontant, bank eller Swish) och markera det sedan som utbetalt.',
      mark_paid: 'Markera som utbetalt',
      task_counts: 'Ger veckopeng',
      task_counts_help: 'Ta bort bocken för vardagsuppgifter som att bädda sängen, så räknas bara större uppgifter in i veckopengen.',
      typical_intro: 'Vanligt i {{country}} per månad:',
      typical_band: '{{ages}} år: {{amount}}',
      typical_under: 'under {{amount}}',
      typical_about: 'cirka {{amount}}',
      typical_source: 'källa: {{source}}',
    },
    getting_started: {
      title: 'Kom igång',
      progress: '{{done}} av {{all}} klara',
      hide: 'Dölj',
      item_approve: 'Godkänn den första klara uppgiften',
      item_own_task: 'Ändra en uppgift, eller gör en egen',
      item_home_screen: 'Lägg AntNest på hemskärmen',
      item_calendar: 'Se uppgifterna i kalendern',
      home_screen_how: 'iPhone/iPad: Tryck på Dela i Safari och välj "Lägg till på hemskärmen". Android: Tryck på ⋮ i Chrome och välj "Installera app" eller "Lägg till på startskärmen". Gör det också på barnens enheter.',
      home_screen_done: 'Det har jag gjort',
      pocket_title: 'Veckopeng för stjärnorna?',
      pocket_text: 'Nu när barnen har kommit igång kan stjärnorna också ge veckopeng – ett fast belopp, ett belopp per 10 stjärnor och en gräns per månad som ni själva bestämmer.',
      pocket_setup: 'Ställ in det',
      pocket_no: 'Nej tack',
    },
    editor: {
      close: 'Stäng',
      unsaved_title: 'Spara dina ändringar?',
      unsaved_body: 'Du har ändrat något. Vill du spara innan du stänger?',
      save: 'Spara',
      discard: 'Spara inte',
      keep_editing: 'Fortsätt redigera',
    },
    timer: {
      start: 'Starta timer',
      pause: 'Pausa',
      resume: 'Fortsätt',
      restart: 'Börja om',
      times_up: 'Tiden är ute!',
      sound_bell: 'Klocka',
      sound_chime: 'Klockspel',
      sound_beeps: 'Väckarklocka',
      sound_marimba: 'Marimba',
      sound_fanfare: 'Fanfar',
      volume_note: 'Ljudet följer telefonens volym – höj den så att du hör det.',
    },
    approval_setting: {
      title: 'Godkännande av vuxnas uppgifter',
      label: 'Vuxnas uppgifter måste godkännas av en annan vuxen',
      help_off: 'Av: när en vuxen blir klar med en uppgift godkänns den direkt och stjärnorna är deras. Barns uppgifter godkänns alltid av en vuxen.',
      help_on: 'På: en vuxens färdiga uppgift väntar tills en annan vuxen godkänner den (har familjen bara en administratör kan den godkänna sina egna).',
      setup_help: 'Lämna den tom så godkänns vuxnas uppgifter så fort de är klara. Du kan ändra det senare under Inställningar.',
      saved: 'Sparat.',
    },
  },
  de: {
    auth: {
      forgot_title: 'Passwort vergessen?',
      forgot_link: 'Passwort vergessen?',
      forgot_confirmation: 'Falls ein Konto existiert, haben wir einen Link gesendet.',
      email_label: 'E‑Mail',
      send_reset_link: 'Link senden',
      reset_title: 'Neues Passwort festlegen',
      new_password_label: 'Neues Passwort',
      confirm_password_label: 'Passwort bestätigen',
      set_new_password: 'Neues Passwort speichern',
      password_mismatch: 'Passwörter stimmen nicht überein',
      password_policy_msg: 'Mindestens 12 Zeichen, Groß‑/Kleinbuchstaben und Zahl.',
      invalid_token: 'Ungültiger oder abgelaufener Link',
      reset_success: 'Passwort aktualisiert. Weiterleitung zur Anmeldung...',
      reset_failed: 'Zurücksetzen fehlgeschlagen. Bitte erneut versuchen.',
      login_failed: 'Anmeldung fehlgeschlagen. Bitte versuche es noch einmal.',
      beta_banner: 'AntNest ist in der Beta. Hast du einen Einladungscode?',
      beta_banner_link: 'Hier registrieren',
      or: 'oder',
      login_google: 'Mit Google anmelden',
      login_facebook: 'Mit Facebook anmelden',
      no_account: 'Noch kein Konto?',
      create_one: 'Jetzt erstellen',
      err_code_required: 'Ein Beta-Code ist nötig.',
      err_agree: 'Bitte stimme den Nutzungsbedingungen und der Datenschutzerklärung zu.',
      err_fields: 'Bitte fülle alle Felder aus.',
      err_code_invalid: 'Dieser Beta-Code ist ungültig oder wurde schon benutzt.',
      err_signup_failed: 'Das Konto konnte nicht erstellt werden. Bitte versuche es noch einmal.',
      signup_title: 'Konto erstellen',
      beta_code: 'Beta-Code',
      beta_code_ph: 'Einladungscode eingeben',
      full_name: 'Dein Name',
      full_name_ph: 'z. B. Anna',
      username: 'Benutzername',
      show_password: 'Passwort anzeigen',
      hide_password: 'Passwort verbergen',
      rule_length: 'Mindestens 12 Zeichen',
      rule_upper: 'Ein Großbuchstabe',
      rule_lower: 'Ein Kleinbuchstabe',
      rule_digit: 'Eine Zahl',
      password_match: 'Passwörter stimmen überein',
      family_name: 'Familienname',
      family_name_ph: 'z. B. Familie Müller',
      family_default: 'Meine Familie',
      agree_before: 'Ich stimme den',
      terms: 'Nutzungsbedingungen',
      and: 'und der',
      privacy: 'Datenschutzerklärung zu',
      creating_account: 'Konto wird erstellt…',
      create_account: 'Konto erstellen',
      signup_google: 'Mit Google registrieren',
      signup_facebook: 'Mit Facebook registrieren',
      choose_language: 'Sprache wählen',
      oauth_failed: 'Die Anmeldung wurde nicht abgeschlossen. Bitte versuche es noch einmal.',
      processing: 'Du wirst angemeldet…',
      complete_setup: 'Fast fertig',
      your_name: 'Dein Name',
      complete_setup_btn: 'Familie erstellen und weiter',
      login_apple: 'Mit Apple anmelden',
      signup_apple: 'Mit Apple registrieren',
    },
    rooms: {
      kitchen: 'Küche',
      living_room: 'Wohnzimmer',
      dining_room: 'Esszimmer',
      bedroom: 'Schlafzimmer',
      bathroom: 'Badezimmer',
      toilet: 'Toilette',
      hallway: 'Flur',
      entryway: 'Eingang',
      laundry_room: 'Waschküche',
      garage: 'Garage',
      office: 'Büro',
      playroom: 'Spielzimmer',
      nursery: 'Kinderzimmer',
      patio: 'Terrasse/Balkon',
      garden: 'Garten',
      storage: 'Abstellraum',
      basement: 'Keller',
      attic: 'Dachboden',
      child_bedroom: 'Kinderzimmer',
    },
    onboarding: {
      title: 'Lass uns anfangen',
      family_name: 'Familienname',
      adults: 'Erwachsene',
      children: 'Kinder',
      member_name: 'Name',
      username: 'Benutzername',
      pin_placeholder: 'PIN (4 Ziffern)',
      role_admin: 'Admin',
      role_member: 'Mitglied',
      next: 'Weiter',
      back: 'Zurück',
      confirm: 'Sieht gut aus',
      processing: 'Speichern…',
      step_rooms_title: 'Richte dein Zuhause ein',
      step_rooms_help: 'Klicke auf die untenstehenden Räume, um sie deinem Zuhause hinzuzufügen. Wenn du einen Raum oder Bereich hast, der nicht in der Liste steht, kannst du ihn manuell hinzufügen.\nDu kannst Räume jederzeit im Admin-Bereich bearbeiten, hinzufügen und entfernen.',
      custom_room_placeholder: 'Benutzerdefinierter Raumname',
      add_custom: 'Benutzerdef. hinzufügen',
      remove: 'Entfernen',
      step_review_title: 'Überprüfen',
      review_family: 'Familie',
      review_members: 'Mitglieder',
      review_rooms: 'Räume',
      error_generic: 'Beim Speichern ist etwas schiefgelaufen',
      step_members_help: 'Füge hier deine Familienmitglieder hinzu.\nGib jedem Familienmitglied einen Namen und einen Benutzernamen.\nJedes Familienmitglied benötigt eine PIN, um sich anzumelden.',
      validation_incomplete: 'Einige Felder sind nicht korrekt ausgefüllt',
      adult_label: 'Erwachsener',
      child_label: 'Kind',
      privacy_note: 'Diese Angaben werden nur zum Einrichten eurer Familie verwendet. Wir geben eure Daten oder persönlichen Informationen nicht weiter und verkaufen sie nicht – und werden das auch nie tun.',
      child_age: 'Alter',
      child_room_name: 'Zimmer von {{name}}',
      child_room_name_s: 'Zimmer von {{name}}',
      hint_members: 'Du kannst Familienmitglieder jederzeit später unter Admin → Mitglieder hinzufügen oder ändern.',
      hint_rooms: 'Jedes Kind bekommt ein eigenes Zimmer, damit seine Aufgaben am richtigen Ort sind. Du kannst die Räume jederzeit später unter Admin → Räume ändern.',
      step_tasks_title: 'Aufgaben für den Start',
      step_tasks_help: 'Wir haben ein paar Aufgaben ausgesucht, die zu euren Räumen und dem Alter eurer Kinder passen. Wählt ab, was ihr nicht wollt – ihr könnt alles später ändern.',
      starters_unavailable: 'Die Vorschläge konnten nicht geladen werden. Du kannst diesen Schritt überspringen und später Aufgaben hinzufügen.',
      tasks_for_child: 'Aufgaben von {{name}}',
      tasks_shared: 'Für alle (jeder kann sie nehmen)',
      whole_home: 'Ganzes Zuhause',
      hint_tasks: 'Unter Admin → Aufgaben gibt es viele weitere Standardaufgaben, und dort könnt ihr auch eigene anlegen.',
      skip_step: 'Überspringen',
      step_rewards_title: 'Belohnungen als Ziel',
      step_rewards_help: 'Kinder bleiben motiviert, wenn die erste Belohnung erreichbar ist. Diese sind so eingestellt, dass die erste in wenigen Tagen erreicht werden kann.',
      rewards_for_child: 'Für {{name}}',
      rewards_family: 'Für die ganze Familie (alle Sterne zählen)',
      hint_rewards: 'Ihr könnt jederzeit eigene Belohnungen hinzufügen oder diese ändern unter Admin → Belohnungen.',
      review_starters: 'Für den Start',
      review_tasks_one: '{{n}} Aufgabe',
      review_tasks_other: '{{n}} Aufgaben',
      review_rewards_one: '{{n}} Belohnung',
      review_rewards_other: '{{n}} Belohnungen',
      create_family: 'Unsere Familie anlegen',
      done_title: 'Eure Familie ist bereit!',
      done_made: 'Wir haben {{tasks}} Aufgaben und {{rewards}} Belohnungen für euch angelegt.',
      done_try: 'Probiert jetzt die erste Aufgabe: wechselt oben mit „{{switch}}" zu einem Kind und tippt bei einer Aufgabe auf „{{start}}".',
      done_admin: 'Wenn eine Aufgabe erledigt ist, bestätigst du sie unter „{{approve}}" – dann werden die Sterne gutgeschrieben.',
      go_home: 'Zur Startseite der Familie',
      go_admin: 'Zum Admin-Bereich',
    },
    common: {
      save: 'Speichern',
      take_photo: 'Foto aufnehmen',
      cancel: 'Abbrechen',
      loading: 'Lädt...',
      all: 'Alle',
      stars_one: '{{n}} Stern',
      stars_other: '{{n}} Sterne',
      update_ready: 'Eine neue Version von AntNest ist da.',
      update_now: 'Aktualisieren',
      remove_image: 'Bild entfernen',
      remove_item: 'Eintrag entfernen',
      ok: 'OK',
      error_generic: 'Etwas ist schiefgelaufen. Bitte versuche es erneut.',
    },
    header: {
      stats: 'Statistik',
      app_title: 'AntNest',
      home: 'Startseite',
      tasks: 'Aufgaben',
      calendar: 'Kalender',
      rewards: 'Belohnungen',
      settings: 'Einstellungen',
      profile: 'Profil',
      admin: 'Admin',
      logout: 'Abmelden',
      points: 'Punkte',
      settings_tooltip: 'Einstellungen',
      change_picture: 'Mein Bild ändern',
      someone_else: 'Jemand anderes ist dran',
      for_grownups: 'Für Erwachsene',
      for_grownups_hint: 'Einstellungen, Freigaben, Abmelden',
      my_page: 'Meine Seite',
      menu_close: 'Menü schließen',
      menu_open: 'Menü öffnen',
      nav_menu: 'Menü',
      master_admin: 'Haupt-Admin',
      switch_user: 'Benutzer wechseln',
      tab_bar_label: 'Hauptbereiche',
    },
    stats: {
      title: 'Statistik',
      tab_me: 'Ich',
      tab_family: 'Familie',
      tab_tasks: 'Aufgaben',
      period_week: 'Woche',
      period_month: 'Monat',
      period_year: 'Jahr',
      period_all: 'Gesamt',
      prev: 'Zurück',
      next: 'Weiter',
      all_time: 'Gesamt',
      tasks_done: 'Aufgaben erledigt',
      points: 'Punkte',
      rewards: 'Belohnungen',
      chart_points: 'Punkte im Zeitverlauf',
      where_helped: 'Wo ich geholfen habe',
      no_room: 'Kein Raum',
      my_top_tasks: 'Meine häufigsten Aufgaben',
      together: 'Zusammen',
      who_helped: 'Wer hat geholfen',
      everyone_over_time: 'Alle im Zeitverlauf',
      repeating_title: 'Aufgaben',
      per_task_title: 'Jede Aufgabe',
      times_done_axis: 'Wie oft erledigt',
      times: '{n}×',
      last_done: 'zuletzt: {date}',
      empty: 'Für diesen Zeitraum gibt es noch nichts.',
      family_hidden: 'Eure Familie zeigt das nur Erwachsenen.',
      week_short: 'KW {n}',
      enc_start: 'Ein frischer Start — jede Aufgabe zählt! 💪',
      enc_first: 'Deine erste Aufgabe — gut gemacht! 🎉',
      enc_done: 'Du hast {n} Aufgaben erledigt — super! ⭐',
      enc_more: 'Mehr Punkte als im Zeitraum davor — weiter so! 🚀',
      visibility_title: 'Statistik: was Kinder sehen',
      visibility_help: 'Lege fest, wie der Tab Familie für Kinder aussieht. Erwachsene Admins sehen immer alles.',
      visibility_team: 'Teamansicht — der Anteil aller, keine Rangliste (empfohlen)',
      visibility_full: 'Voller Vergleich — nach Punkten sortiert',
      visibility_admins: 'Ausgeblendet — nur Erwachsene sehen den Familienvergleich',
      visibility_saved: 'Gespeichert',
      team_view_note: 'Anteil aller an den Familienpunkten',
    },
    calendar: {
      title: 'Kalender',
      coming_soon: 'Der Kalender wird aufgebaut. Grundlegende Ansichten und Ereignisse erscheinen hier.',
      view_day: 'Tag',
      view_week: 'Woche',
      view_month: 'Monat',
      week_label: 'Woche',
      no_events_for_day: 'Keine Ereignisse',
      today_label: 'heute',
      prev_day: 'Vorheriger Tag',
      next_day: 'Nächster Tag',
      prev_week: 'Vorherige Woche',
      next_week: 'Nächste Woche',
      prev_month: 'Vorheriger Monat',
      next_month: 'Nächster Monat',
      go_to_today: 'Heute',
      not_allowed_edit_event: 'Du kannst nur Termine ändern, die du selbst erstellt hast.',
      filters_title: 'Nach Mitglied filtern',
      single_view_button: 'Einzelansicht',
      family_view_button: 'Familienansicht',
      no_events_for_range: 'Keine Ereignisse in diesem Zeitraum',
      no_events: 'Keine Ereignisse in diesem Zeitraum.',
      add_event: 'Termin hinzufügen',
      edit_event: 'Ereignis bearbeiten',
      event_form_title: 'Titel',
      event_form_description: 'Beschreibung (optional)',
      event_form_location: 'Ort (optional)',
      event_form_start: 'Beginn',
      event_form_end: 'Ende',
      event_form_all_day: 'Ganztägig',
      event_form_attendees: 'Teilnehmer',
      event_form_save: 'Speichern',
      event_form_cancel: 'Abbrechen',
      event_form_delete: 'Löschen',
      delete_confirm_title: 'Ereignis löschen',
      delete_confirm_message: 'Möchten Sie dieses Ereignis wirklich löschen?',
      success_created: 'Ereignis erstellt',
      success_updated: 'Ereignis aktualisiert',
      success_deleted: 'Ereignis gelöscht',
      error_generic: 'Etwas ist schief gelaufen',
      validation_title_required: 'Titel ist erforderlich',
      validation_end_after_start: 'Ende muss nach Beginn liegen',
      create_calendar_event: 'Kalenderereignis erstellen',
      event_modal_title_create: 'Kalenderereignis erstellen',
      event_modal_title_edit_prefix: 'Bearbeiten'
      ,
      reminder_label: 'Erinnerung',
      reminder_none: 'Keine Erinnerung',
      reminder_10m: '10 Minuten vorher',
      reminder_30m: '30 Minuten vorher',
      reminder_1h: '1 Stunde vorher',
      reminder_12h: '12 Stunden vorher',
      reminder_1d: '1 Tag vorher',
      nothing_planned: 'Noch nichts geplant',
      nothing_this_month: 'In diesem Monat ist nichts geplant',
      today_day: 'Heute, {{day}}',
      tomorrow_day: 'Morgen, {{day}}',
      at_time: 'Um {{time}}',
      all_day_label: 'Ganztägig',
      with_names: 'mit {{names}}',
      task_label: 'Aufgabe',
      everyone: 'Alle',
      back: 'Zurück',
      due_today: 'Heute fertig machen',
      due_today_at: 'Heute bis {{time}} fertig machen',
      due_tomorrow: 'Bis morgen fertig machen',
      due_tomorrow_at: 'Morgen bis {{time}} fertig machen',
      due_later: 'Bis zu diesem Tag fertig machen',
      due_later_at: 'Bis {{time}} fertig machen',
      overdue_since: 'Überfällig seit {{day}}',
      event_form_image: 'Bild zum Termin (optional)',
      remove_image: 'Bild entfernen',
      planned: 'Geplant',
    },
    settings: {
      title: 'Einstellungen',
      subtitle: 'Verwalten Sie Ihr Konto und Ihre Anwendungseinstellungen',
      dark_mode_section: 'Dunkelmodus',
      current_theme: 'Aktuelles Thema',
      following_system: 'Systemeinstellung wird befolgt',
      manual_selection: 'Manuelle Auswahl',
      follow_system_label: 'Systemeinstellung verwenden',
      auto_switch_hint: 'Automatisches Umschalten basierend auf Ihren Systemeinstellungen',
      enable_dark_mode: 'Dunkelmodus aktivieren',
      manual_toggle_hint: 'Manuell zwischen hellen und dunklen Themen wechseln',
      light_mode: 'Heller Modus',
      dark_mode: 'Dunkler Modus',
      more_settings_title: 'Weitere Einstellungen folgen bald',
      more_settings_desc: 'Weitere Anpassungsoptionen werden hier verfügbar sein, einschließlich Benachrichtigungen, Datenschutz und Kontoverwaltung.',
      language_section: 'Sprache',
      language_hint: 'Wählen Sie Ihre bevorzugte Sprache für die Oberfläche',
      back: 'Zurück',
      profile_parent: 'Elternteil. Name, Bild und Passwort ändern',
      profile_member: 'Name, Bild und PIN ändern',
      look: 'Aussehen',
      auto: 'Auto',
      tell_me_when: 'Sag mir Bescheid, wenn',
      notif_this_device: 'Benachrichtigungen auf diesem Gerät',
      notif_blocked_parent: 'In diesem Browser blockiert. Erlaube Benachrichtigungen für diese Seite in den Browser-Einstellungen.',
      n_review_ready: 'Eine Aufgabe muss bestätigt werden',
      n_task_approved: 'Meine Aufgabe wurde bestätigt',
      n_reward_ready: 'Eine Belohnung ist bereit',
      n_due_soon: 'Eine meiner Aufgaben ist bald fällig',
      n_overdue: 'Eine meiner Aufgaben ist überfällig',
      n_new_task: 'Eine neue Aufgabe ist frei',
      n_event: 'Kalender-Erinnerungen',
      daily_summary: 'Tägliche Zusammenfassung',
      daily_summary_time: 'Uhrzeit der täglichen Zusammenfassung',
      quiet_hours: 'Ruhezeiten',
      quiet_hours_hint: 'Keine Benachrichtigungen zwischen diesen Uhrzeiten',
      quiet_from: 'Ruhe ab',
      quiet_to: 'Ruhe bis',
      this_device: 'Dieses Gerät',
      change_pin: 'Meine PIN ändern',
      switch_person: 'Person wechseln',
      refresh_session: 'Dieses Gerät aktualisieren',
      session_refreshed: 'Dieses Gerät ist aktuell.',
      locked_until: 'Gesperrt bis {{time}}',
      unlock: 'Entsperren',
      unlocked: '@{{name}} kann sich wieder anmelden.',
      log_out_device: 'Von diesem Gerät abmelden',
      logout_confirm_title: 'Von diesem Gerät abmelden?',
      logout_confirm_text: 'Die ganze Familie {{family}} wird hier abgemeldet. Ein Erwachsener muss sich wieder anmelden, bevor jemand AntNest auf diesem Gerät nutzen kann.',
      logout_confirm_yes: 'Abmelden',
      cancel: 'Abbrechen',
      n_reward_claimed: 'Eine Belohnung wird eingelöst',
    },
    rewards: {
      title: 'Belohnungen',
      subtitle: 'Verfolgen Sie Ihren Fortschritt und lösen Sie Ihre Belohnungen ein',
      your_points: 'Ihre Punkte',
      family_points: 'Familienpunkte',
      claimed_rewards: 'Eingelöste Belohnungen',
      your_coins: 'Ihre Münzen',
      individual_rewards: 'Individuelle Belohnungen',
      no_individual_rewards: 'Noch keine individuellen Belohnungen verfügbar.',
      family_rewards: 'Familienbelohnungen',
      no_family_rewards: 'Noch keine Familienbelohnungen verfügbar.',
      goal_reached: 'Ziel erreicht!',
      complete_suffix: 'abgeschlossen',
      target_whole_family: 'Ganze Familie',
      claim_reward_button: 'Belohnung einlösen!',
      claimed_rewards_section: 'Eingelöste Belohnungen',
      no_claimed_rewards: 'Noch keine Belohnungen eingelöst. Weiter so!',
      claimed_on: 'Eingelöst am',
      unknown_date: 'Unbekanntes Datum',
      unknown_member: 'Unbekanntes Mitglied',
      reward_header_alt: 'Belohnungsbild',
      show_more: 'Mehr anzeigen',
      show_fewer: 'Weniger anzeigen',
      points_abbrev: 'pts',
      progress_counts_from: 'Fortschritt zählt Punkte ab dem {{date}} (und später).',
      lifetime_total_hint: 'Gesamtkontostand (unabhängig von Belohnungs-Zeitfenstern).',
      your_stars_label: 'deine Sterne',
      ready_to_get: 'Bereit zum Holen',
      keep_saving: 'Spare weiter für',
      n_more: 'noch {{n}}',
      get_it: 'Holen',
      grownup_gets: 'Die holt ein Erwachsener',
      got_one: '{{n}} Belohnung bekommen',
      got_other: '{{n}} Belohnungen bekommen',
      see_all: 'Alle ansehen',
      counting_since: 'Sterne zählen seit {{date}}',
      nothing_yet: 'Noch keine Belohnungen. Frag einen Erwachsenen!',
      claim_failed: 'Die Belohnung konnte nicht abgeholt werden. Bitte versuch es noch einmal.',
    },
    dashboard: {
      welcome_back: 'Willkommen zurück',
      subheading: 'Das passiert heute in Ihrer Familie',
      leaderboard_title: 'Familienrangliste',
      family_member: 'Familienmitglied',
      tasks_title: 'Aufgaben',
      available_tasks: 'Verfügbare Aufgaben',
      no_available_tasks: 'Zurzeit keine Aufgaben verfügbar!',
      my_active_tasks: 'Meine aktiven Aufgaben',
      no_my_tasks: 'Sie haben keine aktiven Aufgaben!',
      recently_completed_tasks: 'Kürzlich abgeschlossene Aufgaben',
      completed_tasks: 'Abgeschlossene Aufgaben',
      no_completed_tasks: 'Noch keine abgeschlossenen Aufgaben!',
      manage_all_tasks: 'Alle Aufgaben verwalten',
      my_rewards_title: 'Meine Belohnungen',
      my_points: 'Meine Punkte',
      available_rewards: 'Verfügbare Belohnungen',
      claimed_rewards: 'Eingelöste Belohnungen',
      next_reward_goal: 'Nächstes Belohnungsziel',
      all_rewards_claimed: 'Alle Belohnungen eingelöst! Gute Arbeit!',
      showing_rewards_help: 'Angezeigt: Ihre persönlichen und familienweiten Belohnungen',
      claim_now: 'Jetzt einlösen!',
      locked: 'Gesperrt',
      go_to_rewards: 'Zu den Belohnungen',
      my_tasks_primary_title: 'Meine Aufgaben',
      my_tasks_home_hint: 'Hier siehst du nur dir zugewiesene Aufgaben. Um neue Aufgaben zu wählen, öffne Aufgaben.',
      due_overdue: 'Überfällig',
      due_today: 'Fällig heute',
      due_no_deadline: 'Kein Fälligkeitsdatum',
      hi_name: 'Hallo {{name}}',
      goal_of: '{{have}} von {{need}} Sternen',
      goal_get_it: 'Holen',
      goal_none: 'Noch keine Belohnungen. Frag einen Erwachsenen, ob er eine anlegt!',
      today: 'Heute',
      no_tasks_now: 'Gerade keine Aufgaben',
      waiting_one: '{{n}} Stern, wenn ein Erwachsener Ja sagt',
      waiting_other: '{{n}} Sterne, wenn ein Erwachsener Ja sagt',
      family_goal: 'Familienziel',
      family_goal_more: 'Alle Sterne zählen. Noch {{n}} bis {{reward}}.',
      family_goal_ready: '{{reward}} ist bereit! Ein Erwachsener kann sie für die Familie holen.',
      find_tasks_cta: 'Aufgabe finden',
      due_tomorrow: 'Morgen',
      due_in_days_one: 'In {{n}} Tag',
      due_in_days_other: 'In {{n}} Tagen',
      more_stars_title: 'Willst du mehr Sterne?',
      more_stars_hint: 'Die darf jeder nehmen. Such dir eine aus, wenn du Lust hast!',
      see_all_can_take: 'Alle {{n}} ansehen',
      take_failed: 'Aufgabe konnte nicht übernommen werden',
    },
    task_board: {
      title: 'Aufgabentafel',
      subtitle: 'Verwalten und verfolgen Sie alle Familienaufgaben',
      available_tasks: 'Verfügbare Aufgaben',
      no_available_tasks: 'Zurzeit keine Aufgaben verfügbar!',
      my_active_tasks: 'Meine aktiven Aufgaben',
      no_my_tasks: 'Sie haben keine aktiven Aufgaben!',
      view: 'Ansehen',
      complete: 'Abschließen',
      tasks_assigned_to_others: 'Aufgaben, die anderen zugewiesen sind',
      no_tasks_assigned_to_others: 'Keine Aufgaben sind anderen Benutzern zugewiesen!',
      view_task: 'Aufgabe ansehen',
      completed_tasks: 'Abgeschlossene Aufgaben',
      no_completed_tasks: 'Noch keine abgeschlossenen Aufgaben!',
      take_task: 'Mach ich',
      take_over_task: 'Aufgabe übernehmen',
      unclaim_task: 'Zurückgeben',
      your_tasks: 'Deine Aufgaben',
      can_take: 'Die kannst du nehmen',
      waiting_heading: 'Wartet auf einen Erwachsenen',
      others_tasks: 'Aufgaben der anderen',
      done_one: '{{n}} Aufgabe erledigt',
      done_other: '{{n}} Aufgaben erledigt',
      moved_to_yours: '{{task}} ist jetzt bei Deine Aufgaben',
      nothing_to_take: 'Gerade keine Aufgaben zum Nehmen',
      no_your_tasks: 'Du hast gerade keine Aufgaben.',
    }
    ,
    login: {
      title: 'AntNest',
      email_label: 'E-Mail',
      password_label: 'Passwort',
      logging_in: 'Anmeldung läuft...',
      login_button: 'Anmelden',
      demo_account: 'Demokonto',
      demo_email: 'E-Mail: admin@family.com',
      demo_password: 'Passwort: admin'
    },
    user_profile: {
      user_not_found: 'Benutzer nicht gefunden.',
      back_to_home: 'Zurück zur Startseite',
      title: 'Benutzerprofil',
      profile_information: 'Profilinformationen',
      name: 'Name',
      email: 'E-Mail',
      email_not_set: 'Keine E-Mail festgelegt',
      role: 'Rolle',
      admin: 'Admin',
      family_member: 'Familienmitglied',
      points: 'Punkte',
      coins: 'Münzen',
      edit_profile: 'Profil bearbeiten',
      save_changes: 'Änderungen speichern',
      cancel: 'Abbrechen',
      email_optional_label: 'E-Mail (optional)',
      new_password_optional_label: 'Neues Passwort (optional)',
      name_placeholder: 'Geben Sie Ihren Namen ein',
      email_enter_placeholder: 'E-Mail-Adresse eingeben',
      email_add_placeholder: 'E-Mail hinzufügen',
      username_for_member_login: 'Benutzername (für Mitgliederanmeldung)',
      pin_label: 'PIN (4–8 Ziffern)',
      avatar: 'Avatar',
      avatar_hint: 'Wähle eine Vorlage oder lade ein eigenes Bild hoch.',
      upload: 'Hochladen',
      avatar_updated: 'Avatar aktualisiert',
      avatar_update_failed: 'Avatar konnte nicht aktualisiert werden',
      linked_accounts: 'Verknüpfte Konten',
      no_linked_accounts: 'Keine verknüpften Konten.',
      unlink: 'Trennen',
      unlink_failed: 'Trennen fehlgeschlagen',
      unlink_confirm: '{{provider}} trennen?',
      link_to_google: 'Mit Google verknüpfen',
      change_password: 'Passwort ändern',
      current_password: 'Aktuelles Passwort',
      new_password: 'Neues Passwort',
      password_changed: 'Passwort erfolgreich geändert',
      password_change_failed: 'Passwortänderung fehlgeschlagen',
      save_new_password: 'Neues Passwort speichern',
      pin_validation: 'PIN muss 4–8 Ziffern (nur Zahlen) haben',
      current_pin_label: 'Aktuelle PIN (nur nötig, wenn du schon eine hast)',
      current_pin_required: 'Gib deine aktuelle PIN ein, um sie zu ändern.',
      current_pin_incorrect: 'Deine aktuelle PIN ist falsch.',
      email_managed_by_login: 'Deine E-Mail-Adresse stammt von deiner Anmeldung und kann hier nicht geändert werden.',
      username_validation: 'Benutzername muss mindestens 2 Zeichen lang sein',
      profile_updated: 'Profil aktualisiert',
      google_link_success: 'Google-Konto verknüpft',
      picture_type_bad: 'Bitte wähle ein Bild im Format PNG, JPG oder WebP.',
      picture_too_big: 'Das Bild ist zu groß (max. 1 MB).',
      choose_picture: 'Dieses Bild wählen',
      or_pick_one: 'Oder wähle eins von diesen',
      tap_picture: 'Tippe auf das Bild, um ein neues zu wählen.',
      facebook_link_success: 'Facebook-Konto verknüpft',
      link_to_facebook: 'Mit Facebook verknüpfen',
      account_unlinked: 'Verknüpfung aufgehoben.',
      link_to_apple: 'Mit Apple verknüpfen',
    },
    admin: {
      header_title: 'Adminbereich',
      header_subtitle: 'Verwalten Sie Aufgaben, Belohnungen und Mitglieder Ihrer Familie',
      tab_tasks: 'Aufgaben',
      tab_rooms: 'Räume',
      tab_members: 'Mitglieder',
      tab_pending: 'Ausstehend',
      tab_rewards: 'Belohnungen',
      tab_tools: 'Werkzeuge'
    },
    checklist_editor: {
      back_to_admin: 'Zurück zum Adminbereich',
      edit_checklist_for: 'Checkliste bearbeiten',
      add_new_item: 'Neuen Checklistenpunkt hinzufügen',
      add_item: 'Hinzufügen',
      current_items: 'Aktuelle Checklistenpunkte',
      no_items: 'Noch keine Checklistenpunkte. Fügen Sie oben welche hinzu!',
      cancel: 'Abbrechen',
      save: 'Speichern'
    },
    task_details: {
      loading: 'Lädt...',
      title: 'Aufgabendetails',
      room: 'Zimmer',
      points: 'Punkte',
      status: 'Status',
      status_completed: 'Abgeschlossen',
      status_active: 'Aktiv',
      assigned_to: 'Zugewiesen an',
      due_date: 'Fälligkeitsdatum',
      tools_required: 'Benötigte Werkzeuge',
      checklist: 'Checkliste',
      task_completed: 'Aufgabe abgeschlossen',
      completed_by: 'Abgeschlossen von',
      message: 'Nachricht',
      submit_for_verification: 'Zur Überprüfung einreichen',
      close: 'Schließen',
      preview_room_placeholder: 'Wird vorgeschlagen, wenn du diese Aufgabe hinzufügst',
      preview_title: 'Aufgabe ansehen',
      preview_checklist_hint: 'Schritte (nur Vorschau — abhaken, nachdem du die Aufgabe hinzugefügt hast)',
      view: 'Ansehen',
    },
    task_flow: {
      start_task: 'Los geht\'s',
      tools_ready_title: 'Hast du alles?',
      tools_ready_subtitle: 'Diese Werkzeuge brauchst du. Hol sie dir, bevor du weitermachst.',
      ready_to_start_title: 'Bereit?',
      ready_to_start_body: 'Bist du bereit, die Aufgabe zu starten?',
      tools_ready_no: 'Noch nicht',
      tools_ready_yes: 'Ja, los geht’s',
      finish_step: 'Geschafft',
      final_put_tools_message:
        'Super! Lege alle Werkzeuge wieder an ihren Platz. Tippe dann auf den Button unten, um die Aufgabe abzuschließen.',
      complete_task_button: 'Fertig',
      no_checklist_warning: 'Diese Aufgabe hat noch keine Schritte. Bitte einen Erwachsenen, Checklistenschritte hinzuzufügen.',
      view_checklist_readonly_hint: 'Du kannst Schritte abhaken, nachdem du Aufgabe starten getippt hast.',
      progress_label: 'Fortschritt',
      step_counter_prefix: 'Schritt',
      not_assigned: 'Noch nicht zugewiesen',
      step_of: 'Schritt {{n}} von {{total}}',
      back_step: 'Einen Schritt zurück',
      tidy_title: 'Fast geschafft!',
      tidy_subtitle: 'Räum die Sachen wieder an ihren Platz',
      tidy_no_tools: 'Ist alles fertig? Dann tippe auf Fertig.',
      back_to_steps: 'Zurück zu den Schritten',
      celebrate_stars_title: '{{n}} Sterne sind unterwegs',
      celebrate_stars_body: 'Du bekommst sie, wenn ein Erwachsener Ja sagt',
      celebrate_button: 'Zurück zur Startseite',
      see_details: 'Details ansehen',
      step_tools: 'Du brauchst:',
      where_room: 'Wo: {{room}}',
      celebrate_heading: 'Super gemacht!',
      celebrate_subtitle: 'Du hast {{title}} geschafft',
      celebrate_stars_got_title: 'Du hast {{n}} Sterne bekommen',
      celebrate_stars_got_body: 'Bestätigt und zu deinen Sternen hinzugefügt',
      tidy_put_all_back: 'Räum all diese Sachen wieder an ihren Platz',
    },
    reward_celebration: {
      congratulations: 'Glückwunsch!',
      you_have_a_reward: 'Sie haben eine Belohnung!',
      check_rewards_prompt: 'Prüfen Sie den Belohnungsbereich, um sie einzulösen!'
    }
    ,
    admin_panel: {
      header_title: 'Adminbereich',
      header_subtitle: 'Verwalten Sie Aufgaben, Belohnungen und Mitglieder Ihrer Familie',
      tab_tasks: 'Aufgaben',
      tab_rooms: 'Räume',
      tab_members: 'Mitglieder',
      tab_pending: 'Ausstehend',
      tab_rewards: 'Belohnungen',
      tab_tools: 'Werkzeuge',
      task_management: 'Aufgabenverwaltung',
      create_new_task: 'Neue Aufgabe erstellen',
      active_tasks: 'Aktive Aufgaben',
      active_tasks_help: 'Aufgaben, die laufen oder verfügbar sind',
      edit: 'Bearbeiten',
      remove: 'Entfernen',
      mark_as_incomplete: 'Als unvollständig markieren',
      confirm_mark_incomplete_title: 'Är du säker på att du vill markera denna uppgift som inte slutförd',
      cancel: 'Abbrechen',
      confirm_yes_im_sure: 'Ja, ich bin sicher',
      no_active_tasks: 'Keine aktiven Aufgaben gefunden.',
      completed_tasks: 'Abgeschlossene Aufgaben',
      completed_tasks_help: 'Aufgaben, die abgeschlossen und verifiziert wurden',
      no_completed_tasks: 'Keine abgeschlossenen Aufgaben gefunden.',
      room_management: 'Raumverwaltung',
      add_new_room: 'Neuen Raum hinzufügen',
      member_management: 'Mitgliederverwaltung',
      add_new_member: 'Neues Mitglied hinzufügen',
      pending_verification: 'Ausstehende Aufgabenüberprüfung',
      verify_this_task: 'Diese Aufgabe verifizieren',
      self_verify_not_allowed_hint:
        'Ein anderer Administrator muss diese Aufgabe prüfen, wenn es mehr als einen Administrator gibt.',
      points_not_awarded_section: 'Verifiziert — Punkte fehlen',
      points_not_awarded_help:
        'Diese Aufgaben wurden als verifiziert markiert, bevor Punkte korrekt gespeichert wurden. Tippen Sie einmal auf «Punkte gutschreiben», um das Mitglied zu belohnen, das die Aufgabe erledigt hat.',
      award_missing_points: 'Punkte gutschreiben',
      no_tasks_needing_points: 'Für alle verifizierten Aufgaben sind Punkte erfasst.',
      verify_success: 'Aufgabe verifiziert. Punkte wurden gutgeschrieben.',
      verify_repaired_points_success:
        'Fehlende Punkte wurden dem Mitglied gutgeschrieben, das die Aufgabe erledigt hat.',
      verify_already_done: 'Diese Aufgabe ist bereits verifiziert und die Punkte sind erfasst.',
      verify_misattribution_credit_confirm:
        'Das System zeigt die Auszahlung als erledigt. Wenn das Guthaben des Mitglieds nie stieg: Einmalige Gutschrift jetzt? (Nicht wenn Punkte schon gutgeschrieben wurden.)',
      verify_misattribution_success: 'Einmalige Gutschrift angewendet. Mitglied soll aktualisieren oder neu anmelden.',
      verify_misattribution_already_done: 'Eine einmalige Korrekturgutschrift wurde für diese Aufgabe bereits verwendet.',
      verify_already_done_hint:
        'Wenn das Mitglied noch keine Punkte hat: erneut Punkte gutschreiben und Einmalbestätigung, oder points_awarded_on_verification in der DB leeren.',
      no_pending_verify: 'Keine Aufgaben warten auf Verifizierung.',
      reward_management: 'Belohnungsverwaltung',
      create_new_reward: 'Neue Belohnung erstellen',
      claimed: 'Eingelöst',
      available: 'Verfügbar',
      tool_management: 'Werkzeugverwaltung',
      create_new_tool: 'Neues Werkzeug erstellen',
      no_tools_msg: 'Noch keine Werkzeuge erstellt. Erstellen Sie das erste Werkzeug, um zu beginnen!',
      task_modal_title_create: 'Neue Aufgabe erstellen',
      task_modal_title_edit_prefix: 'Aufgabe bearbeiten',
      task_title_label: 'Aufgabentitel',
      task_title_placeholder: 'Aufgabentitel eingeben',
      description_label: 'Beschreibung (optional)',
      description_placeholder: 'Aufgabenbeschreibung eingeben',
      room_label: 'Zimmer',
      no_room_option: 'Kein Zimmer',
      assignment_label: 'Aufgabenzuweisung',
      available_anyone_option: 'Für alle verfügbar',
      assign_to_member_prefix: 'Zuweisen an',
      assignment_hint_assigned: 'Diese Aufgabe ist nur für das zugewiesene Mitglied sichtbar',
      assignment_hint_anyone: 'Diese Aufgabe ist für alle Familienmitglieder verfügbar',
      points_label: 'Zu vergebende Punkte',
      points_placeholder: 'Punkte',
      due_date_label: 'Fälligkeitsdatum',
      recurrence_label: 'Alle X Tage wiederholen (0 = keine Wiederholung)',
      auto_available_label: 'Automatisch verfügbar',
      header_image_label: 'Kopfzeilenbild (optional)',
      current_header_image_label: 'Aktuelles Kopfzeilenbild',
      checklist_label: 'Checklistenpunkte',
      checklist_item_placeholder: 'Checklistenpunkt eingeben',
      images_optional_label: 'Bilder (optional):',
      required_tools_label: 'Erforderliche Werkzeuge',
      no_tools_available_msg: 'Keine Werkzeuge verfügbar. Erstellen Sie zuerst Werkzeuge im Tab Werkzeuge.',
      cancel_btn: 'Abbrechen',
      create_task_btn: 'Aufgabe erstellen',
      save_changes_btn: 'Änderungen speichern',
      remove_task_title: 'Aufgabe entfernen',
      remove_task_prompt_prefix: 'Möchten Sie die Aufgabe wirklich entfernen',
      irreversible_note: 'Diese Aktion kann nicht rückgängig gemacht werden.',
      remove_task_btn: 'Aufgabe entfernen',
      reward_modal_title_create: 'Neue Belohnung erstellen',
      reward_modal_title_edit_prefix: 'Belohnung bearbeiten',
      reward_title_label: 'Belohnungstitel',
      reward_title_placeholder: 'Belohnungstitel eingeben',
      reward_desc_label: 'Beschreibung',
      reward_desc_placeholder: 'Belohnungsbeschreibung eingeben',
      points_required_label: 'Erforderliche Punkte',
      reward_progress_start_label: 'Punkte zählen ab (Datum)',
      reward_progress_start_hint: 'Nur Punkte ab Tagesbeginn (lokale Zeit) zählen für diese Belohnung.',
      target_type_label: 'Zieltyp',
      target_type_individual: 'Individuell',
      target_type_family: 'Familie',
      assign_member_label: 'Familienmitglied zuweisen',
      select_member_option: 'Familienmitglied auswählen',
      current_header_image_id_label: 'Aktuelle Kopfzeilenbild-ID',
      create_reward_btn: 'Belohnung erstellen',
      tool_modal_title_create: 'Neues Werkzeug erstellen',
      tool_modal_title_edit_prefix: 'Werkzeug bearbeiten',
      tool_name_label: 'Werkzeugname',
      tool_name_placeholder: 'Werkzeugnamen eingeben',
      tool_image_label: 'Werkzeugbild (optional)',
      current_tool_image_label: 'Aktuelles Werkzeugbild',
      create_tool_btn: 'Werkzeug erstellen',
      room_modal_title_add: 'Neuen Raum hinzufügen',
      room_modal_title_edit: 'Raum bearbeiten',
      delete_room_title: 'Raum löschen',
      delete_room_btn: 'Löschen',
      delete_room_prompt_prefix: 'Möchten Sie den Raum wirklich dauerhaft löschen',
      delete_room_blocked_message: 'Dieser Raum kann nicht gelöscht werden, da er von einer oder mehreren Aufgaben verwendet wird. Bitte löschen oder verschieben Sie diese Aufgaben zuerst.',
      go_to_tasks_btn: 'Aufgaben anzeigen',
      room_name_label_required: 'Raumname *',
      room_name_placeholder: 'Raumnamen eingeben',
      add_room_btn: 'Raum hinzufügen',
      member_modal_title_add: 'Neues Familienmitglied hinzufügen',
      member_name_label_required: 'Name *',
      member_name_placeholder: 'Mitgliedsnamen eingeben',
      member_password_label_required: 'Passwort *',
      member_password_placeholder: 'Passwort eingeben',
      member_email_label_optional: 'E-Mail (optional)',
      member_email_placeholder: 'E-Mail-Adresse eingeben',
      admin_access_label: 'Administrator-tilgang',
      add_member_btn: 'Mitglied hinzufügen',
      member_type_label: 'Medlemstyp',
      member_type_adult: 'Erwachsener',
      member_type_child: 'Kind',
      err_room_name: 'Bitte gib dem Raum einen Namen.',
      err_room_create: 'Der Raum konnte nicht erstellt werden. Bitte versuche es erneut.',
      err_room_update: 'Der Raum konnte nicht gespeichert werden. Bitte versuche es erneut.',
      err_generic_missing: 'Etwas fehlt. Bitte schließen und erneut versuchen.',
      err_task_title_points: 'Bitte gib der Aufgabe einen Titel und eine Anzahl Sterne.',
      err_checklist_empty: 'Bitte füge mindestens einen Schritt mit Text hinzu.',
      err_task_create: 'Die Aufgabe konnte nicht erstellt werden. Bitte versuche es erneut.',
      err_task_update: 'Die Aufgabe konnte nicht gespeichert werden. Bitte versuche es erneut.',
      err_task_remove: 'Die Aufgabe konnte nicht entfernt werden. Bitte versuche es erneut.',
      err_template_use: 'Aus dieser Vorlage konnte keine Aufgabe erstellt werden. Bitte versuche es erneut.',
      err_save_template: 'Die Vorlage konnte nicht gespeichert werden. Bitte versuche es erneut.',
      saved_template: 'Als Vorlage gespeichert.',
      err_header_image: 'Das Bild konnte nicht gespeichert werden. Bitte versuche es erneut.',
      err_reward_title: 'Bitte gib der Belohnung einen Titel.',
      err_reward_points: 'Bitte gib ein, wie viele Sterne die Belohnung braucht.',
      err_reward_member: 'Bitte wähle, für wen die Belohnung ist.',
      err_reward_create: 'Die Belohnung konnte nicht erstellt werden. Bitte versuche es erneut.',
      err_reward_update: 'Die Belohnung konnte nicht gespeichert werden. Bitte versuche es erneut.',
      reward_updated: 'Belohnung gespeichert.',
      err_tool_name: 'Bitte gib dem Werkzeug einen Namen.',
      err_tool_create: 'Das Werkzeug konnte nicht erstellt werden. Bitte versuche es erneut.',
      err_tool_update: 'Das Werkzeug konnte nicht gespeichert werden. Bitte versuche es erneut.',
      err_member_required: 'Ein neues Mitglied braucht Name, Benutzername und PIN.',
      err_pin_format: 'Die PIN muss 4–8 Ziffern haben (nur Zahlen).',
      err_username_short: 'Der Benutzername muss mindestens 2 Zeichen haben.',
      err_member_create: 'Das Mitglied konnte nicht hinzugefügt werden. Bitte versuche es erneut.',
      verify_misattribution_title: 'Die Sterne jetzt geben?',
      verify_misattribution_yes: 'Ja, geben',
      status_assigned: 'Für {{name}}',
      status_open: 'Jeder kann sie nehmen',
      comes_back_on: 'Kommt wieder am {{date}}',
      anyone_can_take: 'Jeder kann die nächste nehmen (sonst geht sie wieder an dieselbe Person)',
      repeat_hint: 'Mit "wiederholen alle" ist die nächste so viele Tage nach der Bestätigung fällig und erscheint "Tage zum Erledigen" Tage vorher.',
      step_tools_label: 'Werkzeuge für diesen Schritt',
      step_tools_hint: 'Wähle zuerst weiter unten die Werkzeuge der Aufgabe. Dann kannst du die für diesen Schritt auswählen.',
      room_picture_label: 'Bild vom Raum (optional)',
      room_picture_hint: 'Ein Foto hilft Kindern zu sehen, in welchem Raum eine Aufgabe ist.',
      room_picture_add: 'Bild hinzufügen',
      room_picture_change: 'Bild ändern',
      room_picture_failed: 'Der Raum wurde gespeichert, aber das Bild konnte nicht hochgeladen werden. Bitte versuch es noch einmal.',
      step_drag: 'Ziehen, um den Schritt zu verschieben',
      step_move_up: 'Schritt nach oben',
      step_move_down: 'Schritt nach unten',
      delete_btn: 'Löschen',
      show_more_completed_tasks: 'Mehr anzeigen',
      member_edit_title: 'Familienmitglied bearbeiten',
      member_name_label: 'Name',
      member_pin_label_optional: 'PIN (optional)',
      member_pin_hint_optional: 'Leer lassen, um die aktuelle PIN zu behalten. Die PIN muss 4–8 Ziffern haben.',
      member_is_admin_label: 'Admin-Zugang',
      member_update_failed: 'Mitglied konnte nicht aktualisiert werden',
      delete_reward_title: 'Belohnung löschen',
      delete_confirm_prefix: 'Wirklich löschen:',
      recreate_on_validation_label: 'Nach Bestätigung neu anlegen',
      recreate_on_validation_help: 'Die Aufgabe sofort neu anlegen, sobald ein Erwachsener sie bestätigt hat',
      days_to_complete_label: 'Tage bis zur Erledigung',
      days_to_complete_placeholder: 'z. B. 7',
      days_to_complete_help: 'Wenn gesetzt, ist das Fälligkeitsdatum das Erstelldatum + so viele Tage.',
      add_checklist_item_btn: 'Schritt hinzufügen',
      camera_title: 'Kamera',
      show_less_completed_tasks: 'Weniger anzeigen',
      delete_tool_title: 'Werkzeug löschen',
      task_saving: 'Aufgabe wird gespeichert…',
      task_saved: 'Aufgabe gespeichert',
      try_again: 'Nochmal versuchen',
      deadline_question: 'Frist',
      deadline_none: 'Keine Frist',
      deadline_within: 'Muss erledigt sein innerhalb von',
      days_unit: 'Tagen',
      repeat_question: 'Wenn sie bestätigt ist…',
      repeat_none: 'ist sie fertig – sie kommt nicht wieder',
      repeat_now: 'kommt sie sofort wieder',
      repeat_after: 'kommt sie wieder nach',
      next_question: 'Wer bekommt die nächste?',
      next_same: 'Wieder {{name}}',
      next_anyone: 'Jeder kann sie nehmen',
      summary_title: 'So läuft es:',
      due_this_time_label: 'Frist für dieses Mal (optional)',
      due_this_time_help: 'Leer lassen, dann gilt die Frist oben.',
      sum_now_person: '{{name}} bekommt die Aufgabe jetzt.',
      sum_now_anyone: 'Jeder in der Familie kann sie jetzt nehmen.',
      sum_within_one: 'Sie muss innerhalb von {{n}} Tag erledigt sein.',
      sum_within_other: 'Sie muss innerhalb von {{n}} Tagen erledigt sein.',
      sum_end: 'Wenn du sie bestätigst, ist sie fertig.',
      sum_back_now_same: 'Wenn du sie bestätigst, kommt sie sofort wieder zu {{name}}.',
      sum_back_now_anyone: 'Wenn du sie bestätigst, kommt sie sofort wieder und jeder kann sie nehmen.',
      sum_back_after_same_one: 'Wenn du sie bestätigst, kommt sie in {{n}} Tag wieder zu {{name}}.',
      sum_back_after_same_other: 'Wenn du sie bestätigst, kommt sie in {{n}} Tagen wieder zu {{name}}.',
      sum_back_after_anyone_one: 'Wenn du sie bestätigst, kommt sie in {{n}} Tag wieder und jeder kann sie nehmen.',
      sum_back_after_anyone_other: 'Wenn du sie bestätigst, kommt sie in {{n}} Tagen wieder und jeder kann sie nehmen.',
      sum_then_within_one: 'Dann ist {{n}} Tag Zeit dafür.',
      sum_then_within_other: 'Dann sind {{n}} Tage Zeit dafür.',
      repeat_next: 'ist die nächste Aufgabe',
      next_ready_after: 'Bereit nach',
      hours_unit: 'Stunden',
      waits_for: 'Wartet auf: {{task}}',
      then_next: 'Danach: {{task}}',
      sum_next: 'Wenn du sie bestätigst, kommt danach "{{task}}".',
      sum_next_wait_one: 'Sie ist {{n}} Stunde nach dem Erledigen dieser Aufgabe bereit.',
      sum_next_wait_other: 'Sie ist {{n}} Stunden nach dem Erledigen dieser Aufgabe bereit.',
      sum_next_waits: 'Bis dahin sehen die Kinder "{{task}}" nicht.',
      reward_saving: 'Belohnung wird gespeichert…',
      reward_saved: 'Belohnung gespeichert',
      assignment_hint_each: 'Jeder bekommt seine eigene Aufgabe: im eigenen Kalender, einzeln bestätigt, mit eigenen Sternen.',
      err_choose_person: 'Wähle mindestens eine Person.',
      names_and: '{{first}} und {{last}}',
      sum_now_each: '{{names}} bekommen jetzt jeder ihre eigene Aufgabe.',
      sum_back_now_each: 'Wenn du die Aufgabe einer Person bestätigst, kommt ihre sofort wieder.',
      sum_back_after_each_one: 'Wenn du die Aufgabe einer Person bestätigst, kommt ihre in {{n}} Tag wieder.',
      sum_back_after_each_other: 'Wenn du die Aufgabe einer Person bestätigst, kommt ihre in {{n}} Tagen wieder.',
      shared_ready: 'Bereit',
      remove_shared_note: 'Sie wird für alle entfernt, denen sie gegeben wurde. Bereits erledigte bleiben im Verlauf.',
      repeat_plan: 'kommt sie nach einem festen Plan wieder',
      plan_weekly: 'jede Woche am',
      plan_every: 'alle',
      plan_every_unit: 'Tage',
      plan_from: 'ab',
      sum_plan_person: '{{name}} bekommt die Aufgabe.',
      sum_plan_anyone: 'Jeder in der Familie kann sie nehmen.',
      sum_plan_each: '{{names}} bekommen jeder ihre eigene Aufgabe.',
      sum_plan_weekly: 'Sie erscheint jeden {{days}}.',
      sum_plan_every_one: 'Sie erscheint jeden Tag ab {{date}}.',
      sum_plan_every_other: 'Sie erscheint alle {{n}} Tage ab {{date}}.',
      sum_plan_back: 'Wenn du sie bestätigst, kommt sie am nächsten geplanten Tag wieder.',
      plan_week_every_one: 'jede Woche',
      plan_week_every_other: 'alle {{n}} Wochen',
      plan_on: 'am',
      plan_monthly: 'jeden Monat',
      plan_month_every_one: 'jeden Monat',
      plan_month_every_other: 'alle {{n}} Monate',
      plan_the: 'am',
      plan_which: 'welcher',
      plan_weekday: 'Wochentag',
      plan_nth_1: 'ersten',
      plan_nth_2: 'zweiten',
      plan_nth_3: 'dritten',
      plan_nth_4: 'vierten',
      plan_nth_last: 'letzten',
      plan_on_date: 'am',
      plan_date_unit: '. des Monats',
      sum_plan_weekly_every: 'Sie erscheint alle {{n}} Wochen, am {{days}}.',
      sum_plan_every_open_one: 'Sie erscheint jeden Tag.',
      sum_plan_every_open_other: 'Sie erscheint alle {{n}} Tage.',
      sum_plan_month_day_one: 'Sie erscheint am {{day}}. jedes Monats.',
      sum_plan_month_day_other: 'Sie erscheint am {{day}}. alle {{n}} Monate.',
      sum_plan_month_nth_one: 'Sie erscheint am {{nth}} {{weekday}} jedes Monats.',
      sum_plan_month_nth_other: 'Sie erscheint am {{nth}} {{weekday}} alle {{n}} Monate.',
      step_timer_label: 'Timer (optional)',
      step_timer_minutes: 'Timer, Minuten',
      step_timer_seconds: 'Timer, Sekunden',
      step_timer_min: 'Min.',
      step_timer_sec: 'Sek.',
      step_timer_clear: 'Kein Timer',
      step_timer_hint: 'Z. B. "Plank – 0:45". Wer die Aufgabe macht, startet ihn und hört einen Ton, wenn die Zeit um ist.',
    },
    notifications: {
      approved_said_yes: '{{admin}} hat Ja gesagt!',
      approved_for: 'für {{task}}',
      approved_note: '{{admin}} sagt: {{note}}',
      approved_now_you_have: 'Jetzt hast du {{n}} Sterne',
      approved_reward_ready: '{{reward}} ist bereit!',
      approved_more_to_goal: 'Noch {{n}} Sterne bis {{reward}}',
      approved_see_reward: 'Meine Belohnung ansehen',
      approved_not_now: 'Jetzt nicht',
      approved_ok: 'Juhu!',
      admin_pending_verification_one: '1 Aufgabe wartet auf deine Bestätigung.',
      admin_pending_verification_other: '{{count}} Aufgaben warten auf deine Bestätigung.',
    },
    member_chooser: {
      who_title: 'Wer bist du?',
      hi_name: 'Hallo {{name}}!',
      enter_pin: 'Gib deine PIN ein',
      log_in: 'Anmelden',
      use_password: 'Stattdessen Passwort verwenden',
      use_pin: 'Stattdessen PIN verwenden',
      password_label: 'Passwort',
      delete_digit: 'Löschen',
      back: 'Zurück',
      locked_until: 'Zu viele Versuche. Versuch es um {{time}} wieder.',
      locked_later: 'Zu viele Versuche. Versuch es etwas später wieder.',
      wrong_pin: 'Das hat nicht geklappt. Versuch es noch einmal.',
      sign_out_device_hint: 'Löscht die Familienanmeldung in diesem Browser. Nutze das, wenn du feststeckst oder dich wieder als Erwachsener mit Google/E-Mail anmelden möchtest.',
      sign_out_device: 'Von diesem Gerät abmelden',
    },
    parent: {
      to_approve_one: '{{n}} Aufgabe zum Freigeben',
      to_approve_other: '{{n}} Aufgaben zum Freigeben',
      finished: '{{name}} hat {{task}} erledigt',
      review: 'Ansehen',
      nothing_to_approve: 'Gerade nichts freizugeben',
      family: 'Familie',
      open_one: '{{n}} Aufgabe offen',
      open_other: '{{n}} Aufgaben offen',
      open_none: 'Keine offenen Aufgaben',
      set_up: 'Einrichten',
      tasks: 'Aufgaben',
      rewards: 'Belohnungen',
      members: 'Familie',
      rooms_tools: 'Räume und Werkzeuge',
      lock: 'Sperren',
      settings: 'Einstellungen',
      more_waiting: '+{{n}} weitere',
      your_tasks: 'Deine eigenen Aufgaben',
    },
    approve: {
      title: 'Aufgaben freigeben',
      approve: 'Freigeben',
      not_done: 'Noch nicht fertig',
      note_so_neat: 'So ordentlich!',
      note_well_done: 'Gut gemacht!',
      note_thank_you: 'Danke!',
      reason_look_again: 'Fast — schau noch mal nach',
      reason_steps_missing: 'Ein paar Schritte fehlen noch',
      reason_do_together: 'Lass es uns zusammen machen',
      pick_reason: 'Was soll {{name}} hören?',
      cancel: 'Abbrechen',
      steps_done_one: 'Der Schritt ist erledigt',
      steps_done_other: 'Alle {{n}} Schritte erledigt',
      approved_toast: '{{task}} freigegeben. {{name}} bekommt {{stars}}.',
      undo: 'Rückgängig',
      sent_back_toast: '{{task}} ging zurück an {{name}}.',
      today_at: 'Heute {{time}}',
      yesterday_at: 'Gestern {{time}}',
      from_grownup: 'Ein Erwachsener sagt: {{reason}}',
      back: 'Zurück',
      self_blocked: 'Eine andere erwachsene Person muss deine eigene Aufgabe freigeben.',
    },
    my_settings: {
      title: 'Meine Einstellungen',
      language: 'Sprache',
      same_as_device: 'Wie dieses Gerät',
      look: 'Hell oder dunkel',
      light: 'Hell',
      dark: 'Dunkel',
      notifications: 'Benachrichtigungen',
      on: 'An',
      off: 'Aus',
      notif_turn_on: 'Auf diesem Gerät einschalten',
      notif_turned_on: 'Benachrichtigungen sind an.',
      notif_turned_off: 'Benachrichtigungen sind aus.',
      notif_parent_off: 'Ein Erwachsener hat deine Benachrichtigungen ausgeschaltet.',
      notif_parent_on: 'Ein Erwachsener möchte, dass deine Benachrichtigungen an sind.',
      notif_unsupported: 'Dieses Gerät kann keine Benachrichtigungen von AntNest anzeigen.',
      notif_blocked: 'Benachrichtigungen sind in diesem Browser blockiert. Bitte einen Erwachsenen, sie in den Browser-Einstellungen zu erlauben.',
      timer_sound: 'Timer-Ton',
      timer_sound_help: 'Der Ton, wenn der Timer eines Schritts null erreicht. Tippe, um ihn zu hören.',
    },
    parent_notifications: {
      title: 'Benachrichtigungen der Kinder',
      help: 'Wähle, wer entscheidet. "Immer an" heißt, das Kind kann sie nicht ausschalten; "Aus" heißt, dem Kind wird nichts geschickt.',
      child: 'Das Kind entscheidet',
      on: 'Immer an',
      off: 'Aus',
      saved: 'Gespeichert.',
    },
    handover: {
      title: 'Belohnungen zum Übergeben',
      help: 'Diese wurden eingelöst. Gib sie dem Kind und tippe dann auf Übergeben.',
      got: '{{name}} hat {{reward}} bekommen',
      handed_over: 'Übergeben',
      done_toast: '{{reward}} an {{name}} übergeben.',
    },
    library: {
      access_denied: 'Nur Erwachsene können das öffnen.',
      title: 'Aufgabenbibliothek',
      intro: 'Starte mit einer Standardaufgabe oder einer Vorlage, die du aus einer Aufgabe gespeichert hast. Nur deine Familie sieht "Meine Vorlagen".',
      age: 'Alter',
      age_group: 'Altersgruppe',
      age_all: 'Alle Altersstufen',
      age_2_3: '2–3 Jahre',
      age_4_5: '4–5 Jahre',
      age_6_7: '6–7 Jahre',
      age_8_9: '8–9 Jahre',
      age_10_12: '10–12 Jahre',
      age_13_plus: '13+ Jahre',
      standard_title: 'Standardaufgaben',
      none_standard: 'Keine Standardaufgaben für diese Sprache und dieses Alter.',
      badge_standard: 'Standard',
      my_title: 'Meine Vorlagen',
      my_intro: 'Mit "Als Vorlage speichern" im Aufgabenformular gespeichert. Nur deine Familie kann sie sehen.',
      none_my: 'Noch keine gespeicherten Vorlagen. Erstelle eine Aufgabe und nutze "Als Vorlage speichern" oder ändere den Altersfilter.',
      badge_my: 'Meine Vorlage',
      use: 'Verwenden',
      save_as_template: 'Als Vorlage speichern',
      days: 'Tage',
      stars: 'Sterne',
      tool_name: 'Name des Werkzeugs',
      tool_name_ph: 'z. B. Staubsauger',
      username_required: 'Benutzername (Pflicht)',
      username_ph: 'Benutzernamen wählen',
      pin_label: 'PIN (4–8 Ziffern)',
      pin_ph: 'PIN eingeben',
      adults: 'Erwachsene',
      show: 'Anzeigen',
      hide: 'Ausblenden',
      added_to_tasks: '„{{title}}“ wurde zu euren Aufgaben hinzugefügt',
      chain_title: '{{n}} Aufgaben nacheinander',
      chain_wait_one: 'die nächste ist nach {{n}} Stunde bereit',
      chain_wait_other: 'die nächste ist nach {{n}} Stunden bereit',
      chain_loops: 'Dann geht es von vorne los.',
    },
    deletion: {
      delete_member: 'Mitglied löschen',
      delete_member_title: '{{name}} löschen?',
      delete_member_text: '{{name}} wird sofort aus der Familie entfernt und auf allen Geräten abgemeldet.',
      delete_member_lost: 'Alle Sterne, Statistiken, Aufgaben und Belohnungen von {{name}} werden gelöscht.',
      undo_30: 'Du kannst das 30 Tage lang rückgängig machen, bis zum {{date}}. Danach wird alles endgültig gelöscht und kann nicht wiederhergestellt werden.',
      delete_member_yes: 'Ja, löschen',
      member_deleted_toast: '{{name}} wurde gelöscht. Du kannst es 30 Tage lang wiederherstellen.',
      recently_deleted: 'Kürzlich gelöscht',
      recently_deleted_hint: 'Du kannst diese Mitglieder bis zum angezeigten Datum wiederherstellen. Danach werden sie mit allen Daten endgültig gelöscht.',
      gone_on: 'Endgültig gelöscht am {{date}}',
      restore: 'Wiederherstellen',
      member_restored_toast: '{{name}} ist wieder in der Familie. Benachrichtigungen müssen wieder eingeschaltet werden.',
      family_section: 'Familie löschen',
      delete_family_row: 'Die ganze Familie löschen',
      delete_family_hint: 'Alle und alles in der Familie. Kann 30 Tage lang rückgängig gemacht werden.',
      delete_family_title: 'Die ganze Familie löschen?',
      delete_family_text: 'Alle in {{family}} werden sofort abgemeldet und können AntNest erst wieder nutzen, wenn die Familie wiederhergestellt ist.',
      delete_family_lost: 'Alles wird gelöscht: alle Mitglieder, Sterne, Statistiken, Aufgaben, Belohnungen, Termine und Bilder.',
      type_family_name: 'Gib zur Bestätigung den Familiennamen ein: {{family}}',
      delete_family_yes: 'Familie löschen',
      pending_title: '{{family}} wird gelöscht',
      pending_text: 'Die Familie und alle ihre Daten werden am {{date}} endgültig gelöscht. Bis dahin kannst du alles genau so wiederherstellen, wie es war.',
      days_left: 'Verbleibende Tage: {{days}}',
      pending_not_admin: 'Nur ein Erwachsener mit Admin-Zugang kann die Familie wiederherstellen.',
      restore_family: 'Familie wiederherstellen',
      family_restored_toast: 'Willkommen zurück! Die Familie wurde wiederhergestellt. Benachrichtigungen müssen wieder eingeschaltet werden.',
      log_out: 'Abmelden',
      cancel: 'Abbrechen',
    },
    pictures: {
      remove_picture: 'Bild entfernen',
      removed: 'Bild entfernt. Du hast wieder die normale Ameise.',
      unreadable: 'Das Bild konnte nicht geöffnet werden. Versuch ein anderes Bild oder mach ein neues Foto.',
      rotate: 'Drehen',
      preparing: 'Bild wird vorbereitet…',
    },
    billing: {
      title: 'Abo',
      trial_title: 'Kostenlose Testphase – verbleibende Tage: {{days}}',
      trial_text: 'Deine kostenlose Testphase endet am {{date}}. Wähle vorher einen Tarif, um AntNest weiter zu nutzen.',
      expired_title: 'Deine kostenlose Testphase ist vorbei',
      expired_text: 'Wähle einen Tarif, um AntNest weiter zu nutzen. Die Aufgaben, Sterne und Belohnungen deiner Familie sind alle noch da.',
      active_title_monthly: 'Du zahlst monatlich',
      active_title_yearly: 'Du zahlst jährlich',
      active_text: 'Dein Abo verlängert sich am {{date}}.',
      grace_title: 'Eine Zahlung hat nicht geklappt',
      grace_text: 'Bitte aktualisiere deine Zahlungsdaten bis zum {{date}}, um AntNest weiter zu nutzen.',
      cancelling_title: 'Dein Abo ist gekündigt',
      cancelling_text: 'Du kannst AntNest noch bis zum {{date}} nutzen. Wähle einen Tarif, um danach weiterzumachen.',
      exempt_title: 'AntNest ist für eure Familie kostenlos',
      exempt_text: 'Du musst nichts bezahlen.',
      plans_title: 'Wähle einen Tarif',
      plans_intro: 'Ein Abo gilt für die ganze Familie, auf allen Geräten.',
      plan_monthly: 'Monatlich',
      plan_yearly: 'Jährlich',
      price_monthly: '{{price}} pro Monat',
      price_yearly: '{{price}} pro Jahr',
      price_soon: 'Preis folgt bald',
      includes_family: 'Die ganze Familie, auf allen Geräten',
      includes_tasks: 'Alle fertigen Aufgaben und Checklisten',
      includes_cancel: 'Jederzeit kündbar',
      choose_monthly: 'Monatlich wählen',
      choose_yearly: 'Jährlich wählen',
      coming_soon: 'Bezahlen ist bald möglich. Bis dahin ist AntNest kostenlos.',
      manage_title: 'Abo verwalten',
      manage_text: 'Tarif wechseln, Karte aktualisieren, Belege ansehen oder kündigen.',
      manage_button: 'Abo verwalten',
      banner_trial: 'Tage bis zum Ende deiner kostenlosen Testphase: {{days}}.',
      banner_trial_last_day: 'Deine kostenlose Testphase endet heute.',
      banner_payment: 'Eine Zahlung hat nicht geklappt. Aktualisiere deine Karte bis zum {{date}}.',
      banner_cancelling: 'Dein Abo endet am {{date}}.',
      banner_action: 'Tarife ansehen',
      paywall_title: 'AntNest macht eine Pause',
      paywall_text: 'Bitte einen Erwachsenen, einen Tarif auszuwählen. Alle deine Sterne und Aufgaben sind sicher und warten auf dich.',
      manage_failed: 'Die Abo-Seite konnte nicht geöffnet werden. Bitte versuche es gleich noch einmal.',
    },
    pocket: {
      title: 'Taschengeld',
      intro: 'Das Taschengeld wird aus den Sternen berechnet, die jedes Kind in einem Monat für bestätigte Aufgaben bekommt. AntNest überweist kein Geld: Ihr zahlt wie gewohnt und markiert es als ausgezahlt.',
      no_children: 'Füge der Familie ein Kind hinzu, um Taschengeld zu geben.',
      currency: 'Währung',
      for_child: 'Taschengeld für {{name}}',
      how_question: 'Wie wird es verdient?',
      per_ten_label: '{{currency}} für je 10 Sterne',
      fixed_label: '{{currency}} im Monat, wenn {{name}} mindestens',
      threshold_label: 'Sterne bekommen hat',
      threshold_help: '0 Sterne = gibt es immer (festes Taschengeld).',
      bonus_label: 'Bonus:',
      bonus_every_label: '{{currency}} extra für je',
      bonus_stars_label: 'weiteren Sterne',
      cap_question: 'Monatliche Grenze — wähle eine',
      cap_none: 'Keine Grenze',
      cap_at: 'Höchstens',
      cap_unit: '{{currency}} im Monat',
      cap_required: 'Wähle eine Grenze oder keine Grenze, bevor du speicherst.',
      example_title: 'Zum Beispiel in einem Monat mit:',
      example_line_one: '{{n}} Stern → {{amount}}',
      example_line_other: '{{n}} Sterne → {{amount}}',
      capped_short: 'Grenze erreicht',
      typical_dk: 'Üblich in Dänemark pro Monat: unter 100 DKK mit 6–10, 50–200 DKK mit 11–14, 200–500 DKK mit 15–17 Jahren.',
      save: 'Speichern',
      saved: 'Taschengeld für {{name}} gespeichert',
      save_failed: 'Konnte nicht speichern. Bitte versuche es noch einmal.',
      my_month: 'Taschengeld im {{month}}: {{amount}}',
      my_next_one: '{{n}} Stern mehr bringt {{amount}} mehr',
      my_next_other: '{{n}} Sterne mehr bringen {{amount}} mehr',
      my_capped: 'Du hast das Maximum für diesen Monat erreicht. Super!',
      my_last_paid: '{{month}}: {{amount}} — ausgezahlt ✓',
      my_last_unpaid: '{{month}}: {{amount}} — wird bald ausgezahlt',
      to_pay_title: 'Taschengeld zum Auszahlen',
      to_pay_help: 'Zahlt wie gewohnt (bar, Überweisung oder App) und markiert es dann als ausgezahlt.',
      mark_paid: 'Als ausgezahlt markieren',
      task_counts: 'Gibt Taschengeld',
      task_counts_help: 'Für Alltagsaufgaben wie Bett machen abwählen, damit nur größere Aufgaben zum Taschengeld zählen.',
      typical_intro: 'Üblich in {{country}} pro Monat:',
      typical_band: '{{ages}} Jahre: {{amount}}',
      typical_under: 'unter {{amount}}',
      typical_about: 'etwa {{amount}}',
      typical_source: 'Quelle: {{source}}',
    },
    getting_started: {
      title: 'Los geht\'s',
      progress: '{{done}} von {{all}} erledigt',
      hide: 'Ausblenden',
      item_approve: 'Die erste erledigte Aufgabe bestätigen',
      item_own_task: 'Eine Aufgabe anpassen oder eine eigene anlegen',
      item_home_screen: 'AntNest auf den Startbildschirm legen',
      item_calendar: 'Die Aufgaben im Kalender ansehen',
      home_screen_how: 'iPhone/iPad: In Safari auf Teilen tippen und „Zum Home-Bildschirm" wählen. Android: In Chrome auf ⋮ tippen und „App installieren" oder „Zum Startbildschirm hinzufügen" wählen. Auch auf den Geräten der Kinder.',
      home_screen_done: 'Erledigt',
      pocket_title: 'Taschengeld für die Sterne?',
      pocket_text: 'Jetzt, wo die Kinder loslegen, können die Sterne auch Taschengeld geben – ein fester Betrag, ein Betrag pro 10 Sterne und eine monatliche Grenze, die ihr selbst festlegt.',
      pocket_setup: 'Einrichten',
      pocket_no: 'Nein danke',
    },
    editor: {
      close: 'Schließen',
      unsaved_title: 'Änderungen speichern?',
      unsaved_body: 'Du hast etwas geändert. Möchtest du es vor dem Schließen speichern?',
      save: 'Speichern',
      discard: 'Nicht speichern',
      keep_editing: 'Weiter bearbeiten',
    },
    timer: {
      start: 'Timer starten',
      pause: 'Pause',
      resume: 'Weiter',
      restart: 'Von vorn',
      times_up: 'Die Zeit ist um!',
      sound_bell: 'Glocke',
      sound_chime: 'Glockenspiel',
      sound_beeps: 'Wecker',
      sound_marimba: 'Marimba',
      sound_fanfare: 'Fanfare',
      volume_note: 'Der Ton folgt der Lautstärke deines Handys – dreh sie auf, damit du ihn hörst.',
    },
    approval_setting: {
      title: 'Aufgaben von Erwachsenen bestätigen',
      label: 'Aufgaben von Erwachsenen muss ein anderer Erwachsener bestätigen',
      help_off: 'Aus: Wenn ein Erwachsener eine Aufgabe erledigt, ist sie sofort bestätigt und die Sterne gehören ihm. Aufgaben von Kindern bestätigt immer ein Erwachsener.',
      help_on: 'An: Die erledigte Aufgabe eines Erwachsenen wartet, bis ein anderer Erwachsener sie bestätigt (gibt es nur einen Admin, kann er seine eigenen bestätigen).',
      setup_help: 'Ohne Häkchen werden Aufgaben von Erwachsenen bestätigt, sobald sie erledigt sind. Du kannst das später in den Einstellungen ändern.',
      saved: 'Gespeichert.',
    },
  },
  nb: {
    auth: {
      forgot_title: 'Glemt passord?',
      forgot_link: 'Glemt passord?',
      forgot_confirmation: 'Hvis det finnes en konto, har vi sendt en tilbakestillingslenke.',
      email_label: 'E‑post',
      send_reset_link: 'Send lenke',
      reset_title: 'Sett nytt passord',
      new_password_label: 'Nytt passord',
      confirm_password_label: 'Bekreft passord',
      set_new_password: 'Lagre nytt passord',
      password_mismatch: 'Passordene er ikke like',
      password_policy_msg: 'Minst 12 tegn, inkluder store/små bokstaver og et tall.',
      invalid_token: 'Ugyldig eller utløpt lenke',
      reset_success: 'Passord oppdatert. Omdirigerer til innlogging...',
      reset_failed: 'Kunne ikke tilbakestille. Prøv igjen.',
      login_failed: 'Innloggingen mislyktes. Prøv igjen.',
      beta_banner: 'AntNest er i beta. Har du en invitasjonskode?',
      beta_banner_link: 'Opprett en konto her',
      or: 'eller',
      login_google: 'Logg inn med Google',
      login_facebook: 'Logg inn med Facebook',
      no_account: 'Har du ikke en konto?',
      create_one: 'Opprett en',
      err_code_required: 'Du trenger en betakode.',
      err_agree: 'Du må godta vilkårene og personvernerklæringen.',
      err_fields: 'Fyll ut alle feltene.',
      err_code_invalid: 'Denne betakoden er ugyldig eller allerede brukt.',
      err_signup_failed: 'Kontoen kunne ikke opprettes. Prøv igjen.',
      signup_title: 'Opprett kontoen din',
      beta_code: 'Betakode',
      beta_code_ph: 'Skriv invitasjonskoden din',
      full_name: 'Navnet ditt',
      full_name_ph: 'f.eks. Anna',
      username: 'Brukernavn',
      show_password: 'Vis passord',
      hide_password: 'Skjul passord',
      rule_length: 'Minst 12 tegn',
      rule_upper: 'En stor bokstav',
      rule_lower: 'En liten bokstav',
      rule_digit: 'Et tall',
      password_match: 'Passordene er like',
      family_name: 'Familiens navn',
      family_name_ph: 'f.eks. Familien Hansen',
      family_default: 'Min familie',
      agree_before: 'Jeg godtar',
      terms: 'vilkårene',
      and: 'og',
      privacy: 'personvernerklæringen',
      creating_account: 'Oppretter konto…',
      create_account: 'Opprett konto',
      signup_google: 'Opprett konto med Google',
      signup_facebook: 'Opprett konto med Facebook',
      choose_language: 'Velg språk',
      oauth_failed: 'Innloggingen ble ikke fullført. Prøv igjen.',
      processing: 'Logger deg inn…',
      complete_setup: 'Nesten ferdig',
      your_name: 'Navnet ditt',
      complete_setup_btn: 'Opprett familie og fortsett',
      login_apple: 'Logg inn med Apple',
      signup_apple: 'Opprett konto med Apple',
    },
    rooms: {
      kitchen: 'Kjøkken',
      living_room: 'Stue',
      dining_room: 'Spisestue',
      bedroom: 'Soverom',
      bathroom: 'Baderom',
      toilet: 'Toalett',
      hallway: 'Gang',
      entryway: 'Entré',
      laundry_room: 'Vaskerom',
      garage: 'Garasje',
      office: 'Kontor',
      playroom: 'Lekerom',
      nursery: 'Barnerom',
      patio: 'Terrasse/Balkong',
      garden: 'Hage',
      storage: 'Bod',
      basement: 'Kjeller',
      attic: 'Loft',
      child_bedroom: 'Barnerom',
    },
    onboarding: {
      title: 'La oss komme i gang',
      family_name: 'Familienavn',
      adults: 'Voksne',
      children: 'Barn',
      member_name: 'Navn',
      username: 'Brukernavn',
      pin_placeholder: 'PIN (4 siffer)',
      role_admin: 'Admin',
      role_member: 'Medlem',
      next: 'Neste',
      back: 'Tilbake',
      confirm: 'Ser bra ut',
      processing: 'Lagrer…',
      step_rooms_title: 'Sett opp hjemmet ditt',
      step_rooms_help: 'Klikk på rom nedenfor for å legge dem til i hjemmet ditt. Hvis du har et rom eller område som ikke er på listen, kan du legge det til manuelt.\nDu kan alltid redigere, legge til og fjerne rom i administrasjonen.',
      custom_room_placeholder: 'Egendefinert romnavn',
      add_custom: 'Legg til egendefinert',
      remove: 'Fjern',
      step_review_title: 'Gjennomgang',
      review_family: 'Familie',
      review_members: 'Medlemmer',
      review_rooms: 'Rom',
      error_generic: 'Noe gikk galt under lagring',
      step_members_help: 'Legg til familiemedlemmer her.\nGi hvert familiemedlem et navn og et brukernavn.\nHvert familiemedlem må ha en PIN-kode for å kunne logge inn.',
      validation_incomplete: 'Noen felter er ikke fylt ut riktig',
      adult_label: 'Voksen',
      child_label: 'Barn',
      privacy_note: 'Opplysningene brukes bare til å sette opp familien deres. Vi deler eller selger ikke – og vil aldri dele eller selge – dataene eller personopplysningene deres til noen.',
      child_age: 'Alder',
      child_room_name: '{{name}}s rom',
      child_room_name_s: '{{name}}\' rom',
      hint_members: 'Du kan alltid legge til eller endre familiemedlemmer senere under Admin → Medlemmer.',
      hint_rooms: 'Hvert barn får sitt eget rom, så deres egne oppgaver havner på riktig sted. Du kan alltid endre rommene senere under Admin → Rom.',
      step_tasks_title: 'Oppgaver for å komme i gang',
      step_tasks_help: 'Vi har valgt noen få oppgaver som passer til rommene deres og barnas alder. Fjern haken på dem dere ikke vil ha – dere kan endre alt senere.',
      starters_unavailable: 'Forslagene kunne ikke hentes. Du kan hoppe over og legge til oppgaver senere.',
      tasks_for_child: '{{name}}s egne oppgaver',
      tasks_shared: 'For alle (alle kan ta dem)',
      whole_home: 'Hele hjemmet',
      hint_tasks: 'Det finnes mange flere standardoppgaver under Admin → Oppgaver, og der kan dere også lage egne.',
      skip_step: 'Hopp over',
      step_rewards_title: 'Belønninger å spare til',
      step_rewards_help: 'Barn holder motivasjonen oppe når den første belønningen er innen rekkevidde. Disse er satt slik at den første kan nås på noen få dager.',
      rewards_for_child: 'Til {{name}}',
      rewards_family: 'For hele familien (alles stjerner teller)',
      hint_rewards: 'Dere kan alltid legge til egne belønninger eller endre disse under Admin → Belønninger.',
      review_starters: 'For å komme i gang',
      review_tasks_one: '{{n}} oppgave',
      review_tasks_other: '{{n}} oppgaver',
      review_rewards_one: '{{n}} belønning',
      review_rewards_other: '{{n}} belønninger',
      create_family: 'Opprett familien vår',
      done_title: 'Familien deres er klar!',
      done_made: 'Vi har laget {{tasks}} oppgaver og {{rewards}} belønninger til dere.',
      done_try: 'Prøv den første oppgaven nå: bytt til et barn med «{{switch}}» øverst, og trykk «{{start}}» på en oppgave.',
      done_admin: 'Når en oppgave er gjort, godkjenner du den under «{{approve}}» – da legges stjernene til.',
      go_home: 'Gå til familiens forside',
      go_admin: 'Gå til Admin',
    },
    common: {
      save: 'Lagre',
      take_photo: 'Ta bilde',
      cancel: 'Avbryt',
      ok: 'OK',
      loading: 'Laster...',
      all: 'Alle',
      stars_one: '{{n}} stjerne',
      stars_other: '{{n}} stjerner',
      update_ready: 'En ny versjon av AntNest er klar.',
      update_now: 'Oppdater',
      remove_image: 'Fjern bilde',
      remove_item: 'Fjern punkt',
      error_generic: 'Noe gikk galt. Prøv igjen.',
    },
    header: {
      stats: 'Statistikk',
      app_title: 'AntNest',
      home: 'Hjem',
      tasks: 'Oppgaver',
      calendar: 'Kalender',
      rewards: 'Belønninger',
      settings: 'Innstillinger',
      profile: 'Profil',
      admin: 'Admin',
      logout: 'Logg ut',
      points: 'poeng',
      settings_tooltip: 'Innstillinger',
      change_picture: 'Bytt bildet mitt',
      someone_else: 'Noen andres tur',
      for_grownups: 'For voksne',
      for_grownups_hint: 'Innstillinger, godkjenninger, logg ut',
      my_page: 'Min side',
      menu_close: 'Lukk meny',
      menu_open: 'Åpne meny',
      nav_menu: 'Meny',
      master_admin: 'Hovedadmin',
      switch_user: 'Bytt bruker',
      tab_bar_label: 'Hoveddeler',
    },
    stats: {
      title: 'Statistikk',
      tab_me: 'Meg',
      tab_family: 'Familie',
      tab_tasks: 'Oppgaver',
      period_week: 'Uke',
      period_month: 'Måned',
      period_year: 'År',
      period_all: 'Hele tiden',
      prev: 'Forrige',
      next: 'Neste',
      all_time: 'Hele tiden',
      tasks_done: 'oppgaver gjort',
      points: 'poeng',
      rewards: 'belønninger',
      chart_points: 'Poeng over tid',
      where_helped: 'Hvor jeg hjalp',
      no_room: 'Uten rom',
      my_top_tasks: 'Mine vanligste oppgaver',
      together: 'Sammen',
      who_helped: 'Hvem hjalp',
      everyone_over_time: 'Alle over tid',
      repeating_title: 'Oppgaver',
      per_task_title: 'Hver oppgave',
      times_done_axis: 'Antall ganger gjort',
      times: '{n}×',
      last_done: 'sist: {date}',
      empty: 'Ingenting her ennå for denne perioden.',
      family_hidden: 'Familien deres viser dette bare for voksne.',
      week_short: 'Uke {n}',
      enc_start: 'En ny start — hver oppgave teller! 💪',
      enc_first: 'Din første oppgave — bra jobbet! 🎉',
      enc_done: 'Du har gjort {n} oppgaver — flott! ⭐',
      enc_more: 'Flere poeng enn perioden før — fortsett sånn! 🚀',
      visibility_title: 'Statistikk: hva barn ser',
      visibility_help: 'Velg hvordan Familie-fanen ser ut for barn. Voksne administratorer ser alltid alt.',
      visibility_team: 'Lagvisning — alles andel, ingen rangering (anbefalt)',
      visibility_full: 'Full sammenligning — rangert etter poeng',
      visibility_admins: 'Skjult — bare voksne ser familiesammenligningen',
      visibility_saved: 'Lagret',
      team_view_note: 'Alles andel av familiens totale poeng',
    },
    calendar: {
      title: 'Kalender',
      coming_soon: 'Kalender bygges. Grunnleggende visninger og hendelser kommer her.',
      view_day: 'Dag',
      view_week: 'Uke',
      view_month: 'Måned',
      week_label: 'Uke',
      no_events_for_day: 'Ingen hendelser',
      today_label: 'i dag',
      prev_day: 'Forrige dag',
      next_day: 'Neste dag',
      prev_week: 'Forrige uke',
      next_week: 'Neste uke',
      prev_month: 'Forrige måned',
      next_month: 'Neste måned',
      go_to_today: 'I dag',
      not_allowed_edit_event: 'Du kan bare endre hendelser du har laget selv.',
      filters_title: 'Filtrer etter medlem',
      single_view_button: 'Enkeltvisning',
      family_view_button: 'Familievisning',
      no_events_for_range: 'Ingen hendelser i dette interval',
      no_events: 'Ingen hendelser i denne perioden.',
      add_event: 'Legg til hendelse',
      edit_event: 'Rediger hendelse',
      event_form_title: 'Tittel',
      event_form_description: 'Beskrivelse (valgfritt)',
      event_form_location: 'Sted (valgfritt)',
      event_form_start: 'Start',
      event_form_end: 'Slutt',
      event_form_all_day: 'Hele dagen',
      event_form_attendees: 'Deltakere',
      event_form_save: 'Lagre',
      event_form_cancel: 'Avbryt',
      event_form_delete: 'Slett',
      delete_confirm_title: 'Slett hendelse',
      delete_confirm_message: 'Er du sikker på at du vil slette denne hendelsen?',
      success_created: 'Hendelse opprettet',
      success_updated: 'Hendelse oppdatert',
      success_deleted: 'Hendelse slettet',
      error_generic: 'Noe gikk galt',
      validation_title_required: 'Tittel er påkrevd',
      validation_end_after_start: 'Slutt må være etter start',
      create_calendar_event: 'Opprett kalenderhendelse',
      event_modal_title_create: 'Opprett kalenderhendelse',
      event_modal_title_edit_prefix: 'Rediger'
      ,
      reminder_label: 'Påminnelse',
      reminder_none: 'Ingen påminnelse',
      reminder_10m: '10 minutter før',
      reminder_30m: '30 minutter før',
      reminder_1h: '1 time før',
      reminder_12h: '12 timer før',
      reminder_1d: '1 dag før',
      nothing_planned: 'Ingenting planlagt ennå',
      nothing_this_month: 'Ingenting planlagt denne måneden',
      today_day: 'I dag, {{day}}',
      tomorrow_day: 'I morgen, {{day}}',
      at_time: 'Kl. {{time}}',
      all_day_label: 'Hele dagen',
      with_names: 'med {{names}}',
      task_label: 'Oppgave',
      everyone: 'Alle',
      back: 'Tilbake',
      due_today: 'Skal gjøres i dag',
      due_today_at: 'Skal gjøres i dag før {{time}}',
      due_tomorrow: 'Skal gjøres innen i morgen',
      due_tomorrow_at: 'Skal gjøres i morgen før {{time}}',
      due_later: 'Skal gjøres innen denne dagen',
      due_later_at: 'Skal gjøres før {{time}}',
      overdue_since: 'Forsinket siden {{day}}',
      event_form_image: 'Bilde til hendelsen (valgfritt)',
      remove_image: 'Fjern bilde',
      planned: 'Planlagt',
    },
    settings: {
      title: 'Innstillinger',
      subtitle: 'Administrer kontoen din og app-innstillinger',
      dark_mode_section: 'Mørk modus',
      current_theme: 'Gjeldende tema',
      following_system: 'Følger systeminnstilling',
      manual_selection: 'Manuelt valg',
      follow_system_label: 'Følg systeminnstilling',
      auto_switch_hint: 'Bytt automatisk mellom lys og mørk modus basert på systeminnstillingene dine',
      enable_dark_mode: 'Aktiver mørk modus',
      manual_toggle_hint: 'Bytt manuelt mellom lyse og mørke temaer',
      light_mode: 'Lys modus',
      dark_mode: 'Mørk modus',
      more_settings_title: 'Flere innstillinger kommer snart',
      more_settings_desc: 'Flere tilpasningsalternativer blir tilgjengelige her, inkludert varsler, personvern og kontoadministrasjon.',
      language_section: 'Språk',
      language_hint: 'Velg ønsket språk for grensesnittet',
      back: 'Tilbake',
      profile_parent: 'Forelder. Endre navn, bilde og passord',
      profile_member: 'Endre navn, bilde og PIN',
      look: 'Utseende',
      auto: 'Auto',
      tell_me_when: 'Si fra når',
      notif_this_device: 'Varsler på denne enheten',
      notif_blocked_parent: 'Blokkert i denne nettleseren. Tillat varsler for siden i nettleserens innstillinger.',
      n_review_ready: 'En oppgave må godkjennes',
      n_task_approved: 'Oppgaven min er godkjent',
      n_reward_ready: 'En belønning er klar',
      n_due_soon: 'En av oppgavene mine skal snart være ferdig',
      n_overdue: 'En av oppgavene mine er forsinket',
      n_new_task: 'En ny oppgave er ledig',
      n_event: 'Påminnelser fra kalenderen',
      daily_summary: 'Daglig oversikt',
      daily_summary_time: 'Tidspunkt for den daglige oversikten',
      quiet_hours: 'Stille timer',
      quiet_hours_hint: 'Ingen varsler mellom disse tidspunktene',
      quiet_from: 'Stille fra',
      quiet_to: 'Stille til',
      this_device: 'Denne enheten',
      change_pin: 'Bytt PIN-koden min',
      switch_person: 'Bytt person',
      refresh_session: 'Oppdater denne enheten',
      session_refreshed: 'Denne enheten er oppdatert.',
      locked_until: 'Låst til {{time}}',
      unlock: 'Lås opp',
      unlocked: '@{{name}} kan logge inn igjen.',
      log_out_device: 'Logg ut av denne enheten',
      logout_confirm_title: 'Logge ut av denne enheten?',
      logout_confirm_text: 'Hele familien {{family}} blir logget ut her. En voksen må logge inn igjen før noen kan bruke AntNest på denne enheten.',
      logout_confirm_yes: 'Logg ut',
      cancel: 'Avbryt',
      n_reward_claimed: 'En belønning blir hentet',
    },
    rewards: {
      title: 'Belønninger',
      subtitle: 'Følg fremgangen din og hent belønningene dine',
      your_points: 'Dine poeng',
      family_points: 'Familiens poeng',
      claimed_rewards: 'Innfridde belønninger',
      your_coins: 'Dine mynter',
      individual_rewards: 'Individuelle belønninger',
      no_individual_rewards: 'Ingen individuelle belønninger tilgjengelig ennå.',
      family_rewards: 'Familiebelønninger',
      no_family_rewards: 'Ingen familiebelønninger tilgjengelig ennå.',
      goal_reached: 'Mål nådd!',
      complete_suffix: 'fullført',
      target_whole_family: 'Hele familien',
      claim_reward_button: 'Hent belønning!',
      claimed_rewards_section: 'Innfridde belønninger',
      no_claimed_rewards: 'Ingen belønninger innfridd ennå. Fortsett det gode arbeidet!',
      claimed_on: 'Innfridd den',
      unknown_date: 'Ukjent dato',
      unknown_member: 'Ukjent medlem',
      reward_header_alt: 'Belønningsbilde',
      show_more: 'Vis mer',
      show_fewer: 'Vis færre',
      points_abbrev: 'pts',
      progress_counts_from: 'Framgang teller poeng opptjent {{date}} eller senere.',
      lifetime_total_hint: 'Totalt saldo (uavhengig av enkelte belønninger).',
      your_stars_label: 'stjernene dine',
      ready_to_get: 'Klar til å hentes',
      keep_saving: 'Spar videre til',
      n_more: '{{n}} til',
      get_it: 'Hent den',
      grownup_gets: 'En voksen henter denne',
      got_one: '{{n}} belønning du har fått',
      got_other: '{{n}} belønninger du har fått',
      see_all: 'Se alle',
      counting_since: 'Teller stjerner fra {{date}}',
      nothing_yet: 'Ingen belønninger ennå. Be en voksen legge til noen!',
      claim_failed: 'Belønningen kunne ikke hentes. Prøv igjen.',
    },
    dashboard: {
      welcome_back: 'Velkommen tilbake',
      subheading: 'Dette skjer i familien din i dag',
      leaderboard_title: 'Familieledertavle',
      family_member: 'Familiemedlem',
      tasks_title: 'Oppgaver',
      available_tasks: 'Tilgjengelige oppgaver',
      no_available_tasks: 'Ingen oppgaver tilgjengelige akkurat nå!',
      my_active_tasks: 'Mine aktive oppgaver',
      no_my_tasks: 'Du har ingen aktive oppgaver!',
      completed_tasks: 'Fullførte oppgaver',
      no_completed_tasks: 'Ingen fullførte oppgaver ennå!',
      manage_all_tasks: 'Administrer alle oppgaver',
      my_rewards_title: 'Mine belønninger',
      my_points: 'Mine poeng',
      available_rewards: 'Tilgjengelige belønninger',
      claimed_rewards: 'Innfridde belønninger',
      next_reward_goal: 'Neste belønningsmål',
      all_rewards_claimed: 'Alle belønninger innfridd! Bra jobba!',
      showing_rewards_help: 'Viser: Dine personlige og familiebelønninger',
      claim_now: 'Hent nå!',
      locked: 'Låst',
      go_to_rewards: 'Gå til belønninger',
      my_tasks_primary_title: 'Mine oppgaver',
      my_tasks_home_hint: 'Her vises bare oppgaver som er tildelt deg. For å velge nye oppgaver, åpne Oppgaver.',
      due_overdue: 'Forfalt',
      due_today: 'Frist i dag',
      due_no_deadline: 'Ingen frist',
      hi_name: 'Hei {{name}}',
      goal_of: '{{have}} av {{need}} stjerner',
      goal_get_it: 'Hent den',
      goal_none: 'Ingen belønninger å jobbe mot ennå. Be en voksen legge til en!',
      today: 'I dag',
      no_tasks_now: 'Ingen oppgaver akkurat nå',
      waiting_one: '{{n}} stjerne når en voksen sier ja',
      waiting_other: '{{n}} stjerner når en voksen sier ja',
      family_goal: 'Familiens mål',
      family_goal_more: 'Alles stjerner teller. {{n}} igjen til {{reward}}.',
      family_goal_ready: '{{reward}} er klar! En voksen kan hente den for familien.',
      find_tasks_cta: 'Finn en oppgave',
      due_tomorrow: 'I morgen',
      due_in_days_one: 'Om {{n}} dag',
      due_in_days_other: 'Om {{n}} dager',
      more_stars_title: 'Vil du ha flere stjerner?',
      more_stars_hint: 'Disse kan alle ta. Velg en når du har lyst!',
      see_all_can_take: 'Se alle {{n}}',
      take_failed: 'Kunne ikke ta oppgaven',
    },
    task_board: {
      title: 'Oppgaveoversikt',
      subtitle: 'Administrer og spor alle familieoppgaver',
      available_tasks: 'Tilgjengelige oppgaver',
      no_available_tasks: 'Ingen oppgaver tilgjengelige akkurat nå!',
      my_active_tasks: 'Mine aktive oppgaver',
      no_my_tasks: 'Du har ingen aktive oppgaver!',
      view: 'Vis',
      complete: 'Fullfør',
      tasks_assigned_to_others: 'Oppgaver tildelt andre',
      no_tasks_assigned_to_others: 'Ingen oppgaver er tildelt andre brukere!',
      view_task: 'Vis oppgave',
      completed_tasks: 'Fullførte oppgaver',
      no_completed_tasks: 'Ingen fullførte oppgaver ennå!',
      take_task: 'Den tar jeg',
      take_over_task: 'Ta over oppgave',
      unclaim_task: 'Gi den tilbake',
      your_tasks: 'Dine oppgaver',
      can_take: 'Disse kan du ta',
      waiting_heading: 'Venter på en voksen',
      others_tasks: 'Andres oppgaver',
      done_one: '{{n}} oppgave gjort',
      done_other: '{{n}} oppgaver gjort',
      moved_to_yours: '{{task}} ligger nå under Dine oppgaver',
      nothing_to_take: 'Ingen oppgaver å ta akkurat nå',
      no_your_tasks: 'Du har ingen oppgaver akkurat nå.',
    }
    ,
    login: {
      title: 'AntNest',
      email_label: 'E-post',
      password_label: 'Passord',
      logging_in: 'Logger inn...',
      login_button: 'Logg inn',
      demo_account: 'Demokonto',
      demo_email: 'E-post: admin@family.com',
      demo_password: 'Passord: admin'
    },
    user_profile: {
      user_not_found: 'Bruker ikke funnet.',
      back_to_home: 'Tilbake til hjem',
      title: 'Brukerprofil',
      profile_information: 'Profilinformasjon',
      name: 'Navn',
      email: 'E-post',
      email_not_set: 'Ingen e-post angitt',
      role: 'Rolle',
      admin: 'Admin',
      family_member: 'Familiemedlem',
      points: 'Poeng',
      coins: 'Mynter',
      edit_profile: 'Rediger profil',
      save_changes: 'Lagre endringer',
      cancel: 'Avbryt',
      email_optional_label: 'E-post (valgfritt)',
      new_password_optional_label: 'Nytt passord (valgfritt)',
      name_placeholder: 'Skriv inn navnet ditt',
      email_enter_placeholder: 'Skriv inn e-postadresse',
      email_add_placeholder: 'Legg til e-post',
      username_for_member_login: 'Brukernavn (for medlemsinnlogging)',
      pin_label: 'PIN (4–8 sifre)',
      avatar: 'Avatar',
      avatar_hint: 'Velg en forhåndsinnstilling eller last opp din egen.',
      upload: 'Last opp',
      avatar_updated: 'Avatar oppdatert',
      avatar_update_failed: 'Kunne ikke oppdatere avatar',
      linked_accounts: 'Tilknyttede kontoer',
      no_linked_accounts: 'Ingen tilknyttede kontoer.',
      unlink: 'Koble fra',
      unlink_failed: 'Kunne ikke koble fra',
      unlink_confirm: 'Koble fra {{provider}}?',
      link_to_google: 'Koble til Google',
      change_password: 'Endre passord',
      current_password: 'Nåværende passord',
      new_password: 'Nytt passord',
      password_changed: 'Passord endret',
      password_change_failed: 'Kunne ikke endre passord',
      save_new_password: 'Lagre nytt passord',
      pin_validation: 'PIN må være 4–8 sifre (kun tall)',
      current_pin_label: 'Nåværende PIN (bare hvis du allerede har en)',
      current_pin_required: 'Skriv inn nåværende PIN for å endre den.',
      current_pin_incorrect: 'Nåværende PIN er feil.',
      email_managed_by_login: 'E-postadressen din kommer fra hvordan du logger inn, og kan ikke endres her.',
      username_validation: 'Brukernavn må være minst 2 tegn',
      profile_updated: 'Profil oppdatert',
      google_link_success: 'Google-konto koblet til',
      picture_type_bad: 'Velg et bilde i PNG, JPG eller WebP.',
      picture_too_big: 'Bildet er for stort (maks 1 MB).',
      choose_picture: 'Velg dette bildet',
      or_pick_one: 'Eller velg et av disse',
      tap_picture: 'Trykk på bildet for å velge et nytt.',
      facebook_link_success: 'Facebook-konto koblet til',
      link_to_facebook: 'Koble til Facebook',
      account_unlinked: 'Kontoen er koblet fra.',
      link_to_apple: 'Koble til Apple',
    },
    admin: {
      header_title: 'Adminpanel',
      header_subtitle: 'Administrer familiens oppgaver, belønninger og medlemmer',
      tab_tasks: 'Oppgaver',
      tab_rooms: 'Rom',
      tab_members: 'Medlemmer',
      tab_pending: 'Ventende',
      tab_rewards: 'Belønninger',
      tab_tools: 'Verktøy'
    },
    checklist_editor: {
      back_to_admin: 'Tilbake til adminpanelet',
      edit_checklist_for: 'Rediger sjekkliste',
      add_new_item: 'Legg til nytt sjekkpunkt',
      add_item: 'Legg til',
      current_items: 'Nåværende sjekkpunkter',
      no_items: 'Ingen sjekkpunkter ennå. Legg til ovenfor!',
      cancel: 'Avbryt',
      save: 'Lagre'
    },
    task_details: {
      loading: 'Laster...',
      title: 'Oppgavedetaljer',
      room: 'Rom',
      points: 'Poeng',
      status: 'Status',
      status_completed: 'Fullført',
      status_active: 'Aktiv',
      assigned_to: 'Tildelt til',
      due_date: 'Forfallsdato',
      tools_required: 'Nødvendige verktøy',
      checklist: 'Sjekkliste',
      task_completed: 'Oppgave fullført',
      completed_by: 'Fullført av',
      message: 'Melding',
      submit_for_verification: 'Send til verifisering',
      close: 'Lukk',
      preview_room_placeholder: 'Foreslås når du legger til oppgaven',
      preview_title: 'Forhåndsvis oppgave',
      preview_checklist_hint: 'Steg (kun forhåndsvisning — kryss av etter at du har lagt til oppgaven)',
      view: 'Vis',
    },
    task_flow: {
      start_task: 'Kjør på',
      tools_ready_title: 'Har du alt du trenger?',
      tools_ready_subtitle: 'Du trenger disse verktøyene. Finn dem før du fortsetter.',
      ready_to_start_title: 'Klar?',
      ready_to_start_body: 'Er du klar til å starte oppgaven?',
      tools_ready_no: 'Ikke ennå',
      tools_ready_yes: 'Ja, la oss starte',
      finish_step: 'Jeg gjorde det',
      final_put_tools_message:
        'Bra jobbet! Legg alle verktøy tilbake på plass. Trykk deretter på knappen under for å fullføre oppgaven.',
      complete_task_button: 'Helt ferdig',
      no_checklist_warning: 'Denne oppgaven har ingen steg ennå. Be en voksen legge til sjekkliste før du kan starte.',
      view_checklist_readonly_hint: 'Du kan krysse av steg etter at du har trykket Start oppgaven.',
      progress_label: 'Framgang',
      step_counter_prefix: 'Steg',
      not_assigned: 'Ikke tildelt ennå',
      step_of: 'Steg {{n}} av {{total}}',
      back_step: 'Gå ett steg tilbake',
      tidy_title: 'Nesten ferdig!',
      tidy_subtitle: 'Legg tingene tilbake der de hører hjemme',
      tidy_no_tools: 'Er alt ferdig? Trykk på Helt ferdig.',
      back_to_steps: 'Tilbake til stegene',
      celebrate_stars_title: '{{n}} stjerner er på vei',
      celebrate_stars_body: 'Du får dem når en voksen sier ja',
      celebrate_button: 'Tilbake til start',
      see_details: 'Se detaljer',
      step_tools: 'Du trenger:',
      where_room: 'Hvor: {{room}}',
      celebrate_heading: 'Godt jobbet!',
      celebrate_subtitle: 'Du er ferdig med {{title}}',
      celebrate_stars_got_title: 'Du fikk {{n}} stjerner',
      celebrate_stars_got_body: 'Godkjent og lagt til stjernene dine',
      tidy_put_all_back: 'Legg alle disse tingene tilbake der de hører hjemme',
    },
    reward_celebration: {
      congratulations: 'Gratulerer!',
      you_have_a_reward: 'Du har en belønning!',
      check_rewards_prompt: 'Gå til belønningssiden for å hente den!'
    }
    ,
    admin_panel: {
      header_title: 'Adminpanel',
      header_subtitle: 'Administrer familiens oppgaver, belønninger og medlemmer',
      tab_tasks: 'Oppgaver',
      tab_rooms: 'Rom',
      tab_members: 'Medlemmer',
      tab_pending: 'Ventende',
      tab_rewards: 'Belønninger',
      tab_tools: 'Verktøy',
      task_management: 'Oppgaveadministrasjon',
      create_new_task: 'Opprett ny oppgave',
      active_tasks: 'Aktive oppgaver',
      active_tasks_help: 'Oppgaver som pågår eller er tilgjengelige',
      edit: 'Rediger',
      remove: 'Fjern',
      no_active_tasks: 'Ingen aktive oppgaver funnet.',
      completed_tasks: 'Fullførte oppgaver',
      completed_tasks_help: 'Oppgaver som er fullført og verifisert',
      no_completed_tasks: 'Ingen fullførte oppgaver funnet.',
      room_management: 'Romadministrasjon',
      add_new_room: 'Tilføj nyt rum',
      member_management: 'Medlemsadministrasjon',
      add_new_member: 'Tilføj nyt medlem',
      pending_verification: 'Ventende oppgaveverifisering',
      verify_this_task: 'Verifiser denne oppgaven',
      self_verify_not_allowed_hint:
        'En annen administrator må verifisere denne oppgaven når det er mer enn én administrator.',
      points_not_awarded_section: 'Verifisert — mangler poeng',
      points_not_awarded_help:
        'Disse oppgavene ble markert som verifisert før poeng ble lagret riktig. Bruk «Tildel poeng» én gang for å godskrive medlemmet som fullførte oppgaven.',
      award_missing_points: 'Tildel poeng',
      no_tasks_needing_points: 'Alle verifiserte oppgaver har registrerte poeng.',
      verify_success: 'Oppgaven er verifisert. Poeng er tildelt.',
      verify_repaired_points_success:
        'Manglende poeng er tildelt medlemmet som fullførte oppgaven.',
      verify_already_done: 'Oppgaven er allerede verifisert og poeng er registrert.',
      verify_misattribution_credit_confirm:
        'Systemet viser utbetaling som fullført. Hvis medlemmets saldo aldri økte: engangskrediter nå? (Ikke hvis poeng allerede er mottatt.)',
      verify_misattribution_success: 'Engangskreditering brukt. Be medlemmet oppdatere eller logge inn på nytt.',
      verify_misattribution_already_done: 'Engangskreditering for feilregistrering er allerede brukt for denne oppgaven.',
      verify_already_done_hint:
        'Medlem mangler fortsatt poeng: prøv «Tildel poeng» igjen og bekreft engangskreditering, eller tøm points_awarded_on_verification i databasen.',
      no_pending_verify: 'Ingen oppgaver venter på verifisering.',
      reward_management: 'Belønningsadministrasjon',
      create_new_reward: 'Opret ny belønning',
      claimed: 'Innfridd',
      available: 'Tilgjengelig',
      tool_management: 'Verktøyadministrasjon',
      create_new_tool: 'Opprett nytt verktøy',
      no_tools_msg: 'Ingen verktøy opprettet ennå. Opprett ditt første verktøy for å starte!',
      task_modal_title_create: 'Opprett ny oppgave',
      task_modal_title_edit_prefix: 'Rediger oppgave',
      task_title_label: 'Oppgavetittel',
      task_title_placeholder: 'Skriv inn oppgavetittel',
      description_label: 'Beskrivelse (valgfritt)',
      description_placeholder: 'Skriv inn oppgavebeskrivelse',
      room_label: 'Rom',
      no_room_option: 'Ingen rom',
      assignment_label: 'Oppgavetildeling',
      available_anyone_option: 'Tilgjengelig for alle',
      assign_to_member_prefix: 'Tildel til',
      assignment_hint_assigned: 'Denne oppgaven er kun synlig for tildelt medlem',
      assignment_hint_anyone: 'Denne oppgaven er tilgjengelig for alle familiemedlemmer',
      points_label: 'Poeng å tildele',
      points_placeholder: 'Poeng',
      due_date_label: 'Forfallsdato',
      recurrence_label: 'Gjenta hver X dag (0 = ingen gjentakelse)',
      auto_available_label: 'Automatisk tilgjengelig',
      header_image_label: 'Toppbilde (valgfritt)',
      current_header_image_label: 'Gjeldende toppbilde',
      checklist_label: 'Sjekklistepunkter',
      checklist_item_placeholder: 'Skriv inn sjekklistepunkt',
      images_optional_label: 'Bilder (valgfritt):',
      required_tools_label: 'Nødvendige verktøy',
      no_tools_available_msg: 'Ingen verktøy tilgjengelige. Opprett verktøy først i fanen Verktøy.',
      cancel_btn: 'Avbryt',
      create_task_btn: 'Opprett oppgave',
      save_changes_btn: 'Lagre endringer',
      remove_task_title: 'Fjern oppgave',
      remove_task_prompt_prefix: 'Er du sikker på at du vil fjerne oppgaven',
      irreversible_note: 'Denne handlingen kan ikke angres.',
      remove_task_btn: 'Fjern oppgave',
      reward_modal_title_create: 'Opprett ny belønning',
      reward_modal_title_edit_prefix: 'Rediger belønning',
      reward_title_label: 'Belønningstittel',
      reward_title_placeholder: 'Skriv inn belønningstittel',
      reward_desc_label: 'Beskrivelse',
      reward_desc_placeholder: 'Skriv inn belønningsbeskrivelse',
      points_required_label: 'Påkrevde poeng',
      reward_progress_start_label: 'Poeng teller fra (dato)',
      reward_progress_start_hint: 'Bare poeng opptjent fra starten av denne dagen (din tid) teller.',
      target_type_label: 'Måltype',
      target_type_individual: 'Individuell',
      target_type_family: 'Familie',
      assign_member_label: 'Tildel familiemedlem',
      select_member_option: 'Velg et familiemedlem',
      current_header_image_id_label: 'Gjeldende toppbilde-ID',
      create_reward_btn: 'Opprett belønning',
      tool_modal_title_create: 'Opprett nytt verktøy',
      tool_modal_title_edit_prefix: 'Rediger verktøy',
      tool_name_label: 'Verktøynavn',
      tool_name_placeholder: 'Skriv inn verktøynavn',
      tool_image_label: 'Verktøybilde (valgfritt)',
      current_tool_image_label: 'Gjeldende verktøybilde',
      create_tool_btn: 'Opprett verktøy',
      room_modal_title_add: 'Legg til nytt rom',
      room_modal_title_edit: 'Rediger rum',
      delete_room_title: 'Slett rom',
      delete_room_btn: 'Slett',
      delete_room_prompt_prefix: 'Er du sikker på at du vil slette rommet permanent',
      delete_room_blocked_message: 'Dette rommet kan ikke slettes fordi en eller flere oppgaver bruker det. Slett eller omfordel disse oppgavene først.',
      go_to_tasks_btn: 'Vis oppgaver',
      room_name_label_required: 'Rumsnamn *',
      room_name_placeholder: 'Ange rumsnamn',
      add_room_btn: 'Lägg till rum',
      member_modal_title_add: 'Lägg till ny familjemedlem',
      member_name_label_required: 'Namn *',
      member_name_placeholder: 'Ange medlemsnamn',
      member_password_label_required: 'Lösenord *',
      member_password_placeholder: 'Ange lösenord',
      member_email_label_optional: 'E-post (valgfritt)',
      member_email_placeholder: 'Ange e-postadress',
      admin_access_label: 'Administrator-tilgang',
      add_member_btn: 'Legg til medlem',
      member_type_label: 'Medlemstype',
      member_type_adult: 'Voksen',
      member_type_child: 'Barn',
      err_room_name: 'Gi rommet et navn.',
      err_room_create: 'Rommet kunne ikke opprettes. Prøv igjen.',
      err_room_update: 'Rommet kunne ikke lagres. Prøv igjen.',
      err_generic_missing: 'Noe mangler. Lukk og prøv igjen.',
      err_task_title_points: 'Gi oppgaven en tittel og et antall stjerner.',
      err_checklist_empty: 'Legg til minst ett steg med tekst.',
      err_task_create: 'Oppgaven kunne ikke opprettes. Prøv igjen.',
      err_task_update: 'Oppgaven kunne ikke lagres. Prøv igjen.',
      err_task_remove: 'Oppgaven kunne ikke fjernes. Prøv igjen.',
      err_template_use: 'Det gikk ikke å lage en oppgave fra malen. Prøv igjen.',
      err_save_template: 'Malen kunne ikke lagres. Prøv igjen.',
      saved_template: 'Lagret som mal.',
      err_header_image: 'Bildet kunne ikke lagres. Prøv igjen.',
      err_reward_title: 'Gi belønningen en tittel.',
      err_reward_points: 'Skriv hvor mange stjerner belønningen krever.',
      err_reward_member: 'Velg hvem belønningen er til.',
      err_reward_create: 'Belønningen kunne ikke opprettes. Prøv igjen.',
      err_reward_update: 'Belønningen kunne ikke lagres. Prøv igjen.',
      reward_updated: 'Belønningen er lagret.',
      err_tool_name: 'Gi redskapet et navn.',
      err_tool_create: 'Redskapet kunne ikke opprettes. Prøv igjen.',
      err_tool_update: 'Redskapet kunne ikke lagres. Prøv igjen.',
      err_member_required: 'Et nytt medlem trenger navn, brukernavn og PIN.',
      err_pin_format: 'PIN må være 4–8 sifre (bare tall).',
      err_username_short: 'Brukernavnet må være minst 2 tegn.',
      err_member_create: 'Medlemmet kunne ikke legges til. Prøv igjen.',
      verify_misattribution_title: 'Gi stjernene nå?',
      verify_misattribution_yes: 'Ja, gi dem',
      status_assigned: 'Til {{name}}',
      status_open: 'Alle kan ta den',
      comes_back_on: 'Kommer igjen {{date}}',
      anyone_can_take: 'Alle kan ta den neste (ellers går den til samme person igjen)',
      repeat_hint: 'Med "gjenta hver" skal den neste være ferdig så mange dager etter godkjenningen og dukker opp "dager til å fullføre" dager før.',
      step_tools_label: 'Verktøy til dette steget',
      step_tools_hint: 'Velg først oppgavens verktøy lenger ned. Så kan du velge dem dette steget trenger.',
      room_picture_label: 'Bilde av rommet (valgfritt)',
      room_picture_hint: 'Et bilde hjelper barna å se hvilket rom en oppgave er i.',
      room_picture_add: 'Legg til bilde',
      room_picture_change: 'Bytt bilde',
      room_picture_failed: 'Rommet er lagret, men bildet kunne ikke lastes opp. Prøv igjen.',
      step_drag: 'Dra for å flytte steget',
      step_move_up: 'Flytt steget opp',
      step_move_down: 'Flytt steget ned',
      delete_btn: 'Slett',
      show_more_completed_tasks: 'Vis flere',
      member_edit_title: 'Rediger familiemedlem',
      member_name_label: 'Navn',
      member_pin_label_optional: 'PIN (valgfritt)',
      member_pin_hint_optional: 'La feltet stå tomt for å beholde nåværende PIN. PIN må være 4–8 sifre.',
      member_is_admin_label: 'Administratortilgang',
      member_update_failed: 'Kunne ikke oppdatere medlemmet',
      delete_reward_title: 'Slett belønning',
      delete_confirm_prefix: 'Er du sikker på at du vil slette',
      recreate_on_validation_label: 'Opprett på nytt ved godkjenning',
      recreate_on_validation_help: 'Opprett oppgaven på nytt med en gang en voksen har godkjent den',
      days_to_complete_label: 'Antall dager til å fullføre oppgaven',
      days_to_complete_placeholder: 'f.eks. 7',
      days_to_complete_help: 'Hvis satt, blir forfallsdatoen opprettelsesdatoen + så mange dager.',
      add_checklist_item_btn: 'Legg til steg',
      camera_title: 'Kamera',
      show_less_completed_tasks: 'Vis færre',
      delete_tool_title: 'Slett verktøy',
      task_saving: 'Lagrer oppgaven…',
      task_saved: 'Oppgaven er lagret',
      try_again: 'Prøv igjen',
      deadline_question: 'Frist',
      deadline_none: 'Ingen frist',
      deadline_within: 'Må gjøres innen',
      days_unit: 'dager',
      repeat_question: 'Når den er godkjent…',
      repeat_none: 'er den ferdig – den kommer ikke igjen',
      repeat_now: 'kommer den tilbake med en gang',
      repeat_after: 'kommer den tilbake etter',
      next_question: 'Hvem får den neste?',
      next_same: '{{name}} igjen',
      next_anyone: 'Alle kan ta den',
      summary_title: 'Slik blir det:',
      due_this_time_label: 'Frist denne gangen (valgfritt)',
      due_this_time_help: 'La den stå tom, så brukes fristen over.',
      sum_now_person: '{{name}} får oppgaven nå.',
      sum_now_anyone: 'Alle i familien kan ta den nå.',
      sum_within_one: 'Den må være gjort innen {{n}} dag.',
      sum_within_other: 'Den må være gjort innen {{n}} dager.',
      sum_end: 'Når du godkjenner den, er den ferdig.',
      sum_back_now_same: 'Når du godkjenner den, kommer den tilbake til {{name}} med en gang.',
      sum_back_now_anyone: 'Når du godkjenner den, kommer den tilbake med en gang, og alle kan ta den.',
      sum_back_after_same_one: 'Når du godkjenner den, kommer den tilbake til {{name}} om {{n}} dag.',
      sum_back_after_same_other: 'Når du godkjenner den, kommer den tilbake til {{name}} om {{n}} dager.',
      sum_back_after_anyone_one: 'Når du godkjenner den, kommer den tilbake om {{n}} dag, og alle kan ta den.',
      sum_back_after_anyone_other: 'Når du godkjenner den, kommer den tilbake om {{n}} dager, og alle kan ta den.',
      sum_then_within_one: 'Da er det {{n}} dag til å gjøre den.',
      sum_then_within_other: 'Da er det {{n}} dager til å gjøre den.',
      repeat_next: 'er neste oppgave',
      next_ready_after: 'Klar etter',
      hours_unit: 'timer',
      waits_for: 'Venter på: {{task}}',
      then_next: 'Deretter: {{task}}',
      sum_next: 'Når du godkjenner den, kommer "{{task}}" etterpå.',
      sum_next_wait_one: 'Den er klar {{n}} time etter at denne ble gjort.',
      sum_next_wait_other: 'Den er klar {{n}} timer etter at denne ble gjort.',
      sum_next_waits: 'Inntil da kan ikke barna se "{{task}}".',
      reward_saving: 'Lagrer belønningen…',
      reward_saved: 'Belønningen er lagret',
      assignment_hint_each: 'Hver får sin egen oppgave: i sin egen kalender, godkjent for seg og med sine egne stjerner.',
      err_choose_person: 'Velg minst én person.',
      names_and: '{{first}} og {{last}}',
      sum_now_each: '{{names}} får hver sin oppgave nå.',
      sum_back_now_each: 'Når du godkjenner en persons oppgave, kommer den personens tilbake med en gang.',
      sum_back_after_each_one: 'Når du godkjenner en persons oppgave, kommer den personens tilbake om {{n}} dag.',
      sum_back_after_each_other: 'Når du godkjenner en persons oppgave, kommer den personens tilbake om {{n}} dager.',
      shared_ready: 'Klar',
      remove_shared_note: 'Den fjernes for alle den ble gitt til. Oppgaver som allerede er gjort, blir i historikken.',
      repeat_plan: 'kommer den tilbake etter en fast plan',
      plan_weekly: 'hver uke på',
      plan_every: 'hver',
      plan_every_unit: '. dag',
      plan_from: 'fra',
      sum_plan_person: '{{name}} får oppgaven.',
      sum_plan_anyone: 'Alle i familien kan ta den.',
      sum_plan_each: '{{names}} får hver sin oppgave.',
      sum_plan_weekly: 'Den dukker opp hver {{days}}.',
      sum_plan_every_one: 'Den dukker opp hver dag fra {{date}}.',
      sum_plan_every_other: 'Den dukker opp hver {{n}}. dag fra {{date}}.',
      sum_plan_back: 'Når du godkjenner den, kommer den tilbake neste planlagte dag.',
      plan_week_every_one: 'hver uke',
      plan_week_every_other: 'hver {{n}}. uke',
      plan_on: 'på',
      plan_monthly: 'hver måned',
      plan_month_every_one: 'hver måned',
      plan_month_every_other: 'hver {{n}}. måned',
      plan_the: 'den',
      plan_which: 'hvilken',
      plan_weekday: 'ukedag',
      plan_nth_1: 'første',
      plan_nth_2: 'andre',
      plan_nth_3: 'tredje',
      plan_nth_4: 'fjerde',
      plan_nth_last: 'siste',
      plan_on_date: 'den',
      plan_date_unit: '. i måneden',
      sum_plan_weekly_every: 'Den dukker opp hver {{n}}. uke, på {{days}}.',
      sum_plan_every_open_one: 'Den dukker opp hver dag.',
      sum_plan_every_open_other: 'Den dukker opp hver {{n}}. dag.',
      sum_plan_month_day_one: 'Den dukker opp den {{day}}. i hver måned.',
      sum_plan_month_day_other: 'Den dukker opp den {{day}}. hver {{n}}. måned.',
      sum_plan_month_nth_one: 'Den dukker opp den {{nth}} {{weekday}} i hver måned.',
      sum_plan_month_nth_other: 'Den dukker opp den {{nth}} {{weekday}} hver {{n}}. måned.',
      step_timer_label: 'Timer (valgfri)',
      step_timer_minutes: 'Timer, minutter',
      step_timer_seconds: 'Timer, sekunder',
      step_timer_min: 'min',
      step_timer_sec: 'sek',
      step_timer_clear: 'Ingen timer',
      step_timer_hint: 'F.eks. "Planke – 0:45". Den som gjør oppgaven, starter den og hører en lyd når tiden er ute.',
    },
    notifications: {
      approved_said_yes: '{{admin}} sa ja!',
      approved_for: 'for {{task}}',
      approved_note: '{{admin}} sier: {{note}}',
      approved_now_you_have: 'Nå har du {{n}} stjerner',
      approved_reward_ready: '{{reward}} er klar!',
      approved_more_to_goal: '{{n}} stjerner igjen til {{reward}}',
      approved_see_reward: 'Se belønningen min',
      approved_not_now: 'Ikke nå',
      approved_ok: 'Hurra!',
      admin_pending_verification_one: '1 oppgave venter på godkjenningen din.',
      admin_pending_verification_other: '{{count}} oppgaver venter på godkjenningen din.',
    },
    member_chooser: {
      who_title: 'Hvem er du?',
      hi_name: 'Hei {{name}}!',
      enter_pin: 'Skriv inn PIN-koden din',
      log_in: 'Logg inn',
      use_password: 'Bruk passord i stedet',
      use_pin: 'Bruk PIN i stedet',
      password_label: 'Passord',
      delete_digit: 'Slett',
      back: 'Tilbake',
      locked_until: 'For mange forsøk. Prøv igjen kl. {{time}}.',
      locked_later: 'For mange forsøk. Prøv igjen litt senere.',
      wrong_pin: 'Det virket ikke. Prøv igjen.',
      sign_out_device_hint: 'Fjerner familieinnloggingen i denne nettleseren. Bruk dette hvis du står fast eller vil logge inn som voksen med Google/e-post igjen.',
      sign_out_device: 'Logg ut av denne enheten',
    },
    parent: {
      to_approve_one: '{{n}} oppgave å godkjenne',
      to_approve_other: '{{n}} oppgaver å godkjenne',
      finished: '{{name}} er ferdig med {{task}}',
      review: 'Se gjennom',
      nothing_to_approve: 'Ingenting å godkjenne akkurat nå',
      family: 'Familie',
      open_one: '{{n}} oppgave å gjøre',
      open_other: '{{n}} oppgaver å gjøre',
      open_none: 'Ingen oppgaver å gjøre',
      set_up: 'Oppsett',
      tasks: 'Oppgaver',
      rewards: 'Belønninger',
      members: 'Familie',
      rooms_tools: 'Rom og redskaper',
      lock: 'Lås',
      settings: 'Innstillinger',
      more_waiting: '+{{n}} til',
      your_tasks: 'Dine egne oppgaver',
    },
    approve: {
      title: 'Godkjenn oppgaver',
      approve: 'Godkjenn',
      not_done: 'Ikke ferdig ennå',
      note_so_neat: 'Så pent!',
      note_well_done: 'Bra jobbet!',
      note_thank_you: 'Takk!',
      reason_look_again: 'Nesten — se en gang til',
      reason_steps_missing: 'Noen steg mangler fortsatt',
      reason_do_together: 'La oss gjøre det sammen',
      pick_reason: 'Hva skal {{name}} få høre?',
      cancel: 'Avbryt',
      steps_done_one: 'Steget er gjort',
      steps_done_other: 'Alle {{n}} steg gjort',
      approved_toast: '{{task}} er godkjent. {{name}} fikk {{stars}}.',
      undo: 'Angre',
      sent_back_toast: '{{task}} ble sendt tilbake til {{name}}.',
      today_at: 'I dag {{time}}',
      yesterday_at: 'I går {{time}}',
      from_grownup: 'En voksen sier: {{reason}}',
      back: 'Tilbake',
      self_blocked: 'En annen voksen må godkjenne din egen oppgave.',
    },
    my_settings: {
      title: 'Mine innstillinger',
      language: 'Språk',
      same_as_device: 'Som denne enheten',
      look: 'Lys eller mørk',
      light: 'Lys',
      dark: 'Mørk',
      notifications: 'Varsler',
      on: 'På',
      off: 'Av',
      notif_turn_on: 'Slå på for denne enheten',
      notif_turned_on: 'Varsler er slått på.',
      notif_turned_off: 'Varsler er slått av.',
      notif_parent_off: 'En voksen har slått av varslene dine.',
      notif_parent_on: 'En voksen vil at varslene dine skal være på.',
      notif_unsupported: 'Denne enheten kan ikke vise varsler fra AntNest.',
      notif_blocked: 'Varsler er blokkert i denne nettleseren. Be en voksen om å tillate dem i nettleserens innstillinger.',
      timer_sound: 'Timerlyd',
      timer_sound_help: 'Lyden når timeren på et steg når null. Trykk for å høre den.',
    },
    parent_notifications: {
      title: 'Barnas varsler',
      help: 'Velg hvem som bestemmer. "Alltid på" betyr at barnet ikke kan slå dem av; "Av" betyr at ingenting sendes til barnet.',
      child: 'Barnet bestemmer',
      on: 'Alltid på',
      off: 'Av',
      saved: 'Lagret.',
    },
    handover: {
      title: 'Belønninger som skal gis',
      help: 'Disse er hentet. Gi dem til barnet, og trykk så på Gitt.',
      got: '{{name}} fikk {{reward}}',
      handed_over: 'Gitt',
      done_toast: '{{reward}} er gitt til {{name}}.',
    },
    library: {
      access_denied: 'Bare voksne kan åpne dette.',
      title: 'Oppgavebibliotek',
      intro: 'Start fra en standardoppgave eller en mal du har lagret fra en oppgave. Bare familien din ser "Mine maler".',
      age: 'Alder',
      age_group: 'Aldersgruppe',
      age_all: 'Alle aldre',
      age_2_3: '2–3 år',
      age_4_5: '4–5 år',
      age_6_7: '6–7 år',
      age_8_9: '8–9 år',
      age_10_12: '10–12 år',
      age_13_plus: '13+ år',
      standard_title: 'Standardoppgaver',
      none_standard: 'Ingen standardoppgaver for dette språket og denne alderen.',
      badge_standard: 'Standard',
      my_title: 'Mine maler',
      my_intro: 'Lagret med "Lagre som mal" i oppgaveskjemaet. Bare familien din kan se dem.',
      none_my: 'Ingen lagrede maler ennå. Lag en oppgave og bruk "Lagre som mal", eller endre aldersfilteret.',
      badge_my: 'Min mal',
      use: 'Bruk',
      save_as_template: 'Lagre som mal',
      days: 'Dager',
      stars: 'Stjerner',
      tool_name: 'Redskapets navn',
      tool_name_ph: 'f.eks. Støvsuger',
      username_required: 'Brukernavn (må fylles ut)',
      username_ph: 'Velg et brukernavn',
      pin_label: 'PIN (4–8 sifre)',
      pin_ph: 'Skriv en PIN',
      adults: 'Voksne',
      show: 'Vis',
      hide: 'Skjul',
      added_to_tasks: '«{{title}}» er lagt til i oppgavene deres',
      chain_title: '{{n}} oppgaver som følger etter hverandre',
      chain_wait_one: 'den neste er klar etter {{n}} time',
      chain_wait_other: 'den neste er klar etter {{n}} timer',
      chain_loops: 'Så starter den på nytt.',
    },
    deletion: {
      delete_member: 'Slett medlem',
      delete_member_title: 'Slette {{name}}?',
      delete_member_text: '{{name}} fjernes fra familien med en gang og logges ut på alle enheter.',
      delete_member_lost: 'Alle stjernene, statistikkene, oppgavene og belønningene til {{name}} blir slettet.',
      undo_30: 'Du kan angre i 30 dager, til {{date}}. Etter det slettes alt for godt og kan ikke gjenopprettes.',
      delete_member_yes: 'Ja, slett',
      member_deleted_toast: '{{name}} er slettet. Du kan gjenopprette i 30 dager.',
      recently_deleted: 'Nylig slettet',
      recently_deleted_hint: 'Du kan gjenopprette disse medlemmene frem til datoen som vises. Etter det slettes de og alle dataene deres for godt.',
      gone_on: 'Slettes for godt {{date}}',
      restore: 'Gjenopprett',
      member_restored_toast: '{{name}} er tilbake i familien. Varsler må slås på igjen.',
      family_section: 'Slett familie',
      delete_family_row: 'Slett hele familien',
      delete_family_hint: 'Alle og alt i familien. Kan angres i 30 dager.',
      delete_family_title: 'Slette hele familien?',
      delete_family_text: 'Alle i {{family}} logges ut med en gang og kan ikke bruke AntNest før familien er gjenopprettet.',
      delete_family_lost: 'Alt blir slettet: alle medlemmer, stjerner, statistikker, oppgaver, belønninger, hendelser og bilder.',
      type_family_name: 'Skriv familiens navn for å bekrefte: {{family}}',
      delete_family_yes: 'Slett familien',
      pending_title: '{{family}} er satt til sletting',
      pending_text: 'Familien og alle dataene slettes for godt {{date}}. Frem til da kan du gjenopprette alt akkurat slik det var.',
      days_left: 'Dager igjen: {{days}}',
      pending_not_admin: 'Bare en voksen med administratortilgang kan gjenopprette familien.',
      restore_family: 'Gjenopprett familien',
      family_restored_toast: 'Velkommen tilbake! Familien er gjenopprettet. Varsler må slås på igjen.',
      log_out: 'Logg ut',
      cancel: 'Avbryt',
    },
    pictures: {
      remove_picture: 'Fjern bilde',
      removed: 'Bildet er fjernet. Du har den vanlige mauren igjen.',
      unreadable: 'Bildet kunne ikke åpnes. Prøv et annet bilde, eller ta et nytt foto.',
      rotate: 'Roter',
      preparing: 'Gjør bildet klart…',
    },
    billing: {
      title: 'Abonnement',
      trial_title: 'Gratis prøveperiode – dager igjen: {{days}}',
      trial_text: 'Den gratis prøveperioden din slutter {{date}}. Velg en plan før det for å fortsette å bruke AntNest.',
      expired_title: 'Den gratis prøveperioden din er over',
      expired_text: 'Velg en plan for å fortsette å bruke AntNest. Familiens oppgaver, stjerner og belønninger er fortsatt her.',
      active_title_monthly: 'Du betaler månedlig',
      active_title_yearly: 'Du betaler årlig',
      active_text: 'Abonnementet ditt fornyes {{date}}.',
      grace_title: 'En betaling gikk ikke gjennom',
      grace_text: 'Oppdater betalingsopplysningene dine innen {{date}} for å fortsette å bruke AntNest.',
      cancelling_title: 'Abonnementet ditt er sagt opp',
      cancelling_text: 'Du kan bruke AntNest til og med {{date}}. Velg en plan for å fortsette etter det.',
      exempt_title: 'AntNest er gratis for familien deres',
      exempt_text: 'Du trenger ikke å betale noe.',
      plans_title: 'Velg en plan',
      plans_intro: 'Ett abonnement dekker hele familien, på alle enheter.',
      plan_monthly: 'Månedlig',
      plan_yearly: 'Årlig',
      price_monthly: '{{price}} i måneden',
      price_yearly: '{{price}} i året',
      price_soon: 'Prisen kommer snart',
      includes_family: 'Hele familien, på alle enheter',
      includes_tasks: 'Alle ferdige oppgaver og sjekklister',
      includes_cancel: 'Si opp når som helst',
      choose_monthly: 'Velg månedlig',
      choose_yearly: 'Velg årlig',
      coming_soon: 'Betaling åpner snart. Inntil da er AntNest gratis.',
      manage_title: 'Administrer abonnementet ditt',
      manage_text: 'Bytt plan, oppdater kortet ditt, se kvitteringer eller si opp.',
      manage_button: 'Administrer abonnement',
      banner_trial: 'Dager til den gratis prøveperioden din slutter: {{days}}.',
      banner_trial_last_day: 'Den gratis prøveperioden din slutter i dag.',
      banner_payment: 'En betaling gikk ikke gjennom. Oppdater kortet ditt innen {{date}}.',
      banner_cancelling: 'Abonnementet ditt slutter {{date}}.',
      banner_action: 'Se planer',
      paywall_title: 'AntNest tar en pause',
      paywall_text: 'Spør en voksen om å velge en plan. Alle stjernene og oppgavene dine er trygt lagret og venter på deg.',
      manage_failed: 'Abonnementssiden kunne ikke åpnes. Prøv igjen om litt.',
    },
    pocket: {
      title: 'Lommepenger',
      intro: 'Lommepenger regnes ut fra stjernene hvert barn får for godkjente oppgaver i løpet av en måned. AntNest flytter ingen penger: dere betaler som vanlig og merker det som utbetalt.',
      no_children: 'Legg til et barn i familien for å gi lommepenger.',
      currency: 'Valuta',
      for_child: 'Lommepenger til {{name}}',
      how_question: 'Hvordan tjenes de?',
      per_ten_label: '{{currency}} for hver 10 stjerner',
      fixed_label: '{{currency}} i måneden, når {{name}} har fått minst',
      threshold_label: 'stjerner',
      threshold_help: '0 stjerner = gis alltid (faste lommepenger).',
      bonus_label: 'Bonus:',
      bonus_every_label: '{{currency}} ekstra for hver',
      bonus_stars_label: 'stjerner til',
      cap_question: 'Grense per måned — velg en',
      cap_none: 'Ingen grense',
      cap_at: 'Høyst',
      cap_unit: '{{currency}} i måneden',
      cap_required: 'Velg en grense eller ingen grense før du lagrer.',
      example_title: 'For eksempel i en måned med:',
      example_line_one: '{{n}} stjerne → {{amount}}',
      example_line_other: '{{n}} stjerner → {{amount}}',
      capped_short: 'grensen nådd',
      typical_dk: 'Vanlig i Danmark per måned: under 100 DKK for 6–10 år, 50–200 DKK for 11–14 år, 200–500 DKK for 15–17 år.',
      save: 'Lagre',
      saved: 'Lommepenger til {{name}} er lagret',
      save_failed: 'Kunne ikke lagre. Prøv igjen.',
      my_month: 'Lommepenger i {{month}}: {{amount}}',
      my_next_one: '{{n}} stjerne til gir {{amount}} mer',
      my_next_other: '{{n}} stjerner til gir {{amount}} mer',
      my_capped: 'Du har nådd månedens høyeste beløp. Flott!',
      my_last_paid: '{{month}}: {{amount}} — utbetalt ✓',
      my_last_unpaid: '{{month}}: {{amount}} — venter på utbetaling',
      to_pay_title: 'Lommepenger til utbetaling',
      to_pay_help: 'Betal som vanlig (kontant, bank eller Vipps), og merk det så som utbetalt.',
      mark_paid: 'Merk som utbetalt',
      task_counts: 'Gir lommepenger',
      task_counts_help: 'Fjern haken for hverdagsoppgaver som å re sengen, så teller bare større oppgaver med i lommepengene.',
      typical_intro: 'Vanlig i {{country}} per måned:',
      typical_band: '{{ages}} år: {{amount}}',
      typical_under: 'under {{amount}}',
      typical_about: 'ca. {{amount}}',
      typical_source: 'kilde: {{source}}',
    },
    getting_started: {
      title: 'Kom godt i gang',
      progress: '{{done}} av {{all}} klart',
      hide: 'Skjul',
      item_approve: 'Godkjenn den første ferdige oppgaven',
      item_own_task: 'Tilpass en oppgave, eller lag deres egen',
      item_home_screen: 'Legg AntNest på hjemskjermen',
      item_calendar: 'Se oppgavene i kalenderen',
      home_screen_how: 'iPhone/iPad: Trykk på Del i Safari og velg «Legg til på Hjem-skjerm». Android: Trykk på ⋮ i Chrome og velg «Installer app» eller «Legg til på startskjermen». Gjør det også på barnas enheter.',
      home_screen_done: 'Det har jeg gjort',
      pocket_title: 'Lommepenger for stjernene?',
      pocket_text: 'Nå som barna har kommet i gang, kan stjernene også gi lommepenger – et fast beløp, et beløp per 10 stjerner og en grense per måned som dere bestemmer selv.',
      pocket_setup: 'Sett det opp',
      pocket_no: 'Nei takk',
    },
    editor: {
      close: 'Lukk',
      unsaved_title: 'Lagre endringene?',
      unsaved_body: 'Du har endret noe. Vil du lagre før du lukker?',
      save: 'Lagre',
      discard: 'Ikke lagre',
      keep_editing: 'Fortsett å redigere',
    },
    timer: {
      start: 'Start timer',
      pause: 'Pause',
      resume: 'Fortsett',
      restart: 'Start på nytt',
      times_up: 'Tiden er ute!',
      sound_bell: 'Klokke',
      sound_chime: 'Klokkespill',
      sound_beeps: 'Vekkerklokke',
      sound_marimba: 'Marimba',
      sound_fanfare: 'Fanfare',
      volume_note: 'Lyden følger telefonens volum – skru opp så du hører den.',
    },
    approval_setting: {
      title: 'Godkjenning av voksnes oppgaver',
      label: 'Voksnes oppgaver må godkjennes av en annen voksen',
      help_off: 'Av: når en voksen blir ferdig med en oppgave, er den godkjent med en gang, og stjernene er deres. Barns oppgaver godkjennes alltid av en voksen.',
      help_on: 'På: en voksens ferdige oppgave venter til en annen voksen godkjenner den (har familien bare én administrator, kan den godkjenne sine egne).',
      setup_help: 'La den stå tom, så blir voksnes oppgaver godkjent så snart de er ferdige. Du kan endre det senere under Innstillinger.',
      saved: 'Lagret.',
    },
  },
  fr: {
    auth: {
      forgot_title: 'Mot de passe oublié ?',
      forgot_link: 'Mot de passe oublié ?',
      forgot_confirmation: "Si un compte existe, nous avons envoyé un lien de réinitialisation.",
      email_label: 'E‑mail',
      send_reset_link: 'Envoyer le lien',
      reset_title: 'Définir un nouveau mot de passe',
      new_password_label: 'Nouveau mot de passe',
      confirm_password_label: 'Confirmer le mot de passe',
      set_new_password: 'Enregistrer le nouveau mot de passe',
      password_mismatch: 'Les mots de passe ne correspondent pas',
      password_policy_msg: 'Au moins 12 caractères, majuscule, minuscule et chiffre.',
      invalid_token: 'Lien invalide ou expiré',
      reset_success: 'Mot de passe mis à jour. Redirection vers la connexion...',
      reset_failed: 'Échec de la réinitialisation. Réessayez.',
      login_failed: 'La connexion a échoué. Réessaie.',
      beta_banner: 'AntNest est en version bêta. Tu as un code d\'invitation ?',
      beta_banner_link: 'Inscris-toi ici',
      or: 'ou',
      login_google: 'Se connecter avec Google',
      login_facebook: 'Se connecter avec Facebook',
      no_account: 'Pas encore de compte ?',
      create_one: 'Crée-en un',
      err_code_required: 'Un code bêta est nécessaire.',
      err_agree: 'Merci d\'accepter les conditions et la politique de confidentialité.',
      err_fields: 'Merci de remplir tous les champs.',
      err_code_invalid: 'Ce code bêta n\'est pas valide ou a déjà été utilisé.',
      err_signup_failed: 'Le compte n\'a pas pu être créé. Réessaie.',
      signup_title: 'Crée ton compte',
      beta_code: 'Code bêta',
      beta_code_ph: 'Saisis ton code d\'invitation',
      full_name: 'Ton nom',
      full_name_ph: 'p. ex. Anna',
      username: 'Nom d\'utilisateur',
      show_password: 'Afficher le mot de passe',
      hide_password: 'Masquer le mot de passe',
      rule_length: 'Au moins 12 caractères',
      rule_upper: 'Une majuscule',
      rule_lower: 'Une minuscule',
      rule_digit: 'Un chiffre',
      password_match: 'Les mots de passe correspondent',
      family_name: 'Nom de la famille',
      family_name_ph: 'p. ex. La famille Martin',
      family_default: 'Ma famille',
      agree_before: 'J\'accepte les',
      terms: 'conditions',
      and: 'et la',
      privacy: 'politique de confidentialité',
      creating_account: 'Création du compte…',
      create_account: 'Créer le compte',
      signup_google: 'S\'inscrire avec Google',
      signup_facebook: 'S\'inscrire avec Facebook',
      choose_language: 'Choisir la langue',
      oauth_failed: 'La connexion n\'a pas abouti. Réessaie.',
      processing: 'Connexion en cours…',
      complete_setup: 'Presque terminé',
      your_name: 'Ton nom',
      complete_setup_btn: 'Créer la famille et continuer',
      login_apple: 'Se connecter avec Apple',
      signup_apple: 'S\'inscrire avec Apple',
    },
    rooms: {
      kitchen: 'Cuisine',
      living_room: 'Salon',
      dining_room: 'Salle à manger',
      bedroom: 'Chambre',
      bathroom: 'Salle de bain',
      toilet: 'Toilettes',
      hallway: 'Couloir',
      entryway: 'Entrée',
      laundry_room: 'Buanderie',
      garage: 'Garage',
      office: 'Bureau',
      playroom: 'Salle de jeux',
      nursery: "Chambre d'enfant",
      patio: 'Terrasse/Balcon',
      garden: 'Jardin',
      storage: 'Rangement',
      basement: 'Sous-sol',
      attic: 'Grenier',
      child_bedroom: 'Chambre d\'enfant',
    },
    onboarding: {
      title: 'Commençons',
      family_name: 'Nom de la famille',
      adults: 'Adultes',
      children: 'Enfants',
      member_name: 'Nom',
      username: "Nom d'utilisateur",
      pin_placeholder: 'PIN (4 chiffres)',
      role_admin: 'Admin',
      role_member: 'Membre',
      next: 'Suivant',
      back: 'Retour',
      confirm: 'Ça me va',
      processing: 'Enregistrement…',
      step_rooms_title: 'Configurez votre maison',
      step_rooms_help: "Cliquez sur les pièces ci-dessous pour les ajouter à votre maison. Si vous avez une pièce ou un espace qui ne figure pas dans la liste, vous pouvez l'ajouter manuellement.\nVous pouvez toujours modifier, ajouter et supprimer des pièces dans votre section d'administration.",
      custom_room_placeholder: 'Nom de pièce personnalisé',
      add_custom: 'Ajouter personnalisé',
      remove: 'Supprimer',
      step_review_title: 'Revoir',
      review_family: 'Famille',
      review_members: 'Membres',
      review_rooms: 'Pièces',
      error_generic: "Une erreur est survenue lors de l'enregistrement",
      step_members_help: "Ajoutez vos membres de famille ici.\nDonnez à chaque membre un nom et un nom d'utilisateur.\nChaque membre doit avoir un code PIN pour se connecter.",
      validation_incomplete: 'Certains champs ne sont pas correctement remplis',
      adult_label: 'Adulte',
      child_label: 'Enfant',
      privacy_note: 'Ces informations servent uniquement à configurer votre famille. Nous ne partageons ni ne vendons vos données ou informations personnelles à qui que ce soit, et ne le ferons jamais.',
      child_age: 'Âge',
      child_room_name: 'Chambre de {{name}}',
      child_room_name_s: 'Chambre de {{name}}',
      hint_members: 'Tu peux toujours ajouter ou modifier des membres plus tard dans Admin → Membres.',
      hint_rooms: 'Chaque enfant a sa propre chambre, pour que ses tâches soient au bon endroit. Tu peux toujours modifier les pièces plus tard dans Admin → Pièces.',
      step_tasks_title: 'Des tâches pour commencer',
      step_tasks_help: 'Nous avons choisi quelques tâches adaptées à vos pièces et à l\'âge de vos enfants. Décochez celles que vous ne voulez pas – tout se modifie plus tard.',
      starters_unavailable: 'Les suggestions n\'ont pas pu être chargées. Tu peux passer cette étape et ajouter des tâches plus tard.',
      tasks_for_child: 'Les tâches de {{name}}',
      tasks_shared: 'Pour tout le monde (chacun peut les prendre)',
      whole_home: 'Toute la maison',
      hint_tasks: 'Il y a beaucoup d\'autres tâches standard dans Admin → Tâches, et vous pouvez y créer les vôtres.',
      skip_step: 'Passer cette étape',
      step_rewards_title: 'Des récompenses à viser',
      step_rewards_help: 'Les enfants restent motivés quand la première récompense est à portée. Celles-ci sont réglées pour que la première soit atteinte en quelques jours.',
      rewards_for_child: 'Pour {{name}}',
      rewards_family: 'Pour toute la famille (les étoiles de chacun comptent)',
      hint_rewards: 'Vous pouvez toujours ajouter vos propres récompenses ou modifier celles-ci dans Admin → Récompenses.',
      review_starters: 'Pour commencer',
      review_tasks_one: '{{n}} tâche',
      review_tasks_other: '{{n}} tâches',
      review_rewards_one: '{{n}} récompense',
      review_rewards_other: '{{n}} récompenses',
      create_family: 'Créer notre famille',
      done_title: 'Votre famille est prête !',
      done_made: 'Nous avons créé {{tasks}} tâches et {{rewards}} récompenses pour vous.',
      done_try: 'Essayez la première tâche maintenant : passez à un enfant avec « {{switch}} » en haut, puis touchez « {{start}} » sur une tâche.',
      done_admin: 'Quand une tâche est faite, valide-la dans « {{approve}} » — les étoiles sont alors ajoutées.',
      go_home: 'Aller à l\'accueil de la famille',
      go_admin: 'Aller à l\'Admin',
    },
    common: {
      save: 'Enregistrer',
      take_photo: 'Prendre une photo',
      cancel: 'Annuler',
      loading: 'Chargement...',
      all: 'Tous',
      stars_one: '{{n}} étoile',
      stars_other: '{{n}} étoiles',
      update_ready: 'Une nouvelle version d\'AntNest est prête.',
      update_now: 'Mettre à jour',
      remove_image: 'Supprimer l’image',
      remove_item: 'Supprimer l’élément',
      ok: 'OK',
      error_generic: 'Une erreur s\'est produite. Veuillez réessayer.',
    },
    header: {
      stats: 'Statistiques',
      app_title: 'AntNest',
      home: 'Accueil',
      tasks: 'Tâches',
      calendar: 'Calendrier',
      rewards: 'Récompenses',
      settings: 'Paramètres',
      profile: 'Profil',
      admin: 'Admin',
      logout: 'Se déconnecter',
      points: 'points',
      settings_tooltip: 'Paramètres',
      change_picture: 'Changer mon image',
      someone_else: 'Au tour de quelqu\'un d\'autre',
      for_grownups: 'Pour les adultes',
      for_grownups_hint: 'Réglages, validations, déconnexion',
      my_page: 'Ma page',
      menu_close: 'Fermer le menu',
      menu_open: 'Ouvrir le menu',
      nav_menu: 'Menu',
      master_admin: 'Admin principal',
      switch_user: 'Changer d’utilisateur',
      tab_bar_label: 'Sections principales',
    },
    stats: {
      title: 'Statistiques',
      tab_me: 'Moi',
      tab_family: 'Famille',
      tab_tasks: 'Tâches',
      period_week: 'Semaine',
      period_month: 'Mois',
      period_year: 'Année',
      period_all: 'Depuis le début',
      prev: 'Précédent',
      next: 'Suivant',
      all_time: 'Depuis le début',
      tasks_done: 'tâches faites',
      points: 'points',
      rewards: 'récompenses',
      chart_points: 'Points dans le temps',
      where_helped: 'Où j\'ai aidé',
      no_room: 'Sans pièce',
      my_top_tasks: 'Mes tâches les plus faites',
      together: 'Ensemble',
      who_helped: 'Qui a aidé',
      everyone_over_time: 'Tout le monde dans le temps',
      repeating_title: 'Tâches',
      per_task_title: 'Chaque tâche',
      times_done_axis: 'Nombre de fois faite',
      times: '{n}×',
      last_done: 'dernière : {date}',
      empty: 'Rien pour cette période pour l\'instant.',
      family_hidden: 'Votre famille ne montre ceci qu\'aux adultes.',
      week_short: 'Sem. {n}',
      enc_start: 'Un nouveau départ — chaque tâche compte ! 💪',
      enc_first: 'Ta première tâche — bravo ! 🎉',
      enc_done: 'Tu as fait {n} tâches — super ! ⭐',
      enc_more: 'Plus de points que la période d\'avant — continue ! 🚀',
      visibility_title: 'Statistiques : ce que voient les enfants',
      visibility_help: 'Choisissez l\'affichage de l\'onglet Famille pour les enfants. Les adultes administrateurs voient toujours tout.',
      visibility_team: 'Vue d\'équipe — la part de chacun, sans classement (recommandé)',
      visibility_full: 'Comparaison complète — classée par points',
      visibility_admins: 'Masqué — seuls les adultes voient la comparaison familiale',
      visibility_saved: 'Enregistré',
      team_view_note: 'La part de chacun dans le total de la famille',
    },
    calendar: {
      title: 'Calendrier',
      coming_soon: 'Le calendrier est en cours de construction. Les vues et événements de base apparaîtront ici.',
      view_day: 'Jour',
      view_week: 'Semaine',
      view_month: 'Mois',
      week_label: 'Semaine',
      no_events_for_day: 'Aucun événement',
      today_label: "aujourd'hui",
      prev_day: 'Jour précédent',
      next_day: 'Jour suivant',
      prev_week: 'Semaine précédente',
      next_week: 'Semaine suivante',
      prev_month: 'Mois précédent',
      next_month: 'Mois suivant',
      go_to_today: 'Aujourd\'hui',
      not_allowed_edit_event: 'Tu ne peux modifier que les événements que tu as créés toi-même.',
      filters_title: 'Filtrer par membre',
      single_view_button: 'Vue individuelle',
      family_view_button: 'Vue famille',
      no_events_for_range: 'Aucun événement dans cette plage',
      no_events: 'Aucun événement pendant cette période.',
      add_event: 'Ajouter un événement',
      edit_event: 'Modifier l\'événement',
      event_form_title: 'Titre',
      event_form_description: 'Description (facultatif)',
      event_form_location: 'Lieu (facultatif)',
      event_form_start: 'Début',
      event_form_end: 'Fin',
      event_form_all_day: 'Toute la journée',
      event_form_attendees: 'Participants',
      event_form_save: 'Enregistrer',
      event_form_cancel: 'Annuler',
      event_form_delete: 'Supprimer',
      delete_confirm_title: 'Supprimer l\'événement',
      delete_confirm_message: 'Êtes-vous sûr de vouloir supprimer cet événement ?',
      success_created: 'Événement créé',
      success_updated: 'Événement mis à jour',
      success_deleted: 'Événement supprimé',
      error_generic: 'Une erreur est survenue',
      validation_title_required: 'Le titre est requis',
      validation_end_after_start: 'La fin doit être après le début',
      create_calendar_event: 'Créer un événement dans le calendrier',
      event_modal_title_create: 'Créer un événement dans le calendrier',
      event_modal_title_edit_prefix: 'Modifier'
      ,
      reminder_label: 'Rappel',
      reminder_none: 'Aucun rappel',
      reminder_10m: '10 minutes avant',
      reminder_30m: '30 minutes avant',
      reminder_1h: '1 heure avant',
      reminder_12h: '12 heures avant',
      reminder_1d: '1 jour avant',
      nothing_planned: 'Rien de prévu pour l\'instant',
      nothing_this_month: 'Rien de prévu ce mois-ci',
      today_day: 'Aujourd\'hui, {{day}}',
      tomorrow_day: 'Demain, {{day}}',
      at_time: 'À {{time}}',
      all_day_label: 'Toute la journée',
      with_names: 'avec {{names}}',
      task_label: 'Tâche',
      everyone: 'Tout le monde',
      back: 'Retour',
      due_today: 'À finir aujourd\'hui',
      due_today_at: 'À finir aujourd\'hui avant {{time}}',
      due_tomorrow: 'À finir d\'ici demain',
      due_tomorrow_at: 'À finir demain avant {{time}}',
      due_later: 'À finir d\'ici ce jour',
      due_later_at: 'À finir avant {{time}}',
      overdue_since: 'En retard depuis {{day}}',
      event_form_image: 'Image de l’événement (facultatif)',
      remove_image: 'Supprimer l’image',
      planned: 'Prévue',
    },
    settings: {
      title: 'Paramètres',
      subtitle: "Gérez votre compte et les préférences de l'application",
      dark_mode_section: 'Mode sombre',
      current_theme: 'Thème actuel',
      following_system: 'Suivre la préférence du système',
      manual_selection: 'Sélection manuelle',
      follow_system_label: 'Suivre la préférence du système',
      auto_switch_hint: 'Basculer automatiquement entre le mode clair et sombre selon le système',
      enable_dark_mode: 'Activer le mode sombre',
      manual_toggle_hint: 'Basculer manuellement entre les thèmes clairs et sombres',
      light_mode: 'Mode clair',
      dark_mode: 'Mode sombre',
      more_settings_title: 'Plus de paramètres bientôt disponibles',
      more_settings_desc: "D'autres options de personnalisation seront disponibles ici, notamment les notifications, la confidentialité et la gestion du compte.",
      language_section: 'Langue',
      language_hint: "Choisissez votre langue préférée pour l'interface",
      back: 'Retour',
      profile_parent: 'Parent. Modifier le nom, la photo et le mot de passe',
      profile_member: 'Modifier le nom, la photo et le code PIN',
      look: 'Apparence',
      auto: 'Auto',
      tell_me_when: 'Préviens-moi quand',
      notif_this_device: 'Notifications sur cet appareil',
      notif_blocked_parent: 'Bloquées dans ce navigateur. Autorise les notifications pour ce site dans les réglages du navigateur.',
      n_review_ready: 'Une tâche attend d\'être validée',
      n_task_approved: 'Ma tâche est validée',
      n_reward_ready: 'Une récompense est prête',
      n_due_soon: 'Une de mes tâches arrive bientôt à échéance',
      n_overdue: 'Une de mes tâches est en retard',
      n_new_task: 'Une nouvelle tâche est libre',
      n_event: 'Rappels du calendrier',
      daily_summary: 'Résumé quotidien',
      daily_summary_time: 'Heure du résumé quotidien',
      quiet_hours: 'Heures calmes',
      quiet_hours_hint: 'Pas de notifications entre ces heures',
      quiet_from: 'Calme à partir de',
      quiet_to: 'Calme jusqu\'à',
      this_device: 'Cet appareil',
      change_pin: 'Changer mon code PIN',
      switch_person: 'Changer de personne',
      refresh_session: 'Actualiser cet appareil',
      session_refreshed: 'Cet appareil est à jour.',
      locked_until: 'Bloqué jusqu\'à {{time}}',
      unlock: 'Débloquer',
      unlocked: '@{{name}} peut se reconnecter.',
      log_out_device: 'Se déconnecter de cet appareil',
      logout_confirm_title: 'Se déconnecter de cet appareil ?',
      logout_confirm_text: 'Toute la famille {{family}} est déconnectée ici. Un adulte devra se reconnecter avant que quiconque puisse utiliser AntNest sur cet appareil.',
      logout_confirm_yes: 'Se déconnecter',
      cancel: 'Annuler',
      n_reward_claimed: 'Une récompense est réclamée',
    },
    rewards: {
      title: 'Récompenses',
      subtitle: 'Suivez vos progrès et réclamez vos récompenses',
      your_points: 'Vos points',
      family_points: 'Points de la famille',
      claimed_rewards: 'Récompenses réclamées',
      your_coins: 'Vos pièces',
      individual_rewards: 'Récompenses individuelles',
      no_individual_rewards: 'Aucune récompense individuelle disponible pour le moment.',
      family_rewards: 'Récompenses familiales',
      no_family_rewards: 'Aucune récompense familiale disponible pour le moment.',
      goal_reached: 'Objectif atteint !',
      complete_suffix: 'terminé',
      target_whole_family: 'Toute la famille',
      claim_reward_button: 'Réclamer la récompense !',
      claimed_rewards_section: 'Récompenses réclamées',
      no_claimed_rewards: 'Aucune récompense réclamée pour le moment. Continuez !',
      claimed_on: 'Réclamée le',
      unknown_date: 'Date inconnue',
      unknown_member: 'Membre inconnu',
      reward_header_alt: 'En-tête de récompense',
      show_more: 'Afficher plus',
      show_fewer: 'Afficher moins',
      points_abbrev: 'pts',
      progress_counts_from: 'Les points à partir du {{date}} comptent pour cette récompense.',
      lifetime_total_hint: 'Total depuis toujours (distinct du suivi par récompense).',
      your_stars_label: 'tes étoiles',
      ready_to_get: 'Prêt à prendre',
      keep_saving: 'Continue d\'économiser pour',
      n_more: 'encore {{n}}',
      get_it: 'La prendre',
      grownup_gets: 'Un adulte s\'en charge',
      got_one: '{{n}} récompense obtenue',
      got_other: '{{n}} récompenses obtenues',
      see_all: 'Tout voir',
      counting_since: 'Étoiles comptées depuis le {{date}}',
      nothing_yet: 'Pas encore de récompenses. Demande à un adulte d\'en ajouter !',
      claim_failed: 'Impossible d\'obtenir la récompense. Réessayez.',
    },
    dashboard: {
      welcome_back: 'Bon retour',
      subheading: 'Voici ce qui se passe dans votre famille aujourd\'hui',
      leaderboard_title: 'Classement familial',
      family_member: 'Membre de la famille',
      tasks_title: 'Tâches',
      available_tasks: 'Tâches disponibles',
      no_available_tasks: 'Aucune tâche disponible pour le moment !',
      my_active_tasks: 'Mes tâches actives',
      no_my_tasks: 'Vous n\'avez aucune tâche active !',
      completed_tasks: 'Tâches terminées',
      no_completed_tasks: 'Aucune tâche terminée pour le moment !',
      manage_all_tasks: 'Gérer toutes les tâches',
      my_rewards_title: 'Mes récompenses',
      my_points: 'Mes points',
      available_rewards: 'Récompenses disponibles',
      claimed_rewards: 'Récompenses réclamées',
      next_reward_goal: 'Prochain objectif de récompense',
      all_rewards_claimed: 'Toutes les récompenses ont été réclamées ! Bravo !',
      showing_rewards_help: 'Affichage : Vos récompenses personnelles et celles de la famille',
      claim_now: 'Réclamer maintenant !',
      locked: 'Verrouillé',
      go_to_rewards: 'Aller aux récompenses',
      my_tasks_primary_title: 'Mes tâches',
      my_tasks_home_hint: 'Seules les tâches qui vous sont assignées apparaissent ici. Pour en choisir de nouvelles, ouvrez Tâches.',
      due_overdue: 'En retard',
      due_today: 'À rendre aujourd\'hui',
      due_no_deadline: 'Sans échéance',
      hi_name: 'Salut {{name}}',
      goal_of: '{{have}} sur {{need}} étoiles',
      goal_get_it: 'La prendre',
      goal_none: 'Pas encore de récompense à viser. Demande à un adulte d\'en ajouter une !',
      today: 'Aujourd\'hui',
      no_tasks_now: 'Pas de tâche pour l\'instant',
      waiting_one: '{{n}} étoile quand un adulte dit oui',
      waiting_other: '{{n}} étoiles quand un adulte dit oui',
      family_goal: 'Objectif de la famille',
      family_goal_more: 'Les étoiles de tous comptent. Encore {{n}} pour {{reward}}.',
      family_goal_ready: '{{reward}} est prêt ! Un adulte peut le prendre pour la famille.',
      find_tasks_cta: 'Trouver une tâche',
      due_tomorrow: 'Demain',
      due_in_days_one: 'Dans {{n}} jour',
      due_in_days_other: 'Dans {{n}} jours',
      more_stars_title: 'Tu veux plus d\'étoiles ?',
      more_stars_hint: 'Tout le monde peut les prendre. Choisis-en une quand tu en as envie !',
      see_all_can_take: 'Voir les {{n}}',
      take_failed: 'Impossible de prendre la tâche',
    },
    task_board: {
      title: 'Tableau des tâches',
      subtitle: 'Gérer et suivre toutes les tâches familiales',
      available_tasks: 'Tâches disponibles',
      no_available_tasks: 'Aucune tâche disponible pour le moment !',
      my_active_tasks: 'Mes tâches actives',
      no_my_tasks: 'Vous n\'avez aucune tâche active !',
      view: 'Voir',
      complete: 'Terminer',
      tasks_assigned_to_others: 'Tâches assignées à d\'autres',
      no_tasks_assigned_to_others: 'Aucune tâche assignée à d\'autres utilisateurs !',
      view_task: 'Voir la tâche',
      completed_tasks: 'Tâches terminées',
      no_completed_tasks: 'Aucune tâche terminée pour le moment !',
      take_task: 'Je la prends',
      take_over_task: 'Reprendre la tâche',
      unclaim_task: 'La rendre',
      your_tasks: 'Tes tâches',
      can_take: 'Tu peux prendre celles-ci',
      waiting_heading: 'En attente d\'un adulte',
      others_tasks: 'Les tâches des autres',
      done_one: '{{n}} tâche faite',
      done_other: '{{n}} tâches faites',
      moved_to_yours: '{{task}} est maintenant dans Tes tâches',
      nothing_to_take: 'Aucune tâche à prendre pour l\'instant',
      no_your_tasks: 'Tu n\'as pas de tâche pour l\'instant.',
    }
    ,
    login: {
      title: 'AntNest',
      email_label: 'E-mail',
      password_label: 'Mot de passe',
      logging_in: 'Connexion en cours...',
      login_button: 'Se connecter',
      demo_account: 'Compte de démonstration',
      demo_email: 'E-mail: admin@family.com',
      demo_password: 'Mot de passe: admin'
    },
    user_profile: {
      user_not_found: 'Utilisateur introuvable.',
      back_to_home: "Retour à l'accueil",
      title: 'Profil utilisateur',
      profile_information: 'Informations du profil',
      name: 'Nom',
      email: 'E-mail',
      email_not_set: 'Aucun e-mail défini',
      role: 'Rôle',
      admin: 'Admin',
      family_member: 'Membre de la famille',
      points: 'Points',
      coins: 'Pièces',
      edit_profile: 'Modifier le profil',
      save_changes: 'Enregistrer les modifications',
      cancel: 'Annuler',
      email_optional_label: 'E-mail (facultatif)',
      new_password_optional_label: 'Nouveau mot de passe (facultatif)',
      name_placeholder: 'Entrez votre nom',
      email_enter_placeholder: "Entrez l'adresse e-mail",
      email_add_placeholder: 'Ajouter un e-mail',
      username_for_member_login: "Nom d'utilisateur (pour la connexion membre)",
      pin_label: 'Code PIN (4–8 chiffres)',
      avatar: 'Avatar',
      avatar_hint: 'Choisissez un préréglage ou importez le vôtre.',
      upload: 'Téléverser',
      avatar_updated: 'Avatar mis à jour',
      avatar_update_failed: "Échec de la mise à jour de l'avatar",
      linked_accounts: 'Comptes liés',
      no_linked_accounts: 'Aucun compte lié.',
      unlink: 'Dissocier',
      unlink_failed: 'Échec de la dissociation',
      unlink_confirm: 'Dissocier {{provider}} ?',
      link_to_google: 'Lier à Google',
      change_password: 'Changer le mot de passe',
      current_password: 'Mot de passe actuel',
      new_password: 'Nouveau mot de passe',
      password_changed: 'Mot de passe modifié',
      password_change_failed: 'Échec du changement de mot de passe',
      save_new_password: 'Enregistrer le nouveau mot de passe',
      pin_validation: 'Le code PIN doit comporter 4 à 8 chiffres (nombres uniquement)',
      current_pin_label: 'Code PIN actuel (seulement si vous en avez déjà un)',
      current_pin_required: 'Saisissez votre code PIN actuel pour le modifier.',
      current_pin_incorrect: 'Votre code PIN actuel est incorrect.',
      email_managed_by_login: 'Votre e-mail provient de votre mode de connexion et ne peut pas être modifié ici.',
      username_validation: "Le nom d'utilisateur doit comporter au moins 2 caractères",
      profile_updated: 'Profil mis à jour',
      google_link_success: 'Compte Google lié avec succès',
      picture_type_bad: 'Choisis une image PNG, JPG ou WebP.',
      picture_too_big: 'Cette image est trop grande (1 Mo max).',
      choose_picture: 'Choisir cette image',
      or_pick_one: 'Ou choisis-en un',
      tap_picture: 'Touche l\'image pour en choisir une nouvelle.',
      facebook_link_success: 'Compte Facebook lié avec succès',
      link_to_facebook: 'Lier à Facebook',
      account_unlinked: 'Compte dissocié.',
      link_to_apple: 'Lier à Apple',
    },
    admin: {
      header_title: "Panneau d'administration",
      header_subtitle: 'Gérez les tâches, récompenses et membres de votre famille',
      tab_tasks: 'Tâches',
      tab_rooms: 'Pièces',
      tab_members: 'Membres',
      tab_pending: 'En attente',
      tab_rewards: 'Récompenses',
      tab_tools: 'Outils'
    },
    checklist_editor: {
      back_to_admin: "Retour au panneau d'administration",
      edit_checklist_for: 'Modifier la liste de contrôle',
      add_new_item: 'Ajouter un nouvel élément',
      add_item: 'Ajouter',
      current_items: 'Éléments actuels',
      no_items: 'Aucun élément pour le moment. Ajoutez-en ci-dessus !',
      cancel: 'Annuler',
      save: 'Enregistrer'
    },
    task_details: {
      loading: 'Chargement...',
      title: 'Détails de la tâche',
      room: 'Pièce',
      points: 'Points',
      status: 'Statut',
      status_completed: 'Terminé',
      status_active: 'Actif',
      assigned_to: 'Attribué à',
      due_date: "Date d'échéance",
      tools_required: 'Outils requis',
      checklist: 'Liste de contrôle',
      task_completed: 'Tâche terminée',
      completed_by: 'Terminé par',
      message: 'Message',
      submit_for_verification: 'Soumettre pour vérification',
      close: 'Fermer',
      preview_room_placeholder: 'Proposée lorsque vous ajoutez cette tâche',
      preview_title: 'Aperçu de la tâche',
      preview_checklist_hint: 'Étapes (aperçu uniquement — à cocher après avoir ajouté la tâche)',
      view: 'Voir',
    },
    task_flow: {
      start_task: 'C\'est parti',
      tools_ready_title: 'Tu as tout ce qu\'il faut ?',
      tools_ready_subtitle: 'Tu auras besoin de ces outils. Rassemble-les avant de continuer.',
      ready_to_start_title: 'Prêt ?',
      ready_to_start_body: 'Es-tu prêt à commencer la tâche ?',
      tools_ready_no: 'Pas encore',
      tools_ready_yes: 'Oui, c’est parti',
      finish_step: 'C\'est fait',
      final_put_tools_message:
        'Bravo ! Remets tous les outils à leur place. Ensuite, appuie sur le bouton ci-dessous pour terminer la tâche.',
      complete_task_button: 'Terminé',
      no_checklist_warning: 'Cette tâche n’a pas encore d’étapes. Demande à un parent d’ajouter une liste avant de commencer.',
      view_checklist_readonly_hint: 'Tu pourras cocher les étapes après avoir appuyé sur Commencer la tâche.',
      progress_label: 'Progression',
      step_counter_prefix: 'Étape',
      not_assigned: 'Pas encore assignée',
      step_of: 'Étape {{n}} sur {{total}}',
      back_step: 'Revenir d\'une étape',
      tidy_title: 'Presque fini !',
      tidy_subtitle: 'Range ces affaires à leur place',
      tidy_no_tools: 'Tout est fini ? Alors appuie sur Terminé.',
      back_to_steps: 'Revenir aux étapes',
      celebrate_stars_title: '{{n}} étoiles arrivent',
      celebrate_stars_body: 'Tu les reçois quand un adulte dit oui',
      celebrate_button: 'Retour à l\'accueil',
      see_details: 'Voir les détails',
      step_tools: 'Il te faut :',
      where_room: 'Où : {{room}}',
      celebrate_heading: 'Super travail !',
      celebrate_subtitle: 'Tu as terminé {{title}}',
      celebrate_stars_got_title: 'Tu as gagné {{n}} étoiles',
      celebrate_stars_got_body: 'Validé et ajouté à tes étoiles',
      tidy_put_all_back: 'Range toutes ces affaires à leur place',
    },
    reward_celebration: {
      congratulations: 'Félicitations !',
      you_have_a_reward: 'Vous avez une récompense !',
      check_rewards_prompt: 'Consultez la section des récompenses pour la réclamer !'
    }
    ,
    admin_panel: {
      header_title: "Panneau d'administration",
      header_subtitle: 'Gérez les tâches, récompenses et membres de votre famille',
      tab_tasks: 'Tâches',
      tab_rooms: 'Pièces',
      tab_members: 'Membres',
      tab_pending: 'En attente',
      tab_rewards: 'Récompenses',
      tab_tools: 'Outils',
      task_management: 'Gestion des tâches',
      create_new_task: 'Créer une nouvelle tâche',
      active_tasks: 'Tâches actives',
      active_tasks_help: 'Tâches en cours ou disponibles',
      edit: 'Modifier',
      remove: 'Supprimer',
      no_active_tasks: 'Aucune tâche active trouvée.',
      completed_tasks: 'Tâches terminées',
      completed_tasks_help: 'Tâches terminées et vérifiées',
      no_completed_tasks: 'Aucune tâche terminée trouvée.',
      room_management: 'Gestion des pièces',
      add_new_room: 'Ajouter une nouvelle pièce',
      member_management: 'Gestion des membres',
      add_new_member: 'Ajouter un nouveau membre',
      pending_verification: 'Vérification des tâches en attente',
      verify_this_task: 'Vérifier cette tâche',
      self_verify_not_allowed_hint:
        'Un autre administrateur doit vérifier cette tâche lorsqu’il existe plusieurs administrateurs.',
      points_not_awarded_section: 'Vérifiée — points manquants',
      points_not_awarded_help:
        'Ces tâches ont été marquées comme vérifiées avant que les points soient bien enregistrés. Utilisez « Attribuer les points » une fois pour créditer le membre ayant terminé la tâche.',
      award_missing_points: 'Attribuer les points',
      no_tasks_needing_points: 'Toutes les tâches vérifiées ont des points enregistrés.',
      verify_success: 'Tâche vérifiée. Les points ont été attribués.',
      verify_repaired_points_success:
        'Les points manquants ont été attribués au membre qui a terminé la tâche.',
      verify_already_done: 'Cette tâche est déjà vérifiée et les points sont enregistrés.',
      verify_misattribution_credit_confirm:
        'Le système indique déjà un paiement. Si le solde du membre n’a jamais augmenté : appliquer un crédit unique maintenant ? (Pas si les points ont déjà été reçus.)',
      verify_misattribution_success: 'Crédit unique appliqué. Demandez au membre de rafraîchir ou de se reconnecter.',
      verify_misattribution_already_done: 'Un crédit de correction unique a déjà été appliqué pour cette tâche.',
      verify_already_done_hint:
        'Si le membre n’a toujours pas de points : réessayez « Attribuer les points » et confirmez, ou effacez points_awarded_on_verification en base.',
      no_pending_verify: 'Aucune tâche en attente de vérification.',
      reward_management: 'Gestion des récompenses',
      create_new_reward: 'Créer une nouvelle récompense',
      claimed: 'Réclamée',
      available: 'Disponible',
      tool_management: 'Gestion des outils',
      create_new_tool: 'Créer un nouvel outil',
      no_tools_msg: "Aucun outil créé pour l'instant. Créez votre premier outil pour commencer !",
      task_modal_title_create: 'Créer une nouvelle tâche',
      task_modal_title_edit_prefix: 'Modifier la tâche',
      task_title_label: 'Titre de la tâche',
      task_title_placeholder: 'Entrez le titre de la tâche',
      description_label: 'Description (facultatif)',
      description_placeholder: 'Entrez la description de la tâche',
      room_label: 'Pièce',
      no_room_option: 'Aucune pièce',
      assignment_label: 'Attribution de la tâche',
      available_anyone_option: 'Disponible pour tous',
      assign_to_member_prefix: 'Attribuer à',
      assignment_hint_assigned: 'Cette tâche sera visible uniquement par le membre attribué',
      assignment_hint_anyone: 'Cette tâche sera disponible pour tout membre de la famille',
      points_label: 'Points à attribuer',
      points_placeholder: 'Points',
      due_date_label: "Date d'échéance",
      recurrence_label: 'Recréer tous les X jours (0 = pas de récurrence)',
      auto_available_label: 'Automatiquement',
      header_image_label: "Image d'en-tête (facultatif)",
      current_header_image_label: "Image d'en-tête actuelle",
      checklist_label: 'Éléments de la liste de contrôle',
      checklist_item_placeholder: 'Entrez un élément de la liste',
      images_optional_label: 'Images (facultatif) :',
      required_tools_label: 'Outils requis',
      no_tools_available_msg: "Aucun outil disponible. Créez d'abord des outils dans l'onglet Outils.",
      cancel_btn: 'Annuler',
      create_task_btn: 'Créer la tâche',
      save_changes_btn: 'Enregistrer les modifications',
      remove_task_title: 'Supprimer la tâche',
      remove_task_prompt_prefix: 'Voulez-vous vraiment supprimer la tâche',
      irreversible_note: 'Cette action est irréversible.',
      remove_task_btn: 'Supprimer la tâche',
      reward_modal_title_create: 'Créer une nouvelle récompense',
      reward_modal_title_edit_prefix: 'Modifier la récompense',
      reward_title_label: 'Titre de la récompense',
      reward_title_placeholder: 'Entrez le titre de la récompense',
      reward_desc_label: 'Description',
      reward_desc_placeholder: 'Entrez la description de la récompense',
      points_required_label: 'Points requis',
      reward_progress_start_label: 'Les points comptent à partir du',
      reward_progress_start_hint: 'Seuls les points gagnés depuis le début de ce jour (heure locale) comptent.',
      target_type_label: 'Type de cible',
      target_type_individual: 'Individuelle',
      target_type_family: 'Familiale',
      assign_member_label: 'Attribuer à un membre de la famille',
      select_member_option: 'Sélectionnez un membre de la famille',
      current_header_image_id_label: "ID de l'image d'en-tête actuelle",
      create_reward_btn: 'Créer la récompense',
      tool_modal_title_create: 'Créer un nouvel outil',
      tool_modal_title_edit_prefix: "Modifier l'outil",
      tool_name_label: "Nom de l'outil",
      tool_name_placeholder: "Entrez le nom de l'outil",
      tool_image_label: "Image de l'outil (facultatif)",
      current_tool_image_label: "Image de l'outil actuelle",
      create_tool_btn: "Créer l'outil",
      room_modal_title_add: 'Ajouter une nouvelle pièce',
      room_modal_title_edit: 'Modifier la pièce',
      delete_room_title: 'Supprimer la pièce',
      delete_room_btn: 'Supprimer',
      delete_room_prompt_prefix: 'Êtes-vous sûr de vouloir supprimer définitivement la pièce',
      delete_room_blocked_message: "Cette pièce ne peut pas être supprimée car une ou plusieurs tâches l'utilisent. Supprimez ou réattribuez ces tâches d'abord.",
      go_to_tasks_btn: 'Voir les tâches',
      room_name_label_required: 'Nom de la pièce *',
      room_name_placeholder: 'Entrez le nom de la pièce',
      add_room_btn: 'Ajouter la pièce',
      member_modal_title_add: 'Ajouter un nouveau membre de la famille',
      member_name_label_required: 'Nom *',
      member_name_placeholder: 'Entrez le nom du membre',
      member_password_label_required: 'Mot de passe *',
      member_password_placeholder: 'Entrez le mot de passe',
      member_email_label_optional: 'E-mail (facultatif)',
      member_email_placeholder: "Entrez l'adresse e-mail",
      admin_access_label: "Accès administrateur",
      add_member_btn: 'Ajouter le membre',
      member_type_label: 'Type de membre',
      member_type_adult: 'Adulte',
      member_type_child: 'Enfant',
      err_room_name: 'Donne un nom à la pièce.',
      err_room_create: 'La pièce n\'a pas pu être créée. Réessaie.',
      err_room_update: 'La pièce n\'a pas pu être enregistrée. Réessaie.',
      err_generic_missing: 'Il manque quelque chose. Ferme et réessaie.',
      err_task_title_points: 'Donne un titre et un nombre d\'étoiles à la tâche.',
      err_checklist_empty: 'Ajoute au moins une étape avec du texte.',
      err_task_create: 'La tâche n\'a pas pu être créée. Réessaie.',
      err_task_update: 'La tâche n\'a pas pu être enregistrée. Réessaie.',
      err_task_remove: 'La tâche n\'a pas pu être supprimée. Réessaie.',
      err_template_use: 'Impossible de créer une tâche à partir de ce modèle. Réessaie.',
      err_save_template: 'Le modèle n\'a pas pu être enregistré. Réessaie.',
      saved_template: 'Enregistré comme modèle.',
      err_header_image: 'L\'image n\'a pas pu être enregistrée. Réessaie.',
      err_reward_title: 'Donne un titre à la récompense.',
      err_reward_points: 'Indique combien d\'étoiles il faut pour la récompense.',
      err_reward_member: 'Choisis à qui est destinée la récompense.',
      err_reward_create: 'La récompense n\'a pas pu être créée. Réessaie.',
      err_reward_update: 'La récompense n\'a pas pu être enregistrée. Réessaie.',
      reward_updated: 'Récompense enregistrée.',
      err_tool_name: 'Donne un nom à l\'outil.',
      err_tool_create: 'L\'outil n\'a pas pu être créé. Réessaie.',
      err_tool_update: 'L\'outil n\'a pas pu être enregistré. Réessaie.',
      err_member_required: 'Un nouveau membre a besoin d\'un nom, d\'un identifiant et d\'un code PIN.',
      err_pin_format: 'Le code PIN doit comporter 4 à 8 chiffres.',
      err_username_short: 'L\'identifiant doit comporter au moins 2 caractères.',
      err_member_create: 'Le membre n\'a pas pu être ajouté. Réessaie.',
      verify_misattribution_title: 'Donner les étoiles maintenant ?',
      verify_misattribution_yes: 'Oui, les donner',
      status_assigned: 'Pour {{name}}',
      status_open: 'Tout le monde peut la prendre',
      comes_back_on: 'Revient le {{date}}',
      anyone_can_take: 'Tout le monde peut prendre la suivante (sinon elle revient à la même personne)',
      repeat_hint: 'Avec « répéter tous les », la suivante est due autant de jours après la validation et apparaît « jours pour terminer » jours avant.',
      step_tools_label: 'Outils pour cette étape',
      step_tools_hint: 'Choisissez d\'abord les outils de la tâche plus bas. Vous pourrez ensuite choisir ceux de cette étape.',
      room_picture_label: 'Photo de la pièce (facultatif)',
      room_picture_hint: 'Une photo aide les enfants à voir dans quelle pièce se trouve une tâche.',
      room_picture_add: 'Ajouter une photo',
      room_picture_change: 'Changer la photo',
      room_picture_failed: 'La pièce a été enregistrée, mais la photo n\'a pas pu être envoyée. Réessayez.',
      step_drag: 'Faites glisser pour déplacer l\'étape',
      step_move_up: 'Monter l\'étape',
      step_move_down: 'Descendre l\'étape',
      delete_btn: 'Supprimer',
      show_more_completed_tasks: 'Afficher plus',
      member_edit_title: 'Modifier le membre de la famille',
      member_name_label: 'Nom',
      member_pin_label_optional: 'PIN (facultatif)',
      member_pin_hint_optional: 'Laissez vide pour conserver le PIN actuel. Le PIN doit comporter 4 à 8 chiffres.',
      member_is_admin_label: 'Accès administrateur',
      member_update_failed: 'Échec de la mise à jour du membre',
      delete_reward_title: 'Supprimer la récompense',
      delete_confirm_prefix: 'Voulez-vous vraiment supprimer',
      recreate_on_validation_label: 'Recréer après validation',
      recreate_on_validation_help: 'Recréer la tâche dès qu’un adulte l’a validée',
      days_to_complete_label: 'Nombre de jours pour terminer la tâche',
      days_to_complete_placeholder: 'ex. 7',
      days_to_complete_help: 'Si renseigné, l’échéance sera la date de création + ce nombre de jours.',
      add_checklist_item_btn: 'Ajouter une étape',
      camera_title: 'Appareil photo',
      show_less_completed_tasks: 'Afficher moins',
      delete_tool_title: 'Supprimer l’outil',
      task_saving: 'Enregistrement de la tâche…',
      task_saved: 'Tâche enregistrée',
      try_again: 'Réessayer',
      deadline_question: 'Délai',
      deadline_none: 'Pas de délai',
      deadline_within: 'À faire en',
      days_unit: 'jours',
      repeat_question: 'Une fois validée…',
      repeat_none: 'elle est terminée – elle ne revient pas',
      repeat_now: 'elle revient tout de suite',
      repeat_after: 'elle revient après',
      next_question: 'Qui aura la suivante ?',
      next_same: '{{name}} à nouveau',
      next_anyone: 'Tout le monde peut la prendre',
      summary_title: 'Ce qui se passe :',
      due_this_time_label: 'Échéance cette fois (facultatif)',
      due_this_time_help: 'Laisse vide pour utiliser le délai ci-dessus.',
      sum_now_person: '{{name}} reçoit la tâche maintenant.',
      sum_now_anyone: 'Tout le monde dans la famille peut la prendre maintenant.',
      sum_within_one: 'Elle doit être faite en {{n}} jour.',
      sum_within_other: 'Elle doit être faite en {{n}} jours.',
      sum_end: 'Quand tu la valides, elle est terminée.',
      sum_back_now_same: 'Quand tu la valides, elle revient tout de suite à {{name}}.',
      sum_back_now_anyone: 'Quand tu la valides, elle revient tout de suite et tout le monde peut la prendre.',
      sum_back_after_same_one: 'Quand tu la valides, elle revient à {{name}} dans {{n}} jour.',
      sum_back_after_same_other: 'Quand tu la valides, elle revient à {{name}} dans {{n}} jours.',
      sum_back_after_anyone_one: 'Quand tu la valides, elle revient dans {{n}} jour et tout le monde peut la prendre.',
      sum_back_after_anyone_other: 'Quand tu la valides, elle revient dans {{n}} jours et tout le monde peut la prendre.',
      sum_then_within_one: 'Il y a alors {{n}} jour pour la faire.',
      sum_then_within_other: 'Il y a alors {{n}} jours pour la faire.',
      repeat_next: 'la tâche suivante est',
      next_ready_after: 'Prête après',
      hours_unit: 'heures',
      waits_for: 'En attente de : {{task}}',
      then_next: 'Ensuite : {{task}}',
      sum_next: 'Quand tu la valides, « {{task}} » vient ensuite.',
      sum_next_wait_one: 'Elle est prête {{n}} heure après que celle-ci a été faite.',
      sum_next_wait_other: 'Elle est prête {{n}} heures après que celle-ci a été faite.',
      sum_next_waits: 'D\'ici là, les enfants ne voient pas « {{task}} ».',
      reward_saving: 'Enregistrement de la récompense…',
      reward_saved: 'Récompense enregistrée',
      assignment_hint_each: 'Chacun a sa propre tâche : dans son calendrier, validée à part, avec ses propres étoiles.',
      err_choose_person: 'Choisis au moins une personne.',
      names_and: '{{first}} et {{last}}',
      sum_now_each: '{{names}} ont chacun leur tâche maintenant.',
      sum_back_now_each: 'Quand tu valides la tâche de quelqu\'un, la sienne revient tout de suite.',
      sum_back_after_each_one: 'Quand tu valides la tâche de quelqu\'un, la sienne revient dans {{n}} jour.',
      sum_back_after_each_other: 'Quand tu valides la tâche de quelqu\'un, la sienne revient dans {{n}} jours.',
      shared_ready: 'Prête',
      remove_shared_note: 'Elle est supprimée pour tous ceux à qui elle a été donnée. Celles déjà faites restent dans l\'historique.',
      repeat_plan: 'elle revient selon un rythme fixe',
      plan_weekly: 'chaque semaine le',
      plan_every: 'tous les',
      plan_every_unit: 'jours',
      plan_from: 'à partir du',
      sum_plan_person: '{{name}} reçoit la tâche.',
      sum_plan_anyone: 'Tout le monde dans la famille peut la prendre.',
      sum_plan_each: '{{names}} ont chacun leur tâche.',
      sum_plan_weekly: 'Elle apparaît chaque {{days}}.',
      sum_plan_every_one: 'Elle apparaît chaque jour à partir du {{date}}.',
      sum_plan_every_other: 'Elle apparaît tous les {{n}} jours à partir du {{date}}.',
      sum_plan_back: 'Quand tu la valides, elle revient le prochain jour prévu.',
      plan_week_every_one: 'chaque semaine',
      plan_week_every_other: 'toutes les {{n}} semaines',
      plan_on: 'le',
      plan_monthly: 'chaque mois',
      plan_month_every_one: 'chaque mois',
      plan_month_every_other: 'tous les {{n}} mois',
      plan_the: 'le',
      plan_which: 'lequel',
      plan_weekday: 'jour',
      plan_nth_1: 'premier',
      plan_nth_2: 'deuxième',
      plan_nth_3: 'troisième',
      plan_nth_4: 'quatrième',
      plan_nth_last: 'dernier',
      plan_on_date: 'le',
      plan_date_unit: 'du mois',
      sum_plan_weekly_every: 'Elle apparaît toutes les {{n}} semaines, le {{days}}.',
      sum_plan_every_open_one: 'Elle apparaît chaque jour.',
      sum_plan_every_open_other: 'Elle apparaît tous les {{n}} jours.',
      sum_plan_month_day_one: 'Elle apparaît le {{day}} de chaque mois.',
      sum_plan_month_day_other: 'Elle apparaît le {{day}} tous les {{n}} mois.',
      sum_plan_month_nth_one: 'Elle apparaît le {{nth}} {{weekday}} de chaque mois.',
      sum_plan_month_nth_other: 'Elle apparaît le {{nth}} {{weekday}} tous les {{n}} mois.',
      step_timer_label: 'Minuteur (facultatif)',
      step_timer_minutes: 'Minuteur, minutes',
      step_timer_seconds: 'Minuteur, secondes',
      step_timer_min: 'min',
      step_timer_sec: 's',
      step_timer_clear: 'Pas de minuteur',
      step_timer_hint: 'Par ex. « Gainage – 0:45 ». La personne qui fait la tâche le lance et entend un son à la fin.',
    },
    notifications: {
      approved_said_yes: '{{admin}} a dit oui !',
      approved_for: 'pour {{task}}',
      approved_note: '{{admin}} dit : {{note}}',
      approved_now_you_have: 'Tu as maintenant {{n}} étoiles',
      approved_reward_ready: '{{reward}} t\'attend !',
      approved_more_to_goal: 'Encore {{n}} étoiles pour {{reward}}',
      approved_see_reward: 'Voir ma récompense',
      approved_not_now: 'Pas maintenant',
      approved_ok: 'Youpi !',
      admin_pending_verification_one: '1 tâche attend votre validation.',
      admin_pending_verification_other: '{{count}} tâches attendent votre validation.',
    },
    member_chooser: {
      who_title: 'Qui es-tu ?',
      hi_name: 'Salut {{name}} !',
      enter_pin: 'Saisis ton code PIN',
      log_in: 'Se connecter',
      use_password: 'Utiliser un mot de passe',
      use_pin: 'Utiliser le code PIN',
      password_label: 'Mot de passe',
      delete_digit: 'Effacer',
      back: 'Retour',
      locked_until: 'Trop d\'essais. Réessaie à {{time}}.',
      locked_later: 'Trop d\'essais. Réessaie un peu plus tard.',
      wrong_pin: 'Ça n\'a pas marché. Réessaie.',
      sign_out_device_hint: 'Efface la connexion familiale de ce navigateur. Utilisez cette option si vous êtes bloqué ou souhaitez vous reconnecter en tant qu’adulte avec Google/e-mail.',
      sign_out_device: 'Se déconnecter de cet appareil',
    },
    parent: {
      to_approve_one: '{{n}} tâche à valider',
      to_approve_other: '{{n}} tâches à valider',
      finished: '{{name}} a fini {{task}}',
      review: 'Voir',
      nothing_to_approve: 'Rien à valider pour l\'instant',
      family: 'Famille',
      open_one: '{{n}} tâche à faire',
      open_other: '{{n}} tâches à faire',
      open_none: 'Aucune tâche à faire',
      set_up: 'Configurer',
      tasks: 'Tâches',
      rewards: 'Récompenses',
      members: 'Famille',
      rooms_tools: 'Pièces et outils',
      lock: 'Verrouiller',
      settings: 'Réglages',
      more_waiting: '+{{n}} de plus',
      your_tasks: 'Tes propres tâches',
    },
    approve: {
      title: 'Valider les tâches',
      approve: 'Valider',
      not_done: 'Pas encore fini',
      note_so_neat: 'Trop bien rangé !',
      note_well_done: 'Bravo !',
      note_thank_you: 'Merci !',
      reason_look_again: 'Presque — regarde encore une fois',
      reason_steps_missing: 'Il reste quelques étapes',
      reason_do_together: 'Faisons-le ensemble',
      pick_reason: 'Que doit entendre {{name}} ?',
      cancel: 'Annuler',
      steps_done_one: 'L\'étape est faite',
      steps_done_other: 'Les {{n}} étapes sont faites',
      approved_toast: '{{task}} validée. {{name}} reçoit {{stars}}.',
      undo: 'Annuler',
      sent_back_toast: '{{task}} est renvoyée à {{name}}.',
      today_at: 'Aujourd\'hui {{time}}',
      yesterday_at: 'Hier {{time}}',
      from_grownup: 'Un adulte dit : {{reason}}',
      back: 'Retour',
      self_blocked: 'Un autre adulte doit valider ta propre tâche.',
    },
    my_settings: {
      title: 'Mes réglages',
      language: 'Langue',
      same_as_device: 'Comme cet appareil',
      look: 'Clair ou sombre',
      light: 'Clair',
      dark: 'Sombre',
      notifications: 'Notifications',
      on: 'Activées',
      off: 'Désactivées',
      notif_turn_on: 'Activer sur cet appareil',
      notif_turned_on: 'Les notifications sont activées.',
      notif_turned_off: 'Les notifications sont désactivées.',
      notif_parent_off: 'Un adulte a désactivé tes notifications.',
      notif_parent_on: 'Un adulte veut que tes notifications soient activées.',
      notif_unsupported: 'Cet appareil ne peut pas afficher les notifications d\'AntNest.',
      notif_blocked: 'Les notifications sont bloquées dans ce navigateur. Demande à un adulte de les autoriser dans les réglages du navigateur.',
      timer_sound: 'Son du minuteur',
      timer_sound_help: 'Le son quand le minuteur d\'une étape arrive à zéro. Touchez pour l\'écouter.',
    },
    parent_notifications: {
      title: 'Notifications des enfants',
      help: 'Choisis qui décide. « Toujours activées » : l\'enfant ne peut pas les désactiver ; « Désactivées » : rien n\'est envoyé à l\'enfant.',
      child: 'L\'enfant décide',
      on: 'Toujours activées',
      off: 'Désactivées',
      saved: 'Enregistré.',
    },
    handover: {
      title: 'Récompenses à remettre',
      help: 'Elles ont été réclamées. Remets-les à l\'enfant, puis touche Remise.',
      got: '{{name}} a obtenu {{reward}}',
      handed_over: 'Remise',
      done_toast: '{{reward}} remise à {{name}}.',
    },
    library: {
      access_denied: 'Seuls les adultes peuvent ouvrir ceci.',
      title: 'Bibliothèque de tâches',
      intro: 'Pars d\'une tâche standard ou d\'un modèle enregistré depuis une tâche. Seule ta famille voit « Mes modèles ».',
      age: 'Âge',
      age_group: 'Tranche d\'âge',
      age_all: 'Tous âges',
      age_2_3: '2–3 ans',
      age_4_5: '4–5 ans',
      age_6_7: '6–7 ans',
      age_8_9: '8–9 ans',
      age_10_12: '10–12 ans',
      age_13_plus: '13 ans et +',
      standard_title: 'Tâches standard',
      none_standard: 'Aucune tâche standard pour cette langue et cet âge.',
      badge_standard: 'Standard',
      my_title: 'Mes modèles',
      my_intro: 'Enregistrés avec « Enregistrer comme modèle » dans le formulaire de tâche. Seule ta famille les voit.',
      none_my: 'Aucun modèle enregistré. Crée une tâche et utilise « Enregistrer comme modèle », ou change le filtre d\'âge.',
      badge_my: 'Mon modèle',
      use: 'Utiliser',
      save_as_template: 'Enregistrer comme modèle',
      days: 'Jours',
      stars: 'Étoiles',
      tool_name: 'Nom de l\'outil',
      tool_name_ph: 'ex. Aspirateur',
      username_required: 'Identifiant (obligatoire)',
      username_ph: 'Choisis un identifiant',
      pin_label: 'Code PIN (4 à 8 chiffres)',
      pin_ph: 'Saisis un code PIN',
      adults: 'Adultes',
      show: 'Afficher',
      hide: 'Masquer',
      added_to_tasks: '« {{title}} » a été ajoutée à vos tâches',
      chain_title: '{{n}} tâches qui se suivent',
      chain_wait_one: 'la suivante est prête après {{n}} heure',
      chain_wait_other: 'la suivante est prête après {{n}} heures',
      chain_loops: 'Puis on recommence.',
    },
    deletion: {
      delete_member: 'Supprimer le membre',
      delete_member_title: 'Supprimer {{name}} ?',
      delete_member_text: '{{name}} est retiré(e) de la famille tout de suite et déconnecté(e) sur tous les appareils.',
      delete_member_lost: 'Toutes les étoiles, statistiques, tâches et récompenses de {{name}} seront supprimées.',
      undo_30: 'Vous pouvez annuler pendant 30 jours, jusqu\'au {{date}}. Ensuite, tout est supprimé définitivement et ne peut pas être récupéré.',
      delete_member_yes: 'Oui, supprimer',
      member_deleted_toast: '{{name}} a été supprimé(e). Vous pouvez restaurer pendant 30 jours.',
      recently_deleted: 'Supprimés récemment',
      recently_deleted_hint: 'Vous pouvez restaurer ces membres jusqu\'à la date indiquée. Ensuite, ils sont supprimés définitivement avec toutes leurs données.',
      gone_on: 'Supprimé définitivement le {{date}}',
      restore: 'Restaurer',
      member_restored_toast: '{{name}} est de retour dans la famille. Les notifications doivent être réactivées.',
      family_section: 'Supprimer la famille',
      delete_family_row: 'Supprimer toute la famille',
      delete_family_hint: 'Tout le monde et tout dans la famille. Annulable pendant 30 jours.',
      delete_family_title: 'Supprimer toute la famille ?',
      delete_family_text: 'Tout le monde dans {{family}} est déconnecté immédiatement et ne peut plus utiliser AntNest tant que la famille n\'est pas restaurée.',
      delete_family_lost: 'Tout sera supprimé : tous les membres, étoiles, statistiques, tâches, récompenses, événements et images.',
      type_family_name: 'Pour confirmer, saisissez le nom de la famille : {{family}}',
      delete_family_yes: 'Supprimer la famille',
      pending_title: 'La suppression de {{family}} est programmée',
      pending_text: 'La famille et toutes ses données seront supprimées définitivement le {{date}}. D\'ici là, vous pouvez tout restaurer tel quel.',
      days_left: 'Jours restants : {{days}}',
      pending_not_admin: 'Seul un adulte avec un accès administrateur peut restaurer la famille.',
      restore_family: 'Restaurer la famille',
      family_restored_toast: 'Bon retour ! La famille a été restaurée. Les notifications doivent être réactivées.',
      log_out: 'Se déconnecter',
      cancel: 'Annuler',
    },
    pictures: {
      remove_picture: 'Supprimer l\'image',
      removed: 'Image supprimée. Vous retrouvez la fourmi de base.',
      unreadable: 'Impossible d\'ouvrir cette image. Essayez-en une autre ou prenez une nouvelle photo.',
      rotate: 'Faire pivoter',
      preparing: 'Préparation de l\'image…',
    },
    billing: {
      title: 'Abonnement',
      trial_title: 'Essai gratuit – jours restants : {{days}}',
      trial_text: 'Votre essai gratuit se termine le {{date}}. Choisissez une formule d\'ici là pour continuer à utiliser AntNest.',
      expired_title: 'Votre essai gratuit est terminé',
      expired_text: 'Choisissez une formule pour continuer à utiliser AntNest. Les tâches, les étoiles et les récompenses de votre famille sont toujours là.',
      active_title_monthly: 'Vous payez chaque mois',
      active_title_yearly: 'Vous payez chaque année',
      active_text: 'Votre abonnement sera renouvelé le {{date}}.',
      grace_title: 'Un paiement n\'a pas abouti',
      grace_text: 'Veuillez mettre à jour vos informations de paiement avant le {{date}} pour continuer à utiliser AntNest.',
      cancelling_title: 'Votre abonnement est résilié',
      cancelling_text: 'Vous pouvez utiliser AntNest jusqu\'au {{date}}. Choisissez une formule pour continuer ensuite.',
      exempt_title: 'AntNest est gratuit pour votre famille',
      exempt_text: 'Vous n\'avez rien à payer.',
      plans_title: 'Choisissez une formule',
      plans_intro: 'Un seul abonnement pour toute la famille, sur tous les appareils.',
      plan_monthly: 'Mensuel',
      plan_yearly: 'Annuel',
      price_monthly: '{{price}} par mois',
      price_yearly: '{{price}} par an',
      price_soon: 'Prix bientôt disponible',
      includes_family: 'Toute la famille, sur tous les appareils',
      includes_tasks: 'Toutes les tâches et listes de contrôle prêtes à l\'emploi',
      includes_cancel: 'Résiliable à tout moment',
      choose_monthly: 'Choisir mensuel',
      choose_yearly: 'Choisir annuel',
      coming_soon: 'Le paiement ouvre bientôt. D\'ici là, AntNest est gratuit.',
      manage_title: 'Gérer votre abonnement',
      manage_text: 'Changez de formule, mettez à jour votre carte, consultez vos reçus ou résiliez.',
      manage_button: 'Gérer l\'abonnement',
      banner_trial: 'Jours restants avant la fin de votre essai gratuit : {{days}}.',
      banner_trial_last_day: 'Votre essai gratuit se termine aujourd\'hui.',
      banner_payment: 'Un paiement n\'a pas abouti. Mettez à jour votre carte avant le {{date}}.',
      banner_cancelling: 'Votre abonnement se termine le {{date}}.',
      banner_action: 'Voir les formules',
      paywall_title: 'AntNest fait une pause',
      paywall_text: 'Demande à un adulte de choisir une formule. Toutes tes étoiles et tes tâches sont bien gardées et t\'attendent.',
      manage_failed: 'La page d\'abonnement n\'a pas pu s\'ouvrir. Réessaie dans un instant.',
    },
    pocket: {
      title: 'Argent de poche',
      intro: 'L\'argent de poche est calculé à partir des étoiles gagnées par chaque enfant pour les tâches validées dans le mois. AntNest ne transfère pas d\'argent : vous payez comme d\'habitude et le marquez comme payé.',
      no_children: 'Ajoute un enfant à la famille pour donner de l\'argent de poche.',
      currency: 'Devise',
      for_child: 'Argent de poche pour {{name}}',
      how_question: 'Comment est-il gagné ?',
      per_ten_label: '{{currency}} toutes les 10 étoiles',
      fixed_label: '{{currency}} par mois, dès que {{name}} a gagné au moins',
      threshold_label: 'étoiles',
      threshold_help: '0 étoile = toujours donné (argent de poche fixe).',
      bonus_label: 'Bonus :',
      bonus_every_label: '{{currency}} de plus toutes les',
      bonus_stars_label: 'étoiles de plus',
      cap_question: 'Plafond mensuel — choisis',
      cap_none: 'Pas de plafond',
      cap_at: 'Au maximum',
      cap_unit: '{{currency}} par mois',
      cap_required: 'Choisis un plafond ou pas de plafond avant d\'enregistrer.',
      example_title: 'Par exemple, un mois avec :',
      example_line_one: '{{n}} étoile → {{amount}}',
      example_line_other: '{{n}} étoiles → {{amount}}',
      capped_short: 'plafond atteint',
      typical_dk: 'Habituel au Danemark par mois : moins de 100 DKK à 6–10 ans, 50–200 DKK à 11–14 ans, 200–500 DKK à 15–17 ans.',
      save: 'Enregistrer',
      saved: 'Argent de poche de {{name}} enregistré',
      save_failed: 'Impossible d\'enregistrer. Réessaie.',
      my_month: 'Argent de poche en {{month}} : {{amount}}',
      my_next_one: '{{n}} étoile de plus donne {{amount}} de plus',
      my_next_other: '{{n}} étoiles de plus donnent {{amount}} de plus',
      my_capped: 'Tu as atteint le maximum du mois. Bravo !',
      my_last_paid: '{{month}} : {{amount}} — payé ✓',
      my_last_unpaid: '{{month}} : {{amount}} — en attente de paiement',
      to_pay_title: 'Argent de poche à verser',
      to_pay_help: 'Payez comme d\'habitude (espèces, virement ou appli), puis marquez-le comme payé.',
      mark_paid: 'Marquer comme payé',
      task_counts: 'Donne de l\'argent de poche',
      task_counts_help: 'Décoche pour les tâches du quotidien comme faire son lit, pour que seules les plus grosses tâches comptent.',
      typical_intro: 'Habituel en {{country}} par mois :',
      typical_band: '{{ages}} ans : {{amount}}',
      typical_under: 'moins de {{amount}}',
      typical_about: 'environ {{amount}}',
      typical_source: 'source : {{source}}',
    },
    getting_started: {
      title: 'Bien démarrer',
      progress: '{{done}} sur {{all}} faits',
      hide: 'Masquer',
      item_approve: 'Valider la première tâche terminée',
      item_own_task: 'Modifie une tâche ou crée la tienne',
      item_home_screen: 'Ajoute AntNest à l\'écran d\'accueil',
      item_calendar: 'Vois les tâches dans le calendrier',
      home_screen_how: 'iPhone/iPad : touche Partager dans Safari puis « Sur l\'écran d\'accueil ». Android : touche ⋮ dans Chrome puis « Installer l\'application » ou « Ajouter à l\'écran d\'accueil ». Fais-le aussi sur les appareils des enfants.',
      home_screen_done: 'C\'est fait',
      pocket_title: 'De l\'argent de poche pour les étoiles ?',
      pocket_text: 'Maintenant que les enfants sont lancés, leurs étoiles peuvent aussi donner de l\'argent de poche – un montant fixe, un montant par 10 étoiles et un plafond mensuel que vous choisissez.',
      pocket_setup: 'Le configurer',
      pocket_no: 'Non merci',
    },
    editor: {
      close: 'Fermer',
      unsaved_title: 'Enregistrer vos modifications ?',
      unsaved_body: 'Vous avez modifié quelque chose. Voulez-vous l\'enregistrer avant de fermer ?',
      save: 'Enregistrer',
      discard: 'Ne pas enregistrer',
      keep_editing: 'Continuer à modifier',
    },
    timer: {
      start: 'Lancer le minuteur',
      pause: 'Pause',
      resume: 'Reprendre',
      restart: 'Recommencer',
      times_up: 'C\'est l\'heure !',
      sound_bell: 'Cloche',
      sound_chime: 'Carillon',
      sound_beeps: 'Réveil',
      sound_marimba: 'Marimba',
      sound_fanfare: 'Fanfare',
      volume_note: 'Le son suit le volume du téléphone : montez-le pour l\'entendre.',
    },
    approval_setting: {
      title: 'Validation des tâches des adultes',
      label: 'Les tâches des adultes doivent être validées par un autre adulte',
      help_off: 'Désactivé : quand un adulte termine une tâche, elle est validée tout de suite et les étoiles sont à lui. Les tâches des enfants sont toujours validées par un adulte.',
      help_on: 'Activé : la tâche terminée d\'un adulte attend qu\'un autre adulte la valide (s\'il n\'y a qu\'un administrateur, il peut valider les siennes).',
      setup_help: 'Sans coche, les tâches des adultes sont validées dès qu\'elles sont faites. Vous pourrez le changer plus tard dans les Réglages.',
      saved: 'Enregistré.',
    },
  },
  fi: {
    auth: {
      forgot_title: 'Unohditko salasanan?',
      forgot_link: 'Unohditko salasanan?',
      forgot_confirmation: 'Jos tili on olemassa, lähetimme palautuslinkin.',
      email_label: 'Sähköposti',
      send_reset_link: 'Lähetä linkki',
      reset_title: 'Aseta uusi salasana',
      new_password_label: 'Uusi salasana',
      confirm_password_label: 'Vahvista salasana',
      set_new_password: 'Tallenna uusi salasana',
      password_mismatch: 'Salasanat eivät täsmää',
      password_policy_msg: 'Vähintään 12 merkkiä, sisältää isot/pienet kirjaimet ja numeron.',
      invalid_token: 'Virheellinen tai vanhentunut linkki',
      reset_success: 'Salasana päivitetty. Siirrytään kirjautumiseen...',
      reset_failed: 'Salasanan palautus epäonnistui. Yritä uudelleen.',
      login_failed: 'Kirjautuminen epäonnistui. Yritä uudelleen.',
      beta_banner: 'AntNest on beetavaiheessa. Onko sinulla kutsukoodi?',
      beta_banner_link: 'Rekisteröidy tästä',
      or: 'tai',
      login_google: 'Kirjaudu Googlella',
      login_facebook: 'Kirjaudu Facebookilla',
      no_account: 'Eikö sinulla ole tiliä?',
      create_one: 'Luo tili',
      err_code_required: 'Tarvitset beetakoodin.',
      err_agree: 'Hyväksy käyttöehdot ja tietosuojakäytäntö.',
      err_fields: 'Täytä kaikki kentät.',
      err_code_invalid: 'Tämä beetakoodi ei kelpaa tai se on jo käytetty.',
      err_signup_failed: 'Tiliä ei voitu luoda. Yritä uudelleen.',
      signup_title: 'Luo tilisi',
      beta_code: 'Beetakoodi',
      beta_code_ph: 'Kirjoita kutsukoodisi',
      full_name: 'Nimesi',
      full_name_ph: 'esim. Anna',
      username: 'Käyttäjänimi',
      show_password: 'Näytä salasana',
      hide_password: 'Piilota salasana',
      rule_length: 'Vähintään 12 merkkiä',
      rule_upper: 'Iso kirjain',
      rule_lower: 'Pieni kirjain',
      rule_digit: 'Numero',
      password_match: 'Salasanat täsmäävät',
      family_name: 'Perheen nimi',
      family_name_ph: 'esim. Virtasen perhe',
      family_default: 'Perheeni',
      agree_before: 'Hyväksyn',
      terms: 'käyttöehdot',
      and: 'ja',
      privacy: 'tietosuojakäytännön',
      creating_account: 'Luodaan tiliä…',
      create_account: 'Luo tili',
      signup_google: 'Rekisteröidy Googlella',
      signup_facebook: 'Rekisteröidy Facebookilla',
      choose_language: 'Valitse kieli',
      oauth_failed: 'Kirjautuminen ei valmistunut. Yritä uudelleen.',
      processing: 'Kirjaudutaan sisään…',
      complete_setup: 'Melkein valmista',
      your_name: 'Nimesi',
      complete_setup_btn: 'Luo perhe ja jatka',
      login_apple: 'Kirjaudu Applella',
      signup_apple: 'Rekisteröidy Applella',
    },
    rooms: {
      kitchen: 'Keittiö',
      living_room: 'Olohuone',
      dining_room: 'Ruokailuhuone',
      bedroom: 'Makuuhuone',
      bathroom: 'Kylpyhuone',
      toilet: 'WC',
      hallway: 'Käytävä',
      entryway: 'Eteinen',
      laundry_room: 'Pyykkihuone',
      garage: 'Autotalli',
      office: 'Työhuone',
      playroom: 'Leikkihuone',
      nursery: 'Lastenhuone',
      patio: 'Terassi/Parveke',
      garden: 'Puutarha',
      storage: 'Varasto',
      basement: 'Kellari',
      attic: 'Ullakko',
      child_bedroom: 'Lastenhuone',
    },
    onboarding: {
      title: 'Aloitetaan',
      family_name: 'Perheen nimi',
      adults: 'Aikuiset',
      children: 'Lapset',
      member_name: 'Nimi',
      username: 'Käyttäjänimi',
      pin_placeholder: 'PIN (4 numeroa)',
      role_admin: 'Ylläpitäjä',
      role_member: 'Jäsen',
      next: 'Seuraava',
      back: 'Takaisin',
      confirm: 'Hyvältä näyttää',
      processing: 'Tallennetaan…',
      step_rooms_title: 'Määritä kotisi',
      step_rooms_help: 'Lisää alla olevista vaihtoehdoista huoneita kotiisi. Jos sinulla on huone tai tila, jota ei ole luettelossa, voit lisätä sen manuaalisesti.\nVoit aina muokata, lisätä ja poistaa huoneita ylläpito-osiossa.',
      custom_room_placeholder: 'Mukautetun huoneen nimi',
      add_custom: 'Lisää mukautettu',
      remove: 'Poista',
      step_review_title: 'Tarkista',
      review_family: 'Perhe',
      review_members: 'Jäsenet',
      review_rooms: 'Huoneet',
      error_generic: 'Tallennuksessa tapahtui virhe',
      step_members_help: 'Lisää perheenjäsenet tähän.\nAnna jokaiselle jäsenelle nimi ja käyttäjätunnus.\nJokaisella jäsenellä on oltava PIN-koodi kirjautumista varten.',
      validation_incomplete: 'Kaikkia kenttiä ei ole täytetty oikein',
      adult_label: 'Aikuinen',
      child_label: 'Lapsi',
      privacy_note: 'Tietoja käytetään vain perheenne asetusten tekemiseen. Emme jaa emmekä myy – emmekä koskaan tule jakamaan tai myymään – tietojanne kenellekään.',
      child_age: 'Ikä',
      child_room_name: '{{name}}n huone',
      child_room_name_s: '{{name}}n huone',
      hint_members: 'Voit aina lisätä tai muuttaa perheenjäseniä myöhemmin kohdassa Admin → Jäsenet.',
      hint_rooms: 'Jokainen lapsi saa oman huoneen, jotta hänen tehtävänsä ovat oikeassa paikassa. Voit aina muuttaa huoneita myöhemmin kohdassa Admin → Huoneet.',
      step_tasks_title: 'Tehtäviä alkuun',
      step_tasks_help: 'Valitsimme muutaman tehtävän, jotka sopivat huoneisiinne ja lasten ikään. Poista valinta niistä, joita ette halua – voitte muuttaa kaiken myöhemmin.',
      starters_unavailable: 'Ehdotuksia ei voitu ladata. Voit ohittaa vaiheen ja lisätä tehtäviä myöhemmin.',
      tasks_for_child: 'Tehtävät: {{name}}',
      tasks_shared: 'Kaikille (kuka tahansa voi ottaa)',
      whole_home: 'Koko koti',
      hint_tasks: 'Kohdassa Admin → Tehtävät on paljon muita vakiotehtäviä, ja siellä voitte tehdä myös omia.',
      skip_step: 'Ohita',
      step_rewards_title: 'Palkintoja tavoitteeksi',
      step_rewards_help: 'Lapset pysyvät motivoituneina, kun ensimmäinen palkinto on ulottuvilla. Nämä on asetettu niin, että ensimmäisen voi saavuttaa muutamassa päivässä.',
      rewards_for_child: '{{name}}',
      rewards_family: 'Koko perheelle (kaikkien tähdet lasketaan)',
      hint_rewards: 'Voitte aina lisätä omia palkintoja tai muuttaa näitä kohdassa Admin → Palkinnot.',
      review_starters: 'Alkuun',
      review_tasks_one: '{{n}} tehtävä',
      review_tasks_other: '{{n}} tehtävää',
      review_rewards_one: '{{n}} palkinto',
      review_rewards_other: '{{n}} palkintoa',
      create_family: 'Luo perheemme',
      done_title: 'Perheenne on valmis!',
      done_made: 'Teimme teille {{tasks}} tehtävää ja {{rewards}} palkintoa.',
      done_try: 'Kokeilkaa ensimmäistä tehtävää nyt: vaihda lapseen ylhäältä kohdasta "{{switch}}" ja napauta tehtävän "{{start}}".',
      done_admin: 'Kun tehtävä on tehty, hyväksy se kohdassa "{{approve}}" – silloin tähdet lisätään.',
      go_home: 'Perheen etusivulle',
      go_admin: 'Siirry Adminiin',
    },
    common: {
      save: 'Tallenna',
      take_photo: 'Ota kuva',
      cancel: 'Peruuta',
      loading: 'Ladataan...',
      all: 'Kaikki',
      stars_one: '{{n}} tähti',
      stars_other: '{{n}} tähteä',
      update_ready: 'AntNestin uusi versio on valmis.',
      update_now: 'Päivitä',
      remove_image: 'Poista kuva',
      remove_item: 'Poista kohta',
      ok: 'OK',
      error_generic: 'Jokin meni pieleen. Yritä uudelleen.',
    },
    header: {
      stats: 'Tilastot',
      app_title: 'AntNest',
      home: 'Koti',
      tasks: 'Tehtävät',
      calendar: 'Kalenteri',
      rewards: 'Palkinnot',
      settings: 'Asetukset',
      profile: 'Profiili',
      admin: 'Ylläpito',
      logout: 'Kirjaudu ulos',
      points: 'pistettä',
      settings_tooltip: 'Asetukset',
      change_picture: 'Vaihda kuvani',
      someone_else: 'Jonkun toisen vuoro',
      for_grownups: 'Aikuisille',
      for_grownups_hint: 'Asetukset, hyväksynnät, kirjaudu ulos',
      my_page: 'Oma sivu',
      menu_close: 'Sulje valikko',
      menu_open: 'Avaa valikko',
      nav_menu: 'Valikko',
      master_admin: 'Pääylläpitäjä',
      switch_user: 'Vaihda käyttäjää',
      tab_bar_label: 'Pääosiot',
    },
    stats: {
      title: 'Tilastot',
      tab_me: 'Minä',
      tab_family: 'Perhe',
      tab_tasks: 'Tehtävät',
      period_week: 'Viikko',
      period_month: 'Kuukausi',
      period_year: 'Vuosi',
      period_all: 'Kaikki',
      prev: 'Edellinen',
      next: 'Seuraava',
      all_time: 'Kaikki',
      tasks_done: 'tehtävää tehty',
      points: 'pistettä',
      rewards: 'palkintoa',
      chart_points: 'Pisteet ajan mittaan',
      where_helped: 'Missä autoin',
      no_room: 'Ei huonetta',
      my_top_tasks: 'Useimmin tekemäni tehtävät',
      together: 'Yhdessä',
      who_helped: 'Kuka auttoi',
      everyone_over_time: 'Kaikki ajan mittaan',
      repeating_title: 'Tehtävät',
      per_task_title: 'Jokainen tehtävä',
      times_done_axis: 'Kertaa tehty',
      times: '{n}×',
      last_done: 'viimeksi: {date}',
      empty: 'Tällä jaksolla ei vielä mitään.',
      family_hidden: 'Perheenne näyttää tämän vain aikuisille.',
      week_short: 'Vko {n}',
      enc_start: 'Uusi alku — jokainen tehtävä lasketaan! 💪',
      enc_first: 'Ensimmäinen tehtäväsi — hienoa! 🎉',
      enc_done: 'Teit {n} tehtävää — hienoa! ⭐',
      enc_more: 'Enemmän pisteitä kuin edellisellä jaksolla — jatka samaan malliin! 🚀',
      visibility_title: 'Tilastot: mitä lapset näkevät',
      visibility_help: 'Valitse, miltä Perhe-välilehti näyttää lapsille. Aikuiset ylläpitäjät näkevät aina kaiken.',
      visibility_team: 'Tiiminäkymä — kaikkien osuus, ei järjestystä (suositeltu)',
      visibility_full: 'Täysi vertailu — pisteiden mukaan järjestettynä',
      visibility_admins: 'Piilotettu — vain aikuiset näkevät perheen vertailun',
      visibility_saved: 'Tallennettu',
      team_view_note: 'Kaikkien osuus perheen yhteispisteistä',
    },
    calendar: {
      title: 'Kalenteri',
      coming_soon: 'Kalenteria rakennetaan. Perusnäkymät ja tapahtumat näkyvät täällä.',
      view_day: 'Päivä',
      view_week: 'Viikko',
      view_month: 'Kuukausi',
      week_label: 'Viikko',
      no_events_for_day: 'Ei tapahtumia',
      today_label: 'tänään',
      prev_day: 'Edellinen päivä',
      next_day: 'Seuraava päivä',
      prev_week: 'Edellinen viikko',
      next_week: 'Seuraava viikko',
      prev_month: 'Edellinen kuukausi',
      next_month: 'Seuraava kuukausi',
      go_to_today: 'Tänään',
      not_allowed_edit_event: 'Voit muuttaa vain itse luomiasi tapahtumia.',
      filters_title: 'Suodata jäsenen mukaan',
      single_view_button: 'Yksittäisnäkymä',
      family_view_button: 'Perhenäkymä',
      no_events_for_range: 'Ei tapahtumia tällä aikavälillä',
      no_events: 'Ei tapahtumia tällä ajanjaksolla.',
      add_event: 'Lisää tapahtuma',
      edit_event: 'Muokkaa tapahtumaa',
      event_form_title: 'Otsikko',
      event_form_description: 'Kuvaus (valinnainen)',
      event_form_location: 'Sijainti (valinnainen)',
      event_form_start: 'Alkaa',
      event_form_end: 'Päättyy',
      event_form_all_day: 'Koko päivä',
      event_form_attendees: 'Osallistujat',
      event_form_save: 'Tallenna',
      event_form_cancel: 'Peruuta',
      event_form_delete: 'Poista',
      delete_confirm_title: 'Poista tapahtuma',
      delete_confirm_message: 'Haluatko varmasti poistaa tämän tapahtuman?',
      success_created: 'Tapahtuma luotu',
      success_updated: 'Tapahtuma päivitetty',
      success_deleted: 'Tapahtuma poistettu',
      error_generic: 'Jokin meni vikaan',
      validation_title_required: 'Otsikko on pakollinen',
      validation_end_after_start: 'Loppuajan tulee olla alun jälkeen',
      create_calendar_event: 'Luo kalenteritapahtuma',
      event_modal_title_create: 'Luo kalenteritapahtuma',
      event_modal_title_edit_prefix: 'Muokkaa'
      ,
      reminder_label: 'Muistutus',
      reminder_none: 'Ei muistutusta',
      reminder_10m: '10 minuuttia ennen',
      reminder_30m: '30 minuuttia ennen',
      reminder_1h: '1 tunti ennen',
      reminder_12h: '12 tuntia ennen',
      reminder_1d: '1 päivä ennen',
      nothing_planned: 'Ei vielä suunnitelmia',
      nothing_this_month: 'Tälle kuulle ei ole suunnitelmia',
      today_day: 'Tänään, {{day}}',
      tomorrow_day: 'Huomenna, {{day}}',
      at_time: 'Klo {{time}}',
      all_day_label: 'Koko päivän',
      with_names: 'mukana {{names}}',
      task_label: 'Tehtävä',
      everyone: 'Kaikki',
      back: 'Takaisin',
      due_today: 'Tee tänään',
      due_today_at: 'Tee tänään klo {{time}} mennessä',
      due_tomorrow: 'Tee huomiseen mennessä',
      due_tomorrow_at: 'Tee huomenna klo {{time}} mennessä',
      due_later: 'Tee tähän päivään mennessä',
      due_later_at: 'Tee klo {{time}} mennessä',
      overdue_since: 'Myöhässä {{day}} alkaen',
      event_form_image: 'Tapahtuman kuva (valinnainen)',
      remove_image: 'Poista kuva',
      planned: 'Suunniteltu',
    },
    settings: {
      title: 'Asetukset',
      subtitle: 'Hallinnoi tiliäsi ja sovelluksen asetuksia',
      dark_mode_section: 'Tumma tila',
      current_theme: 'Nykyinen teema',
      following_system: 'Seuraa järjestelmän asetusta',
      manual_selection: 'Manuaalinen valinta',
      follow_system_label: 'Seuraa järjestelmän asetusta',
      auto_switch_hint: 'Vaihda automaattisesti vaalean ja tumman tilan välillä järjestelmäasetuksen mukaan',
      enable_dark_mode: 'Ota tumma tila käyttöön',
      manual_toggle_hint: 'Vaihda manuaalisesti vaaleiden ja tummien teemojen välillä',
      light_mode: 'Vaalea tila',
      dark_mode: 'Tumma tila',
      more_settings_title: 'Lisää asetuksia tulossa pian',
      more_settings_desc: 'Lisää mukautusvaihtoehtoja tulee saataville, kuten ilmoitukset, tietosuoja ja tilinhallinta.',
      language_section: 'Kieli',
      language_hint: 'Valitse käyttöliittymän kieli',
      back: 'Takaisin',
      profile_parent: 'Vanhempi. Muokkaa nimeä, kuvaa ja salasanaa',
      profile_member: 'Muokkaa nimeä, kuvaa ja PIN-koodia',
      look: 'Ulkoasu',
      auto: 'Auto',
      tell_me_when: 'Kerro minulle, kun',
      notif_this_device: 'Ilmoitukset tässä laitteessa',
      notif_blocked_parent: 'Estetty tässä selaimessa. Salli tämän sivuston ilmoitukset selaimen asetuksista.',
      n_review_ready: 'Tehtävä odottaa hyväksyntää',
      n_task_approved: 'Tehtäväni on hyväksytty',
      n_reward_ready: 'Palkinto on valmiina',
      n_due_soon: 'Tehtäväni määräaika lähestyy',
      n_overdue: 'Tehtäväni on myöhässä',
      n_new_task: 'Uusi tehtävä on vapaana',
      n_event: 'Kalenterimuistutukset',
      daily_summary: 'Päivittäinen yhteenveto',
      daily_summary_time: 'Päivittäisen yhteenvedon aika',
      quiet_hours: 'Hiljaiset tunnit',
      quiet_hours_hint: 'Ei ilmoituksia näiden aikojen välillä',
      quiet_from: 'Hiljaista alkaen',
      quiet_to: 'Hiljaista asti',
      this_device: 'Tämä laite',
      change_pin: 'Vaihda PIN-koodini',
      switch_person: 'Vaihda henkilöä',
      refresh_session: 'Päivitä tämä laite',
      session_refreshed: 'Tämä laite on ajan tasalla.',
      locked_until: 'Lukittu klo {{time}} asti',
      unlock: 'Avaa',
      unlocked: '@{{name}} voi kirjautua taas sisään.',
      log_out_device: 'Kirjaudu ulos tästä laitteesta',
      logout_confirm_title: 'Kirjaudutaanko ulos tästä laitteesta?',
      logout_confirm_text: 'Koko perhe {{family}} kirjataan ulos tästä laitteesta. Aikuisen on kirjauduttava uudelleen, ennen kuin kukaan voi käyttää AntNestiä tässä laitteessa.',
      logout_confirm_yes: 'Kirjaudu ulos',
      cancel: 'Peruuta',
      n_reward_claimed: 'Palkinto lunastetaan',
    },
    rewards: {
      title: 'Palkinnot',
      subtitle: 'Seuraa edistymistäsi ja lunasta palkintosi',
      your_points: 'Pisteesi',
      family_points: 'Perheen pisteet',
      claimed_rewards: 'Lunastetut palkinnot',
      your_coins: 'Kolikkosi',
      individual_rewards: 'Henkilökohtaiset palkinnot',
      no_individual_rewards: 'Ei henkilökohtaisia palkintoja vielä saatavilla.',
      family_rewards: 'Perhepalkinnot',
      no_family_rewards: 'Ei perhepalkintoja vielä saatavilla.',
      goal_reached: 'Tavoite saavutettu!',
      complete_suffix: 'valmis',
      target_whole_family: 'Koko perhe',
      claim_reward_button: 'Lunasta palkinto!',
      claimed_rewards_section: 'Lunastetut palkinnot',
      no_claimed_rewards: 'Palkintoja ei ole vielä lunastettu. Jatka samaan malliin!',
      claimed_on: 'Lunastettu',
      unknown_date: 'Tuntematon päivä',
      unknown_member: 'Tuntematon jäsen',
      reward_header_alt: 'Palkinnon otsake',
      show_more: 'Näytä lisää',
      show_fewer: 'Näytä vähemmän',
      points_abbrev: 'pts',
      progress_counts_from: 'Edistyminen laskee pisteitä, jotka on ansaittu {{date}} tai sen jälkeen.',
      lifetime_total_hint: 'Koko saldo (ei vain tiettyä palkintoikkunaa).',
      your_stars_label: 'tähtesi',
      ready_to_get: 'Valmiina haettavaksi',
      keep_saving: 'Säästä edelleen',
      n_more: 'vielä {{n}}',
      get_it: 'Hae se',
      grownup_gets: 'Aikuinen hakee tämän',
      got_one: '{{n}} saatu palkinto',
      got_other: '{{n}} saatua palkintoa',
      see_all: 'Näytä kaikki',
      counting_since: 'Tähdet lasketaan {{date}} alkaen',
      nothing_yet: 'Ei vielä palkintoja. Pyydä aikuista lisäämään niitä!',
      claim_failed: 'Palkintoa ei voitu lunastaa. Yritä uudelleen.',
    },
    dashboard: {
      welcome_back: 'Tervetuloa takaisin',
      subheading: 'Tässä mitä perheessäsi tapahtuu tänään',
      leaderboard_title: 'Perheen pistetaulukko',
      family_member: 'Perheenjäsen',
      tasks_title: 'Tehtävät',
      available_tasks: 'Saatavilla olevat tehtävät',
      no_available_tasks: 'Ei tehtäviä juuri nyt!',
      my_active_tasks: 'Omat aktiiviset tehtävät',
      no_my_tasks: 'Sinulla ei ole aktiivisia tehtäviä!',
      completed_tasks: 'Valmiit tehtävät',
      no_completed_tasks: 'Ei valmiita tehtäviä vielä!',
      manage_all_tasks: 'Hallinnoi kaikkia tehtäviä',
      my_rewards_title: 'Omat palkinnot',
      my_points: 'Omat pisteet',
      available_rewards: 'Saatavilla olevat palkinnot',
      claimed_rewards: 'Lunastetut palkinnot',
      next_reward_goal: 'Seuraava palkintotavoite',
      all_rewards_claimed: 'Kaikki palkinnot lunastettu! Hienoa!',
      showing_rewards_help: 'Näytetään: Omat ja perheen yhteiset palkinnot',
      claim_now: 'Lunasta nyt!',
      locked: 'Lukittu',
      go_to_rewards: 'Siirry palkintoihin',
      my_tasks_primary_title: 'Omat tehtävät',
      my_tasks_home_hint: 'Näytetään vain sinulle annetut tehtävät. Valitaksesi uusia tehtäviä, avaa Tehtävät.',
      due_overdue: 'Myöhässä',
      due_today: 'Erääntyy tänään',
      due_no_deadline: 'Ei määräpäivää',
      hi_name: 'Hei {{name}}',
      goal_of: '{{have}}/{{need}} tähteä',
      goal_get_it: 'Hae se',
      goal_none: 'Ei vielä tavoiteltavia palkintoja. Pyydä aikuista lisäämään yksi!',
      today: 'Tänään',
      no_tasks_now: 'Ei tehtäviä juuri nyt',
      waiting_one: '{{n}} tähti, kun aikuinen sanoo kyllä',
      waiting_other: '{{n}} tähteä, kun aikuinen sanoo kyllä',
      family_goal: 'Perheen tavoite',
      family_goal_more: 'Kaikkien tähdet lasketaan. Vielä {{n}}: {{reward}}.',
      family_goal_ready: '{{reward}} on valmis! Aikuinen voi hakea sen perheelle.',
      find_tasks_cta: 'Etsi tehtävä',
      due_tomorrow: 'Huomenna',
      due_in_days_one: '{{n}} päivän päästä',
      due_in_days_other: '{{n}} päivän päästä',
      more_stars_title: 'Haluatko lisää tähtiä?',
      more_stars_hint: 'Kuka tahansa voi ottaa nämä. Valitse yksi, kun huvittaa!',
      see_all_can_take: 'Näytä kaikki {{n}}',
      take_failed: 'Tehtävää ei voitu ottaa',
    },
    task_board: {
      title: 'Tehtävätaulu',
      subtitle: 'Hallinnoi ja seuraa kaikkia perheen tehtäviä',
      available_tasks: 'Saatavilla olevat tehtävät',
      no_available_tasks: 'Ei tehtäviä juuri nyt!',
      my_active_tasks: 'Omat aktiiviset tehtävät',
      no_my_tasks: 'Sinulla ei ole aktiivisia tehtäviä!',
      view: 'Näytä',
      complete: 'Valmis',
      tasks_assigned_to_others: 'Toisille osoitetut tehtävät',
      no_tasks_assigned_to_others: 'Ei tehtäviä osoitettu muille käyttäjille!',
      view_task: 'Näytä tehtävä',
      completed_tasks: 'Valmiit tehtävät',
      no_completed_tasks: 'Ei valmiita tehtäviä vielä!',
      take_task: 'Minä otan',
      take_over_task: 'Ota tehtävä haltuun',
      unclaim_task: 'Palauta',
      your_tasks: 'Sinun tehtäväsi',
      can_take: 'Nämä voit ottaa',
      waiting_heading: 'Odottaa aikuista',
      others_tasks: 'Muiden tehtävät',
      done_one: '{{n}} tehtävä tehty',
      done_other: '{{n}} tehtävää tehty',
      moved_to_yours: '{{task}} on nyt kohdassa Sinun tehtäväsi',
      nothing_to_take: 'Ei otettavia tehtäviä juuri nyt',
      no_your_tasks: 'Sinulla ei ole tehtäviä juuri nyt.',
    }
    ,
    login: {
      title: 'AntNest',
      email_label: 'Sähköposti',
      password_label: 'Salasana',
      logging_in: 'Kirjaudutaan...',
      login_button: 'Kirjaudu',
      demo_account: 'Demotili',
      demo_email: 'Sähköposti: admin@family.com',
      demo_password: 'Salasana: admin'
    },
    user_profile: {
      user_not_found: 'Käyttäjää ei löytynyt.',
      back_to_home: 'Takaisin kotiin',
      title: 'Käyttäjäprofiili',
      profile_information: 'Profiilin tiedot',
      name: 'Nimi',
      email: 'Sähköposti',
      email_not_set: 'Ei sähköpostia',
      role: 'Rooli',
      admin: 'Ylläpitäjä',
      family_member: 'Perheenjäsen',
      points: 'Pisteet',
      coins: 'Kolikot',
      edit_profile: 'Muokkaa profiilia',
      save_changes: 'Tallenna muutokset',
      cancel: 'Peruuta',
      email_optional_label: 'Sähköposti (valinnainen)',
      new_password_optional_label: 'Uusi salasana (valinnainen)',
      name_placeholder: 'Syötä nimesi',
      email_enter_placeholder: 'Syötä sähköpostiosoite',
      email_add_placeholder: 'Lisää sähköposti',
      username_for_member_login: 'Käyttäjätunnus (jäsenkirjautumista varten)',
      pin_label: 'PIN-koodi (4–8 numeroa)',
      avatar: 'Avatar',
      avatar_hint: 'Valitse esiasetus tai lähetä oma kuvasi.',
      upload: 'Lataa',
      avatar_updated: 'Avatar päivitetty',
      avatar_update_failed: 'Avatarin päivitys epäonnistui',
      linked_accounts: 'Linkitetyt tilit',
      no_linked_accounts: 'Ei linkitettyjä tilejä.',
      unlink: 'Poista linkitys',
      unlink_failed: 'Linkityksen poisto epäonnistui',
      unlink_confirm: 'Poista linkitys palvelusta {{provider}}?',
      link_to_google: 'Linkitä Googleen',
      change_password: 'Vaihda salasana',
      current_password: 'Nykyinen salasana',
      new_password: 'Uusi salasana',
      password_changed: 'Salasana vaihdettu',
      password_change_failed: 'Salasanan vaihto epäonnistui',
      save_new_password: 'Tallenna uusi salasana',
      pin_validation: 'PIN-koodissa tulee olla 4–8 numeroa',
      current_pin_label: 'Nykyinen PIN-koodi (vain jos sinulla on jo sellainen)',
      current_pin_required: 'Anna nykyinen PIN-koodisi vaihtaaksesi sen.',
      current_pin_incorrect: 'Nykyinen PIN-koodisi on väärä.',
      email_managed_by_login: 'Sähköpostiosoitteesi tulee kirjautumistavastasi, eikä sitä voi muuttaa täällä.',
      username_validation: 'Käyttäjätunnuksen tulee olla vähintään 2 merkkiä',
      profile_updated: 'Profiili päivitetty',
      google_link_success: 'Google-tili linkitetty',
      picture_type_bad: 'Valitse PNG-, JPG- tai WebP-kuva.',
      picture_too_big: 'Kuva on liian suuri (enintään 1 Mt).',
      choose_picture: 'Valitse tämä kuva',
      or_pick_one: 'Tai valitse jokin näistä',
      tap_picture: 'Napauta kuvaa valitaksesi uuden.',
      facebook_link_success: 'Facebook-tili linkitetty',
      link_to_facebook: 'Linkitä Facebookiin',
      account_unlinked: 'Tilin linkitys poistettu.',
      link_to_apple: 'Linkitä Appleen',
    },
    admin: {
      header_title: 'Ylläpito',
      header_subtitle: 'Hallinnoi perheen tehtäviä, palkintoja ja jäseniä',
      tab_tasks: 'Tehtävät',
      tab_rooms: 'Huoneet',
      tab_members: 'Jäsenet',
      tab_pending: 'Odottaa',
      tab_rewards: 'Palkinnot',
      tab_tools: 'Työkalut'
    },
    checklist_editor: {
      back_to_admin: 'Takaisin ylläpitoon',
      edit_checklist_for: 'Muokkaa tarkistuslistaa',
      add_new_item: 'Lisää uusi kohta',
      add_item: 'Lisää',
      current_items: 'Nykyiset kohdat',
      no_items: 'Ei kohteita vielä. Lisää niitä yllä!',
      cancel: 'Peruuta',
      save: 'Tallenna'
    },
    task_details: {
      loading: 'Ladataan...',
      title: 'Tehtävän tiedot',
      room: 'Huone',
      points: 'Pisteet',
      status: 'Tila',
      status_completed: 'Valmis',
      status_active: 'Aktiivinen',
      assigned_to: 'Osoitettu',
      due_date: 'Eräpäivä',
      tools_required: 'Tarvittavat työkalut',
      checklist: 'Tarkistuslista',
      task_completed: 'Tehtävä valmis',
      completed_by: 'Tekijä',
      message: 'Viesti',
      submit_for_verification: 'Lähetä tarkistettavaksi',
      close: 'Sulje',
      preview_room_placeholder: 'Ehdotetaan, kun lisäät tämän tehtävän',
      preview_title: 'Tehtävän esikatselu',
      preview_checklist_hint: 'Vaiheet (vain esikatselu — merkitse tehdyksi, kun olet lisännyt tehtävän)',
      view: 'Näytä',
    },
    task_flow: {
      start_task: 'Mennään',
      tools_ready_title: 'Onko sinulla kaikki?',
      tools_ready_subtitle: 'Tarvitset nämä työkalut. Hae ne ennen kuin jatkat.',
      ready_to_start_title: 'Valmista?',
      ready_to_start_body: 'Oletko valmis aloittamaan tehtävän?',
      tools_ready_no: 'Ei vielä',
      tools_ready_yes: 'Kyllä, mennään',
      finish_step: 'Tein sen',
      final_put_tools_message:
        'Hienoa! Palauta kaikki työkalut paikoilleen. Paina sitten alla olevaa painiketta viimeistelläksesi tehtävän.',
      complete_task_button: 'Valmis',
      no_checklist_warning: 'Tässä tehtävässä ei ole vielä vaiheita. Pyydä aikuista lisäämään lista ennen aloitusta.',
      view_checklist_readonly_hint: 'Voit ruksata vaiheet sen jälkeen, kun painat Aloita tehtävä.',
      progress_label: 'Edistyminen',
      step_counter_prefix: 'Vaihe',
      not_assigned: 'Ei vielä osoitettu',
      step_of: 'Vaihe {{n}}/{{total}}',
      back_step: 'Palaa edelliseen vaiheeseen',
      tidy_title: 'Melkein valmista!',
      tidy_subtitle: 'Laita nämä takaisin paikoilleen',
      tidy_no_tools: 'Onko kaikki valmista? Napauta Valmis.',
      back_to_steps: 'Takaisin vaiheisiin',
      celebrate_stars_title: '{{n}} tähteä on tulossa',
      celebrate_stars_body: 'Saat ne, kun aikuinen sanoo kyllä',
      celebrate_button: 'Takaisin etusivulle',
      see_details: 'Näytä tiedot',
      step_tools: 'Tarvitset:',
      where_room: 'Missä: {{room}}',
      celebrate_heading: 'Hienoa työtä!',
      celebrate_subtitle: 'Teit tehtävän {{title}}',
      celebrate_stars_got_title: 'Sait {{n}} tähteä',
      celebrate_stars_got_body: 'Hyväksytty ja lisätty tähtiisi',
      tidy_put_all_back: 'Laita kaikki nämä tavarat takaisin paikoilleen',
    },
    reward_celebration: {
      congratulations: 'Onnittelut!',
      you_have_a_reward: 'Sinulla on palkinto!',
      check_rewards_prompt: 'Siirry palkinnot-sivulle lunastaaksesi sen!'
    }
    ,
    admin_panel: {
      header_title: 'Ylläpito',
      header_subtitle: 'Hallinnoi perheen tehtäviä, palkintoja ja jäseniä',
      tab_tasks: 'Tehtävät',
      tab_rooms: 'Huoneet',
      tab_members: 'Jäsenet',
      tab_pending: 'Odottaa',
      tab_rewards: 'Palkinnot',
      tab_tools: 'Työkalut',
      task_management: 'Tehtävien hallinta',
      create_new_task: 'Luo uusi tehtävä',
      active_tasks: 'Aktiiviset tehtävät',
      active_tasks_help: 'Tehtävät, jotka ovat käynnissä tai saatavilla',
      edit: 'Muokkaa',
      remove: 'Poista',
      no_active_tasks: 'Aktiivisia tehtäviä ei löytynyt.',
      completed_tasks: 'Valmiit tehtävät',
      completed_tasks_help: 'Tehtävät, jotka on saatu valmiiksi ja tarkastettu',
      no_completed_tasks: 'Valmiita tehtäviä ei löytynyt.',
      room_management: 'Huoneiden hallinta',
      add_new_room: 'Lisää uusi huone',
      member_management: 'Jäsenten hallinta',
      add_new_member: 'Lisää uusi jäsen',
      pending_verification: 'Odottaa tarkistusta',
      verify_this_task: 'Tarkista tämä tehtävä',
      self_verify_not_allowed_hint:
        'Toisen ylläpitäjän on tarkistettava tämä tehtävä, kun ylläpitäjiä on useampi kuin yksi.',
      points_not_awarded_section: 'Tarkistettu — pisteet puuttuvat',
      points_not_awarded_help:
        'Nämä tehtävät merkittiin tarkistetuiksi ennen kuin pisteet tallentuivat oikein. Käytä «Myönnä pisteet» kerran hyvittääksesi tehtävän suorittaneen jäsenen.',
      award_missing_points: 'Myönnä pisteet',
      no_tasks_needing_points: 'Kaikilla tarkistetuilla tehtävillä on pisteet kirjattuna.',
      verify_success: 'Tehtävä tarkistettu. Pisteet on myönnetty.',
      verify_repaired_points_success:
        'Puuttuvat pisteet on myönnetty tehtävän suorittaneelle jäsenelle.',
      verify_already_done: 'Tehtävä on jo tarkistettu ja pisteet on kirjattu.',
      verify_misattribution_credit_confirm:
        'Järjestelmä näyttää maksun jo tehdyn. Jos jäsenen saldo ei koskaan noussut: myönnetäänkö kertaluonteinen hyvitys? (Ei jos pisteet on jo saatu.)',
      verify_misattribution_success: 'Kertakorjaus tehty. Pyydä jäsentä päivittämään tai kirjautumaan uudelleen.',
      verify_misattribution_already_done: 'Kertaluonteinen korjaushyvitys on jo käytetty tälle tehtävälle.',
      verify_already_done_hint:
        'Jos jäsenellä ei vieläkään ole pisteitä: yritä «Myönnä pisteet» uudelleen ja vahvista, tai tyhjennä points_awarded_on_verification tietokannassa.',
      no_pending_verify: 'Ei tehtäviä odottamassa tarkistusta.',
      reward_management: 'Palkintojen hallinta',
      create_new_reward: 'Luo uusi palkinto',
      claimed: 'Lunastettu',
      available: 'Saatavilla',
      tool_management: 'Työkalujen hallinta',
      create_new_tool: 'Luo uusi työkalu',
      no_tools_msg: 'Työkaluja ei ole vielä luotu. Luo ensimmäinen työkalu aloittaaksesi!',
      task_modal_title_create: 'Luo uusi tehtävä',
      task_modal_title_edit_prefix: 'Muokkaa tehtävää',
      task_title_label: 'Tehtävän otsikko',
      task_title_placeholder: 'Kirjoita tehtävän otsikko',
      description_label: 'Kuvaus (valinnainen)',
      description_placeholder: 'Kirjoita tehtävän kuvaus',
      room_label: 'Huone',
      no_room_option: 'Ei huonetta',
      assignment_label: 'Tehtävän osoitus',
      available_anyone_option: 'Saatavilla kaikille',
      assign_to_member_prefix: 'Osoita',
      assignment_hint_assigned: 'Tämä tehtävä näkyy vain osoitetulle jäsenelle',
      assignment_hint_anyone: 'Tämä tehtävä on saatavilla kaikille perheenjäsenille',
      points_label: 'Annettavat pisteet',
      points_placeholder: 'Pisteet',
      due_date_label: 'Eräpäivä',
      recurrence_label: 'Luo uudelleen X päivän välein (0 = ei toistoa)',
      auto_available_label: 'Automaattisesti saatavilla',
      header_image_label: 'Otsikkokuva (valinnainen)',
      current_header_image_label: 'Nykyinen otsikkokuva',
      checklist_label: 'Tarkistuslistan kohteet',
      checklist_item_placeholder: 'Kirjoita tarkistuslistan kohta',
      images_optional_label: 'Kuvat (valinnainen):',
      required_tools_label: 'Tarvittavat työkalut',
      no_tools_available_msg: 'Työkaluja ei ole saatavilla. Luo työkaluja ensin Työkalut-välilehdellä.',
      cancel_btn: 'Peruuta',
      create_task_btn: 'Luo tehtävä',
      save_changes_btn: 'Tallenna muutokset',
      remove_task_title: 'Poista tehtävä',
      remove_task_prompt_prefix: 'Haluatko varmasti poistaa tehtävän',
      irreversible_note: 'Tätä toimintoa ei voi kumota.',
      remove_task_btn: 'Poista tehtävä',
      reward_modal_title_create: 'Luo uusi palkinto',
      reward_modal_title_edit_prefix: 'Muokkaa palkintoa',
      reward_title_label: 'Palkinnon otsikko',
      reward_title_placeholder: 'Kirjoita palkinnon otsikko',
      reward_desc_label: 'Kuvaus',
      reward_desc_placeholder: 'Kirjoita palkinnon kuvaus',
      points_required_label: 'Vaaditut pisteet',
      reward_progress_start_label: 'Pisteet lasketaan alkaen (päivä)',
      reward_progress_start_hint: 'Vain tämän päivän alusta (paikallinen aika) ansaitut pisteet lasketaan mukaan.',
      target_type_label: 'Tyyppi',
      target_type_individual: 'Henkilökohtainen',
      target_type_family: 'Perhe',
      assign_member_label: 'Osoita perheenjäsenelle',
      select_member_option: 'Valitse perheenjäsen',
      current_header_image_id_label: 'Gjeldende toppbilde-ID',
      create_reward_btn: 'Luo palkinto',
      tool_modal_title_create: 'Luo uusi työkalu',
      tool_modal_title_edit_prefix: 'Muokkaa työkalua',
      tool_name_label: 'Työkalun nimi',
      tool_name_placeholder: 'Kirjoita työkalun nimi',
      tool_image_label: 'Työkalun kuva (valinnainen)',
      current_tool_image_label: 'Nykyinen työkalun kuva',
      create_tool_btn: 'Luo työkalu',
      room_modal_title_add: 'Lisää uusi huone',
      room_modal_title_edit: 'Muokkaa huonetta',
      delete_room_title: 'Poista huone',
      delete_room_btn: 'Poista',
      delete_room_prompt_prefix: 'Haluatko varmasti poistaa huoneen pysyvästi',
      delete_room_blocked_message: 'Huonetta ei voi poistaa, koska yksi tai useampi tehtävä käyttää sitä. Poista tai siirrä nämä tehtävät ensin.',
      go_to_tasks_btn: 'Näytä tehtävät',
      room_name_label_required: 'Huoneen nimi *',
      room_name_placeholder: 'Kirjoita huoneen nimi',
      add_room_btn: 'Lisää huone',
      member_modal_title_add: 'Lisää uusi perheenjäsen',
      member_name_label_required: 'Nimi *',
      member_name_placeholder: 'Kirjoita jäsenen nimi',
      member_password_label_required: 'Salasana *',
      member_password_placeholder: 'Skriv inn passord',
      member_email_label_optional: 'Sähköposti (valinnainen)',
      member_email_placeholder: 'Skriv inn e-postadresse',
      admin_access_label: 'Ylläpitäjäoikeudet',
      add_member_btn: 'Lisää jäsen',
      member_type_label: 'Jäsentyyppi',
      member_type_adult: 'Aikuinen',
      member_type_child: 'Lapsi',
      err_room_name: 'Anna huoneelle nimi.',
      err_room_create: 'Huonetta ei voitu luoda. Yritä uudelleen.',
      err_room_update: 'Huonetta ei voitu tallentaa. Yritä uudelleen.',
      err_generic_missing: 'Jotain puuttuu. Sulje ja yritä uudelleen.',
      err_task_title_points: 'Anna tehtävälle nimi ja tähtien määrä.',
      err_checklist_empty: 'Lisää vähintään yksi vaihe, jossa on tekstiä.',
      err_task_create: 'Tehtävää ei voitu luoda. Yritä uudelleen.',
      err_task_update: 'Tehtävää ei voitu tallentaa. Yritä uudelleen.',
      err_task_remove: 'Tehtävää ei voitu poistaa. Yritä uudelleen.',
      err_template_use: 'Mallista ei voitu tehdä tehtävää. Yritä uudelleen.',
      err_save_template: 'Mallia ei voitu tallentaa. Yritä uudelleen.',
      saved_template: 'Tallennettu malliksi.',
      err_header_image: 'Kuvaa ei voitu tallentaa. Yritä uudelleen.',
      err_reward_title: 'Anna palkinnolle nimi.',
      err_reward_points: 'Kirjoita, montako tähteä palkintoon tarvitaan.',
      err_reward_member: 'Valitse, kenelle palkinto on.',
      err_reward_create: 'Palkintoa ei voitu luoda. Yritä uudelleen.',
      err_reward_update: 'Palkintoa ei voitu tallentaa. Yritä uudelleen.',
      reward_updated: 'Palkinto tallennettu.',
      err_tool_name: 'Anna välineelle nimi.',
      err_tool_create: 'Välinettä ei voitu luoda. Yritä uudelleen.',
      err_tool_update: 'Välinettä ei voitu tallentaa. Yritä uudelleen.',
      err_member_required: 'Uusi jäsen tarvitsee nimen, käyttäjänimen ja PIN-koodin.',
      err_pin_format: 'PIN-koodissa on oltava 4–8 numeroa.',
      err_username_short: 'Käyttäjänimessä on oltava vähintään 2 merkkiä.',
      err_member_create: 'Jäsentä ei voitu lisätä. Yritä uudelleen.',
      verify_misattribution_title: 'Annetaanko tähdet nyt?',
      verify_misattribution_yes: 'Kyllä, anna ne',
      status_assigned: 'Henkilölle {{name}}',
      status_open: 'Kuka tahansa voi ottaa sen',
      comes_back_on: 'Palaa {{date}}',
      anyone_can_take: 'Kuka tahansa voi ottaa seuraavan (muuten se menee samalle henkilölle)',
      repeat_hint: 'Kun "toista joka" on asetettu, seuraava erääntyy niin monen päivän päästä hyväksynnästä ja näkyy "päiviä suorittaa" päivää ennen.',
      step_tools_label: 'Tämän vaiheen työkalut',
      step_tools_hint: 'Valitse ensin tehtävän työkalut alempana. Sitten voit valita ne, joita tämä vaihe tarvitsee.',
      room_picture_label: 'Kuva huoneesta (valinnainen)',
      room_picture_hint: 'Kuva auttaa lapsia näkemään, missä huoneessa tehtävä on.',
      room_picture_add: 'Lisää kuva',
      room_picture_change: 'Vaihda kuva',
      room_picture_failed: 'Huone tallennettiin, mutta kuvaa ei voitu ladata. Yritä uudelleen.',
      step_drag: 'Siirrä vaihetta vetämällä',
      step_move_up: 'Siirrä vaihetta ylös',
      step_move_down: 'Siirrä vaihetta alas',
      delete_btn: 'Poista',
      show_more_completed_tasks: 'Näytä lisää',
      member_edit_title: 'Muokkaa perheenjäsentä',
      member_name_label: 'Nimi',
      member_pin_label_optional: 'PIN (valinnainen)',
      member_pin_hint_optional: 'Jätä tyhjäksi, jos haluat pitää nykyisen PIN-koodin. PIN-koodissa on oltava 4–8 numeroa.',
      member_is_admin_label: 'Ylläpitäjän oikeudet',
      member_update_failed: 'Jäsenen päivitys epäonnistui',
      delete_reward_title: 'Poista palkinto',
      delete_confirm_prefix: 'Haluatko varmasti poistaa',
      recreate_on_validation_label: 'Luo uudelleen hyväksynnän jälkeen',
      recreate_on_validation_help: 'Luo tehtävä heti uudelleen, kun aikuinen on hyväksynyt sen',
      days_to_complete_label: 'Päiviä tehtävän tekemiseen',
      days_to_complete_placeholder: 'esim. 7',
      days_to_complete_help: 'Jos asetettu, eräpäivä on luontipäivä + näin monta päivää.',
      add_checklist_item_btn: 'Lisää vaihe',
      camera_title: 'Kamera',
      show_less_completed_tasks: 'Näytä vähemmän',
      delete_tool_title: 'Poista työkalu',
      task_saving: 'Tallennetaan tehtävää…',
      task_saved: 'Tehtävä tallennettu',
      try_again: 'Yritä uudelleen',
      deadline_question: 'Määräaika',
      deadline_none: 'Ei määräaikaa',
      deadline_within: 'Tehtävä',
      days_unit: 'päivässä',
      repeat_question: 'Kun se on hyväksytty…',
      repeat_none: 'se on valmis – se ei palaa',
      repeat_now: 'se palaa heti',
      repeat_after: 'se palaa',
      next_question: 'Kuka saa seuraavan?',
      next_same: 'Taas {{name}}',
      next_anyone: 'Kuka tahansa voi ottaa sen',
      summary_title: 'Näin käy:',
      due_this_time_label: 'Määräpäivä tällä kertaa (valinnainen)',
      due_this_time_help: 'Jätä tyhjäksi, niin käytetään yllä olevaa määräaikaa.',
      sum_now_person: '{{name}} saa tehtävän nyt.',
      sum_now_anyone: 'Kuka tahansa perheestä voi ottaa sen nyt.',
      sum_within_one: 'Se on tehtävä {{n}} päivässä.',
      sum_within_other: 'Se on tehtävä {{n}} päivässä.',
      sum_end: 'Kun hyväksyt sen, se on valmis.',
      sum_back_now_same: 'Kun hyväksyt sen, se palaa heti henkilölle {{name}}.',
      sum_back_now_anyone: 'Kun hyväksyt sen, se palaa heti ja kuka tahansa voi ottaa sen.',
      sum_back_after_same_one: 'Kun hyväksyt sen, se palaa henkilölle {{name}} {{n}} päivän päästä.',
      sum_back_after_same_other: 'Kun hyväksyt sen, se palaa henkilölle {{name}} {{n}} päivän päästä.',
      sum_back_after_anyone_one: 'Kun hyväksyt sen, se palaa {{n}} päivän päästä ja kuka tahansa voi ottaa sen.',
      sum_back_after_anyone_other: 'Kun hyväksyt sen, se palaa {{n}} päivän päästä ja kuka tahansa voi ottaa sen.',
      sum_then_within_one: 'Sitten sen tekemiseen on {{n}} päivä.',
      sum_then_within_other: 'Sitten sen tekemiseen on {{n}} päivää.',
      repeat_next: 'seuraava tehtävä on',
      next_ready_after: 'Valmis',
      hours_unit: 'tunnin päästä',
      waits_for: 'Odottaa: {{task}}',
      then_next: 'Sitten: {{task}}',
      sum_next: 'Kun hyväksyt sen, seuraavaksi tulee "{{task}}".',
      sum_next_wait_one: 'Se on valmis {{n}} tunnin kuluttua tämän tekemisestä.',
      sum_next_wait_other: 'Se on valmis {{n}} tunnin kuluttua tämän tekemisestä.',
      sum_next_waits: 'Siihen asti lapset eivät näe tehtävää "{{task}}".',
      reward_saving: 'Tallennetaan palkintoa…',
      reward_saved: 'Palkinto tallennettu',
      assignment_hint_each: 'Jokainen saa oman tehtävänsä: omaan kalenteriin, hyväksytään erikseen ja omilla tähdillä.',
      err_choose_person: 'Valitse vähintään yksi henkilö.',
      names_and: '{{first}} ja {{last}}',
      sum_now_each: '{{names}} saavat nyt kukin oman tehtävänsä.',
      sum_back_now_each: 'Kun hyväksyt jonkun tehtävän, hänen tehtävänsä palaa heti.',
      sum_back_after_each_one: 'Kun hyväksyt jonkun tehtävän, hänen tehtävänsä palaa {{n}} päivän päästä.',
      sum_back_after_each_other: 'Kun hyväksyt jonkun tehtävän, hänen tehtävänsä palaa {{n}} päivän päästä.',
      shared_ready: 'Valmis',
      remove_shared_note: 'Se poistetaan kaikilta, joille se annettiin. Jo tehdyt jäävät historiaan.',
      repeat_plan: 'se palaa kiinteän aikataulun mukaan',
      plan_weekly: 'joka viikko',
      plan_every: 'joka',
      plan_every_unit: '. päivä',
      plan_from: 'alkaen',
      sum_plan_person: '{{name}} saa tehtävän.',
      sum_plan_anyone: 'Kuka tahansa perheestä voi ottaa sen.',
      sum_plan_each: '{{names}} saavat kukin oman tehtävänsä.',
      sum_plan_weekly: 'Se tulee näkyviin joka {{days}}.',
      sum_plan_every_one: 'Se tulee näkyviin joka päivä {{date}} alkaen.',
      sum_plan_every_other: 'Se tulee näkyviin joka {{n}}. päivä {{date}} alkaen.',
      sum_plan_back: 'Kun hyväksyt sen, se palaa seuraavana suunniteltuna päivänä.',
      plan_week_every_one: 'joka viikko',
      plan_week_every_other: 'joka {{n}}. viikko',
      plan_on: 'päivinä',
      plan_monthly: 'joka kuukausi',
      plan_month_every_one: 'joka kuukausi',
      plan_month_every_other: 'joka {{n}}. kuukausi',
      plan_the: 'kuukauden',
      plan_which: 'mikä',
      plan_weekday: 'viikonpäivä',
      plan_nth_1: 'ensimmäinen',
      plan_nth_2: 'toinen',
      plan_nth_3: 'kolmas',
      plan_nth_4: 'neljäs',
      plan_nth_last: 'viimeinen',
      plan_on_date: 'päivänä',
      plan_date_unit: 'kuukaudessa',
      sum_plan_weekly_every: 'Se tulee näkyviin joka {{n}}. viikko: {{days}}.',
      sum_plan_every_open_one: 'Se tulee näkyviin joka päivä.',
      sum_plan_every_open_other: 'Se tulee näkyviin joka {{n}}. päivä.',
      sum_plan_month_day_one: 'Se tulee näkyviin joka kuukauden {{day}}. päivä.',
      sum_plan_month_day_other: 'Se tulee näkyviin {{day}}. päivä joka {{n}}. kuukausi.',
      sum_plan_month_nth_one: 'Se tulee näkyviin joka kuukausi: {{nth}} {{weekday}}.',
      sum_plan_month_nth_other: 'Se tulee näkyviin joka {{n}}. kuukausi: {{nth}} {{weekday}}.',
      step_timer_label: 'Ajastin (valinnainen)',
      step_timer_minutes: 'Ajastin, minuutit',
      step_timer_seconds: 'Ajastin, sekunnit',
      step_timer_min: 'min',
      step_timer_sec: 's',
      step_timer_clear: 'Ei ajastinta',
      step_timer_hint: 'Esim. "Lankku – 0:45". Tehtävän tekijä käynnistää sen ja kuulee äänen, kun aika loppuu.',
    },
    notifications: {
      approved_said_yes: '{{admin}} sanoi kyllä!',
      approved_for: 'tehtävästä {{task}}',
      approved_note: '{{admin}} sanoo: {{note}}',
      approved_now_you_have: 'Nyt sinulla on {{n}} tähteä',
      approved_reward_ready: '{{reward}} on valmis!',
      approved_more_to_goal: 'Vielä {{n}} tähteä: {{reward}}',
      approved_see_reward: 'Katso palkintoni',
      approved_not_now: 'Ei nyt',
      approved_ok: 'Jee!',
      admin_pending_verification_one: '1 tehtävä odottaa hyväksyntääsi.',
      admin_pending_verification_other: '{{count}} tehtävää odottaa hyväksyntääsi.',
    },
    member_chooser: {
      who_title: 'Kuka olet?',
      hi_name: 'Hei {{name}}!',
      enter_pin: 'Anna PIN-koodisi',
      log_in: 'Kirjaudu',
      use_password: 'Käytä salasanaa',
      use_pin: 'Käytä PIN-koodia',
      password_label: 'Salasana',
      delete_digit: 'Poista',
      back: 'Takaisin',
      locked_until: 'Liian monta yritystä. Yritä uudelleen klo {{time}}.',
      locked_later: 'Liian monta yritystä. Yritä hetken päästä uudelleen.',
      wrong_pin: 'Se ei toiminut. Yritä uudelleen.',
      sign_out_device_hint: 'Tyhjentää perheen kirjautumisen tästä selaimesta. Käytä tätä, jos olet jumissa tai haluat kirjautua uudelleen aikuisena Googlella/sähköpostilla.',
      sign_out_device: 'Kirjaudu ulos tältä laitteelta',
    },
    parent: {
      to_approve_one: '{{n}} hyväksyttävä tehtävä',
      to_approve_other: '{{n}} hyväksyttävää tehtävää',
      finished: '{{name}} teki tehtävän {{task}}',
      review: 'Tarkista',
      nothing_to_approve: 'Ei hyväksyttävää juuri nyt',
      family: 'Perhe',
      open_one: '{{n}} tehtävä tekemättä',
      open_other: '{{n}} tehtävää tekemättä',
      open_none: 'Ei tehtäviä',
      set_up: 'Asetukset',
      tasks: 'Tehtävät',
      rewards: 'Palkinnot',
      members: 'Perhe',
      rooms_tools: 'Huoneet ja välineet',
      lock: 'Lukitse',
      settings: 'Asetukset',
      more_waiting: '+{{n}} lisää',
      your_tasks: 'Omat tehtäväsi',
    },
    approve: {
      title: 'Hyväksy tehtävät',
      approve: 'Hyväksy',
      not_done: 'Ei vielä valmis',
      note_so_neat: 'Tosi siistiä!',
      note_well_done: 'Hyvin tehty!',
      note_thank_you: 'Kiitos!',
      reason_look_again: 'Melkein — katso vielä kerran',
      reason_steps_missing: 'Muutama vaihe puuttuu vielä',
      reason_do_together: 'Tehdään se yhdessä',
      pick_reason: 'Mitä {{name}} kuulee?',
      cancel: 'Peruuta',
      steps_done_one: 'Vaihe on tehty',
      steps_done_other: 'Kaikki {{n}} vaihetta tehty',
      approved_toast: '{{task}} hyväksytty. {{name}} sai {{stars}}.',
      undo: 'Kumoa',
      sent_back_toast: '{{task}} palautettiin: {{name}}.',
      today_at: 'Tänään {{time}}',
      yesterday_at: 'Eilen {{time}}',
      from_grownup: 'Aikuinen sanoo: {{reason}}',
      back: 'Takaisin',
      self_blocked: 'Toisen aikuisen on hyväksyttävä oma tehtäväsi.',
    },
    my_settings: {
      title: 'Omat asetukset',
      language: 'Kieli',
      same_as_device: 'Kuten tämä laite',
      look: 'Vaalea vai tumma',
      light: 'Vaalea',
      dark: 'Tumma',
      notifications: 'Ilmoitukset',
      on: 'Päällä',
      off: 'Pois',
      notif_turn_on: 'Laita päälle tässä laitteessa',
      notif_turned_on: 'Ilmoitukset ovat päällä.',
      notif_turned_off: 'Ilmoitukset ovat pois päältä.',
      notif_parent_off: 'Aikuinen on laittanut ilmoituksesi pois päältä.',
      notif_parent_on: 'Aikuinen haluaa, että ilmoituksesi ovat päällä.',
      notif_unsupported: 'Tämä laite ei voi näyttää AntNestin ilmoituksia.',
      notif_blocked: 'Ilmoitukset on estetty tässä selaimessa. Pyydä aikuista sallimaan ne selaimen asetuksista.',
      timer_sound: 'Ajastimen ääni',
      timer_sound_help: 'Ääni, kun vaiheen ajastin on nollassa. Napauta kuullaksesi.',
    },
    parent_notifications: {
      title: 'Lasten ilmoitukset',
      help: 'Valitse, kuka päättää. "Aina päällä" tarkoittaa, ettei lapsi voi laittaa niitä pois; "Pois" tarkoittaa, ettei lapselle lähetetä mitään.',
      child: 'Lapsi päättää',
      on: 'Aina päällä',
      off: 'Pois',
      saved: 'Tallennettu.',
    },
    handover: {
      title: 'Luovutettavat palkinnot',
      help: 'Nämä on lunastettu. Anna ne lapselle ja napauta sitten Annettu.',
      got: '{{name}} sai palkinnon {{reward}}',
      handed_over: 'Annettu',
      done_toast: '{{reward}} annettu: {{name}}.',
    },
    library: {
      access_denied: 'Vain aikuiset voivat avata tämän.',
      title: 'Tehtäväkirjasto',
      intro: 'Aloita vakiotehtävästä tai mallista, jonka tallensit tehtävästä. Vain perheesi näkee "Omat mallit".',
      age: 'Ikä',
      age_group: 'Ikäryhmä',
      age_all: 'Kaikenikäiset',
      age_2_3: '2–3 v',
      age_4_5: '4–5 v',
      age_6_7: '6–7 v',
      age_8_9: '8–9 v',
      age_10_12: '10–12 v',
      age_13_plus: '13+ v',
      standard_title: 'Vakiotehtävät',
      none_standard: 'Ei vakiotehtäviä tälle kielelle ja iälle.',
      badge_standard: 'Vakio',
      my_title: 'Omat mallit',
      my_intro: 'Tallennettu tehtävälomakkeen "Tallenna malliksi" -toiminnolla. Vain perheesi näkee ne.',
      none_my: 'Ei vielä tallennettuja malleja. Tee tehtävä ja käytä "Tallenna malliksi" tai vaihda ikäsuodatinta.',
      badge_my: 'Oma malli',
      use: 'Käytä',
      save_as_template: 'Tallenna malliksi',
      days: 'Päivää',
      stars: 'Tähdet',
      tool_name: 'Välineen nimi',
      tool_name_ph: 'esim. Pölynimuri',
      username_required: 'Käyttäjänimi (pakollinen)',
      username_ph: 'Valitse käyttäjänimi',
      pin_label: 'PIN (4–8 numeroa)',
      pin_ph: 'Kirjoita PIN',
      adults: 'Aikuiset',
      show: 'Näytä',
      hide: 'Piilota',
      added_to_tasks: '”{{title}}” lisättiin tehtäviinne',
      chain_title: '{{n}} peräkkäistä tehtävää',
      chain_wait_one: 'seuraava valmis {{n}} tunnin päästä',
      chain_wait_other: 'seuraava valmis {{n}} tunnin päästä',
      chain_loops: 'Sitten alusta.',
    },
    deletion: {
      delete_member: 'Poista jäsen',
      delete_member_title: 'Poistetaanko {{name}}?',
      delete_member_text: '{{name}} poistetaan perheestä heti ja kirjataan ulos kaikilta laitteilta.',
      delete_member_lost: 'Kaikki käyttäjän {{name}} tähdet, tilastot, tehtävät ja palkinnot poistetaan.',
      undo_30: 'Voit perua tämän 30 päivän ajan, {{date}} asti. Sen jälkeen kaikki poistetaan lopullisesti eikä sitä voi palauttaa.',
      delete_member_yes: 'Kyllä, poista',
      member_deleted_toast: '{{name}} on poistettu. Voit palauttaa 30 päivän ajan.',
      recently_deleted: 'Äskettäin poistetut',
      recently_deleted_hint: 'Voit palauttaa nämä jäsenet näytettyyn päivään asti. Sen jälkeen he ja kaikki heidän tietonsa poistetaan lopullisesti.',
      gone_on: 'Poistetaan lopullisesti {{date}}',
      restore: 'Palauta',
      member_restored_toast: '{{name}} on taas perheessä. Ilmoitukset pitää ottaa uudelleen käyttöön.',
      family_section: 'Poista perhe',
      delete_family_row: 'Poista koko perhe',
      delete_family_hint: 'Kaikki perheenjäsenet ja kaikki tiedot. Voidaan perua 30 päivän ajan.',
      delete_family_title: 'Poistetaanko koko perhe?',
      delete_family_text: 'Kaikki perheessä {{family}} kirjataan heti ulos, eikä AntNestiä voi käyttää ennen kuin perhe palautetaan.',
      delete_family_lost: 'Kaikki poistetaan: kaikki jäsenet, tähdet, tilastot, tehtävät, palkinnot, tapahtumat ja kuvat.',
      type_family_name: 'Vahvista kirjoittamalla perheen nimi: {{family}}',
      delete_family_yes: 'Poista perhe',
      pending_title: '{{family}} on merkitty poistettavaksi',
      pending_text: 'Perhe ja kaikki sen tiedot poistetaan lopullisesti {{date}}. Siihen asti voit palauttaa kaiken ennalleen.',
      days_left: 'Päiviä jäljellä: {{days}}',
      pending_not_admin: 'Vain aikuinen, jolla on ylläpitäjän oikeudet, voi palauttaa perheen.',
      restore_family: 'Palauta perhe',
      family_restored_toast: 'Tervetuloa takaisin! Perhe on palautettu. Ilmoitukset pitää ottaa uudelleen käyttöön.',
      log_out: 'Kirjaudu ulos',
      cancel: 'Peruuta',
    },
    pictures: {
      remove_picture: 'Poista kuva',
      removed: 'Kuva poistettu. Sinulla on taas tavallinen muurahainen.',
      unreadable: 'Kuvaa ei voitu avata. Kokeile toista kuvaa tai ota uusi valokuva.',
      rotate: 'Kierrä',
      preparing: 'Valmistellaan kuvaa…',
    },
    billing: {
      title: 'Tilaus',
      trial_title: 'Ilmainen kokeilu – päiviä jäljellä: {{days}}',
      trial_text: 'Ilmainen kokeilusi päättyy {{date}}. Valitse paketti sitä ennen, niin voit jatkaa AntNestin käyttöä.',
      expired_title: 'Ilmainen kokeilusi on päättynyt',
      expired_text: 'Valitse paketti, niin voit jatkaa AntNestin käyttöä. Perheesi tehtävät, tähdet ja palkinnot ovat yhä tallessa.',
      active_title_monthly: 'Maksat kuukausittain',
      active_title_yearly: 'Maksat vuosittain',
      active_text: 'Tilauksesi uusitaan {{date}}.',
      grace_title: 'Maksu ei mennyt läpi',
      grace_text: 'Päivitä maksutietosi viimeistään {{date}}, niin voit jatkaa AntNestin käyttöä.',
      cancelling_title: 'Tilauksesi on peruttu',
      cancelling_text: 'Voit käyttää AntNestiä {{date}} asti. Valitse paketti, jos haluat jatkaa sen jälkeen.',
      exempt_title: 'AntNest on perheellenne ilmainen',
      exempt_text: 'Sinun ei tarvitse maksaa mitään.',
      plans_title: 'Valitse paketti',
      plans_intro: 'Yksi tilaus kattaa koko perheen kaikilla laitteilla.',
      plan_monthly: 'Kuukausi',
      plan_yearly: 'Vuosi',
      price_monthly: '{{price}} kuukaudessa',
      price_yearly: '{{price}} vuodessa',
      price_soon: 'Hinta tulossa pian',
      includes_family: 'Koko perhe kaikilla laitteilla',
      includes_tasks: 'Kaikki valmiit tehtävät ja tarkistuslistat',
      includes_cancel: 'Peru milloin tahansa',
      choose_monthly: 'Valitse kuukausi',
      choose_yearly: 'Valitse vuosi',
      coming_soon: 'Maksaminen avautuu pian. Siihen asti AntNest on ilmainen.',
      manage_title: 'Hallitse tilaustasi',
      manage_text: 'Vaihda pakettia, päivitä korttisi, katso kuitit tai peru tilaus.',
      manage_button: 'Hallitse tilausta',
      banner_trial: 'Päiviä ilmaisen kokeilusi loppuun: {{days}}.',
      banner_trial_last_day: 'Ilmainen kokeilusi päättyy tänään.',
      banner_payment: 'Maksu ei mennyt läpi. Päivitä korttisi viimeistään {{date}}.',
      banner_cancelling: 'Tilauksesi päättyy {{date}}.',
      banner_action: 'Katso paketit',
      paywall_title: 'AntNest pitää taukoa',
      paywall_text: 'Pyydä aikuista valitsemaan paketti. Kaikki tähtesi ja tehtäväsi ovat tallessa ja odottavat sinua.',
      manage_failed: 'Tilaussivua ei voitu avata. Yritä hetken päästä uudelleen.',
    },
    pocket: {
      title: 'Viikkoraha',
      intro: 'Viikkoraha lasketaan tähdistä, jotka lapsi saa hyväksytyistä tehtävistä kuukauden aikana. AntNest ei siirrä rahaa: maksatte tavalliseen tapaan ja merkitsette sen maksetuksi.',
      no_children: 'Lisää perheeseen lapsi antaaksesi viikkorahaa.',
      currency: 'Valuutta',
      for_child: 'Viikkoraha: {{name}}',
      how_question: 'Miten se ansaitaan?',
      per_ten_label: '{{currency}} jokaisesta 10 tähdestä',
      fixed_label: '{{currency}} kuukaudessa, kun {{name}} on saanut vähintään',
      threshold_label: 'tähteä',
      threshold_help: '0 tähteä = annetaan aina (kiinteä viikkoraha).',
      bonus_label: 'Bonus:',
      bonus_every_label: '{{currency}} lisää jokaisesta',
      bonus_stars_label: 'tähteä lisää',
      cap_question: 'Kuukausiraja — valitse yksi',
      cap_none: 'Ei rajaa',
      cap_at: 'Enintään',
      cap_unit: '{{currency}} kuukaudessa',
      cap_required: 'Valitse raja tai ei rajaa ennen tallennusta.',
      example_title: 'Esimerkiksi kuukausi, jossa on:',
      example_line_one: '{{n}} tähti → {{amount}}',
      example_line_other: '{{n}} tähteä → {{amount}}',
      capped_short: 'raja täynnä',
      typical_dk: 'Tavallista Tanskassa kuukaudessa: alle 100 DKK 6–10-vuotiaille, 50–200 DKK 11–14-vuotiaille, 200–500 DKK 15–17-vuotiaille.',
      save: 'Tallenna',
      saved: 'Viikkoraha ({{name}}) tallennettu',
      save_failed: 'Tallennus ei onnistunut. Yritä uudelleen.',
      my_month: 'Viikkoraha ({{month}}): {{amount}}',
      my_next_one: '{{n}} tähti lisää antaa {{amount}} lisää',
      my_next_other: '{{n}} tähteä lisää antaa {{amount}} lisää',
      my_capped: 'Saavutit kuukauden enimmäismäärän. Hienoa!',
      my_last_paid: '{{month}}: {{amount}} — maksettu ✓',
      my_last_unpaid: '{{month}}: {{amount}} — odottaa maksua',
      to_pay_title: 'Maksettava viikkoraha',
      to_pay_help: 'Maksakaa tavalliseen tapaan (käteinen, pankki tai MobilePay) ja merkitkää se maksetuksi.',
      mark_paid: 'Merkitse maksetuksi',
      task_counts: 'Antaa viikkorahaa',
      task_counts_help: 'Poista valinta arkitehtäviltä, kuten sängyn petaamiselta, niin vain isommat työt lasketaan viikkorahaan.',
      typical_intro: 'Tavallista ({{country}}) kuukaudessa:',
      typical_band: '{{ages}} v: {{amount}}',
      typical_under: 'alle {{amount}}',
      typical_about: 'noin {{amount}}',
      typical_source: 'lähde: {{source}}',
    },
    getting_started: {
      title: 'Pääse alkuun',
      progress: '{{done}}/{{all}} tehty',
      hide: 'Piilota',
      item_approve: 'Hyväksy ensimmäinen valmis tehtävä',
      item_own_task: 'Muokkaa tehtävää tai tee oma',
      item_home_screen: 'Lisää AntNest aloitusnäytölle',
      item_calendar: 'Katso tehtäviä kalenterissa',
      home_screen_how: 'iPhone/iPad: napauta Safarissa Jaa ja valitse "Lisää Koti-valikkoon". Android: napauta Chromessa ⋮ ja valitse "Asenna sovellus" tai "Lisää aloitusnäytölle". Tee sama myös lasten laitteilla.',
      home_screen_done: 'Tehty',
      pocket_title: 'Viikkorahaa tähdistä?',
      pocket_text: 'Nyt kun lapset ovat päässeet alkuun, tähdet voivat antaa myös viikkorahaa – kiinteän summan, summan 10 tähteä kohden ja itse valitsemanne kuukausirajan.',
      pocket_setup: 'Ota käyttöön',
      pocket_no: 'Ei kiitos',
    },
    editor: {
      close: 'Sulje',
      unsaved_title: 'Tallennetaanko muutokset?',
      unsaved_body: 'Olet muuttanut jotain. Haluatko tallentaa ennen sulkemista?',
      save: 'Tallenna',
      discard: 'Älä tallenna',
      keep_editing: 'Jatka muokkausta',
    },
    timer: {
      start: 'Käynnistä ajastin',
      pause: 'Tauko',
      resume: 'Jatka',
      restart: 'Aloita alusta',
      times_up: 'Aika loppui!',
      sound_bell: 'Kello',
      sound_chime: 'Kilinä',
      sound_beeps: 'Herätyskello',
      sound_marimba: 'Marimba',
      sound_fanfare: 'Fanfaari',
      volume_note: 'Ääni seuraa puhelimen äänenvoimakkuutta – nosta sitä, jotta kuulet sen.',
    },
    approval_setting: {
      title: 'Aikuisten tehtävien hyväksyntä',
      label: 'Toisen aikuisen on hyväksyttävä aikuisten tehtävät',
      help_off: 'Pois: kun aikuinen saa tehtävän valmiiksi, se hyväksytään heti ja tähdet ovat hänen. Lasten tehtävät hyväksyy aina aikuinen.',
      help_on: 'Päällä: aikuisen valmis tehtävä odottaa, kunnes toinen aikuinen hyväksyy sen (jos perheessä on vain yksi ylläpitäjä, hän voi hyväksyä omansa).',
      setup_help: 'Jos ruutu jää tyhjäksi, aikuisten tehtävät hyväksytään heti, kun ne ovat valmiita. Voit muuttaa tätä myöhemmin asetuksissa.',
      saved: 'Tallennettu.',
    },
  },
  es: {
    auth: {
      forgot_title: '¿Olvidaste tu contraseña?',
      forgot_link: '¿Olvidaste tu contraseña?',
      forgot_confirmation: 'Si existe una cuenta, hemos enviado un enlace de restablecimiento.',
      email_label: 'Correo electrónico',
      send_reset_link: 'Enviar enlace',
      reset_title: 'Establecer una nueva contraseña',
      new_password_label: 'Nueva contraseña',
      confirm_password_label: 'Confirmar contraseña',
      set_new_password: 'Guardar nueva contraseña',
      password_mismatch: 'Las contraseñas no coinciden',
      password_policy_msg: 'Al menos 12 caracteres, incluir mayúscula, minúscula y un número.',
      invalid_token: 'Enlace inválido o vencido',
      reset_success: 'Contraseña actualizada. Redirigiendo al inicio de sesión...',
      reset_failed: 'No se pudo restablecer. Intenta de nuevo.',
      login_failed: 'No se pudo iniciar sesión. Inténtalo de nuevo.',
      beta_banner: 'AntNest está en beta. ¿Tienes un código de invitación?',
      beta_banner_link: 'Regístrate aquí',
      or: 'o',
      login_google: 'Iniciar sesión con Google',
      login_facebook: 'Iniciar sesión con Facebook',
      no_account: '¿No tienes cuenta?',
      create_one: 'Crea una',
      err_code_required: 'Necesitas un código beta.',
      err_agree: 'Acepta los términos y la política de privacidad.',
      err_fields: 'Rellena todos los campos.',
      err_code_invalid: 'Este código beta no es válido o ya se ha usado.',
      err_signup_failed: 'No se pudo crear la cuenta. Inténtalo de nuevo.',
      signup_title: 'Crea tu cuenta',
      beta_code: 'Código beta',
      beta_code_ph: 'Escribe tu código de invitación',
      full_name: 'Tu nombre',
      full_name_ph: 'p. ej. Anna',
      username: 'Nombre de usuario',
      show_password: 'Mostrar contraseña',
      hide_password: 'Ocultar contraseña',
      rule_length: 'Al menos 12 caracteres',
      rule_upper: 'Una mayúscula',
      rule_lower: 'Una minúscula',
      rule_digit: 'Un número',
      password_match: 'Las contraseñas coinciden',
      family_name: 'Nombre de la familia',
      family_name_ph: 'p. ej. La familia García',
      family_default: 'Mi familia',
      agree_before: 'Acepto los',
      terms: 'términos',
      and: 'y la',
      privacy: 'política de privacidad',
      creating_account: 'Creando la cuenta…',
      create_account: 'Crear cuenta',
      signup_google: 'Registrarse con Google',
      signup_facebook: 'Registrarse con Facebook',
      choose_language: 'Elegir idioma',
      oauth_failed: 'No se completó el inicio de sesión. Inténtalo de nuevo.',
      processing: 'Iniciando sesión…',
      complete_setup: 'Casi listo',
      your_name: 'Tu nombre',
      complete_setup_btn: 'Crear familia y continuar',
      login_apple: 'Iniciar sesión con Apple',
      signup_apple: 'Registrarse con Apple',
    },
    rooms: {
      kitchen: 'Cocina',
      living_room: 'Sala de estar',
      dining_room: 'Comedor',
      bedroom: 'Dormitorio',
      bathroom: 'Baño',
      toilet: 'Aseo',
      hallway: 'Pasillo',
      entryway: 'Entrada',
      laundry_room: 'Lavadero',
      garage: 'Garaje',
      office: 'Oficina',
      playroom: 'Sala de juegos',
      nursery: 'Cuarto de niños',
      patio: 'Patio/Balcón',
      garden: 'Jardín',
      storage: 'Trastero',
      basement: 'Sótano',
      attic: 'Ático',
      child_bedroom: 'Habitación infantil',
    },
    onboarding: {
      title: 'Empecemos',
      family_name: 'Nombre de la familia',
      adults: 'Adultos',
      children: 'Niños',
      member_name: 'Nombre',
      username: 'Usuario',
      pin_placeholder: 'PIN (4 dígitos)',
      role_admin: 'Admin',
      role_member: 'Miembro',
      next: 'Siguiente',
      back: 'Atrás',
      confirm: 'Está bien',
      processing: 'Guardando…',
      step_rooms_title: 'Configura tu hogar',
      step_rooms_help: 'Haz clic en las habitaciones de abajo para añadirlas a tu casa. Si tienes una habitación o espacio que no está en la lista, puedes añadirlo manualmente.\nSiempre puedes editar, añadir y quitar habitaciones en tu sección de administración.',
      custom_room_placeholder: 'Nombre de habitación personalizado',
      add_custom: 'Añadir personalizado',
      remove: 'Eliminar',
      step_review_title: 'Revisar',
      review_family: 'Familia',
      review_members: 'Miembros',
      review_rooms: 'Habitaciones',
      error_generic: 'Algo salió mal al guardar',
      step_members_help: 'Agrega aquí a los miembros de tu familia.\nDale a cada miembro un nombre y un nombre de usuario.\nCada miembro debe tener un PIN para poder iniciar sesión.',
      validation_incomplete: 'Algunos campos no están correctamente completados',
      adult_label: 'Adulto',
      child_label: 'Niño',
      privacy_note: 'Esta información solo se usa para configurar vuestra familia. No compartimos ni vendemos, ni lo haremos nunca, vuestros datos ni información personal a nadie.',
      child_age: 'Edad',
      child_room_name: 'Habitación de {{name}}',
      child_room_name_s: 'Habitación de {{name}}',
      hint_members: 'Siempre puedes añadir o cambiar miembros más tarde en Admin → Miembros.',
      hint_rooms: 'Cada niño tiene su propia habitación, para que sus tareas estén en el lugar correcto. Siempre puedes cambiar las habitaciones más tarde en Admin → Habitaciones.',
      step_tasks_title: 'Tareas para empezar',
      step_tasks_help: 'Hemos elegido algunas tareas que encajan con vuestras habitaciones y la edad de vuestros hijos. Desmarcad las que no queráis: podéis cambiarlo todo después.',
      starters_unavailable: 'No se pudieron cargar las sugerencias. Puedes saltar este paso y añadir tareas después.',
      tasks_for_child: 'Tareas de {{name}}',
      tasks_shared: 'Para todos (cualquiera puede cogerlas)',
      whole_home: 'Toda la casa',
      hint_tasks: 'Hay muchas más tareas estándar en Admin → Tareas, y allí también podéis crear las vuestras.',
      skip_step: 'Saltar este paso',
      step_rewards_title: 'Recompensas que conseguir',
      step_rewards_help: 'Los niños se mantienen motivados cuando la primera recompensa está a su alcance. Estas están pensadas para conseguir la primera en pocos días.',
      rewards_for_child: 'Para {{name}}',
      rewards_family: 'Para toda la familia (cuentan las estrellas de todos)',
      hint_rewards: 'Siempre podéis añadir vuestras propias recompensas o cambiar estas en Admin → Recompensas.',
      review_starters: 'Para empezar',
      review_tasks_one: '{{n}} tarea',
      review_tasks_other: '{{n}} tareas',
      review_rewards_one: '{{n}} recompensa',
      review_rewards_other: '{{n}} recompensas',
      create_family: 'Crear nuestra familia',
      done_title: '¡Vuestra familia está lista!',
      done_made: 'Hemos creado {{tasks}} tareas y {{rewards}} recompensas para vosotros.',
      done_try: 'Probad la primera tarea ahora: cambiad a un niño con «{{switch}}» arriba y pulsad «{{start}}» en una tarea.',
      done_admin: 'Cuando una tarea esté hecha, apruébala en «{{approve}}»: entonces se suman las estrellas.',
      go_home: 'Ir al inicio de la familia',
      go_admin: 'Ir a Admin',
    },
    common: {
      save: 'Guardar',
      take_photo: 'Tomar foto',
      cancel: 'Cancelar',
      loading: 'Cargando...',
      all: 'Todos',
      remove_image: 'Eliminar imagen',
      remove_item: 'Eliminar elemento',
      error_generic: 'Algo salió mal. Inténtalo de nuevo.',
      stars_one: '{{n}} estrella',
      stars_other: '{{n}} estrellas',
      update_ready: 'Hay una nueva versión de AntNest.',
      update_now: 'Actualizar',
      ok: 'OK',
    },
    header: {
      stats: 'Estadísticas',
      app_title: 'Gestor de Tareas Familiares',
      home: 'Inicio',
      tasks: 'Tareas',
      calendar: 'Calendario',
      rewards: 'Recompensas',
      settings: 'Configuración',
      profile: 'Perfil',
      admin: 'Admin',
      logout: 'Cerrar sesión',
      points: 'puntos',
      settings_tooltip: 'Configuración',
      change_picture: 'Cambiar mi imagen',
      someone_else: 'Le toca a otra persona',
      for_grownups: 'Para adultos',
      for_grownups_hint: 'Ajustes, aprobaciones, cerrar sesión',
      my_page: 'Mi página',
      menu_close: 'Cerrar menú',
      menu_open: 'Abrir menú',
      nav_menu: 'Menú',
      master_admin: 'Administrador principal',
      switch_user: 'Cambiar de usuario',
      tab_bar_label: 'Secciones principales',
    },
    stats: {
      title: 'Estadísticas',
      tab_me: 'Yo',
      tab_family: 'Familia',
      tab_tasks: 'Tareas',
      period_week: 'Semana',
      period_month: 'Mes',
      period_year: 'Año',
      period_all: 'Todo',
      prev: 'Anterior',
      next: 'Siguiente',
      all_time: 'Todo',
      tasks_done: 'tareas hechas',
      points: 'puntos',
      rewards: 'recompensas',
      chart_points: 'Puntos a lo largo del tiempo',
      where_helped: 'Dónde ayudé',
      no_room: 'Sin habitación',
      my_top_tasks: 'Mis tareas más hechas',
      together: 'Juntos',
      who_helped: 'Quién ayudó',
      everyone_over_time: 'Todos a lo largo del tiempo',
      repeating_title: 'Tareas',
      per_task_title: 'Cada tarea',
      times_done_axis: 'Veces hecha',
      times: '{n}×',
      last_done: 'última: {date}',
      empty: 'Aún no hay nada en este periodo.',
      family_hidden: 'Vuestra familia solo muestra esto a los adultos.',
      week_short: 'Sem. {n}',
      enc_start: 'Un nuevo comienzo — ¡cada tarea cuenta! 💪',
      enc_first: 'Tu primera tarea — ¡bien hecho! 🎉',
      enc_done: 'Has hecho {n} tareas — ¡genial! ⭐',
      enc_more: 'Más puntos que el periodo anterior — ¡sigue así! 🚀',
      visibility_title: 'Estadísticas: qué ven los niños',
      visibility_help: 'Elige cómo ven los niños la pestaña Familia. Los administradores adultos siempre lo ven todo.',
      visibility_team: 'Vista de equipo — la parte de cada uno, sin clasificación (recomendado)',
      visibility_full: 'Comparación completa — ordenada por puntos',
      visibility_admins: 'Oculto — solo los adultos ven la comparación familiar',
      visibility_saved: 'Guardado',
      team_view_note: 'La parte de cada uno del total de la familia',
    },
    calendar: {
      title: 'Calendario',
      coming_soon: 'El calendario se está construyendo. Aquí aparecerán vistas y eventos básicos.',
      view_day: 'Día',
      view_week: 'Semana',
      view_month: 'Mes',
      week_label: 'Semana',
      no_events_for_day: 'Sin eventos',
      today_label: 'hoy',
      prev_day: 'Día anterior',
      next_day: 'Día siguiente',
      prev_week: 'Semana anterior',
      next_week: 'Semana siguiente',
      prev_month: 'Mes anterior',
      next_month: 'Mes siguiente',
      go_to_today: 'Hoy',
      not_allowed_edit_event: 'Solo puedes cambiar los eventos que has creado tú.',
      filters_title: 'Filtrar por miembro',
      single_view_button: 'Vista individual',
      family_view_button: 'Vista familiar',
      no_events_for_range: 'No hay eventos en este rango',
      no_events: 'No hay eventos en este período.',
      add_event: 'Añadir evento',
      edit_event: 'Editar evento',
      event_form_title: 'Título',
      event_form_description: 'Descripción (opcional)',
      event_form_location: 'Ubicación (opcional)',
      event_form_start: 'Inicio',
      event_form_end: 'Fin',
      event_form_all_day: 'Todo el día',
      event_form_attendees: 'Asistentes',
      event_form_save: 'Guardar',
      event_form_cancel: 'Cancelar',
      event_form_delete: 'Eliminar',
      delete_confirm_title: 'Eliminar evento',
      delete_confirm_message: '¿Seguro que quieres eliminar este evento?',
      success_created: 'Evento creado',
      success_updated: 'Evento actualizado',
      success_deleted: 'Evento eliminado',
      error_generic: 'Algo salió mal',
      validation_title_required: 'El título es obligatorio',
      validation_end_after_start: 'El fin debe ser posterior al inicio',
      create_calendar_event: 'Crear evento de calendario',
      event_modal_title_create: 'Crear evento de calendario',
      event_modal_title_edit_prefix: 'Editar'
      ,
      reminder_label: 'Recordatorio',
      reminder_none: 'Sin recordatorio',
      reminder_10m: '10 minutos antes',
      reminder_30m: '30 minutos antes',
      reminder_1h: '1 hora antes',
      reminder_12h: '12 horas antes',
      reminder_1d: '1 día antes',
      nothing_planned: 'Nada planeado todavía',
      nothing_this_month: 'Nada planeado este mes',
      today_day: 'Hoy, {{day}}',
      tomorrow_day: 'Mañana, {{day}}',
      at_time: 'A las {{time}}',
      all_day_label: 'Todo el día',
      with_names: 'con {{names}}',
      task_label: 'Tarea',
      everyone: 'Todos',
      back: 'Volver',
      due_today: 'Para hoy',
      due_today_at: 'Para hoy antes de las {{time}}',
      due_tomorrow: 'Para mañana',
      due_tomorrow_at: 'Para mañana antes de las {{time}}',
      due_later: 'Para este día',
      due_later_at: 'Antes de las {{time}}',
      overdue_since: 'Atrasada desde el {{day}}',
      event_form_image: 'Imagen del evento (opcional)',
      remove_image: 'Quitar imagen',
      planned: 'Prevista',
    },
    settings: {
      title: 'Configuración',
      subtitle: 'Administra tu cuenta y las preferencias de la aplicación',
      dark_mode_section: 'Modo oscuro',
      current_theme: 'Tema actual',
      following_system: 'Siguiendo la preferencia del sistema',
      manual_selection: 'Selección manual',
      follow_system_label: 'Seguir preferencia del sistema',
      auto_switch_hint: 'Cambia automáticamente entre modo claro y oscuro según el sistema',
      enable_dark_mode: 'Activar modo oscuro',
      manual_toggle_hint: 'Alterna manualmente entre temas claros y oscuros',
      light_mode: 'Modo claro',
      dark_mode: 'Modo oscuro',
      more_settings_title: 'Más ajustes próximamente',
      more_settings_desc: 'Aquí habrá opciones adicionales, incluyendo notificaciones, privacidad y gestión de la cuenta.',
      language_section: 'Idioma',
      language_hint: 'Elige tu idioma preferido para la interfaz',
      back: 'Volver',
      profile_parent: 'Padre/madre. Editar nombre, foto y contraseña',
      profile_member: 'Editar nombre, foto y PIN',
      look: 'Aspecto',
      auto: 'Auto',
      tell_me_when: 'Avísame cuando',
      notif_this_device: 'Notificaciones en este dispositivo',
      notif_blocked_parent: 'Bloqueadas en este navegador. Permite las notificaciones de este sitio en los ajustes del navegador.',
      n_review_ready: 'Una tarea necesita aprobación',
      n_task_approved: 'Mi tarea está aprobada',
      n_reward_ready: 'Una recompensa está lista',
      n_due_soon: 'Una de mis tareas vence pronto',
      n_overdue: 'Una de mis tareas va con retraso',
      n_new_task: 'Hay una tarea nueva libre',
      n_event: 'Recordatorios del calendario',
      daily_summary: 'Resumen diario',
      daily_summary_time: 'Hora del resumen diario',
      quiet_hours: 'Horas de silencio',
      quiet_hours_hint: 'Sin notificaciones entre estas horas',
      quiet_from: 'Silencio desde',
      quiet_to: 'Silencio hasta',
      this_device: 'Este dispositivo',
      change_pin: 'Cambiar mi PIN',
      switch_person: 'Cambiar de persona',
      refresh_session: 'Actualizar este dispositivo',
      session_refreshed: 'Este dispositivo está actualizado.',
      locked_until: 'Bloqueado hasta las {{time}}',
      unlock: 'Desbloquear',
      unlocked: '@{{name}} puede volver a entrar.',
      log_out_device: 'Cerrar sesión en este dispositivo',
      logout_confirm_title: '¿Cerrar sesión en este dispositivo?',
      logout_confirm_text: 'Toda la familia {{family}} cerrará sesión aquí. Un adulto tendrá que volver a entrar antes de que nadie pueda usar AntNest en este dispositivo.',
      logout_confirm_yes: 'Cerrar sesión',
      cancel: 'Cancelar',
      n_reward_claimed: 'Se canjea una recompensa',
    },
    rewards: {
      title: 'Recompensas',
      subtitle: 'Sigue tu progreso y canjea tus recompensas',
      your_points: 'Tus puntos',
      family_points: 'Puntos de la familia',
      claimed_rewards: 'Recompensas canjeadas',
      your_coins: 'Tus monedas',
      individual_rewards: 'Recompensas individuales',
      no_individual_rewards: 'No hay recompensas individuales disponibles aún.',
      family_rewards: 'Recompensas familiares',
      no_family_rewards: 'No hay recompensas familiares disponibles aún.',
      goal_reached: '¡Objetivo alcanzado!',
      complete_suffix: 'completado',
      target_whole_family: 'Toda la familia',
      claim_reward_button: '¡Canjear recompensa!',
      claimed_rewards_section: 'Recompensas canjeadas',
      no_claimed_rewards: 'Aún no se han canjeado recompensas. ¡Sigue trabajando!',
      claimed_on: 'Canjeada el',
      unknown_date: 'Fecha desconocida',
      unknown_member: 'Miembro desconocido',
      reward_header_alt: 'Encabezado de recompensa',
      show_more: 'Mostrar más',
      show_fewer: 'Mostrar menos',
      points_abbrev: 'pts',
      progress_counts_from: 'El progreso cuenta puntos ganados desde el {{date}}.',
      lifetime_total_hint: 'Total histórico (aparte del progreso de cada recompensa).',
      your_stars_label: 'tus estrellas',
      ready_to_get: 'Listo para conseguir',
      keep_saving: 'Sigue ahorrando para',
      n_more: 'faltan {{n}}',
      get_it: 'Conseguirla',
      grownup_gets: 'Esta la consigue un adulto',
      got_one: '{{n}} recompensa conseguida',
      got_other: '{{n}} recompensas conseguidas',
      see_all: 'Ver todas',
      counting_since: 'Cuenta estrellas desde el {{date}}',
      nothing_yet: 'Todavía no hay recompensas. ¡Pide a un adulto que añada algunas!',
      claim_failed: 'No se pudo conseguir la recompensa. Inténtalo de nuevo.',
    },
    dashboard: {
      welcome_back: 'Bienvenido de nuevo',
      subheading: 'Esto es lo que ocurre en tu familia hoy',
      leaderboard_title: 'Clasificación familiar',
      family_member: 'Miembro de la familia',
      tasks_title: 'Tareas',
      available_tasks: 'Tareas disponibles',
      no_available_tasks: 'No hay tareas disponibles por el momento',
      my_active_tasks: 'Mis tareas activas',
      no_my_tasks: 'No tienes tareas activas',
      completed_tasks: 'Tareas completadas',
      no_completed_tasks: 'No hay tareas completadas aún',
      manage_all_tasks: 'Gestionar todas las tareas',
      my_rewards_title: 'Mis recompensas',
      my_points: 'Mis puntos',
      available_rewards: 'Recompensas disponibles',
      claimed_rewards: 'Recompensas canjeadas',
      next_reward_goal: 'Próximo objetivo de recompensa',
      all_rewards_claimed: '¡Todas las recompensas canjeadas! ¡Buen trabajo!',
      showing_rewards_help: 'Mostrando: Tus recompensas personales y las de toda la familia',
      claim_now: '¡Canjear ahora!',
      locked: 'Bloqueado',
      go_to_rewards: 'Ir a Recompensas',
      my_tasks_primary_title: 'Mis tareas',
      my_tasks_home_hint: 'Solo se muestran las tareas asignadas a ti. Para elegir tareas nuevas, abre Tareas.',
      due_overdue: 'Vencida',
      due_today: 'Vence hoy',
      due_no_deadline: 'Sin fecha límite',
      hi_name: 'Hola, {{name}}',
      goal_of: '{{have}} de {{need}} estrellas',
      goal_get_it: 'Conseguirla',
      goal_none: 'Todavía no hay recompensas. ¡Pide a un adulto que añada una!',
      today: 'Hoy',
      no_tasks_now: 'No hay tareas ahora',
      waiting_one: '{{n}} estrella cuando un adulto diga que sí',
      waiting_other: '{{n}} estrellas cuando un adulto diga que sí',
      family_goal: 'Meta de la familia',
      family_goal_more: 'Cuentan las estrellas de todos. Faltan {{n}} para {{reward}}.',
      family_goal_ready: '¡{{reward}} está listo! Un adulto puede conseguirlo para la familia.',
      find_tasks_cta: 'Buscar una tarea',
      due_tomorrow: 'Mañana',
      due_in_days_one: 'En {{n}} día',
      due_in_days_other: 'En {{n}} días',
      more_stars_title: '¿Quieres más estrellas?',
      more_stars_hint: 'Cualquiera puede cogerlas. ¡Elige una cuando te apetezca!',
      see_all_can_take: 'Ver las {{n}}',
      take_failed: 'No se pudo coger la tarea',
    },
    task_board: {
      title: 'Tablero de tareas',
      subtitle: 'Gestiona y realiza un seguimiento de todas las tareas familiares',
      available_tasks: 'Tareas disponibles',
      no_available_tasks: 'No hay tareas disponibles por el momento',
      my_active_tasks: 'Mis tareas activas',
      no_my_tasks: 'No tienes tareas activas',
      view: 'Ver',
      complete: 'Completar',
      tasks_assigned_to_others: 'Tareas asignadas a otros',
      no_tasks_assigned_to_others: 'No hay tareas asignadas a otros usuarios',
      view_task: 'Ver tarea',
      completed_tasks: 'Tareas completadas',
      no_completed_tasks: 'No hay tareas completadas aún',
      take_task: 'Me la quedo',
      take_over_task: 'Asumir tarea',
      unclaim_task: 'Devolverla',
      your_tasks: 'Tus tareas',
      can_take: 'Puedes coger estas',
      waiting_heading: 'Esperando a un adulto',
      others_tasks: 'Tareas de los demás',
      done_one: '{{n}} tarea hecha',
      done_other: '{{n}} tareas hechas',
      moved_to_yours: '{{task}} está ahora en Tus tareas',
      nothing_to_take: 'No hay tareas para coger ahora',
      no_your_tasks: 'Ahora no tienes tareas.',
    },
    login: {
      title: 'AntNest',
      email_label: 'Correo',
      password_label: 'Contraseña',
      logging_in: 'Iniciando sesión...',
      login_button: 'Iniciar sesión',
      demo_account: 'Cuenta de demostración',
      demo_email: 'Correo: admin@family.com',
      demo_password: 'Contraseña: admin'
    },
    user_profile: {
      user_not_found: 'Usuario no encontrado.',
      back_to_home: 'Volver al inicio',
      title: 'Perfil de usuario',
      profile_information: 'Información del perfil',
      name: 'Nombre',
      email: 'Correo',
      email_not_set: 'Sin correo',
      role: 'Rol',
      admin: 'Admin',
      family_member: 'Miembro de la familia',
      points: 'Puntos',
      coins: 'Monedas',
      edit_profile: 'Editar perfil',
      save_changes: 'Guardar cambios',
      cancel: 'Cancelar',
      email_optional_label: 'Correo (opcional)',
      new_password_optional_label: 'Nueva contraseña (opcional)',
      name_placeholder: 'Escribe tu nombre',
      email_enter_placeholder: 'Escribe la dirección de correo',
      email_add_placeholder: 'Añadir correo',
      username_for_member_login: 'Usuario (para inicio de sesión de miembro)',
      pin_label: 'PIN (4–8 dígitos)',
      avatar: 'Avatar',
      avatar_hint: 'Elige un avatar o sube el tuyo.',
      upload: 'Subir',
      avatar_updated: 'Avatar actualizado',
      avatar_update_failed: 'Error al actualizar el avatar',
      linked_accounts: 'Cuentas vinculadas',
      no_linked_accounts: 'No hay cuentas vinculadas.',
      unlink: 'Desvincular',
      unlink_failed: 'Error al desvincular',
      unlink_confirm: '¿Desvincular {{provider}}?',
      link_to_google: 'Vincular con Google',
      change_password: 'Cambiar contraseña',
      current_password: 'Contraseña actual',
      new_password: 'Nueva contraseña',
      password_changed: 'Contraseña cambiada',
      password_change_failed: 'Error al cambiar la contraseña',
      save_new_password: 'Guardar nueva contraseña',
      pin_validation: 'El PIN debe tener entre 4 y 8 dígitos (solo números)',
      current_pin_label: 'PIN actual (solo si ya tienes uno)',
      current_pin_required: 'Introduce tu PIN actual para cambiarlo.',
      current_pin_incorrect: 'Tu PIN actual es incorrecto.',
      email_managed_by_login: 'Tu correo electrónico viene de tu forma de iniciar sesión y no se puede cambiar aquí.',
      username_validation: 'El usuario debe tener al menos 2 caracteres',
      profile_updated: 'Perfil actualizado',
      google_link_success: 'Cuenta de Google vinculada',
      picture_type_bad: 'Elige una imagen PNG, JPG o WebP.',
      picture_too_big: 'La imagen es demasiado grande (máx. 1 MB).',
      choose_picture: 'Elegir esta imagen',
      or_pick_one: 'O elige uno de estos',
      tap_picture: 'Toca la imagen para elegir una nueva.',
      facebook_link_success: 'Cuenta de Facebook vinculada',
      link_to_facebook: 'Vincular con Facebook',
      account_unlinked: 'Cuenta desvinculada.',
      link_to_apple: 'Vincular con Apple',
    },
    admin_panel: {
      header_title: 'Panel de administración',
      header_subtitle: 'Gestiona las tareas, recompensas y miembros de tu familia',
      tab_tasks: 'Tareas',
      tab_rooms: 'Habitaciones',
      tab_members: 'Miembros',
      tab_pending: 'Pendientes',
      tab_rewards: 'Recompensas',
      tab_tools: 'Herramientas',
      task_management: 'Gestión de tareas',
      create_new_task: 'Crear nueva tarea',
      active_tasks: 'Tareas activas',
      active_tasks_help: 'Tareas en curso o disponibles',
      edit: 'Editar',
      remove: 'Eliminar',
      no_active_tasks: 'No se encontraron tareas activas.',
      completed_tasks: 'Tareas completadas',
      completed_tasks_help: 'Tareas finalizadas y verificadas',
      no_completed_tasks: 'No se encontraron tareas completadas.',
      room_management: 'Gestión de habitaciones',
      add_new_room: 'Añadir nueva habitación',
      member_management: 'Gestión de miembros',
      add_new_member: 'Añadir nuevo miembro',
      pending_verification: 'Verificación de tareas pendientes',
      verify_this_task: 'Verificar esta tarea',
      self_verify_not_allowed_hint:
        'Otro administrador debe verificar esta tarea cuando hay más de un administrador.',
      points_not_awarded_section: 'Verificada — faltan puntos',
      points_not_awarded_help:
        'Estas tareas se marcaron como verificadas antes de que los puntos se guardaran bien. Usa «Otorgar puntos» una vez para acreditar al miembro que completó la tarea.',
      award_missing_points: 'Otorgar puntos',
      no_tasks_needing_points: 'Todas las tareas verificadas tienen puntos registrados.',
      verify_success: 'Tarea verificada. Se han otorgado los puntos.',
      verify_repaired_points_success:
        'Se han otorgado al miembro que completó la tarea los puntos que faltaban.',
      verify_already_done: 'Esta tarea ya está verificada y los puntos están registrados.',
      verify_misattribution_credit_confirm:
        'El sistema ya marca el pago. Si el saldo del miembro nunca subió: ¿aplicar un crédito único ahora? (No si ya recibió los puntos.)',
      verify_misattribution_success: 'Crédito único aplicado. Pide al miembro que actualice o vuelva a entrar.',
      verify_misattribution_already_done: 'Ya se aplicó un crédito único de corrección para esta tarea.',
      verify_already_done_hint:
        'Si el miembro sigue sin puntos: vuelve a «Otorgar puntos» y confirma, o borra points_awarded_on_verification en la base de datos.',
      no_pending_verify: 'No hay tareas pendientes de verificación.',
      reward_management: 'Gestión de recompensas',
      create_new_reward: 'Crear nueva recompensa',
      claimed: 'Canjeada',
      available: 'Disponible',
      tool_management: 'Gestión de herramientas',
      create_new_tool: 'Crear nueva herramienta',
      no_tools_msg: 'Aún no hay herramientas creadas. Crea tu primera herramienta para empezar.',
      // Modals and form fields
      task_modal_title_create: 'Crear nueva tarea',
      task_modal_title_edit_prefix: 'Editar tarea',
      task_title_label: 'Título de la tarea',
      task_title_placeholder: 'Introduce el título de la tarea',
      description_label: 'Descripción (opcional)',
      description_placeholder: 'Introduce la descripción de la tarea',
      room_label: 'Habitación',
      no_room_option: 'Sin habitación',
      assignment_label: 'Asignación de la tarea',
      available_anyone_option: 'Disponible para todos',
      assign_to_member_prefix: 'Asignar a',
      assignment_hint_assigned: 'Esta tarea será visible solo para el miembro asignado',
      assignment_hint_anyone: 'Esta tarea estará disponible para cualquier miembro de la familia',
      points_label: 'Puntos a otorgar',
      points_placeholder: 'Puntos',
      due_date_label: 'Fecha de vencimiento',
      recurrence_label: 'Recrear cada X días (0 = sin recurrencia)',
      header_image_label: 'Imagen de encabezado (opcional)',
      current_header_image_label: 'Imagen de encabezado actual',
      checklist_label: 'Elementos de la lista de verificación',
      checklist_item_placeholder: 'Introduce el texto del elemento',
      images_optional_label: 'Imágenes (opcional):',
      required_tools_label: 'Herramientas necesarias',
      no_tools_available_msg: 'No hay herramientas disponibles. Crea herramientas primero en la pestaña Herramientas.',
      cancel_btn: 'Cancelar',
      create_task_btn: 'Crear tarea',
      save_changes_btn: 'Guardar cambios',
      remove_task_title: 'Eliminar tarea',
      remove_task_prompt_prefix: '¿Seguro que quieres eliminar la tarea',
      irreversible_note: 'Esta acción no se puede deshacer.',
      remove_task_btn: 'Eliminar tarea',
      reward_modal_title_create: 'Crear nueva recompensa',
      reward_modal_title_edit_prefix: 'Editar recompensa',
      reward_title_label: 'Título de la recompensa',
      reward_title_placeholder: 'Introduce el título de la recompensa',
      reward_desc_label: 'Descripción',
      reward_desc_placeholder: 'Introduce la descripción de la recompensa',
      points_required_label: 'Puntos requeridos',
      reward_progress_start_label: 'Los puntos cuentan desde (fecha)',
      reward_progress_start_hint: 'Solo cuentan los puntos ganados desde el inicio de este día (tu hora local).',
      target_type_label: 'Tipo de objetivo',
      target_type_individual: 'Individual',
      target_type_family: 'Familiar',
      assign_member_label: 'Asignar a un miembro de la familia',
      select_member_option: 'Selecciona un miembro de la familia',
      current_header_image_id_label: 'ID de la imagen de encabezado actual',
      create_reward_btn: 'Crear recompensa',
      tool_modal_title_create: 'Crear nueva herramienta',
      tool_modal_title_edit_prefix: 'Editar herramienta',
      tool_name_label: 'Nombre de la herramienta',
      tool_name_placeholder: 'Introduce el nombre de la herramienta',
      tool_image_label: 'Imagen de la herramienta (opcional)',
      current_tool_image_label: 'Imagen actual de la herramienta',
      create_tool_btn: 'Crear herramienta',
      room_modal_title_add: 'Añadir nueva habitación',
      room_modal_title_edit: 'Editar habitación',
      delete_room_title: 'Eliminar habitación',
      delete_room_btn: 'Eliminar',
      delete_room_prompt_prefix: '¿Seguro que quieres eliminar permanentemente la habitación',
      delete_room_blocked_message: 'No se puede eliminar esta habitación porque una o más tareas la están usando. Elimina o reasigna esas tareas primero.',
      go_to_tasks_btn: 'Ver tareas',
      room_name_label_required: 'Nombre de la habitación *',
      room_name_placeholder: 'Introduce el nombre de la habitación',
      add_room_btn: 'Añadir habitación',
      member_modal_title_add: 'Añadir nuevo miembro de la familia',
      member_name_label_required: 'Nombre *',
      member_name_placeholder: 'Introduce el nombre del miembro',
      member_password_label_required: 'Contraseña *',
      member_password_placeholder: 'Introduce la contraseña',
      member_email_label_optional: 'Correo (opcional)',
      member_email_placeholder: 'Introduce la dirección de correo',
      admin_access_label: 'Acceso de administrador',
      add_member_btn: 'Añadir miembro',
      add_checklist_item_btn: 'Añadir elemento a la lista',
      member_type_label: 'Tipo de miembro',
      member_type_adult: 'Adulto',
      member_type_child: 'Niño',
      err_room_name: 'Ponle un nombre a la habitación.',
      err_room_create: 'No se pudo crear la habitación. Inténtalo de nuevo.',
      err_room_update: 'No se pudo guardar la habitación. Inténtalo de nuevo.',
      err_generic_missing: 'Falta algo. Cierra e inténtalo de nuevo.',
      err_task_title_points: 'Ponle a la tarea un título y un número de estrellas.',
      err_checklist_empty: 'Añade al menos un paso con texto.',
      err_task_create: 'No se pudo crear la tarea. Inténtalo de nuevo.',
      err_task_update: 'No se pudo guardar la tarea. Inténtalo de nuevo.',
      err_task_remove: 'No se pudo quitar la tarea. Inténtalo de nuevo.',
      err_template_use: 'No se pudo crear una tarea con esta plantilla. Inténtalo de nuevo.',
      err_save_template: 'No se pudo guardar la plantilla. Inténtalo de nuevo.',
      saved_template: 'Guardado como plantilla.',
      err_header_image: 'No se pudo guardar la imagen. Inténtalo de nuevo.',
      err_reward_title: 'Ponle un título a la recompensa.',
      err_reward_points: 'Indica cuántas estrellas necesita la recompensa.',
      err_reward_member: 'Elige para quién es la recompensa.',
      err_reward_create: 'No se pudo crear la recompensa. Inténtalo de nuevo.',
      err_reward_update: 'No se pudo guardar la recompensa. Inténtalo de nuevo.',
      reward_updated: 'Recompensa guardada.',
      err_tool_name: 'Ponle un nombre a la herramienta.',
      err_tool_create: 'No se pudo crear la herramienta. Inténtalo de nuevo.',
      err_tool_update: 'No se pudo guardar la herramienta. Inténtalo de nuevo.',
      err_member_required: 'Un miembro nuevo necesita nombre, usuario y PIN.',
      err_pin_format: 'El PIN debe tener de 4 a 8 dígitos (solo números).',
      err_username_short: 'El usuario debe tener al menos 2 caracteres.',
      err_member_create: 'No se pudo añadir al miembro. Inténtalo de nuevo.',
      verify_misattribution_title: '¿Dar las estrellas ahora?',
      verify_misattribution_yes: 'Sí, darlas',
      status_assigned: 'Para {{name}}',
      status_open: 'Cualquiera puede cogerla',
      comes_back_on: 'Vuelve el {{date}}',
      anyone_can_take: 'Cualquiera puede coger la siguiente (si no, vuelve a la misma persona)',
      repeat_hint: 'Con «repetir cada», la siguiente vence esos días después de aprobarla y aparece «días para completar» días antes.',
      step_tools_label: 'Herramientas para este paso',
      step_tools_hint: 'Primero elige las herramientas de la tarea más abajo. Luego podrás elegir las que necesita este paso.',
      room_picture_label: 'Foto de la habitación (opcional)',
      room_picture_hint: 'Una foto ayuda a los niños a ver en qué habitación está una tarea.',
      room_picture_add: 'Añadir foto',
      room_picture_change: 'Cambiar foto',
      room_picture_failed: 'La habitación se guardó, pero no se pudo subir la foto. Inténtalo de nuevo.',
      step_drag: 'Arrastra para mover el paso',
      step_move_up: 'Subir el paso',
      step_move_down: 'Bajar el paso',
      delete_btn: 'Eliminar',
      show_more_completed_tasks: 'Mostrar más',
      member_edit_title: 'Editar miembro de la familia',
      member_name_label: 'Nombre',
      member_pin_label_optional: 'PIN (opcional)',
      member_pin_hint_optional: 'Déjalo en blanco para mantener el PIN actual. El PIN debe tener 4–8 dígitos.',
      member_is_admin_label: 'Acceso de administrador',
      member_update_failed: 'No se pudo actualizar el miembro',
      delete_reward_title: 'Eliminar recompensa',
      delete_confirm_prefix: '¿Seguro que quieres eliminar',
      recreate_on_validation_label: 'Volver a crear al aprobar',
      recreate_on_validation_help: 'Volver a crear la tarea en cuanto un adulto la apruebe',
      days_to_complete_label: 'Días para completar la tarea',
      days_to_complete_placeholder: 'p. ej., 7',
      days_to_complete_help: 'Si se indica, la fecha de vencimiento será la fecha de creación + estos días.',
      camera_title: 'Cámara',
      show_less_completed_tasks: 'Mostrar menos',
      delete_tool_title: 'Eliminar herramienta',
      task_saving: 'Guardando la tarea…',
      task_saved: 'Tarea guardada',
      try_again: 'Reintentar',
      deadline_question: 'Plazo',
      deadline_none: 'Sin plazo',
      deadline_within: 'Hay que hacerla en',
      days_unit: 'días',
      repeat_question: 'Cuando se apruebe…',
      repeat_none: 'está terminada: no vuelve',
      repeat_now: 'vuelve enseguida',
      repeat_after: 'vuelve después de',
      next_question: '¿Quién se queda la siguiente?',
      next_same: '{{name}} otra vez',
      next_anyone: 'Cualquiera puede cogerla',
      summary_title: 'Lo que pasará:',
      due_this_time_label: 'Fecha límite esta vez (opcional)',
      due_this_time_help: 'Déjalo vacío y se usará el plazo de arriba.',
      sum_now_person: '{{name}} recibe la tarea ahora.',
      sum_now_anyone: 'Cualquiera de la familia puede cogerla ahora.',
      sum_within_one: 'Hay que hacerla en {{n}} día.',
      sum_within_other: 'Hay que hacerla en {{n}} días.',
      sum_end: 'Cuando la apruebes, se termina.',
      sum_back_now_same: 'Cuando la apruebes, vuelve enseguida a {{name}}.',
      sum_back_now_anyone: 'Cuando la apruebes, vuelve enseguida y cualquiera puede cogerla.',
      sum_back_after_same_one: 'Cuando la apruebes, vuelve a {{name}} en {{n}} día.',
      sum_back_after_same_other: 'Cuando la apruebes, vuelve a {{name}} en {{n}} días.',
      sum_back_after_anyone_one: 'Cuando la apruebes, vuelve en {{n}} día y cualquiera puede cogerla.',
      sum_back_after_anyone_other: 'Cuando la apruebes, vuelve en {{n}} días y cualquiera puede cogerla.',
      sum_then_within_one: 'Entonces hay {{n}} día para hacerla.',
      sum_then_within_other: 'Entonces hay {{n}} días para hacerla.',
      repeat_next: 'la siguiente tarea es',
      next_ready_after: 'Lista después de',
      hours_unit: 'horas',
      waits_for: 'Esperando: {{task}}',
      then_next: 'Después: {{task}}',
      sum_next: 'Cuando la apruebes, sigue "{{task}}".',
      sum_next_wait_one: 'Está lista {{n}} hora después de terminar esta.',
      sum_next_wait_other: 'Está lista {{n}} horas después de terminar esta.',
      sum_next_waits: 'Hasta entonces, los niños no ven "{{task}}".',
      reward_saving: 'Guardando la recompensa…',
      reward_saved: 'Recompensa guardada',
      assignment_hint_each: 'Cada uno tiene su propia tarea: en su calendario, aprobada por separado y con sus propias estrellas.',
      err_choose_person: 'Elige al menos una persona.',
      names_and: '{{first}} y {{last}}',
      sum_now_each: '{{names}} reciben ahora cada uno su tarea.',
      sum_back_now_each: 'Cuando apruebas la tarea de alguien, la suya vuelve enseguida.',
      sum_back_after_each_one: 'Cuando apruebas la tarea de alguien, la suya vuelve en {{n}} día.',
      sum_back_after_each_other: 'Cuando apruebas la tarea de alguien, la suya vuelve en {{n}} días.',
      shared_ready: 'Lista',
      remove_shared_note: 'Se quita para todos a los que se dio. Las ya hechas quedan en el historial.',
      repeat_plan: 'vuelve según un plan fijo',
      plan_weekly: 'cada semana el',
      plan_every: 'cada',
      plan_every_unit: 'días',
      plan_from: 'desde',
      sum_plan_person: '{{name}} recibe la tarea.',
      sum_plan_anyone: 'Cualquiera de la familia puede cogerla.',
      sum_plan_each: '{{names}} reciben cada uno su tarea.',
      sum_plan_weekly: 'Aparece cada {{days}}.',
      sum_plan_every_one: 'Aparece cada día desde el {{date}}.',
      sum_plan_every_other: 'Aparece cada {{n}} días desde el {{date}}.',
      sum_plan_back: 'Cuando la apruebes, vuelve el siguiente día previsto.',
      plan_week_every_one: 'cada semana',
      plan_week_every_other: 'cada {{n}} semanas',
      plan_on: 'el',
      plan_monthly: 'cada mes',
      plan_month_every_one: 'cada mes',
      plan_month_every_other: 'cada {{n}} meses',
      plan_the: 'el',
      plan_which: 'cuál',
      plan_weekday: 'día',
      plan_nth_1: 'primer',
      plan_nth_2: 'segundo',
      plan_nth_3: 'tercer',
      plan_nth_4: 'cuarto',
      plan_nth_last: 'último',
      plan_on_date: 'el día',
      plan_date_unit: 'del mes',
      sum_plan_weekly_every: 'Aparece cada {{n}} semanas, el {{days}}.',
      sum_plan_every_open_one: 'Aparece cada día.',
      sum_plan_every_open_other: 'Aparece cada {{n}} días.',
      sum_plan_month_day_one: 'Aparece el día {{day}} de cada mes.',
      sum_plan_month_day_other: 'Aparece el día {{day}} cada {{n}} meses.',
      sum_plan_month_nth_one: 'Aparece el {{nth}} {{weekday}} de cada mes.',
      sum_plan_month_nth_other: 'Aparece el {{nth}} {{weekday}} cada {{n}} meses.',
      step_timer_label: 'Temporizador (opcional)',
      step_timer_minutes: 'Temporizador, minutos',
      step_timer_seconds: 'Temporizador, segundos',
      step_timer_min: 'min',
      step_timer_sec: 's',
      step_timer_clear: 'Sin temporizador',
      step_timer_hint: 'P. ej., "Plancha – 0:45". Quien hace la tarea lo inicia y oye un sonido al acabar el tiempo.',
    },
    checklist_editor: {
      back_to_admin: 'Volver al panel de administración',
      edit_checklist_for: 'Editar lista de verificación',
      add_new_item: 'Añadir nuevo elemento',
      add_item: 'Añadir',
      current_items: 'Elementos actuales',
      no_items: 'No hay elementos aún. ¡Añade algunos arriba!',
      cancel: 'Cancelar',
      save: 'Guardar'
    },
    task_details: {
      loading: 'Cargando...',
      title: 'Detalles de la tarea',
      room: 'Habitación',
      points: 'Puntos',
      status: 'Estado',
      status_completed: 'Completada',
      status_active: 'Activa',
      assigned_to: 'Asignada a',
      due_date: 'Fecha de vencimiento',
      tools_required: 'Herramientas necesarias',
      checklist: 'Lista de verificación',
      task_completed: 'Tarea completada',
      completed_by: 'Completada por',
      message: 'Mensaje',
      submit_for_verification: 'Enviar para verificación',
      close: 'Cerrar',
      preview_room_placeholder: 'Se sugiere cuando añades esta tarea',
      preview_title: 'Vista previa de la tarea',
      preview_checklist_hint: 'Pasos (solo vista previa — márcalos después de añadir la tarea)',
      view: 'Ver',
    },
    task_flow: {
      start_task: '¡Vamos!',
      tools_ready_title: '¿Lo tienes todo?',
      tools_ready_subtitle: 'Necesitarás estas herramientas. Reúnelas antes de continuar.',
      ready_to_start_title: '¿Listo?',
      ready_to_start_body: '¿Estás listo para empezar la tarea?',
      tools_ready_no: 'Todavía no',
      tools_ready_yes: 'Sí, vamos',
      finish_step: '¡Hecho!',
      final_put_tools_message:
        '¡Muy bien! Devuelve todas las herramientas a su sitio. Luego pulsa el botón de abajo para completar la tarea.',
      complete_task_button: 'Listo',
      no_checklist_warning: 'Esta tarea aún no tiene pasos. Pide a un adulto que añada la lista antes de empezar.',
      view_checklist_readonly_hint: 'Podrás marcar los pasos después de pulsar Empezar tarea.',
      progress_label: 'Progreso',
      step_counter_prefix: 'Paso',
      not_assigned: 'Aún sin asignar',
      step_of: 'Paso {{n}} de {{total}}',
      back_step: 'Volver un paso',
      tidy_title: '¡Casi listo!',
      tidy_subtitle: 'Guarda estas cosas en su sitio',
      tidy_no_tools: '¿Está todo terminado? Pulsa Listo.',
      back_to_steps: 'Volver a los pasos',
      celebrate_stars_title: '{{n}} estrellas están en camino',
      celebrate_stars_body: 'Las recibes cuando un adulto diga que sí',
      celebrate_button: 'Volver al inicio',
      see_details: 'Ver detalles',
      step_tools: 'Necesitas:',
      where_room: 'Dónde: {{room}}',
      celebrate_heading: '¡Buen trabajo!',
      celebrate_subtitle: 'Has terminado {{title}}',
      celebrate_stars_got_title: 'Has ganado {{n}} estrellas',
      celebrate_stars_got_body: 'Aprobado y sumado a tus estrellas',
      tidy_put_all_back: 'Devuelve todas estas cosas a su sitio',
    },
    reward_celebration: {
      congratulations: '¡Felicidades!',
      you_have_a_reward: '¡Tienes una recompensa!',
      check_rewards_prompt: '¡Ve a la sección de recompensas para canjearla!'
    },
    notifications: {
      approved_said_yes: '¡{{admin}} dijo que sí!',
      approved_for: 'por {{task}}',
      approved_note: '{{admin}} dice: {{note}}',
      approved_now_you_have: 'Ahora tienes {{n}} estrellas',
      approved_reward_ready: '¡{{reward}} está listo!',
      approved_more_to_goal: '{{n}} estrellas más para {{reward}}',
      approved_see_reward: 'Ver mi recompensa',
      approved_not_now: 'Ahora no',
      approved_ok: '¡Bien!',
      admin_pending_verification_one: '1 tarea está esperando tu aprobación.',
      admin_pending_verification_other: '{{count}} tareas están esperando tu aprobación.',
    },
    member_chooser: {
      who_title: '¿Quién eres?',
      hi_name: '¡Hola, {{name}}!',
      enter_pin: 'Escribe tu PIN',
      log_in: 'Entrar',
      use_password: 'Usar contraseña',
      use_pin: 'Usar PIN',
      password_label: 'Contraseña',
      delete_digit: 'Borrar',
      back: 'Atrás',
      locked_until: 'Demasiados intentos. Vuelve a intentarlo a las {{time}}.',
      locked_later: 'Demasiados intentos. Inténtalo un poco más tarde.',
      wrong_pin: 'No ha funcionado. Inténtalo otra vez.',
      sign_out_device_hint: 'Borra el inicio de sesión familiar de este navegador. Úsalo si te has quedado atascado o quieres volver a iniciar sesión como adulto con Google/correo electrónico.',
      sign_out_device: 'Cerrar sesión en este dispositivo',
    },
    parent: {
      to_approve_one: '{{n}} tarea por aprobar',
      to_approve_other: '{{n}} tareas por aprobar',
      finished: '{{name}} terminó {{task}}',
      review: 'Revisar',
      nothing_to_approve: 'Nada que aprobar ahora',
      family: 'Familia',
      open_one: '{{n}} tarea por hacer',
      open_other: '{{n}} tareas por hacer',
      open_none: 'Ninguna tarea por hacer',
      set_up: 'Configurar',
      tasks: 'Tareas',
      rewards: 'Recompensas',
      members: 'Familia',
      rooms_tools: 'Habitaciones y herramientas',
      lock: 'Bloquear',
      settings: 'Ajustes',
      more_waiting: '+{{n}} más',
      your_tasks: 'Tus propias tareas',
    },
    approve: {
      title: 'Aprobar tareas',
      approve: 'Aprobar',
      not_done: 'Aún no está hecha',
      note_so_neat: '¡Qué ordenado!',
      note_well_done: '¡Bien hecho!',
      note_thank_you: '¡Gracias!',
      reason_look_again: 'Casi — míralo otra vez',
      reason_steps_missing: 'Faltan algunos pasos',
      reason_do_together: 'Hagámoslo juntos',
      pick_reason: '¿Qué le decimos a {{name}}?',
      cancel: 'Cancelar',
      steps_done_one: 'El paso está hecho',
      steps_done_other: 'Los {{n}} pasos hechos',
      approved_toast: '{{task}} aprobada. {{name}} recibe {{stars}}.',
      undo: 'Deshacer',
      sent_back_toast: '{{task}} vuelve a {{name}}.',
      today_at: 'Hoy {{time}}',
      yesterday_at: 'Ayer {{time}}',
      from_grownup: 'Un adulto dice: {{reason}}',
      back: 'Atrás',
      self_blocked: 'Otro adulto debe aprobar tu propia tarea.',
    },
    my_settings: {
      title: 'Mis ajustes',
      language: 'Idioma',
      same_as_device: 'Como este dispositivo',
      look: 'Claro u oscuro',
      light: 'Claro',
      dark: 'Oscuro',
      notifications: 'Notificaciones',
      on: 'Activadas',
      off: 'Desactivadas',
      notif_turn_on: 'Activar en este dispositivo',
      notif_turned_on: 'Las notificaciones están activadas.',
      notif_turned_off: 'Las notificaciones están desactivadas.',
      notif_parent_off: 'Un adulto ha desactivado tus notificaciones.',
      notif_parent_on: 'Un adulto quiere que tus notificaciones estén activadas.',
      notif_unsupported: 'Este dispositivo no puede mostrar notificaciones de AntNest.',
      notif_blocked: 'Las notificaciones están bloqueadas en este navegador. Pide a un adulto que las permita en los ajustes del navegador.',
      timer_sound: 'Sonido del temporizador',
      timer_sound_help: 'El sonido cuando el temporizador de un paso llega a cero. Toca para oírlo.',
    },
    parent_notifications: {
      title: 'Notificaciones de los niños',
      help: 'Elige quién decide. «Siempre activadas» significa que el niño no puede desactivarlas; «Desactivadas» significa que no se le envía nada.',
      child: 'El niño decide',
      on: 'Siempre activadas',
      off: 'Desactivadas',
      saved: 'Guardado.',
    },
    handover: {
      title: 'Recompensas por entregar',
      help: 'Se han canjeado. Entrégaselas al niño y luego toca Entregada.',
      got: '{{name}} consiguió {{reward}}',
      handed_over: 'Entregada',
      done_toast: '{{reward}} entregada a {{name}}.',
    },
    library: {
      access_denied: 'Solo los adultos pueden abrir esto.',
      title: 'Biblioteca de tareas',
      intro: 'Empieza con una tarea estándar o una plantilla que guardaste de una tarea. Solo tu familia ve «Mis plantillas».',
      age: 'Edad',
      age_group: 'Grupo de edad',
      age_all: 'Todas las edades',
      age_2_3: '2–3 años',
      age_4_5: '4–5 años',
      age_6_7: '6–7 años',
      age_8_9: '8–9 años',
      age_10_12: '10–12 años',
      age_13_plus: '13+ años',
      standard_title: 'Tareas estándar',
      none_standard: 'No hay tareas estándar para este idioma y esta edad.',
      badge_standard: 'Estándar',
      my_title: 'Mis plantillas',
      my_intro: 'Guardadas con «Guardar como plantilla» en el formulario de tarea. Solo tu familia las ve.',
      none_my: 'Aún no hay plantillas. Crea una tarea y usa «Guardar como plantilla», o cambia el filtro de edad.',
      badge_my: 'Mi plantilla',
      use: 'Usar',
      save_as_template: 'Guardar como plantilla',
      days: 'Días',
      stars: 'Estrellas',
      tool_name: 'Nombre de la herramienta',
      tool_name_ph: 'p. ej. Aspiradora',
      username_required: 'Usuario (obligatorio)',
      username_ph: 'Elige un usuario',
      pin_label: 'PIN (4–8 dígitos)',
      pin_ph: 'Escribe un PIN',
      adults: 'Adultos',
      show: 'Mostrar',
      hide: 'Ocultar',
      added_to_tasks: '«{{title}}» se añadió a vuestras tareas',
      chain_title: '{{n}} tareas seguidas',
      chain_wait_one: 'la siguiente está lista tras {{n}} hora',
      chain_wait_other: 'la siguiente está lista tras {{n}} horas',
      chain_loops: 'Luego vuelve a empezar.',
    },
    deletion: {
      delete_member: 'Eliminar miembro',
      delete_member_title: '¿Eliminar a {{name}}?',
      delete_member_text: '{{name}} sale de la familia de inmediato y se cierra su sesión en todos los dispositivos.',
      delete_member_lost: 'Se eliminarán todas las estrellas, estadísticas, tareas y recompensas de {{name}}.',
      undo_30: 'Puedes deshacerlo durante 30 días, hasta el {{date}}. Después, todo se elimina para siempre y no se puede recuperar.',
      delete_member_yes: 'Sí, eliminar',
      member_deleted_toast: '{{name}} se ha eliminado. Puedes restaurarlo durante 30 días.',
      recently_deleted: 'Eliminados recientemente',
      recently_deleted_hint: 'Puedes restaurar a estos miembros hasta la fecha indicada. Después, se eliminan para siempre junto con todos sus datos.',
      gone_on: 'Se elimina para siempre el {{date}}',
      restore: 'Restaurar',
      member_restored_toast: '{{name}} ha vuelto a la familia. Hay que volver a activar las notificaciones.',
      family_section: 'Eliminar familia',
      delete_family_row: 'Eliminar toda la familia',
      delete_family_hint: 'Todos y todo en la familia. Se puede deshacer durante 30 días.',
      delete_family_title: '¿Eliminar toda la familia?',
      delete_family_text: 'Todos en {{family}} cierran sesión al instante y no pueden usar AntNest hasta que se restaure la familia.',
      delete_family_lost: 'Se eliminará todo: todos los miembros, estrellas, estadísticas, tareas, recompensas, eventos e imágenes.',
      type_family_name: 'Para confirmar, escribe el nombre de la familia: {{family}}',
      delete_family_yes: 'Eliminar la familia',
      pending_title: '{{family}} está programada para eliminarse',
      pending_text: 'La familia y todos sus datos se eliminarán para siempre el {{date}}. Hasta entonces, puedes restaurarlo todo tal como estaba.',
      days_left: 'Días restantes: {{days}}',
      pending_not_admin: 'Solo un adulto con acceso de administrador puede restaurar la familia.',
      restore_family: 'Restaurar la familia',
      family_restored_toast: '¡Bienvenido de nuevo! La familia se ha restaurado. Hay que volver a activar las notificaciones.',
      log_out: 'Cerrar sesión',
      cancel: 'Cancelar',
    },
    pictures: {
      remove_picture: 'Quitar imagen',
      removed: 'Imagen quitada. Vuelves a tener la hormiga básica.',
      unreadable: 'No se pudo abrir la imagen. Prueba con otra o haz una foto nueva.',
      rotate: 'Girar',
      preparing: 'Preparando la imagen…',
    },
    billing: {
      title: 'Suscripción',
      trial_title: 'Prueba gratis – días restantes: {{days}}',
      trial_text: 'Tu prueba gratis termina el {{date}}. Elige un plan antes de esa fecha para seguir usando AntNest.',
      expired_title: 'Tu prueba gratis ha terminado',
      expired_text: 'Elige un plan para seguir usando AntNest. Las tareas, estrellas y recompensas de tu familia siguen aquí.',
      active_title_monthly: 'Pagas cada mes',
      active_title_yearly: 'Pagas cada año',
      active_text: 'Tu suscripción se renueva el {{date}}.',
      grace_title: 'Un pago no se ha completado',
      grace_text: 'Actualiza tus datos de pago antes del {{date}} para seguir usando AntNest.',
      cancelling_title: 'Tu suscripción está cancelada',
      cancelling_text: 'Puedes seguir usando AntNest hasta el {{date}}. Elige un plan para continuar después.',
      exempt_title: 'AntNest es gratis para tu familia',
      exempt_text: 'No tienes que pagar nada.',
      plans_title: 'Elige un plan',
      plans_intro: 'Una sola suscripción para toda la familia, en todos los dispositivos.',
      plan_monthly: 'Mensual',
      plan_yearly: 'Anual',
      price_monthly: '{{price}} al mes',
      price_yearly: '{{price}} al año',
      price_soon: 'Precio disponible pronto',
      includes_family: 'Toda la familia, en todos los dispositivos',
      includes_tasks: 'Todas las tareas y listas de verificación ya preparadas',
      includes_cancel: 'Cancela cuando quieras',
      choose_monthly: 'Elegir mensual',
      choose_yearly: 'Elegir anual',
      coming_soon: 'El pago estará disponible pronto. Hasta entonces, AntNest es gratis.',
      manage_title: 'Gestiona tu suscripción',
      manage_text: 'Cambia de plan, actualiza tu tarjeta, consulta tus recibos o cancela.',
      manage_button: 'Gestionar suscripción',
      banner_trial: 'Días para que termine tu prueba gratis: {{days}}.',
      banner_trial_last_day: 'Tu prueba gratis termina hoy.',
      banner_payment: 'Un pago no se ha completado. Actualiza tu tarjeta antes del {{date}}.',
      banner_cancelling: 'Tu suscripción termina el {{date}}.',
      banner_action: 'Ver planes',
      paywall_title: 'AntNest se está tomando un descanso',
      paywall_text: 'Pide a un adulto que elija un plan. Todas tus estrellas y tareas están a salvo y te esperan.',
      manage_failed: 'No se pudo abrir la página de suscripción. Inténtalo de nuevo en un momento.',
    },
    pocket: {
      title: 'Paga',
      intro: 'La paga se calcula a partir de las estrellas que cada niño gana por tareas aprobadas en un mes. AntNest no mueve dinero: pagáis como siempre y lo marcáis como pagado.',
      no_children: 'Añade un niño a la familia para dar paga.',
      currency: 'Moneda',
      for_child: 'Paga para {{name}}',
      how_question: '¿Cómo se gana?',
      per_ten_label: '{{currency}} por cada 10 estrellas',
      fixed_label: '{{currency}} al mes, cuando {{name}} haya ganado al menos',
      threshold_label: 'estrellas',
      threshold_help: '0 estrellas = se da siempre (paga fija).',
      bonus_label: 'Extra:',
      bonus_every_label: '{{currency}} más por cada',
      bonus_stars_label: 'estrellas más',
      cap_question: 'Límite mensual — elige uno',
      cap_none: 'Sin límite',
      cap_at: 'Como máximo',
      cap_unit: '{{currency}} al mes',
      cap_required: 'Elige un límite o sin límite antes de guardar.',
      example_title: 'Por ejemplo, en un mes con:',
      example_line_one: '{{n}} estrella → {{amount}}',
      example_line_other: '{{n}} estrellas → {{amount}}',
      capped_short: 'límite alcanzado',
      typical_dk: 'Habitual en Dinamarca al mes: menos de 100 DKK a los 6–10, 50–200 DKK a los 11–14, 200–500 DKK a los 15–17.',
      save: 'Guardar',
      saved: 'Paga de {{name}} guardada',
      save_failed: 'No se pudo guardar. Inténtalo de nuevo.',
      my_month: 'Paga de {{month}}: {{amount}}',
      my_next_one: '{{n}} estrella más da {{amount}} más',
      my_next_other: '{{n}} estrellas más dan {{amount}} más',
      my_capped: 'Has alcanzado el máximo del mes. ¡Muy bien!',
      my_last_paid: '{{month}}: {{amount}} — pagado ✓',
      my_last_unpaid: '{{month}}: {{amount}} — pendiente de pago',
      to_pay_title: 'Paga por pagar',
      to_pay_help: 'Pagad como siempre (efectivo, banco o app) y luego marcadlo como pagado.',
      mark_paid: 'Marcar como pagado',
      task_counts: 'Da paga',
      task_counts_help: 'Desmárcalo en tareas diarias como hacer la cama, para que solo cuenten las tareas más grandes.',
      typical_intro: 'Habitual en {{country}} al mes:',
      typical_band: '{{ages}} años: {{amount}}',
      typical_under: 'menos de {{amount}}',
      typical_about: 'unos {{amount}}',
      typical_source: 'fuente: {{source}}',
    },
    getting_started: {
      title: 'Para empezar',
      progress: '{{done}} de {{all}} hechos',
      hide: 'Ocultar',
      item_approve: 'Aprueba la primera tarea terminada',
      item_own_task: 'Cambia una tarea o crea la tuya',
      item_home_screen: 'Pon AntNest en la pantalla de inicio',
      item_calendar: 'Mira las tareas en el calendario',
      home_screen_how: 'iPhone/iPad: pulsa Compartir en Safari y elige «Añadir a pantalla de inicio». Android: pulsa ⋮ en Chrome y elige «Instalar aplicación» o «Añadir a pantalla de inicio». Hazlo también en los dispositivos de los niños.',
      home_screen_done: 'Ya lo he hecho',
      pocket_title: '¿Paga por las estrellas?',
      pocket_text: 'Ahora que los niños han empezado, sus estrellas también pueden dar paga: una cantidad fija, una cantidad por cada 10 estrellas y un límite mensual que decidís vosotros.',
      pocket_setup: 'Configurarlo',
      pocket_no: 'No, gracias',
    },
    editor: {
      close: 'Cerrar',
      unsaved_title: '¿Guardar los cambios?',
      unsaved_body: 'Has cambiado algo. ¿Quieres guardarlo antes de cerrar?',
      save: 'Guardar',
      discard: 'No guardar',
      keep_editing: 'Seguir editando',
    },
    timer: {
      start: 'Iniciar temporizador',
      pause: 'Pausa',
      resume: 'Continuar',
      restart: 'Empezar de nuevo',
      times_up: '¡Se acabó el tiempo!',
      sound_bell: 'Campana',
      sound_chime: 'Carillón',
      sound_beeps: 'Despertador',
      sound_marimba: 'Marimba',
      sound_fanfare: 'Fanfarria',
      volume_note: 'El sonido sigue el volumen del móvil: súbelo para oírlo.',
    },
    approval_setting: {
      title: 'Aprobación de tareas de adultos',
      label: 'Las tareas de los adultos las debe aprobar otro adulto',
      help_off: 'Desactivado: cuando un adulto termina una tarea, queda aprobada al momento y las estrellas son suyas. Las tareas de los niños siempre las aprueba un adulto.',
      help_on: 'Activado: la tarea terminada de un adulto espera a que otro adulto la apruebe (si la familia solo tiene un administrador, puede aprobar las suyas).',
      setup_help: 'Si no la marcas, las tareas de los adultos se aprueban en cuanto están hechas. Puedes cambiarlo más tarde en Ajustes.',
      saved: 'Guardado.',
    },
  },
  /** UI strings fall back to English until translated */
  fo: {
    auth: {
      forgot_title: 'Gloymt loyniorðið?',
      forgot_link: 'Gloymt loyniorðið?',
      forgot_confirmation: 'Um ein konta hoyrir til henda teldupostin, hava vit sent eina leinkju at endurstilla loyniorðið.',
      email_label: 'Teldupostur',
      send_reset_link: 'Send endurstillingarleinkju',
      reset_title: 'Set eitt nýtt loyniorð',
      new_password_label: 'Nýtt loyniorð',
      confirm_password_label: 'Vátta loyniorð',
      set_new_password: 'Set nýtt loyniorð',
      password_mismatch: 'Loyniorðini eru ikki eins',
      password_policy_msg: 'Í minsta lagi 12 tekn, við stórum bókstavi, smáum bókstavi og tali.',
      invalid_token: 'Ógildug ella útrunnin endurstillingarleinkja',
      reset_success: 'Loyniorðið er dagført. Vísa víðari til innritan...',
      reset_failed: 'Tað eydnaðist ikki at endurstilla loyniorðið. Royn aftur.',
      login_failed: 'Innritanin miseydnaðist. Royn aftur.',
      beta_banner: 'AntNest er í beta. Hevur tú ein innbjóðingarkoda?',
      beta_banner_link: 'Stovna eina konto her',
      or: 'ella',
      login_google: 'Rita inn við Google',
      login_facebook: 'Rita inn við Facebook',
      no_account: 'Hevur tú onga konto?',
      create_one: 'Stovna eina',
      err_code_required: 'Tú tørvar ein beta-koda.',
      err_agree: 'Tú mást góðtaka treytirnar og privatlívspolitikkin.',
      err_fields: 'Fyll út øll feltini.',
      err_code_invalid: 'Henda beta-kodan er ógyldug ella longu brúkt.',
      err_signup_failed: 'Kontan kundi ikki stovnast. Royn aftur.',
      signup_title: 'Stovna tína konto',
      beta_code: 'Beta-koda',
      beta_code_ph: 'Skriva tín innbjóðingarkoda',
      full_name: 'Títt navn',
      full_name_ph: 't.d. Anna',
      username: 'Brúkaranavn',
      show_password: 'Vís loyniorð',
      hide_password: 'Fjal loyniorð',
      rule_length: 'Minst 12 tekn',
      rule_upper: 'Ein stórur bókstavur',
      rule_lower: 'Ein lítil bókstavur',
      rule_digit: 'Eitt tal',
      password_match: 'Loyniorðini eru eins',
      family_name: 'Navnið á familjuni',
      family_name_ph: 't.d. Familjan Hansen',
      family_default: 'Mín familja',
      agree_before: 'Eg góðtaki',
      terms: 'treytirnar',
      and: 'og',
      privacy: 'privatlívspolitikkina',
      creating_account: 'Stovnar konto…',
      create_account: 'Stovna konto',
      signup_google: 'Stovna konto við Google',
      signup_facebook: 'Stovna konto við Facebook',
      choose_language: 'Vel mál',
      oauth_failed: 'Innritanin varð ikki liðug. Royn aftur.',
      processing: 'Ritar teg inn…',
      complete_setup: 'Næstan liðugt',
      your_name: 'Títt navn',
      complete_setup_btn: 'Stovna familju og halt fram',
      login_apple: 'Rita inn við Apple',
      signup_apple: 'Stovna konto við Apple',
    },
    rooms: {
      kitchen: 'Køkur',
      living_room: 'Stova',
      dining_room: 'Borðstova',
      bedroom: 'Sovikamar',
      bathroom: 'Baðirúm',
      toilet: 'Vesi',
      hallway: 'Gangur',
      entryway: 'Forstova',
      laundry_room: 'Vaskirúm',
      garage: 'Bilskúr',
      office: 'Skrivstova',
      playroom: 'Leikirúm',
      nursery: 'Barnakamar',
      patio: 'Pallur/Altan',
      garden: 'Urtagarður',
      storage: 'Goymsla',
      basement: 'Kjallari',
      attic: 'Loft',
      child_bedroom: 'Barnakamar',
    },
    onboarding: {
      title: 'Latum okkum byrja',
      family_name: 'Familjunavn',
      adults: 'Vaksin',
      children: 'Børn',
      member_name: 'Navn',
      username: 'Brúkaranavn',
      pin_placeholder: 'PIN (4 tøl)',
      role_admin: 'Umsitari',
      role_member: 'Limur',
      next: 'Næsta',
      back: 'Aftur',
      confirm: 'Tað sær gott út',
      processing: 'Goymi…',
      step_rooms_title: 'Set upp heimið hjá tær',
      step_rooms_help: 'Trýst á rúmini niðanfyri fyri at leggja tey afturat húsinum hjá tær. Hevur tú eitt rúm ella eitt øki, sum ikki er á listanum, kanst tú leggja tað inn sjálv/ur.\nTú kanst altíð broyta, leggja afturat og taka rúm burtur í umsitingarpartinum.',
      custom_room_placeholder: 'Egið rúmnavn',
      add_custom: 'Legg egið afturat',
      remove: 'Tak burtur',
      step_review_title: 'Yvirlit',
      review_family: 'Familja',
      review_members: 'Limir',
      review_rooms: 'Rúm',
      error_generic: 'Okkurt fór skeivt, meðan goymt varð',
      step_members_help: 'Legg familjulimirnar inn her.\nGev hvørjum familjulimi eitt navn og eitt brúkaranavn.\nHvør familjulimur má hava eina PIN-kodu fyri at kunna rita inn.',
      validation_incomplete: 'Nøkur felt eru ikki rætt útfylt',
      adult_label: 'Vaksin',
      child_label: 'Barn',
      privacy_note: 'Upplýsingarnar verða bert brúktar til at seta familjuna upp. Vit deila ella selja ikki – og fara aldri at deila ella selja – tykkara dátur ella persónupplýsingar til nakran.',
      child_age: 'Aldur',
      child_room_name: 'Kamarið hjá {{name}}',
      child_room_name_s: 'Kamarið hjá {{name}}',
      hint_members: 'Tú kanst altíð leggja afturat ella broyta familjulimir seinni undir Admin → Limir.',
      hint_rooms: 'Hvørt barn fær sítt egna kamar, so teirra egnu uppgávur koma á rætta stað. Tú kanst altíð broyta rúmini seinni undir Admin → Rúm.',
      step_tasks_title: 'Uppgávur at koma í gongd',
      step_tasks_help: 'Vit hava valt nakrar fáar uppgávur, ið hóska til tykkara rúm og aldurin hjá børnunum. Takið flugubeinið burtur við teimum, tit ikki vilja hava – tit kunnu broyta alt seinni.',
      starters_unavailable: 'Uppskotini kundu ikki heintast. Tú kanst leypa um og leggja uppgávur afturat seinni.',
      tasks_for_child: 'Uppgávurnar hjá {{name}}',
      tasks_shared: 'Fyri øll (øll kunnu taka tær)',
      whole_home: 'Alt heimið',
      hint_tasks: 'Nógv fleiri standarduppgávur eru undir Admin → Uppgávur, og har kunnu tit eisini gera tykkara egnu.',
      skip_step: 'Leyp um',
      step_rewards_title: 'Lønir at savna til',
      step_rewards_help: 'Børn halda áhuganum, tá ið fyrsta lønin er innan rokkar. Hesar eru settar soleiðis, at tann fyrsta kann rokkast upp á fáum døgum.',
      rewards_for_child: 'Til {{name}}',
      rewards_family: 'Fyri alla familjuna (øll stjørnur telja)',
      hint_rewards: 'Tit kunnu altíð leggja tykkara egnu lønir afturat ella broyta hesar undir Admin → Lønir.',
      review_starters: 'At koma í gongd',
      review_tasks_one: '{{n}} uppgáva',
      review_tasks_other: '{{n}} uppgávur',
      review_rewards_one: '{{n}} løn',
      review_rewards_other: '{{n}} lønir',
      create_family: 'Stovna okkara familju',
      done_title: 'Familjan er klár!',
      done_made: 'Vit hava gjørt {{tasks}} uppgávur og {{rewards}} lønir til tykkum.',
      done_try: 'Royn fyrstu uppgávuna nú: skift til eitt barn við "{{switch}}" ovast, og trýst "{{start}}" á eina uppgávu.',
      done_admin: 'Tá ið ein uppgáva er gjørd, góðkennir tú hana undir "{{approve}}" – so verða stjørnurnar lagdar afturat.',
      go_home: 'Far til forsíðuna hjá familjuni',
      go_admin: 'Far til Admin',
    },
    common: {
      save: 'Goym',
      take_photo: 'Tak mynd',
      cancel: 'Avlýs',
      ok: 'Í lagi',
      loading: 'Innlesur...',
      all: 'Alt',
      remove_item: 'Tak punkt burtur',
      remove_image: 'Tak mynd burtur',
      error_generic: 'Okkurt gekk galið. Royn aftur.',
      you: 'Tú',
      stars_one: '{{n}} stjørna',
      stars_other: '{{n}} stjørnur',
      update_ready: 'Ein nýggj útgáva av AntNest er klár.',
      update_now: 'Dagfør',
    },
    header: {
      stats: 'Hagtøl',
      app_title: 'AntNest',
      home: 'Heim',
      tasks: 'Uppgávur',
      calendar: 'Kalendari',
      rewards: 'Vinningar',
      settings: 'Stillingar',
      profile: 'Vangi',
      admin: 'Umsiting',
      logout: 'Rita út',
      points: 'stig',
      settings_tooltip: 'Stillingar',
      menu_open: 'Lat valmyndina upp',
      menu_close: 'Lat valmyndina aftur',
      nav_menu: 'Valmynd',
      tab_bar_label: 'Høvuðspartar',
      switch_user: 'Skift brúkara',
      master_admin: 'Høvuðsumsitari',
      change_picture: 'Broyt mína mynd',
      someone_else: 'Ein annar hevur tørn',
      for_grownups: 'Fyri vaksin',
      for_grownups_hint: 'Stillingar, góðkenningar, rita út',
      my_page: 'Mín síða',
    },
    stats: {
      title: 'Hagtøl',
      tab_me: 'Eg',
      tab_family: 'Familja',
      tab_tasks: 'Uppgávur',
      period_week: 'Vika',
      period_month: 'Mánaður',
      period_year: 'Ár',
      period_all: 'Altíð',
      prev: 'Fyrra',
      next: 'Næsta',
      all_time: 'Altíð',
      tasks_done: 'uppgávur gjørdar',
      points: 'stig',
      rewards: 'løn',
      chart_points: 'Stig yvir tíð',
      where_helped: 'Har eg hjálpti',
      no_room: 'Einki rúm',
      my_top_tasks: 'Mínar oftast gjørdu uppgávur',
      together: 'Saman',
      who_helped: 'Hvør hjálpti',
      everyone_over_time: 'Øll yvir tíð',
      repeating_title: 'Uppgávur',
      per_task_title: 'Hvør uppgáva',
      times_done_axis: 'Ferðir gjørd',
      times: '{n}×',
      last_done: 'seinast: {date}',
      empty: 'Einki her enn í hesum tíðarskeiði.',
      family_hidden: 'Familjan hjá tykkum vísir hetta bara fyri vaksnum.',
      week_short: 'Vika {n}',
      enc_start: 'Ein nýggj byrjan — hvør uppgáva teljur! 💪',
      enc_first: 'Tín fyrsta uppgáva — væl gjørt! 🎉',
      enc_done: 'Tú hevur gjørt {n} uppgávur — flott! ⭐',
      enc_more: 'Fleiri stig enn tíðarskeiðið áðrenn — halt fram! 🚀',
      visibility_title: 'Hagtøl: hvat børn síggja',
      visibility_help: 'Vel, hvussu Familja-teigurin sær út fyri børn. Vaksnir umsitarar síggja altíð alt.',
      visibility_team: 'Liðsvísing — partur hjá øllum, eingin raðfesting (mælt til)',
      visibility_full: 'Full samanbering — raðfest eftir stigum',
      visibility_admins: 'Fjalt — bara vaksnir síggja familjusamanberingina',
      visibility_saved: 'Goymt',
      team_view_note: 'Partur hjá øllum av samlaðu stigunum hjá familjuni',
    },
    calendar: {
      title: 'Kalendari',
      coming_soon: 'Kalendarin verður bygdur. Vanlig sýni og hendingar koma her.',
      view_day: 'Dagur',
      view_week: 'Vika',
      view_month: 'Mánaður',
      week_label: 'Vika',
      no_events_for_day: 'Ongar hendingar',
      today_label: 'í dag',
      prev_day: 'Fyrri dagur',
      next_day: 'Næsti dagur',
      prev_week: 'Fyrra vika',
      next_week: 'Næsta vika',
      prev_month: 'Fyrri mánaður',
      next_month: 'Næsti mánaður',
      go_to_today: 'Í dag',
      not_allowed_edit_event: 'Tú kanst bara broyta tilburðir, ið tú sjálv/ur hevur stovnað.',
      filters_title: 'Filtrera eftir limi',
      single_view_button: 'Einstakt sýni',
      family_view_button: 'Familjusýni',
      no_events_for_range: 'Ongar hendingar í hesum tíðarbilinum',
      no_events: 'Ongar hendingar í hesum tíðarskeiðinum.',
      add_event: 'Legg tilburð afturat',
      edit_event: 'Broyt hending',
      event_form_title: 'Heiti',
      event_form_description: 'Lýsing (valfrítt)',
      event_form_location: 'Staður (valfrítt)',
      event_form_start: 'Byrjan',
      event_form_end: 'Endi',
      event_form_all_day: 'Allan dagin',
      event_form_attendees: 'Luttakarar',
      event_form_save: 'Goym',
      event_form_cancel: 'Avlýs',
      event_form_delete: 'Strika',
      delete_confirm_title: 'Strika hending',
      delete_confirm_message: 'Ert tú vís/ur í, at tú vilt strika hesa hendingina?',
      success_created: 'Hending stovnað',
      success_updated: 'Hending dagførd',
      success_deleted: 'Hending strikað',
      error_generic: 'Okkurt fór skeivt',
      validation_title_required: 'Heiti skal útfyllast',
      validation_end_after_start: 'Endin má vera aftaná byrjanina',
      event_form_image: 'Hendingarmynd (valfrítt)',
      remove_image: 'Tak mynd burtur',
      create_calendar_event: 'Stovna kalendarahending',
      event_modal_title_create: 'Stovna kalendarahending',
      event_modal_title_edit_prefix: 'Broyt',
      reminder_label: 'Áminning',
      reminder_none: 'Eingin áminning',
      reminder_10m: '10 minuttir frammanundan',
      reminder_30m: '30 minuttir frammanundan',
      reminder_1h: '1 tíma frammanundan',
      reminder_12h: '12 tímar frammanundan',
      reminder_1d: '1 dag frammanundan',
      nothing_planned: 'Einki ætlað enn',
      nothing_this_month: 'Einki ætlað hendan mánaðin',
      today_day: 'Í dag, {{day}}',
      tomorrow_day: 'Í morgin, {{day}}',
      at_time: 'Kl. {{time}}',
      all_day_label: 'Allan dagin',
      with_names: 'við {{names}}',
      task_label: 'Uppgáva',
      everyone: 'Øll',
      back: 'Aftur',
      due_today: 'Skal gerast í dag',
      due_today_at: 'Skal gerast í dag áðrenn {{time}}',
      due_tomorrow: 'Skal gerast seinast í morgin',
      due_tomorrow_at: 'Skal gerast í morgin áðrenn {{time}}',
      due_later: 'Skal gerast seinast hendan dagin',
      due_later_at: 'Skal gerast áðrenn {{time}}',
      overdue_since: 'Seinkað síðan {{day}}',
      planned: 'Ætlað',
    },
    settings: {
      title: 'Stillingar',
      subtitle: 'Umsit kontuna og innstillingarnar í appini',
      dark_mode_section: 'Myrk støða',
      current_theme: 'Núverandi tema',
      following_system: 'Fylgir stillingunum í skipanini',
      manual_selection: 'Manuelt val',
      follow_system_label: 'Fylg stillingunum í skipanini',
      auto_switch_hint: 'Skiftir sjálvvirkandi millum ljósa og myrka støðu eftir stillingunum í stýrisskipanini hjá tær',
      enable_dark_mode: 'Tendra myrka støðu',
      manual_toggle_hint: 'Skift sjálv/ur millum ljóst og myrkt tema',
      light_mode: 'Ljós støða',
      dark_mode: 'Myrk støða',
      more_settings_title: 'Fleiri stillingar koma skjótt',
      more_settings_desc: 'Fleiri møguleikar at tillaga koma her, harímillum stillingar fyri fráboðanir, privatlív og umsiting av kontuni.',
      language_section: 'Mál',
      language_hint: 'Vel tað málið, tú vilt hava í appini',
      back: 'Aftur',
      profile_parent: 'Foreldur. Broyt navn, mynd og loyniorð',
      profile_member: 'Broyt navn, mynd og PIN',
      look: 'Útsjónd',
      auto: 'Sjálvvirkið',
      tell_me_when: 'Sig mær frá, tá ið',
      notif_this_device: 'Fráboðanir á hesum tólinum',
      notif_blocked_parent: 'Forðað í hesum kaganum. Loyv fráboðanum fyri hesa síðuna í stillingunum í kaganum.',
      n_review_ready: 'Ein uppgáva skal góðkennast',
      n_task_approved: 'Mín uppgáva er góðkend',
      n_reward_ready: 'Ein løn er klár',
      n_due_soon: 'Ein av mínum uppgávum skal skjótt vera liðug',
      n_overdue: 'Ein av mínum uppgávum er seinkað',
      n_new_task: 'Ein nýggj uppgáva er leys',
      n_event: 'Áminningar úr kalendaranum',
      daily_summary: 'Dagligt yvirlit',
      daily_summary_time: 'Tíð fyri dagliga yvirlitið',
      quiet_hours: 'Kyrrir tímar',
      quiet_hours_hint: 'Ongar fráboðanir millum hesar tíðir',
      quiet_from: 'Kyrt frá',
      quiet_to: 'Kyrt til',
      this_device: 'Hetta tólið',
      change_pin: 'Broyt mítt PIN',
      switch_person: 'Skift persón',
      refresh_session: 'Dagfør hetta tólið',
      session_refreshed: 'Hetta tólið er dagført.',
      locked_until: 'Læst til {{time}}',
      unlock: 'Lat upp',
      unlocked: '@{{name}} kann rita inn aftur.',
      log_out_device: 'Rita út av hesum tólinum',
      logout_confirm_title: 'Rita út av hesum tólinum?',
      logout_confirm_text: 'Øll familjan {{family}} verður ritað út her. Ein vaksin má rita inn aftur, áðrenn nakar kann brúka AntNest á hesum tólinum.',
      logout_confirm_yes: 'Rita út',
      cancel: 'Angra',
      n_reward_claimed: 'Ein løn verður heintað',
    },
    rewards: {
      title: 'Vinningar',
      subtitle: 'Fylg við framgongdini og tak vinningarnar tínar',
      your_points: 'Stigini tíni',
      family_points: 'Familjustig',
      claimed_rewards: 'Tiknir vinningar',
      your_coins: 'Myntirnar tínar',
      individual_rewards: 'Persónligir vinningar',
      no_individual_rewards: 'Eingir persónligir vinningar eru tøkir til tín enn.',
      family_rewards: 'Familjuvinningar',
      no_family_rewards: 'Eingir familjuvinningar eru tøkir enn.',
      goal_reached: 'Málið er nátt!',
      complete_suffix: 'liðugt',
      target_whole_family: 'Øll familjan',
      claim_reward_button: 'Tak vinningin!',
      family_claim_parent_only: 'Bara eitt av foreldrunum kann taka familjuvinningar',
      claimed_rewards_section: 'Tiknir vinningar',
      no_claimed_rewards: 'Eingin vinningur tikin enn. Halt fram við uppgávunum!',
      claimed_on: 'Tikin tann',
      unknown_date: 'Ókend dagfesting',
      unknown_member: 'Ókendur limur',
      reward_header_alt: 'Yvirmynd fyri vinning',
      show_more: 'Vís fleiri',
      show_fewer: 'Vís færri',
      points_abbrev: 'stig',
      need_more_prefix: 'Vantar',
      rewards_to_claim_heading: 'Vinningar at taka',
      more_stars_for_prefix: '{{count}} stjørnur afturat til',
      progress_counts_from: 'Framgongdin roknar stig, sum eru vunnin {{date}} ella seinni.',
      lifetime_total_hint: 'Samlað í alt (skilt frá tíðarskeiðunum fyri vinningsframgongd).',
      your_stars_label: 'tínar stjørnur',
      ready_to_get: 'Klárt at fáa',
      keep_saving: 'Spar víðari til',
      n_more: '{{n}} aftrat',
      get_it: 'Fá hana',
      grownup_gets: 'Ein vaksin fær hesa',
      got_one: '{{n}} vinning, tú hevur fingið',
      got_other: '{{n}} vinningar, tú hevur fingið',
      see_all: 'Sí øll',
      counting_since: 'Telur stjørnur frá {{date}}',
      nothing_yet: 'Ongar vinningar enn. Bið ein vaksnan um at leggja nakrar afturat!',
      claim_failed: 'Ikki møguligt at fáa vinningin. Royn aftur.',
    },
    dashboard: {
      welcome_back: 'Vælkomin aftur',
      subheading: 'Her er tað, ið hendir í familjuni í dag',
      leaderboard_title: 'Stigatalva familjunnar',
      family_member: 'Familjulimur',
      tasks_title: 'Uppgávur',
      available_tasks: 'Tøkar uppgávur',
      no_available_tasks: 'Ongar uppgávur eru tøkar í løtuni!',
      my_active_tasks: 'Mínar virknu uppgávur',
      no_my_tasks: 'Tú hevur ongar virknar uppgávur!',
      recently_completed_tasks: 'Nýliga loknar uppgávur',
      completed_tasks: 'Loknar uppgávur',
      no_completed_tasks: 'Ongar loknar uppgávur enn!',
      manage_all_tasks: 'Umsit allar uppgávur',
      my_rewards_title: 'Mínir vinningar',
      my_points: 'Mín stig',
      available_rewards: 'Tøkir vinningar',
      claimed_rewards: 'Tiknir vinningar',
      next_reward_goal: 'Næsta vinningsmál',
      all_rewards_claimed: 'Allir vinningar eru tiknir! Væl gjørt!',
      reward_ready: 'Klárt at taka!',
      showing_rewards_help: 'Vísir: Tínar persónligu vinningar og vinningar fyri alla familjuna',
      claim_now: 'Tak nú!',
      locked: 'Læst',
      go_to_rewards: 'Far til vinningar',
      my_tasks_primary_title: 'Mínar uppgávur',
      my_tasks_home_hint: 'Hetta eru bara uppgávur, sum eru givnar tær. Opna Uppgávur fyri at velja nýggjar.',
      due_overdue: 'Yvir tíðina',
      due_today: 'Freistin er í dag',
      due_no_deadline: 'Eingin freist',
      tasks_heading_possessive: 'Uppgávurnar hjá {{name}}',
      missions_banner: 'Tú hevur {{count}} uppgávur klárar í dag. Avstað!',
      missions_banner_empty: 'Ongar uppgávur bíða beint nú — væl gjørt!',
      available_tasks_banner_one: 'Tú kanst vinna fleiri stig! Ein uppgáva er tøk.',
      available_tasks_banner_other: 'Tú kanst vinna fleiri stig! {{count}} uppgávur eru tøkar.',
      find_tasks_cta: 'Finn eina uppgávu',
      missions_heading: 'Uppgávur í dag',
      mission_cta: 'Avstað!',
      hi_name: 'Hey {{name}}',
      goal_of: '{{have}} av {{need}} stjørnum',
      goal_get_it: 'Fá hana',
      goal_none: 'Ongar vinningar enn. Bið ein vaksnan um at leggja eina afturat!',
      today: 'Í dag',
      no_tasks_now: 'Ongar uppgávur júst nú',
      waiting_one: '{{n}} stjørna, tá ið ein vaksin sigur ja',
      waiting_other: '{{n}} stjørnur, tá ið ein vaksin sigur ja',
      family_goal: 'Familjumál',
      family_goal_more: 'Øll stjørnur telja. {{n}} aftrat til {{reward}}.',
      family_goal_ready: '{{reward}} er klárt! Ein vaksin kann fáa tað til familjuna.',
      due_tomorrow: 'Í morgin',
      due_in_days_one: 'Um {{n}} dag',
      due_in_days_other: 'Um {{n}} dagar',
      more_stars_title: 'Vilt tú hava fleiri stjørnur?',
      more_stars_hint: 'Hesar kunnu øll taka. Vel eina, tá ið tú hevur hug!',
      see_all_can_take: 'Sí øll {{n}}',
      take_failed: 'Kundi ikki taka uppgávuna',
    },
    task_board: {
      title: 'Uppgávutalva',
      subtitle: 'Umsit og fylg við øllum uppgávum í familjuni',
      available_tasks: 'Tøkar uppgávur',
      no_available_tasks: 'Ongar uppgávur eru tøkar í løtuni!',
      my_active_tasks: 'Mínar virknu uppgávur',
      no_my_tasks: 'Tú hevur ongar virknar uppgávur!',
      view: 'Vís',
      complete: 'Ger liðugt',
      awaiting_verification: 'Bíðar eftir góðkenning',
      awaiting_verification_pill: 'Bíðar eftir góðkenning',
      tasks_assigned_to_others: 'Uppgávur givnar øðrum',
      no_tasks_assigned_to_others: 'Ongar uppgávur eru givnar øðrum brúkarum!',
      view_task: 'Vís uppgávu',
      completed_tasks: 'Loknar uppgávur',
      no_completed_tasks: 'Ongar loknar uppgávur enn!',
      take_task: 'Hana taki eg',
      take_over_task: 'Yvirtak uppgávuna',
      unclaim_task: 'Lat hana aftur',
      your_tasks: 'Tínar uppgávur',
      can_take: 'Hesar kanst tú taka',
      waiting_heading: 'Bíðar eftir einum vaksnum',
      others_tasks: 'Uppgávur hjá øðrum',
      done_one: '{{n}} uppgáva gjørd',
      done_other: '{{n}} uppgávur gjørdar',
      moved_to_yours: '{{task}} er nú undir Tínar uppgávur',
      nothing_to_take: 'Ongar uppgávur at taka júst nú',
      no_your_tasks: 'Tú hevur ongar uppgávur júst nú.',
    },
    login: {
      title: 'AntNest',
      email_label: 'Teldupostur',
      password_label: 'Loyniorð',
      logging_in: 'Ritar inn...',
      login_button: 'Rita inn',
      demo_account: 'Vísingarkonta',
      demo_email: 'Teldupostur: admin@family.com',
      demo_password: 'Loyniorð: admin'
    },
    member_chooser: {
      title: 'Vel lim',
      sign_out_device: 'Rita út av hesi eindini',
      sign_out_device_hint: 'Reinsar familjuinnritanina í hesum kagara. Brúka hetta, um tú ert fastur ella vilt rita inn sum verji við Google/telduposti aftur.',
      who_title: 'Hvør ert tú?',
      hi_name: 'Hey {{name}}!',
      enter_pin: 'Skriva PIN-koduna',
      log_in: 'Rita inn',
      use_password: 'Brúka loyniorð í staðin',
      use_pin: 'Brúka PIN í staðin',
      password_label: 'Loyniorð',
      delete_digit: 'Strika',
      back: 'Aftur',
      locked_until: 'Ov nógvar royndir. Royn aftur kl. {{time}}.',
      locked_later: 'Ov nógvar royndir. Royn aftur eitt sindur seinni.',
      wrong_pin: 'Tað riggaði ikki. Royn aftur.',
    },
    user_profile: {
      user_not_found: 'Brúkarin varð ikki funnin.',
      back_to_home: 'Aftur til heimasíðuna',
      title: 'Brúkaravangi',
      profile_information: 'Vangaupplýsingar',
      name: 'Navn',
      email: 'Teldupostur',
      email_not_set: 'Eingin teldupostur skrásettur',
      role: 'Leiklutur',
      admin: 'Umsitari',
      family_member: 'Familjulimur',
      points: 'Stig',
      coins: 'Myntir',
      edit_profile: 'Broyt vanga',
      save_changes: 'Goym broytingar',
      cancel: 'Avlýs',
      email_optional_label: 'Teldupostur (valfrítt)',
      new_password_optional_label: 'Nýtt loyniorð (valfrítt)',
      name_placeholder: 'Skriva navn títt',
      email_enter_placeholder: 'Skriva teldupostadressu',
      email_add_placeholder: 'Legg teldupost afturat',
      username_for_member_login: 'Brúkaranavn (til innritan hjá limi)',
      pin_label: 'PIN (4–8 tøl)',
      avatar: 'Vangamynd',
      avatar_hint: 'Trýst á eina av myndunum ella legg tína egnu upp.',
      upload: 'Legg upp',
      avatar_updated: 'Vangamyndin er dagførd',
      avatar_update_failed: 'Tað eydnaðist ikki at dagføra vangamyndina',
      linked_accounts: 'Tilknýttar sosialar kontur',
      no_linked_accounts: 'Ongar tilknýttar kontur.',
      unlink: 'Loys knýtið',
      unlink_failed: 'Tað eydnaðist ikki at loysa knýtið',
      unlink_confirm: 'Loysa knýtið til {{provider}}?',
      account_unlinked: 'Kontan er loyst.',
      link_to_google: 'Knýt til Google',
      change_password: 'Broyt loyniorð',
      current_password: 'Núverandi loyniorð',
      new_password: 'Nýtt loyniorð',
      password_changed: 'Loyniorðið er broytt',
      password_change_failed: 'Tað eydnaðist ikki at broyta loyniorðið',
      save_new_password: 'Goym nýtt loyniorð',
      pin_validation: 'PIN skal vera 4–8 tøl (bara tøl)',
      current_pin_label: 'Núverandi PIN (bara um tú longu hevur eitt)',
      current_pin_required: 'Skriva títt núverandi PIN fyri at broyta tað.',
      current_pin_incorrect: 'Títt núverandi PIN er skeivt.',
      email_managed_by_login: 'Teldupostur tín kemur frá, hvussu tú ritar inn, og kann ikki broytast her.',
      username_validation: 'Brúkaranavnið skal vera í minsta lagi 2 tekn',
      profile_updated: 'Vangin er dagførdur',
      google_link_success: 'Google-kontan er knýtt til',
      picture_type_bad: 'Vel eina mynd í PNG, JPG ella WebP.',
      picture_too_big: 'Myndin er ov stór (í mesta lagi 1 MB).',
      choose_picture: 'Vel hesa myndina',
      or_pick_one: 'Ella vel eina av hesum',
      tap_picture: 'Trýst á myndina fyri at velja eina nýggja.',
      facebook_link_success: 'Facebook-kontan er knýtt til',
      link_to_facebook: 'Knýt til Facebook',
      link_to_apple: 'Knýt til Apple',
    },
    admin: {
      header_title: 'Umsitingarpanel',
      header_subtitle: 'Umsit uppgávur, vinningar og limir í familjuni',
      tab_tasks: 'Uppgávur',
      tab_rooms: 'Rúm',
      tab_members: 'Limir',
      tab_pending: 'Bíðar',
      tab_rewards: 'Vinningar',
      tab_tools: 'Amboð'
    },
    checklist_editor: {
      back_to_admin: 'Aftur til umsitingarpanelið',
      edit_checklist_for: 'Broyt kanningarlista',
      add_new_item: 'Legg nýtt punkt afturat',
      add_item: 'Legg punkt afturat',
      current_items: 'Núverandi punkt á kanningarlistanum',
      no_items: 'Eingi punkt á kanningarlistanum enn. Legg nøkur afturat omanfyri!',
      cancel: 'Avlýs',
      save: 'Goym'
    },
    task_details: {
      loading: 'Innlesur...',
      title: 'Um uppgávuna',
      room: 'Rúm',
      points: 'Stig',
      status: 'Støða',
      status_completed: 'Liðug',
      status_active: 'Virkin',
      assigned_to: 'Givin',
      due_date: 'Freist',
      tools_required: 'Neyðug amboð',
      checklist: 'Kanningarlisti',
      task_completed: 'Uppgávan er liðug',
      completed_by: 'Gjørd av',
      message: 'Boð',
      submit_for_verification: 'Send til góðkenningar',
      close: 'Lat aftur',
      preview_title: 'Forskoða uppgávu',
      preview_room_placeholder: 'Verður uppskotið, tá tú brúkar hesa uppgávuna',
      preview_checklist_hint: 'Steg (bara forskoðan — kroyssa av, tá tú hevur lagt uppgávuna afturat)',
      view: 'Vís'
    },
    task_flow: {
      start_task: 'Byrja',
      tools_ready_title: 'Hevur tú alt, tú tørvar?',
      tools_ready_subtitle: 'Tú fert at brúka hesi amboðini. Finn tey fram, áðrenn tú heldur fram.',
      ready_to_start_title: 'Klár/ur?',
      ready_to_start_body: 'Ert tú klár/ur at byrja uppgávuna?',
      tools_ready_no: 'Ikki enn',
      tools_ready_yes: 'Ja, avstað!',
      finish_step: 'Tað havi eg gjørt',
      final_put_tools_message: 'Væl gjørt! Set øll amboðini aftur á sín stað. Trýst so á knøttin niðanfyri fyri at gera uppgávuna liðuga.',
      complete_task_button: 'Liðugt',
      no_checklist_warning: 'Hendan uppgávan hevur eingi stig enn. Bið eitt av foreldrunum leggja stig á kanningarlistan, áðrenn tú kanst byrja.',
      view_checklist_readonly_hint: 'Tú kanst kroyssa stigini av, tá tú hevur trýst á Byrja uppgávuna.',
      progress_label: 'Framgongd',
      step_counter_prefix: 'Stig',
      not_assigned: 'Enn ikki givin nøkrum',
      mission_label: 'Uppdrag',
      celebrate_heading: 'Frálíkt gjørt!',
      celebrate_subtitle: 'Tú gjørdi {{title}} liðuga',
      celebrate_note: 'Væl gjørt — halt fram soleiðis!',
      celebrate_button: 'Aftur til forsíðuna',
      step_of: 'Stig {{n}} av {{total}}',
      back_step: 'Far eitt stig aftur',
      tidy_title: 'Næstan liðugt!',
      tidy_subtitle: 'Legg lutirnar aftur, har tær hoyra heima',
      tidy_no_tools: 'Er alt liðugt? Trýst so á Liðugt.',
      back_to_steps: 'Aftur til stigini',
      celebrate_stars_title: '{{n}} stjørnur eru á veg',
      celebrate_stars_body: 'Tú fært tær, tá ið ein vaksin sigur ja',
      see_details: 'Sí smálutir',
      step_tools: 'Tú hevur brúk fyri:',
      where_room: 'Hvar: {{room}}',
      celebrate_stars_got_title: 'Tú fekst {{n}} stjørnur',
      celebrate_stars_got_body: 'Góðkent og lagt afturat tínum stjørnum',
      tidy_put_all_back: 'Legg allar hesar lutirnar aftur, har tær hoyra til',
    },
    reward_celebration: {
      congratulations: 'Til lukku!',
      you_have_a_reward: 'Tú hevur fingið ein vinning!',
      check_rewards_prompt: 'Far inn undir vinningar og tak hann!'
    },
    notifications: {
      task_verified_heading: 'Væl gjørt!',
      task_verified_message: '{{task}} varð góðkend av {{admin}}. Tú hevur fingið {{points}} stig!',
      task_verified_message_zero_points: '{{task}} varð góðkend av {{admin}}.',
      dismiss_ok: 'Í lagi',
      dismiss_aria: 'Lat aftur',
      admin_pending_verification_one: '1 uppgáva bíðar eftir góðkenning hjá tær.',
      admin_pending_verification_other: '{{count}} uppgávur bíða eftir góðkenning hjá tær.',
      approved_said_yes: '{{admin}} segði ja!',
      approved_for: 'fyri {{task}}',
      approved_note: '{{admin}} sigur: {{note}}',
      approved_now_you_have: 'Nú hevur tú {{n}} stjørnur',
      approved_reward_ready: '{{reward}} er klárt!',
      approved_more_to_goal: '{{n}} stjørnur aftrat til {{reward}}',
      approved_see_reward: 'Sí mína vinning',
      approved_not_now: 'Ikki nú',
      approved_ok: 'Hurra!',
    },
    admin_panel: {
      header_title: 'Umsitingarpanel',
      header_subtitle: 'Umsit uppgávur, vinningar og limir í familjuni',
      tab_tasks: 'Uppgávur',
      tab_rooms: 'Rúm',
      tab_members: 'Limir',
      tab_pending: 'Bíðar',
      tab_rewards: 'Vinningar',
      tab_tools: 'Amboð',
      task_management: 'Uppgávuumsiting',
      create_new_task: 'Stovna nýggja uppgávu',
      active_tasks: 'Virknar uppgávur',
      active_tasks_help: 'Uppgávur, sum eru í gongd ella tøkar',
      edit: 'Broyt',
      remove: 'Tak burtur',
      mark_as_incomplete: 'Merk sum ikki liðuga',
      confirm_mark_incomplete_title: 'Ert tú vís/ur í, at tú vilt merkja hesa uppgávuna sum ikki liðuga',
      cancel: 'Avlýs',
      confirm_yes_im_sure: 'Ja, eg eri vís/ur',
      no_active_tasks: 'Ongar virknar uppgávur funnar.',
      completed_tasks: 'Loknar uppgávur',
      completed_tasks_help: 'Uppgávur, sum eru gjørdar og góðkendar',
      no_completed_tasks: 'Ongar loknar uppgávur funnar.',
      room_management: 'Rúmumsiting',
      add_new_room: 'Legg nýtt rúm afturat',
      member_management: 'Limaumsiting',
      add_new_member: 'Legg nýggjan lim afturat',
      pending_verification: 'Uppgávur, ið bíða eftir góðkenning',
      verify_this_task: 'Góðkenn hesa uppgávuna',
      self_verify_not_allowed_hint: 'Ein annar umsitari má góðkenna hesa uppgávuna, tá fleiri umsitarar eru.',
      points_not_awarded_section: 'Góðkent — stig vanta',
      points_not_awarded_help: 'Hesar uppgávurnar vórðu merktar sum góðkendar, áðrenn stigini vórðu goymd rætt. Brúka “Gev stig” eina ferð fyri at skriva stigini til tann limin, sum gjørdi uppgávuna.',
      award_missing_points: 'Gev stig',
      no_tasks_needing_points: 'Allar góðkendar uppgávur hava stig skrásett.',
      verify_success: 'Uppgávan er góðkend. Stigini eru givin.',
      verify_repaired_points_success: 'Stigini, sum vantaðu, eru nú givin liminum, sum gjørdi hesa uppgávuna.',
      verify_already_done: 'Hendan uppgávan er longu góðkend, og stigini eru skrásett.',
      verify_misattribution_credit_confirm: 'Skipanin vísir, at hendan uppgávan longu er goldin út. Um støðan hjá liminum ongantíð økaðist, er tað í lagi at leggja stigini afturat eina einstaka ferð nú? (Brúka ikki hetta, um stigini longu eru fingin.)',
      verify_misattribution_success: 'Einstøk ískoyting er gjørd. Bið limin dagføra síðuna ella rita inn av nýggjum fyri at síggja dagførd stig.',
      verify_misattribution_already_done: 'Ein einstøk rætting av feilskrásetingini er longu gjørd fyri hesa uppgávuna.',
      verify_already_done_hint: 'Hevur limurin framvegis eingi stig, trýst á Gev stig aftur og vátta einstøku ískoytingina, ella tøm points_awarded_on_verification fyri hesa uppgávuna í dátugrunninum og royn aftur.',
      no_pending_verify: 'Ongar uppgávur bíða eftir góðkenning.',
      reward_management: 'Vinningsumsiting',
      create_new_reward: 'Stovna nýggjan vinning',
      claimed: 'Tikin',
      available: 'Tøkur',
      tool_management: 'Amboðsumsiting',
      create_new_tool: 'Stovna nýtt amboð',
      no_tools_msg: 'Eingi amboð eru stovnað enn. Stovna títt fyrsta amboð fyri at koma í gongd!',
      task_modal_title_create: 'Stovna nýggja uppgávu',
      task_modal_title_edit_prefix: 'Broyt uppgávu',
      reward_modal_title_create: 'Stovna nýggjan vinning',
      reward_modal_title_edit_prefix: 'Broyt vinning',
      tool_modal_title_create: 'Stovna nýtt amboð',
      tool_modal_title_edit_prefix: 'Broyt amboð',
      room_modal_title_add: 'Legg nýtt rúm afturat',
      room_modal_title_edit: 'Broyt rúm',
      delete_room_title: 'Strika rúm',
      delete_room_btn: 'Strika',
      delete_btn: 'Strika',
      delete_tool_title: 'Strika amboð',
      delete_reward_title: 'Strika vinning',
      delete_confirm_prefix: 'Ert tú vís/ur í, at tú vilt strika',
      delete_room_prompt_prefix: 'Ert tú vís/ur í, at tú vilt strika fyri altíð rúmið',
      delete_room_blocked_message: 'Hetta rúmið kann ikki strikast, tí ein ella fleiri uppgávur brúka tað. Strika ella flyt hesar uppgávurnar fyrst.',
      go_to_tasks_btn: 'Vís uppgávur',
      member_modal_title_add: 'Legg nýggjan familjulim afturat',
      member_edit_title: 'Broyt familjulim',
      cancel_btn: 'Avlýs',
      save_changes_btn: 'Goym broytingar',
      create_task_btn: 'Stovna uppgávu',
      create_reward_btn: 'Stovna vinning',
      create_tool_btn: 'Stovna amboð',
      add_room_btn: 'Legg rúm afturat',
      add_member_btn: 'Legg lim afturat',
      remove_image_btn: 'Tak mynd burtur',
      remove_item_btn: 'Tak punkt burtur',
      remove_task_btn: 'Tak uppgávu burtur',
      task_title_label: 'Heiti á uppgávu',
      task_title_placeholder: 'Skriva heiti á uppgávuni',
      description_label: 'Lýsing (valfrítt)',
      description_placeholder: 'Skriva lýsing av uppgávuni',
      room_label: 'Rúm',
      no_room_option: 'Einki rúm',
      assignment_label: 'Tillutan av uppgávu',
      available_anyone_option: 'Tøk fyri øllum',
      assign_to_member_prefix: 'Gev til',
      assignment_hint_assigned: 'Hendan uppgávan verður bara sýnlig fyri tann limin, hon er givin',
      assignment_hint_anyone: 'Hendan uppgávan verður tøk fyri øllum familjulimum at taka',
      points_label: 'Stig at geva',
      points_placeholder: 'Stig',
      due_date_label: 'Freist',
      recurrence_label: 'Endurstovna hvørt X dag (0 = eingin endurtaka)',
      recreate_on_validation_label: 'Endurstovna við góðkenning',
      recreate_on_validation_help: 'Endurstovna beinanvegin, tá umsitari hevur góðkent',
      days_to_complete_label: 'Tal av døgum at gera uppgávuna',
      days_to_complete_placeholder: 't.d. 7',
      days_to_complete_help: 'Um tað verður sett, verður freistin stovningardagurin + so nógvir dagar.',
      auto_available_label: 'Sjálvvirkandi tøk',
      header_image_label: 'Høvuðsmynd (valfrítt)',
      current_header_image_label: 'Núverandi høvuðsmynd',
      checklist_label: 'Punkt á kanningarlista',
      checklist_item_placeholder: 'Skriva tekst til punktið',
      add_checklist_item_btn: 'Legg punkt afturat',
      images_optional_label: 'Myndir (valfrítt):',
      camera_title: 'Kamera',
      required_tools_label: 'Neyðug amboð',
      no_tools_available_msg: 'Eingi amboð tøk. Stovna amboð fyrst undir Amboð.',
      remove_task_title: 'Tak uppgávu burtur',
      remove_task_prompt_prefix: 'Ert tú vís/ur í, at tú vilt taka burtur uppgávuna',
      irreversible_note: 'Hetta kann ikki gerast um aftur.',
      reward_title_label: 'Heiti á vinningi',
      reward_title_placeholder: 'Skriva heiti á vinninginum',
      reward_desc_label: 'Lýsing',
      reward_desc_placeholder: 'Skriva lýsing av vinninginum',
      points_required_label: 'Kravd stig',
      reward_progress_start_label: 'Stig telja frá (dagfesting)',
      reward_progress_start_hint: 'Bara stig, sum eru vunnin frá byrjanini av hesum degi (tín tíð), telja við í hesum vinninginum. Samlaðu tølini aðrastaðni broytast ikki.',
      target_type_label: 'Málbólkur',
      target_type_individual: 'Einstaklingur',
      target_type_family: 'Familja',
      assign_member_label: 'Gev einum familjulimi',
      select_member_option: 'Vel ein familjulim',
      current_header_image_id_label: 'Núverandi ID á høvuðsmynd',
      tool_name_label: 'Navn á amboði',
      tool_name_placeholder: 'Skriva navn á amboðnum',
      tool_image_label: 'Mynd av amboði (valfrítt)',
      current_tool_image_label: 'Núverandi mynd av amboði',
      room_name_label_required: 'Navn á rúmi *',
      room_name_placeholder: 'Skriva navn á rúminum',
      member_name_label_required: 'Navn *',
      member_name_label: 'Navn',
      member_name_placeholder: 'Skriva navn á liminum',
      member_pin_label_optional: 'PIN (valfrítt)',
      member_pin_hint_optional: 'Lat tað vera tómt fyri at halda núverandi PIN. PIN skal vera 4–8 tøl.',
      member_is_admin_label: 'Umsitaraatgongd',
      member_update_success: 'Limurin er dagførdur',
      member_update_failed: 'Tað eydnaðist ikki at dagføra limin',
      member_password_label_required: 'Loyniorð *',
      member_password_placeholder: 'Skriva loyniorð',
      member_email_label_optional: 'Teldupostur (valfrítt)',
      member_email_placeholder: 'Skriva teldupostadressu',
      admin_access_label: 'Umsitaraatgongd',
      member_type_label: 'Slag av limi',
      member_type_adult: 'Vaksin',
      member_type_child: 'Barn',
      err_room_name: 'Gev rúminum eitt navn.',
      err_room_create: 'Rúmið kundi ikki stovnast. Royn aftur.',
      err_room_update: 'Rúmið kundi ikki goymast. Royn aftur.',
      err_generic_missing: 'Okkurt manglar. Lat aftur og royn aftur.',
      err_task_title_points: 'Gev uppgávuni eitt heiti og eitt tal av stjørnum.',
      err_checklist_empty: 'Legg í minsta lagi eitt stig við teksti afturat.',
      err_task_create: 'Uppgávan kundi ikki stovnast. Royn aftur.',
      err_task_update: 'Uppgávan kundi ikki goymast. Royn aftur.',
      err_task_remove: 'Uppgávan kundi ikki takast burtur. Royn aftur.',
      err_template_use: 'Ein uppgáva kundi ikki gerast úr hesi skabelónini. Royn aftur.',
      err_save_template: 'Skabelónin kundi ikki goymast. Royn aftur.',
      saved_template: 'Goymt sum skabelón.',
      err_header_image: 'Myndin kundi ikki goymast. Royn aftur.',
      err_reward_title: 'Gev lønini eitt heiti.',
      err_reward_points: 'Skriva hvussu nógvar stjørnur lønin krevur.',
      err_reward_member: 'Vel hvør lønin er til.',
      err_reward_create: 'Lønin kundi ikki stovnast. Royn aftur.',
      err_reward_update: 'Lønin kundi ikki goymast. Royn aftur.',
      reward_updated: 'Lønin er goymd.',
      err_tool_name: 'Gev amboðnum eitt navn.',
      err_tool_create: 'Amboðið kundi ikki stovnast. Royn aftur.',
      err_tool_update: 'Amboðið kundi ikki goymast. Royn aftur.',
      err_member_required: 'Ein nýggjur limur skal hava navn, brúkaranavn og PIN.',
      err_pin_format: 'PIN skal vera 4–8 tøl (bert tøl).',
      err_username_short: 'Brúkaranavnið skal vera í minsta lagi 2 tekn.',
      err_member_create: 'Limurin kundi ikki leggjast afturat. Royn aftur.',
      verify_misattribution_title: 'Gev stjørnurnar nú?',
      verify_misattribution_yes: 'Ja, gev tær',
      status_assigned: 'Til {{name}}',
      status_open: 'Øll kunnu taka hana',
      comes_back_on: 'Kemur aftur {{date}}',
      anyone_can_take: 'Øll kunnu taka ta næstu (annars fer hon aftur til sama persón)',
      repeat_hint: 'Við "endurtak hvønn" skal tann næsta vera liðug so nógvar dagar eftir góðkenningina og kemur "dagar at gera lidnan" dagar áðrenn.',
      step_tools_label: 'Amboð til hetta stigið',
      step_tools_hint: 'Vel fyrst amboðini til uppgávuna longri niðri. So kanst tú velja tey, sum hetta stigið hevur brúk fyri.',
      room_picture_label: 'Mynd av rúminum (valfrítt)',
      room_picture_hint: 'Ein mynd hjálpir børnunum at síggja, í hvørjum rúmi ein uppgáva er.',
      room_picture_add: 'Legg mynd afturat',
      room_picture_change: 'Skift mynd',
      room_picture_failed: 'Rúmið er goymt, men myndin kundi ikki leggjast upp. Royn aftur.',
      step_drag: 'Drag fyri at flyta stigið',
      step_move_up: 'Flyt stigið upp',
      step_move_down: 'Flyt stigið niður',
      show_more_completed_tasks: 'Vís fleiri',
      show_less_completed_tasks: 'Vís færri',
      task_saving: 'Goymir uppgávuna…',
      task_saved: 'Uppgávan er goymd',
      try_again: 'Royn aftur',
      deadline_question: 'Freist',
      deadline_none: 'Eingin freist',
      deadline_within: 'Skal gerast innan',
      days_unit: 'dagar',
      repeat_question: 'Tá ið hon er góðkend…',
      repeat_none: 'er hon liðug – hon kemur ikki aftur',
      repeat_now: 'kemur hon aftur við tað sama',
      repeat_after: 'kemur hon aftur eftir',
      next_question: 'Hvør fær ta næstu?',
      next_same: '{{name}} aftur',
      next_anyone: 'Øll kunnu taka hana',
      summary_title: 'Soleiðis verður tað:',
      due_this_time_label: 'Freist hesaferð (valfrítt)',
      due_this_time_help: 'Lat tað standa tómt, so verður freistin omanfyri brúkt.',
      sum_now_person: '{{name}} fær uppgávuna nú.',
      sum_now_anyone: 'Øll í familjuni kunnu taka hana nú.',
      sum_within_one: 'Hon skal vera gjørd innan {{n}} dag.',
      sum_within_other: 'Hon skal vera gjørd innan {{n}} dagar.',
      sum_end: 'Tá ið tú góðkennir hana, er hon liðug.',
      sum_back_now_same: 'Tá ið tú góðkennir hana, kemur hon aftur til {{name}} við tað sama.',
      sum_back_now_anyone: 'Tá ið tú góðkennir hana, kemur hon aftur við tað sama, og øll kunnu taka hana.',
      sum_back_after_same_one: 'Tá ið tú góðkennir hana, kemur hon aftur til {{name}} um {{n}} dag.',
      sum_back_after_same_other: 'Tá ið tú góðkennir hana, kemur hon aftur til {{name}} um {{n}} dagar.',
      sum_back_after_anyone_one: 'Tá ið tú góðkennir hana, kemur hon aftur um {{n}} dag, og øll kunnu taka hana.',
      sum_back_after_anyone_other: 'Tá ið tú góðkennir hana, kemur hon aftur um {{n}} dagar, og øll kunnu taka hana.',
      sum_then_within_one: 'So er {{n}} dagur at gera hana.',
      sum_then_within_other: 'So eru {{n}} dagar at gera hana.',
      repeat_next: 'er næsta uppgáva',
      next_ready_after: 'Klár eftir',
      hours_unit: 'tímar',
      waits_for: 'Bíðar eftir: {{task}}',
      then_next: 'Síðan: {{task}}',
      sum_next: 'Tá ið tú góðkennir hana, kemur "{{task}}" aftaná.',
      sum_next_wait_one: 'Hon er klár {{n}} tíma eftir, at henda varð gjørd.',
      sum_next_wait_other: 'Hon er klár {{n}} tímar eftir, at henda varð gjørd.',
      sum_next_waits: 'Til tá síggja børnini ikki "{{task}}".',
      reward_saving: 'Goymir lønina…',
      reward_saved: 'Lønin er goymd',
      assignment_hint_each: 'Hvør fær sína egnu uppgávu: í sínari egnu kalendara, góðkend fyri seg og við sínum egnu stjørnum.',
      err_choose_person: 'Vel í minsta lagi ein persón.',
      names_and: '{{first}} og {{last}}',
      sum_now_each: '{{names}} fáa hvør sína uppgávu nú.',
      sum_back_now_each: 'Tá ið tú góðkennir uppgávuna hjá einum, kemur hansara ella hennara aftur við tað sama.',
      sum_back_after_each_one: 'Tá ið tú góðkennir uppgávuna hjá einum, kemur hansara ella hennara aftur um {{n}} dag.',
      sum_back_after_each_other: 'Tá ið tú góðkennir uppgávuna hjá einum, kemur hansara ella hennara aftur um {{n}} dagar.',
      shared_ready: 'Klár',
      remove_shared_note: 'Hon verður strikað fyri øll, ið hon varð givin. Uppgávur, ið longu eru gjørdar, verða verandi í søguni.',
      repeat_plan: 'kemur hon aftur eftir eini fastari ætlan',
      plan_weekly: 'hvørja viku',
      plan_every: 'hvønn',
      plan_every_unit: '. dag',
      plan_from: 'frá',
      sum_plan_person: '{{name}} fær uppgávuna.',
      sum_plan_anyone: 'Øll í familjuni kunnu taka hana.',
      sum_plan_each: '{{names}} fáa hvør sína uppgávu.',
      sum_plan_weekly: 'Hon kemur fram hvønn {{days}}.',
      sum_plan_every_one: 'Hon kemur fram hvønn dag frá {{date}}.',
      sum_plan_every_other: 'Hon kemur fram hvønn {{n}}. dag frá {{date}}.',
      sum_plan_back: 'Tá ið tú góðkennir hana, kemur hon aftur næsta ætlaða dag.',
      plan_week_every_one: 'hvørja viku',
      plan_week_every_other: 'hvørja {{n}}. viku',
      plan_on: 'á',
      plan_monthly: 'hvønn mánað',
      plan_month_every_one: 'hvønn mánað',
      plan_month_every_other: 'hvønn {{n}}. mánað',
      plan_the: 'tann',
      plan_which: 'hvør',
      plan_weekday: 'vikudagur',
      plan_nth_1: 'fyrsta',
      plan_nth_2: 'annan',
      plan_nth_3: 'triðja',
      plan_nth_4: 'fjórða',
      plan_nth_last: 'síðsta',
      plan_on_date: 'tann',
      plan_date_unit: '. í mánaðinum',
      sum_plan_weekly_every: 'Hon kemur fram hvørja {{n}}. viku, á {{days}}.',
      sum_plan_every_open_one: 'Hon kemur fram hvønn dag.',
      sum_plan_every_open_other: 'Hon kemur fram hvønn {{n}}. dag.',
      sum_plan_month_day_one: 'Hon kemur fram tann {{day}}. í hvørjum mánaði.',
      sum_plan_month_day_other: 'Hon kemur fram tann {{day}}. hvønn {{n}}. mánað.',
      sum_plan_month_nth_one: 'Hon kemur fram {{nth}} {{weekday}} í hvørjum mánaði.',
      sum_plan_month_nth_other: 'Hon kemur fram {{nth}} {{weekday}} hvønn {{n}}. mánað.',
      step_timer_label: 'Tíðarmátari (valfrítt)',
      step_timer_minutes: 'Tíðarmátari, minuttir',
      step_timer_seconds: 'Tíðarmátari, sekund',
      step_timer_min: 'min',
      step_timer_sec: 'sek',
      step_timer_clear: 'Ongin tíðarmátari',
      step_timer_hint: 'T.d. "Planki – 0:45". Tann, ið ger uppgávuna, byrjar hann og hoyrir eitt ljóð, tá ið tíðin er farin.',
    },
    parent: {
      to_approve_one: '{{n}} uppgáva at góðkenna',
      to_approve_other: '{{n}} uppgávur at góðkenna',
      finished: '{{name}} er liðug(ur) við {{task}}',
      review: 'Kanna',
      nothing_to_approve: 'Einki at góðkenna júst nú',
      family: 'Familja',
      open_one: '{{n}} uppgáva at gera',
      open_other: '{{n}} uppgávur at gera',
      open_none: 'Ongar uppgávur at gera',
      set_up: 'Uppseting',
      tasks: 'Uppgávur',
      rewards: 'Vinningar',
      members: 'Familja',
      rooms_tools: 'Rúm og amboð',
      lock: 'Lás',
      settings: 'Stillingar',
      more_waiting: '+{{n}} aftrat',
      your_tasks: 'Tínar egnu uppgávur',
    },
    approve: {
      title: 'Góðkenn uppgávur',
      approve: 'Góðkenn',
      not_done: 'Ikki liðugt enn',
      note_so_neat: 'So pent!',
      note_well_done: 'Væl gjørt!',
      note_thank_you: 'Takk!',
      reason_look_again: 'Næstan — hygg eina ferð afturat',
      reason_steps_missing: 'Nøkur stig vanta enn',
      reason_do_together: 'Lat okkum gera tað saman',
      pick_reason: 'Hvat skal {{name}} hoyra?',
      cancel: 'Angra',
      steps_done_one: 'Stigið er gjørt',
      steps_done_other: 'Øll {{n}} stigini gjørd',
      approved_toast: '{{task}} er góðkend. {{name}} fekk {{stars}}.',
      undo: 'Angra',
      sent_back_toast: '{{task}} er send aftur til {{name}}.',
      today_at: 'Í dag {{time}}',
      yesterday_at: 'Í gjár {{time}}',
      from_grownup: 'Ein vaksin sigur: {{reason}}',
      back: 'Aftur',
      self_blocked: 'Ein annar vaksin má góðkenna tína egnu uppgávu.',
    },
    my_settings: {
      title: 'Mínar stillingar',
      language: 'Mál',
      same_as_device: 'Sum hetta tól',
      look: 'Ljóst ella myrkt',
      light: 'Ljóst',
      dark: 'Myrkt',
      notifications: 'Fráboðanir',
      on: 'Tendrað',
      off: 'Sløkt',
      notif_turn_on: 'Tendra á hesum tólinum',
      notif_turned_on: 'Fráboðanir eru tendraðar.',
      notif_turned_off: 'Fráboðanir eru sløktar.',
      notif_parent_off: 'Ein vaksin hevur sløkt tínar fráboðanir.',
      notif_parent_on: 'Ein vaksin vil hava tínar fráboðanir tendraðar.',
      notif_unsupported: 'Hetta tólið kann ikki vísa fráboðanir frá AntNest.',
      notif_blocked: 'Fráboðanir eru forðaðar í hesum kaganum. Bið ein vaksin um at loyva teimum í stillingunum í kaganum.',
      timer_sound: 'Ljóð á tíðarmátara',
      timer_sound_help: 'Ljóðið, tá ið tíðarmátarin á einum stigi røkkur null. Trýst fyri at hoyra tað.',
    },
    parent_notifications: {
      title: 'Fráboðanir hjá børnunum',
      help: 'Vel hvør ið avger. "Altíð tendrað" merkir, at barnið ikki kann sløkkja tær; "Sløkt" merkir, at einki verður sent barninum.',
      child: 'Barnið avger',
      on: 'Altíð tendrað',
      off: 'Sløkt',
      saved: 'Goymt.',
    },
    handover: {
      title: 'Lønir at handa',
      help: 'Hesar eru heintaðar. Gev barninum tær, og trýst so á Handað.',
      got: '{{name}} fekk {{reward}}',
      handed_over: 'Handað',
      done_toast: '{{reward}} er handað {{name}}.',
    },
    library: {
      access_denied: 'Bert vaksin kunnu lata hetta upp.',
      title: 'Uppgávusavn',
      intro: 'Byrja við eini vanligari uppgávu ella eini skabelón, tú goymdi úr eini uppgávu. Bert familjan sær "Mínar skabelónir".',
      age: 'Aldur',
      age_group: 'Aldursbólkur',
      age_all: 'Allur aldur',
      age_2_3: '2–3 ár',
      age_4_5: '4–5 ár',
      age_6_7: '6–7 ár',
      age_8_9: '8–9 ár',
      age_10_12: '10–12 ár',
      age_13_plus: '13+ ár',
      standard_title: 'Vanligar uppgávur',
      none_standard: 'Ongar vanligar uppgávur til hetta mál og henda aldur.',
      badge_standard: 'Vanlig',
      my_title: 'Mínar skabelónir',
      my_intro: 'Goymdar við "Goym sum skabelón" í uppgávuskjalinum. Bert familjan kann síggja tær.',
      none_my: 'Ongar goymdar skabelónir enn. Ger eina uppgávu og brúka "Goym sum skabelón", ella broyt aldursfiltrið.',
      badge_my: 'Mín skabelón',
      use: 'Brúka',
      save_as_template: 'Goym sum skabelón',
      days: 'Dagar',
      stars: 'Stjørnur',
      tool_name: 'Navn á amboðnum',
      tool_name_ph: 't.d. Dustsúgvari',
      username_required: 'Brúkaranavn (skal fyllast út)',
      username_ph: 'Vel eitt brúkaranavn',
      pin_label: 'PIN (4–8 tøl)',
      pin_ph: 'Skriva eitt PIN',
      adults: 'Vaksin',
      show: 'Vís',
      hide: 'Fjal',
      added_to_tasks: '„{{title}}“ er lagt afturat tykkara uppgávum',
      chain_title: '{{n}} uppgávur, ið fylgja hvørji aðrari',
      chain_wait_one: 'næsta er klár eftir {{n}} tíma',
      chain_wait_other: 'næsta er klár eftir {{n}} tímar',
      chain_loops: 'So byrjar hon av nýggjum.',
    },
    deletion: {
      delete_member: 'Strika lim',
      delete_member_title: 'Strika {{name}}?',
      delete_member_text: '{{name}} verður beinanvegin tikin úr familjuni og ritað út á øllum eindum.',
      delete_member_lost: 'Allar stjørnur, hagtøl, uppgávur og lønir hjá {{name}} verða strikaðar.',
      undo_30: 'Tú kanst angra hetta í 30 dagar, til {{date}}. Eftir tað verður alt strikað endaliga og kann ikki endurskapast.',
      delete_member_yes: 'Ja, strika',
      member_deleted_toast: '{{name}} er strikað(ur). Tú kanst endurskapa í 30 dagar.',
      recently_deleted: 'Nýliga strikað',
      recently_deleted_hint: 'Tú kanst endurskapa hesi limini til vísta dagin. Eftir tað verða tey og allar dátur teirra strikaðar endaliga.',
      gone_on: 'Strikað endaliga {{date}}',
      restore: 'Endurskapa',
      member_restored_toast: '{{name}} er aftur í familjuni. Fráboðanir mugu setast til aftur.',
      family_section: 'Strika familju',
      delete_family_row: 'Strika alla familjuna',
      delete_family_hint: 'Øll og alt í familjuni. Kann angrast í 30 dagar.',
      delete_family_title: 'Strika alla familjuna?',
      delete_family_text: 'Øll í {{family}} verða beinanvegin ritað út og kunnu ikki nýta AntNest, fyrr enn familjan er endurskapað.',
      delete_family_lost: 'Alt verður strikað: øll limir, stjørnur, hagtøl, uppgávur, lønir, tiltøk og myndir.',
      type_family_name: 'Skriva navnið á familjuni fyri at vátta: {{family}}',
      delete_family_yes: 'Strika familjuna',
      pending_title: '{{family}} er sett til striking',
      pending_text: 'Familjan og allar dátur hennara verða strikaðar endaliga {{date}}. Inntil tá kanst tú endurskapa alt, júst sum tað var.',
      days_left: 'Dagar eftir: {{days}}',
      pending_not_admin: 'Bert ein vaksin við umsitingarrættindum kann endurskapa familjuna.',
      restore_family: 'Endurskapa familjuna',
      family_restored_toast: 'Vælkomin aftur! Familjan er endurskapað. Fráboðanir mugu setast til aftur.',
      log_out: 'Rita út',
      cancel: 'Angra',
    },
    pictures: {
      remove_picture: 'Tak mynd burtur',
      removed: 'Myndin er tikin burtur. Tú hevur vanliga maurin aftur.',
      unreadable: 'Myndin kundi ikki latast upp. Royn eina aðra mynd, ella tak eina nýggja mynd.',
      rotate: 'Snara',
      preparing: 'Myndin verður gjørd klár…',
    },
    billing: {
      title: 'Hald',
      trial_title: 'Ókeypis royndartíð – dagar eftir: {{days}}',
      trial_text: 'Ókeypis royndartíðin hjá tær endar {{date}}. Vel eitt hald áðrenn tá, so tú kanst halda fram at brúka AntNest.',
      expired_title: 'Ókeypis royndartíðin hjá tær er liðug',
      expired_text: 'Vel eitt hald, so tú kanst halda fram at brúka AntNest. Uppgávurnar, stjørnurnar og vinningarnir hjá familjuni eru framvegis her.',
      active_title_monthly: 'Tú gjaldar hvønn mánað',
      active_title_yearly: 'Tú gjaldar hvørt ár',
      active_text: 'Haldið hjá tær verður endurnýggjað {{date}}.',
      grace_title: 'Ein gjalding fór ikki ígjøgnum',
      grace_text: 'Dagfør gjaldsupplýsingarnar hjá tær í seinasta lagi {{date}}, so tú kanst halda fram at brúka AntNest.',
      cancelling_title: 'Haldið hjá tær er sagt upp',
      cancelling_text: 'Tú kanst brúka AntNest til {{date}}. Vel eitt hald, um tú vilt halda fram eftir tað.',
      exempt_title: 'AntNest er ókeypis fyri familjuna hjá tykkum',
      exempt_text: 'Tú skalt ikki gjalda nakað.',
      plans_title: 'Vel eitt hald',
      plans_intro: 'Eitt hald fevnir um alla familjuna á øllum tólum.',
      plan_monthly: 'Mánaðarligt',
      plan_yearly: 'Árligt',
      price_monthly: '{{price}} um mánaðin',
      price_yearly: '{{price}} um árið',
      price_soon: 'Prísurin kemur skjótt',
      includes_family: 'Øll familjan á øllum tólum',
      includes_tasks: 'Allar lidnar uppgávur og kanningarlistar',
      includes_cancel: 'Sig upp nær tú vilt',
      choose_monthly: 'Vel mánaðarligt',
      choose_yearly: 'Vel árligt',
      coming_soon: 'Gjalding verður møgulig skjótt. Inntil tá er AntNest ókeypis.',
      manage_title: 'Stýr haldinum hjá tær',
      manage_text: 'Skift hald, dagfør kortið, sí kvittanir ella sig upp.',
      manage_button: 'Stýr haldi',
      banner_trial: 'Dagar til ókeypis royndartíðin hjá tær endar: {{days}}.',
      banner_trial_last_day: 'Ókeypis royndartíðin hjá tær endar í dag.',
      banner_payment: 'Ein gjalding fór ikki ígjøgnum. Dagfør kortið í seinasta lagi {{date}}.',
      banner_cancelling: 'Haldið hjá tær endar {{date}}.',
      banner_action: 'Sí hald',
      paywall_title: 'AntNest hvílir seg',
      paywall_text: 'Bið ein vaksnan velja eitt hald. Allar stjørnurnar og uppgávurnar hjá tær eru tryggar og bíða eftir tær.',
      manage_failed: 'Haldssíðan kundi ikki latast upp. Royn aftur um eina løtu.',
    },
    pocket: {
      title: 'Lummapengar',
      intro: 'Lummapengar verða roknaðir út frá stjørnunum, sum hvørt barn fær fyri góðkendar uppgávur í einum mánaði. AntNest flytir ongar pengar: tit gjalda sum vant og merkja tað sum útgoldið.',
      no_children: 'Legg eitt barn afturat familjuni fyri at geva lummapengar.',
      currency: 'Gjaldoyra',
      for_child: 'Lummapengar til {{name}}',
      how_question: 'Hvussu verða teir forvunnir?',
      per_ten_label: '{{currency}} fyri hvørjar 10 stjørnur',
      fixed_label: '{{currency}} um mánaðin, tá ið {{name}} hevur fingið minst',
      threshold_label: 'stjørnur',
      threshold_help: '0 stjørnur = verður altíð givið (fastir lummapengar).',
      bonus_label: 'Bonus:',
      bonus_every_label: '{{currency}} eyka fyri hvørjar',
      bonus_stars_label: 'stjørnur afturat',
      cap_question: 'Mark um mánaðin — vel eitt',
      cap_none: 'Einki mark',
      cap_at: 'Í mesta lagi',
      cap_unit: '{{currency}} um mánaðin',
      cap_required: 'Vel eitt mark ella einki mark, áðrenn tú goymir.',
      example_title: 'Til dømis í einum mánaði við:',
      example_line_one: '{{n}} stjørna → {{amount}}',
      example_line_other: '{{n}} stjørnur → {{amount}}',
      capped_short: 'markið rokkið',
      typical_dk: 'Vanligt í Danmark um mánaðin: undir 100 DKK fyri 6–10 ár, 50–200 DKK fyri 11–14 ár, 200–500 DKK fyri 15–17 ár.',
      save: 'Goym',
      saved: 'Lummapengar til {{name}} eru goymdir',
      save_failed: 'Kundi ikki goyma. Royn aftur.',
      my_month: 'Lummapengar í {{month}}: {{amount}}',
      my_next_one: '{{n}} stjørna afturat gevur {{amount}} afturat',
      my_next_other: '{{n}} stjørnur afturat geva {{amount}} afturat',
      my_capped: 'Tú hevur rokkið hægsta upphæddina í mánaðinum. Flott!',
      my_last_paid: '{{month}}: {{amount}} — útgoldið ✓',
      my_last_unpaid: '{{month}}: {{amount}} — bíðar eftir útgjaldi',
      to_pay_title: 'Lummapengar at gjalda út',
      to_pay_help: 'Gjaldið sum vant (kontant, banki ella MobilePay), og merkið tað so sum útgoldið.',
      mark_paid: 'Merk sum útgoldið',
      task_counts: 'Gevur lummapengar',
      task_counts_help: 'Tak flugubeinið burtur við gerandisuppgávum sum at gera songina, so bert størri uppgávur telja við.',
      typical_intro: 'Vanligt í {{country}} um mánaðin:',
      typical_band: '{{ages}} ár: {{amount}}',
      typical_under: 'undir {{amount}}',
      typical_about: 'umleið {{amount}}',
      typical_source: 'kelda: {{source}}',
    },
    getting_started: {
      title: 'Kom væl í gongd',
      progress: '{{done}} av {{all}} gjørt',
      hide: 'Fjal',
      item_approve: 'Góðkenn fyrstu liðugu uppgávuna',
      item_own_task: 'Tillaga eina uppgávu, ella ger tykkara egnu',
      item_home_screen: 'Legg AntNest á heimaskermin',
      item_calendar: 'Sí uppgávurnar í kalendaranum',
      home_screen_how: 'iPhone/iPad: Trýst á Deil í Safari, og vel "Legg afturat heimaskermi". Android: Trýst á ⋮ í Chrome, og vel "Installera app" ella "Legg afturat heimaskermi". Ger tað eisini á tólunum hjá børnunum.',
      home_screen_done: 'Tað havi eg gjørt',
      pocket_title: 'Lummapengar fyri stjørnurnar?',
      pocket_text: 'Nú tá ið børnini eru komin í gongd, kunnu stjørnurnar eisini geva lummapengar – ein fasta upphædd, eina upphædd fyri hvørjar 10 stjørnur og eitt mark um mánaðin, sum tit sjálvi avgera.',
      pocket_setup: 'Set tað upp',
      pocket_no: 'Nei takk',
    },
    editor: {
      close: 'Lat aftur',
      unsaved_title: 'Goym broytingarnar?',
      unsaved_body: 'Tú hevur broytt okkurt. Vilt tú goyma tað, áðrenn tú letur aftur?',
      save: 'Goym',
      discard: 'Goym ikki',
      keep_editing: 'Halt fram at broyta',
    },
    timer: {
      start: 'Byrja tíðarmátara',
      pause: 'Steðga',
      resume: 'Halt fram',
      restart: 'Byrja umaftur',
      times_up: 'Tíðin er farin!',
      sound_bell: 'Klokka',
      sound_chime: 'Klokkuspæl',
      sound_beeps: 'Vekjari',
      sound_marimba: 'Marimba',
      sound_fanfare: 'Fanfara',
      volume_note: 'Ljóðið fylgir ljóðstyrkinum á telefonini – skrúva upp, so tú hoyrir tað.',
    },
    approval_setting: {
      title: 'Góðkenning av uppgávum hjá vaksnum',
      label: 'Uppgávur hjá vaksnum skulu góðkennast av øðrum vaksnum',
      help_off: 'Sløkt: Tá ið ein vaksin er liðug/ur við eina uppgávu, er hon góðkend beinanvegin, og stjørnurnar eru teirra. Uppgávur hjá børnum verða altíð góðkendar av einum vaksnum.',
      help_on: 'Tendrað: Ein liðug uppgáva hjá einum vaksnum bíðar, til ein annar vaksin góðkennir hana (hevur familjan bert ein umsitara, kann hann góðkenna sínar egnu).',
      setup_help: 'Lat hana vera tóma, so verða uppgávur hjá vaksnum góðkendar, so skjótt tær eru lidnar. Tú kanst broyta tað seinni undir Stillingar.',
      saved: 'Goymt.',
    },
  },
  is: {
    auth: {
      forgot_title: 'Gleymdirðu lykilorðinu?',
      forgot_link: 'Gleymdirðu lykilorðinu?',
      forgot_confirmation: 'Ef reikningur er til fyrir þetta netfang höfum við sent hlekk til að endurstilla lykilorðið.',
      email_label: 'Netfang',
      send_reset_link: 'Senda endurstillingarhlekk',
      reset_title: 'Veldu nýtt lykilorð',
      new_password_label: 'Nýtt lykilorð',
      confirm_password_label: 'Staðfestu lykilorðið',
      set_new_password: 'Vista nýtt lykilorð',
      password_mismatch: 'Lykilorðin stemma ekki',
      password_policy_msg: 'Að minnsta kosti 12 stafir, með hástaf, lágstaf og tölustaf.',
      invalid_token: 'Ógildur eða útrunninn endurstillingarhlekkur',
      reset_success: 'Lykilorðið þitt hefur verið uppfært. Áfram á innskráningu...',
      reset_failed: 'Ekki tókst að endurstilla lykilorðið. Reyndu aftur.',
      login_failed: 'Innskráning mistókst. Reyndu aftur.',
      beta_banner: 'AntNest er í beta. Ertu með boðskóða?',
      beta_banner_link: 'Skráðu þig hér',
      or: 'eða',
      login_google: 'Skrá inn með Google',
      login_facebook: 'Skrá inn með Facebook',
      no_account: 'Ertu ekki með aðgang?',
      create_one: 'Búðu til aðgang',
      err_code_required: 'Þú þarft beta-kóða.',
      err_agree: 'Samþykktu skilmálana og persónuverndarstefnuna.',
      err_fields: 'Fylltu út alla reiti.',
      err_code_invalid: 'Þessi beta-kóði er ógildur eða þegar notaður.',
      err_signup_failed: 'Ekki tókst að stofna aðganginn. Reyndu aftur.',
      signup_title: 'Stofnaðu aðganginn þinn',
      beta_code: 'Beta-kóði',
      beta_code_ph: 'Sláðu inn boðskóðann þinn',
      full_name: 'Nafnið þitt',
      full_name_ph: 't.d. Anna',
      username: 'Notandanafn',
      show_password: 'Sýna lykilorð',
      hide_password: 'Fela lykilorð',
      rule_length: 'Að minnsta kosti 12 stafir',
      rule_upper: 'Hástafur',
      rule_lower: 'Lágstafur',
      rule_digit: 'Tölustafur',
      password_match: 'Lykilorðin passa',
      family_name: 'Nafn fjölskyldunnar',
      family_name_ph: 't.d. Fjölskyldan Jónsson',
      family_default: 'Fjölskyldan mín',
      agree_before: 'Ég samþykki',
      terms: 'skilmálana',
      and: 'og',
      privacy: 'persónuverndarstefnuna',
      creating_account: 'Stofna aðgang…',
      create_account: 'Stofna aðgang',
      signup_google: 'Skrá sig með Google',
      signup_facebook: 'Skrá sig með Facebook',
      choose_language: 'Veldu tungumál',
      oauth_failed: 'Innskráning kláraðist ekki. Reyndu aftur.',
      processing: 'Skráir þig inn…',
      complete_setup: 'Næstum búið',
      your_name: 'Nafnið þitt',
      complete_setup_btn: 'Stofna fjölskyldu og halda áfram',
      login_apple: 'Skrá inn með Apple',
      signup_apple: 'Skrá sig með Apple',
    },
    rooms: {
      kitchen: 'Eldhús',
      living_room: 'Stofa',
      dining_room: 'Borðstofa',
      bedroom: 'Svefnherbergi',
      bathroom: 'Baðherbergi',
      toilet: 'Snyrting',
      hallway: 'Gangur',
      entryway: 'Forstofa',
      laundry_room: 'Þvottahús',
      garage: 'Bílskúr',
      office: 'Skrifstofa',
      playroom: 'Leikherbergi',
      nursery: 'Barnaherbergi',
      patio: 'Verönd/svalir',
      garden: 'Garður',
      storage: 'Geymsla',
      basement: 'Kjallari',
      attic: 'Ris',
      child_bedroom: 'Barnaherbergi',
    },
    onboarding: {
      title: 'Byrjum!',
      family_name: 'Nafn fjölskyldunnar',
      adults: 'Fullorðnir',
      children: 'Börn',
      member_name: 'Nafn',
      username: 'Notandanafn',
      pin_placeholder: 'PIN (4 tölustafir)',
      role_admin: 'Stjórnandi',
      role_member: 'Meðlimur',
      next: 'Áfram',
      back: 'Til baka',
      confirm: 'Lítur vel út',
      processing: 'Vista…',
      step_rooms_title: 'Settu upp heimilið þitt',
      step_rooms_help: 'Smelltu á herbergin hér að neðan til að bæta þeim við húsið þitt. Ef þú ert með herbergi eða rými sem er ekki á listanum geturðu bætt því við handvirkt.\nÞú getur alltaf breytt, bætt við og fjarlægt herbergi í stjórnendahlutanum.',
      custom_room_placeholder: 'Eigið heiti á herbergi',
      add_custom: 'Bæta við eigin',
      remove: 'Fjarlægja',
      step_review_title: 'Yfirlit',
      review_family: 'Fjölskylda',
      review_members: 'Meðlimir',
      review_rooms: 'Herbergi',
      error_generic: 'Eitthvað fór úrskeiðis við vistun',
      step_members_help: 'Bættu fjölskyldumeðlimunum við hér.\nGefðu hverjum fjölskyldumeðlim nafn og notandanafn.\nHver fjölskyldumeðlimur þarf PIN-númer til að geta skráð sig inn.',
      validation_incomplete: 'Sumir reitir eru ekki rétt útfylltir',
      adult_label: 'Fullorðinn',
      child_label: 'Barn',
      privacy_note: 'Upplýsingarnar eru aðeins notaðar til að setja upp fjölskylduna ykkar. Við deilum hvorki né seljum – og munum aldrei deila eða selja – gögnin ykkar eða persónuupplýsingar til neins.',
      child_age: 'Aldur',
      child_room_name: 'Herbergi {{name}}',
      child_room_name_s: 'Herbergi {{name}}',
      hint_members: 'Þú getur alltaf bætt við eða breytt fjölskyldumeðlimum síðar undir Admin → Meðlimir.',
      hint_rooms: 'Hvert barn fær sitt eigið herbergi svo verkefnin þeirra séu á réttum stað. Þú getur alltaf breytt herbergjunum síðar undir Admin → Herbergi.',
      step_tasks_title: 'Verkefni til að byrja',
      step_tasks_help: 'Við höfum valið nokkur verkefni sem passa við herbergin og aldur barnanna. Takið hakið af því sem þið viljið ekki – þið getið breytt öllu síðar.',
      starters_unavailable: 'Ekki tókst að sækja tillögurnar. Þú getur sleppt þessu skrefi og bætt við verkefnum síðar.',
      tasks_for_child: 'Verkefni {{name}}',
      tasks_shared: 'Fyrir alla (allir geta tekið þau)',
      whole_home: 'Allt heimilið',
      hint_tasks: 'Mun fleiri stöðluð verkefni eru undir Admin → Verkefni og þar getið þið líka búið til ykkar eigin.',
      skip_step: 'Sleppa',
      step_rewards_title: 'Verðlaun til að stefna að',
      step_rewards_help: 'Börn halda áhuganum þegar fyrstu verðlaunin eru innan seilingar. Þessi eru stillt þannig að fyrstu má ná á nokkrum dögum.',
      rewards_for_child: 'Fyrir {{name}}',
      rewards_family: 'Fyrir alla fjölskylduna (stjörnur allra teljast)',
      hint_rewards: 'Þið getið alltaf bætt við ykkar eigin verðlaunum eða breytt þessum undir Admin → Verðlaun.',
      review_starters: 'Til að byrja',
      review_tasks_one: '{{n}} verkefni',
      review_tasks_other: '{{n}} verkefni',
      review_rewards_one: '{{n}} verðlaun',
      review_rewards_other: '{{n}} verðlaun',
      create_family: 'Stofna fjölskylduna okkar',
      done_title: 'Fjölskyldan ykkar er tilbúin!',
      done_made: 'Við bjuggum til {{tasks}} verkefni og {{rewards}} verðlaun fyrir ykkur.',
      done_try: 'Prófið fyrsta verkefnið núna: skiptið yfir í barn með „{{switch}}" efst og ýtið á „{{start}}" við verkefni.',
      done_admin: 'Þegar verkefni er lokið samþykkir þú það undir „{{approve}}" – þá bætast stjörnurnar við.',
      go_home: 'Fara á forsíðu fjölskyldunnar',
      go_admin: 'Fara í Admin',
    },
    common: {
      save: 'Vista',
      take_photo: 'Taka mynd',
      cancel: 'Hætta við',
      ok: 'Í lagi',
      loading: 'Hleð...',
      all: 'Allt',
      remove_item: 'Fjarlægja atriði',
      remove_image: 'Fjarlægja mynd',
      error_generic: 'Eitthvað fór úrskeiðis. Reyndu aftur.',
      you: 'Þú',
      stars_one: '{{n}} stjarna',
      stars_other: '{{n}} stjörnur',
      update_ready: 'Ný útgáfa af AntNest er tilbúin.',
      update_now: 'Uppfæra',
    },
    header: {
      stats: 'Tölfræði',
      app_title: 'AntNest',
      home: 'Heim',
      tasks: 'Verkefni',
      calendar: 'Dagatal',
      rewards: 'Verðlaun',
      settings: 'Stillingar',
      profile: 'Prófíll',
      admin: 'Stjórnun',
      logout: 'Útskrá',
      points: 'stig',
      settings_tooltip: 'Stillingar',
      menu_open: 'Opna valmynd',
      menu_close: 'Loka valmynd',
      nav_menu: 'Valmynd',
      tab_bar_label: 'Aðalhlutar',
      switch_user: 'Skipta um notanda',
      master_admin: 'Aðalstjórnandi',
      change_picture: 'Breyta myndinni minni',
      someone_else: 'Röðin að einhverjum öðrum',
      for_grownups: 'Fyrir fullorðna',
      for_grownups_hint: 'Stillingar, samþykktir, skrá út',
      my_page: 'Mín síða',
    },
    stats: {
      title: 'Tölfræði',
      tab_me: 'Ég',
      tab_family: 'Fjölskylda',
      tab_tasks: 'Verkefni',
      period_week: 'Vika',
      period_month: 'Mánuður',
      period_year: 'Ár',
      period_all: 'Allur tíminn',
      prev: 'Fyrra',
      next: 'Næsta',
      all_time: 'Allur tíminn',
      tasks_done: 'verkefni kláruð',
      points: 'stig',
      rewards: 'verðlaun',
      chart_points: 'Stig yfir tíma',
      where_helped: 'Hvar ég hjálpaði',
      no_room: 'Ekkert herbergi',
      my_top_tasks: 'Verkefnin sem ég geri oftast',
      together: 'Saman',
      who_helped: 'Hver hjálpaði',
      everyone_over_time: 'Allir yfir tíma',
      repeating_title: 'Verkefni',
      per_task_title: 'Hvert verkefni',
      times_done_axis: 'Fjöldi skipta',
      times: '{n}×',
      last_done: 'síðast: {date}',
      empty: 'Ekkert hér enn á þessu tímabili.',
      family_hidden: 'Fjölskyldan ykkar sýnir þetta aðeins fullorðnum.',
      week_short: 'Vika {n}',
      enc_start: 'Ný byrjun — hvert verkefni skiptir máli! 💪',
      enc_first: 'Fyrsta verkefnið þitt — vel gert! 🎉',
      enc_done: 'Þú hefur klárað {n} verkefni — frábært! ⭐',
      enc_more: 'Fleiri stig en á tímabilinu á undan — haltu áfram! 🚀',
      visibility_title: 'Tölfræði: hvað börn sjá',
      visibility_help: 'Veldu hvernig Fjölskylda-flipinn lítur út fyrir börn. Fullorðnir stjórnendur sjá alltaf allt.',
      visibility_team: 'Liðsyfirlit — hlutur allra, engin röðun (mælt með)',
      visibility_full: 'Fullur samanburður — raðað eftir stigum',
      visibility_admins: 'Falið — aðeins fullorðnir sjá samanburð fjölskyldunnar',
      visibility_saved: 'Vistað',
      team_view_note: 'Hlutur hvers og eins af heildarstigum fjölskyldunnar',
    },
    calendar: {
      title: 'Dagatal',
      coming_soon: 'Dagatalið er í smíðum. Grunnyfirlit og viðburðir birtast hér.',
      view_day: 'Dagur',
      view_week: 'Vika',
      view_month: 'Mánuður',
      week_label: 'Vika',
      no_events_for_day: 'Engir viðburðir',
      today_label: 'í dag',
      prev_day: 'Fyrri dagur',
      next_day: 'Næsti dagur',
      prev_week: 'Fyrri vika',
      next_week: 'Næsta vika',
      prev_month: 'Fyrri mánuður',
      next_month: 'Næsti mánuður',
      go_to_today: 'Í dag',
      not_allowed_edit_event: 'Þú getur aðeins breytt viðburðum sem þú hefur búið til.',
      filters_title: 'Sía eftir meðlim',
      single_view_button: 'Einn meðlimur',
      family_view_button: 'Öll fjölskyldan',
      no_events_for_range: 'Engir viðburðir á þessu tímabili',
      no_events: 'Engir viðburðir á þessu tímabili.',
      add_event: 'Bæta við viðburði',
      edit_event: 'Breyta viðburði',
      event_form_title: 'Titill',
      event_form_description: 'Lýsing (valfrjálst)',
      event_form_location: 'Staðsetning (valfrjálst)',
      event_form_start: 'Byrjar',
      event_form_end: 'Endar',
      event_form_all_day: 'Allan daginn',
      event_form_attendees: 'Þátttakendur',
      event_form_save: 'Vista',
      event_form_cancel: 'Hætta við',
      event_form_delete: 'Eyða',
      delete_confirm_title: 'Eyða viðburði',
      delete_confirm_message: 'Ertu viss um að þú viljir eyða þessum viðburði?',
      success_created: 'Viðburður búinn til',
      success_updated: 'Viðburður uppfærður',
      success_deleted: 'Viðburði eytt',
      error_generic: 'Eitthvað fór úrskeiðis',
      validation_title_required: 'Titill er nauðsynlegur',
      validation_end_after_start: 'Lokatími verður að vera á eftir upphafstíma',
      event_form_image: 'Mynd viðburðar (valfrjálst)',
      remove_image: 'Fjarlægja mynd',
      create_calendar_event: 'Búa til viðburð í dagatali',
      event_modal_title_create: 'Búa til viðburð í dagatali',
      event_modal_title_edit_prefix: 'Breyta',
      reminder_label: 'Áminning',
      reminder_none: 'Engin áminning',
      reminder_10m: '10 mínútum áður',
      reminder_30m: '30 mínútum áður',
      reminder_1h: '1 klukkustund áður',
      reminder_12h: '12 klukkustundum áður',
      reminder_1d: '1 degi áður',
      nothing_planned: 'Ekkert skipulagt enn',
      nothing_this_month: 'Ekkert skipulagt í þessum mánuði',
      today_day: 'Í dag, {{day}}',
      tomorrow_day: 'Á morgun, {{day}}',
      at_time: 'Kl. {{time}}',
      all_day_label: 'Allan daginn',
      with_names: 'með {{names}}',
      task_label: 'Verkefni',
      everyone: 'Allir',
      back: 'Til baka',
      due_today: 'Klára í dag',
      due_today_at: 'Klára í dag fyrir kl. {{time}}',
      due_tomorrow: 'Klára fyrir morgundaginn',
      due_tomorrow_at: 'Klára á morgun fyrir kl. {{time}}',
      due_later: 'Klára fyrir þennan dag',
      due_later_at: 'Klára fyrir kl. {{time}}',
      overdue_since: 'Seint síðan {{day}}',
      planned: 'Áætlað',
    },
    settings: {
      title: 'Stillingar',
      subtitle: 'Hafðu umsjón með reikningnum þínum og stillingum forritsins',
      dark_mode_section: 'Dökkur hamur',
      current_theme: 'Núverandi þema',
      following_system: 'Fylgir stillingum kerfisins',
      manual_selection: 'Handvirkt val',
      follow_system_label: 'Fylgja stillingum kerfisins',
      auto_switch_hint: 'Skiptir sjálfkrafa milli ljóss og dökks hams eftir stillingum stýrikerfisins',
      enable_dark_mode: 'Kveikja á dökkum ham',
      manual_toggle_hint: 'Skiptu handvirkt milli ljóss og dökks þema',
      light_mode: 'Ljós hamur',
      dark_mode: 'Dökkur hamur',
      more_settings_title: 'Fleiri stillingar væntanlegar',
      more_settings_desc: 'Fleiri valkostir verða í boði hér, þar á meðal stillingar fyrir tilkynningar, persónuvernd og umsjón reiknings.',
      language_section: 'Tungumál',
      language_hint: 'Veldu tungumálið sem þú vilt nota í viðmótinu',
      back: 'Til baka',
      profile_parent: 'Foreldri. Breyta nafni, mynd og lykilorði',
      profile_member: 'Breyta nafni, mynd og PIN',
      look: 'Útlit',
      auto: 'Sjálfvirkt',
      tell_me_when: 'Láttu mig vita þegar',
      notif_this_device: 'Tilkynningar á þessu tæki',
      notif_blocked_parent: 'Lokað í þessum vafra. Leyfðu tilkynningar fyrir þessa síðu í stillingum vafrans.',
      n_review_ready: 'Verkefni þarf samþykki',
      n_task_approved: 'Verkefnið mitt er samþykkt',
      n_reward_ready: 'Verðlaun eru tilbúin',
      n_due_soon: 'Eitt af verkefnunum mínum á að klárast bráðum',
      n_overdue: 'Eitt af verkefnunum mínum er seint',
      n_new_task: 'Nýtt verkefni er laust',
      n_event: 'Áminningar úr dagatalinu',
      daily_summary: 'Dagleg samantekt',
      daily_summary_time: 'Tími daglegrar samantektar',
      quiet_hours: 'Þagnartímar',
      quiet_hours_hint: 'Engar tilkynningar á milli þessara tíma',
      quiet_from: 'Þögn frá',
      quiet_to: 'Þögn til',
      this_device: 'Þetta tæki',
      change_pin: 'Breyta PIN-númerinu mínu',
      switch_person: 'Skipta um manneskju',
      refresh_session: 'Uppfæra þetta tæki',
      session_refreshed: 'Þetta tæki er uppfært.',
      locked_until: 'Læst til {{time}}',
      unlock: 'Opna',
      unlocked: '@{{name}} getur skráð sig inn aftur.',
      log_out_device: 'Skrá út af þessu tæki',
      logout_confirm_title: 'Skrá út af þessu tæki?',
      logout_confirm_text: 'Öll fjölskyldan {{family}} er skráð út hér. Fullorðinn þarf að skrá sig inn aftur áður en einhver getur notað AntNest á þessu tæki.',
      logout_confirm_yes: 'Skrá út',
      cancel: 'Hætta við',
      n_reward_claimed: 'Verðlaun eru sótt',
    },
    rewards: {
      title: 'Verðlaun',
      subtitle: 'Fylgstu með framvindunni og sæktu verðlaunin þín',
      your_points: 'Stigin þín',
      family_points: 'Stig fjölskyldunnar',
      claimed_rewards: 'Sótt verðlaun',
      your_coins: 'Myntin þín',
      individual_rewards: 'Einstaklingsverðlaun',
      no_individual_rewards: 'Engin einstaklingsverðlaun eru í boði fyrir þig ennþá.',
      family_rewards: 'Fjölskylduverðlaun',
      no_family_rewards: 'Engin fjölskylduverðlaun eru í boði ennþá.',
      goal_reached: 'Markmiði náð!',
      complete_suffix: 'lokið',
      target_whole_family: 'Öll fjölskyldan',
      claim_reward_button: 'Sækja verðlaun!',
      family_claim_parent_only: 'Aðeins foreldri getur sótt fjölskylduverðlaun',
      claimed_rewards_section: 'Sótt verðlaun',
      no_claimed_rewards: 'Engin verðlaun sótt ennþá. Haltu áfram með verkefnin þín!',
      claimed_on: 'Sótt þann',
      unknown_date: 'Óþekkt dagsetning',
      unknown_member: 'Óþekktur meðlimur',
      reward_header_alt: 'Haus verðlauna',
      show_more: 'Sýna meira',
      show_fewer: 'Sýna færri',
      points_abbrev: 'stig',
      need_more_prefix: 'Vantar',
      rewards_to_claim_heading: 'Verðlaun til að sækja',
      more_stars_for_prefix: '{{count}} stjörnur í viðbót fyrir',
      progress_counts_from: 'Framvindan telur stig sem safnast frá og með {{date}}.',
      lifetime_total_hint: 'Heildarstig frá upphafi (óháð tímabilum verðlauna).',
      your_stars_label: 'stjörnurnar þínar',
      ready_to_get: 'Tilbúið að sækja',
      keep_saving: 'Haltu áfram að safna fyrir',
      n_more: '{{n}} í viðbót',
      get_it: 'Sækja',
      grownup_gets: 'Fullorðinn sækir þessi',
      got_one: '{{n}} verðlaun sem þú fékkst',
      got_other: '{{n}} verðlaun sem þú fékkst',
      see_all: 'Sjá allt',
      counting_since: 'Telur stjörnur frá {{date}}',
      nothing_yet: 'Engin verðlaun enn. Biddu fullorðinn að bæta einhverjum við!',
      claim_failed: 'Ekki tókst að sækja verðlaunin. Reyndu aftur.',
    },
    dashboard: {
      welcome_back: 'Velkomin aftur',
      subheading: 'Hér er það sem er að gerast hjá fjölskyldunni í dag',
      leaderboard_title: 'Stigatafla fjölskyldunnar',
      family_member: 'Fjölskyldumeðlimur',
      tasks_title: 'Verkefni',
      available_tasks: 'Laus verkefni',
      no_available_tasks: 'Engin verkefni laus í augnablikinu!',
      my_active_tasks: 'Verkefnin mín í vinnslu',
      no_my_tasks: 'Þú ert ekki með nein verkefni í gangi!',
      recently_completed_tasks: 'Nýlega kláruð verkefni',
      completed_tasks: 'Kláruð verkefni',
      no_completed_tasks: 'Engin verkefni kláruð ennþá!',
      manage_all_tasks: 'Stjórna öllum verkefnum',
      my_rewards_title: 'Verðlaunin mín',
      my_points: 'Stigin mín',
      available_rewards: 'Verðlaun í boði',
      claimed_rewards: 'Sótt verðlaun',
      next_reward_goal: 'Næsta verðlaunamarkmið',
      all_rewards_claimed: 'Öll verðlaun sótt! Vel gert!',
      reward_ready: 'Tilbúið til að sækja!',
      showing_rewards_help: 'Sýnir: Þín eigin verðlaun og verðlaun fyrir alla fjölskylduna',
      claim_now: 'Sækja núna!',
      locked: 'Læst',
      go_to_rewards: 'Fara í verðlaun',
      my_tasks_primary_title: 'Verkefnin mín',
      my_tasks_home_hint: 'Hér eru aðeins verkefni sem eru skráð á þig. Opnaðu Verkefni til að velja ný verkefni.',
      due_overdue: 'Of seint',
      due_today: 'Á að klárast í dag',
      due_no_deadline: 'Enginn skiladagur',
      tasks_heading_possessive: 'Verkefni {{name}}',
      missions_banner: 'Þú ert með {{count}} verkefni tilbúin í dag. Af stað!',
      missions_banner_empty: 'Engin verkefni bíða núna — flott hjá þér!',
      available_tasks_banner_one: 'Þú getur unnið þér inn fleiri stig! Það er 1 verkefni laust.',
      available_tasks_banner_other: 'Þú getur unnið þér inn fleiri stig! Það eru {{count}} verkefni laus.',
      find_tasks_cta: 'Finna verkefni',
      missions_heading: 'Verkefni dagsins',
      mission_cta: 'Af stað!',
      hi_name: 'Hæ {{name}}',
      goal_of: '{{have}} af {{need}} stjörnum',
      goal_get_it: 'Sækja',
      goal_none: 'Engin verðlaun enn. Biddu fullorðinn að bæta einum við!',
      today: 'Í dag',
      no_tasks_now: 'Engin verkefni núna',
      waiting_one: '{{n}} stjarna þegar fullorðinn segir já',
      waiting_other: '{{n}} stjörnur þegar fullorðinn segir já',
      family_goal: 'Markmið fjölskyldunnar',
      family_goal_more: 'Stjörnur allra telja. {{n}} eftir í {{reward}}.',
      family_goal_ready: '{{reward}} er tilbúið! Fullorðinn getur sótt það fyrir fjölskylduna.',
      due_tomorrow: 'Á morgun',
      due_in_days_one: 'Eftir {{n}} dag',
      due_in_days_other: 'Eftir {{n}} daga',
      more_stars_title: 'Viltu fá fleiri stjörnur?',
      more_stars_hint: 'Allir mega taka þessi. Veldu eitt þegar þig langar!',
      see_all_can_take: 'Sjá öll {{n}}',
      take_failed: 'Ekki tókst að taka verkefnið',
    },
    task_board: {
      title: 'Verkefnatafla',
      subtitle: 'Hafðu umsjón með öllum verkefnum fjölskyldunnar',
      available_tasks: 'Laus verkefni',
      no_available_tasks: 'Engin verkefni laus í augnablikinu!',
      my_active_tasks: 'Verkefnin mín í vinnslu',
      no_my_tasks: 'Þú ert ekki með nein verkefni í gangi!',
      view: 'Skoða',
      complete: 'Klára',
      awaiting_verification: 'Bíður samþykkis',
      awaiting_verification_pill: 'Bíður samþykkis',
      tasks_assigned_to_others: 'Verkefni sem aðrir hafa fengið',
      no_tasks_assigned_to_others: 'Engin verkefni eru skráð á aðra notendur!',
      view_task: 'Skoða verkefni',
      completed_tasks: 'Kláruð verkefni',
      no_completed_tasks: 'Engin verkefni kláruð ennþá!',
      take_task: 'Ég tek það',
      take_over_task: 'Taka yfir verkefni',
      unclaim_task: 'Skila því',
      your_tasks: 'Verkefnin þín',
      can_take: 'Þessi getur þú tekið',
      waiting_heading: 'Bíður eftir fullorðnum',
      others_tasks: 'Verkefni annarra',
      done_one: '{{n}} verkefni klárað',
      done_other: '{{n}} verkefni kláruð',
      moved_to_yours: '{{task}} er núna undir Verkefnin þín',
      nothing_to_take: 'Engin verkefni til að taka núna',
      no_your_tasks: 'Þú ert ekki með nein verkefni núna.',
    },
    login: {
      title: 'AntNest',
      email_label: 'Netfang',
      password_label: 'Lykilorð',
      logging_in: 'Skrái inn...',
      login_button: 'Skrá inn',
      demo_account: 'Prufureikningur',
      demo_email: 'Netfang: admin@family.com',
      demo_password: 'Lykilorð: admin'
    },
    member_chooser: {
      title: 'Veldu meðlim',
      sign_out_device: 'Skrá út úr þessu tæki',
      sign_out_device_hint: 'Hreinsar fjölskylduinnskráninguna í þessum vafra. Notaðu þetta ef þú kemst ekki áfram eða vilt skrá þig inn aftur sem forráðamaður með Google eða netfangi.',
      who_title: 'Hver ert þú?',
      hi_name: 'Hæ {{name}}!',
      enter_pin: 'Sláðu inn PIN-númerið',
      log_in: 'Skrá inn',
      use_password: 'Nota lykilorð í staðinn',
      use_pin: 'Nota PIN í staðinn',
      password_label: 'Lykilorð',
      delete_digit: 'Eyða',
      back: 'Til baka',
      locked_until: 'Of margar tilraunir. Reyndu aftur kl. {{time}}.',
      locked_later: 'Of margar tilraunir. Reyndu aftur aðeins seinna.',
      wrong_pin: 'Þetta virkaði ekki. Reyndu aftur.',
    },
    user_profile: {
      user_not_found: 'Notandi fannst ekki.',
      back_to_home: 'Til baka á forsíðu',
      title: 'Notandasnið',
      profile_information: 'Upplýsingar um notanda',
      name: 'Nafn',
      email: 'Netfang',
      email_not_set: 'Ekkert netfang skráð',
      role: 'Hlutverk',
      admin: 'Stjórnandi',
      family_member: 'Fjölskyldumeðlimur',
      points: 'Stig',
      coins: 'Mynt',
      edit_profile: 'Breyta notandasniði',
      save_changes: 'Vista breytingar',
      cancel: 'Hætta við',
      email_optional_label: 'Netfang (valfrjálst)',
      new_password_optional_label: 'Nýtt lykilorð (valfrjálst)',
      name_placeholder: 'Sláðu inn nafnið þitt',
      email_enter_placeholder: 'Sláðu inn netfang',
      email_add_placeholder: 'Bæta við netfangi',
      username_for_member_login: 'Notandanafn (fyrir innskráningu meðlims)',
      pin_label: 'PIN (4–8 tölustafir)',
      avatar: 'Prófílmynd',
      avatar_hint: 'Smelltu á tilbúna mynd eða hladdu upp þinni eigin.',
      upload: 'Hlaða upp',
      avatar_updated: 'Prófílmynd uppfærð',
      avatar_update_failed: 'Ekki tókst að uppfæra prófílmynd',
      linked_accounts: 'Tengdir samfélagsreikningar',
      no_linked_accounts: 'Engir tengdir reikningar.',
      unlink: 'Aftengja',
      unlink_failed: 'Ekki tókst að aftengja',
      unlink_confirm: 'Aftengja {{provider}}?',
      account_unlinked: 'Reikningur aftengdur.',
      link_to_google: 'Tengja við Google',
      change_password: 'Breyta lykilorði',
      current_password: 'Núverandi lykilorð',
      new_password: 'Nýtt lykilorð',
      password_changed: 'Lykilorðinu var breytt',
      password_change_failed: 'Ekki tókst að breyta lykilorðinu',
      save_new_password: 'Vista nýtt lykilorð',
      pin_validation: 'PIN verður að vera 4–8 tölustafir (aðeins tölur)',
      current_pin_label: 'Núverandi PIN (aðeins ef þú ert nú þegar með eitt)',
      current_pin_required: 'Sláðu inn núverandi PIN til að breyta því.',
      current_pin_incorrect: 'Núverandi PIN er rangt.',
      email_managed_by_login: 'Netfangið þitt kemur frá innskráningunni þinni og er ekki hægt að breyta hér.',
      username_validation: 'Notandanafn verður að vera að minnsta kosti 2 stafir',
      profile_updated: 'Notandasnið uppfært',
      google_link_success: 'Google-reikningur tengdur',
      picture_type_bad: 'Veldu mynd á PNG-, JPG- eða WebP-sniði.',
      picture_too_big: 'Myndin er of stór (mest 1 MB).',
      choose_picture: 'Velja þessa mynd',
      or_pick_one: 'Eða veldu eina af þessum',
      tap_picture: 'Ýttu á myndina til að velja nýja.',
      facebook_link_success: 'Facebook-reikningur tengdur',
      link_to_facebook: 'Tengja við Facebook',
      link_to_apple: 'Tengja við Apple',
    },
    admin: {
      header_title: 'Stjórnborð',
      header_subtitle: 'Hafðu umsjón með verkefnum, verðlaunum og meðlimum fjölskyldunnar',
      tab_tasks: 'Verkefni',
      tab_rooms: 'Herbergi',
      tab_members: 'Meðlimir',
      tab_pending: 'Í bið',
      tab_rewards: 'Verðlaun',
      tab_tools: 'Áhöld'
    },
    checklist_editor: {
      back_to_admin: 'Til baka í stjórnborð',
      edit_checklist_for: 'Breyta gátlista',
      add_new_item: 'Bæta við nýju atriði á gátlista',
      add_item: 'Bæta við atriði',
      current_items: 'Núverandi atriði á gátlista',
      no_items: 'Engin atriði á gátlistanum ennþá. Bættu einhverjum við hér að ofan!',
      cancel: 'Hætta við',
      save: 'Vista'
    },
    task_details: {
      loading: 'Hleð...',
      title: 'Upplýsingar um verkefni',
      room: 'Herbergi',
      points: 'Stig',
      status: 'Staða',
      status_completed: 'Lokið',
      status_active: 'Í gangi',
      assigned_to: 'Skráð á',
      due_date: 'Skiladagur',
      tools_required: 'Áhöld sem þarf',
      checklist: 'Gátlisti',
      task_completed: 'Verkefni klárað',
      completed_by: 'Klárað af',
      message: 'Skilaboð',
      submit_for_verification: 'Senda til staðfestingar',
      close: 'Loka',
      preview_title: 'Forskoða verkefni',
      preview_room_placeholder: 'Stungið upp á þegar þú notar þetta verkefni',
      preview_checklist_hint: 'Skref (aðeins forskoðun — hakaðu við eftir að þú bætir verkefninu við)',
      view: 'Skoða'
    },
    task_flow: {
      start_task: 'Af stað',
      tools_ready_title: 'Ertu með allt?',
      tools_ready_subtitle: 'Þú þarft þessi áhöld. Náðu í þau áður en þú heldur áfram.',
      ready_to_start_title: 'Ertu til?',
      ready_to_start_body: 'Ertu til í að byrja á verkefninu?',
      tools_ready_no: 'Ekki alveg',
      tools_ready_yes: 'Já, af stað!',
      finish_step: 'Ég gerði það',
      final_put_tools_message: 'Vel gert! Settu öll áhöld aftur á sinn stað. Ýttu svo á hnappinn hér að neðan til að klára verkefnið.',
      complete_task_button: 'Búið',
      no_checklist_warning: 'Þetta verkefni hefur engin skref ennþá. Biddu foreldri um að bæta skrefum við gátlistann svo þú getir byrjað.',
      view_checklist_readonly_hint: 'Þú getur hakað við skrefin eftir að þú ýtir á Byrja verkefni.',
      progress_label: 'Framvinda',
      step_counter_prefix: 'Skref',
      not_assigned: 'Ekki skráð á neinn ennþá',
      mission_label: 'Verkefni',
      celebrate_heading: 'Frábært hjá þér!',
      celebrate_subtitle: 'Þú kláraðir {{title}}',
      celebrate_note: 'Flott vinna — haltu svona áfram!',
      celebrate_button: 'Aftur á forsíðu',
      step_of: 'Skref {{n}} af {{total}}',
      back_step: 'Fara eitt skref til baka',
      tidy_title: 'Næstum búið!',
      tidy_subtitle: 'Settu hlutina aftur á sinn stað',
      tidy_no_tools: 'Er allt búið? Ýttu þá á Búið.',
      back_to_steps: 'Aftur í skrefin',
      celebrate_stars_title: '{{n}} stjörnur eru á leiðinni',
      celebrate_stars_body: 'Þú færð þær þegar fullorðinn segir já',
      see_details: 'Sjá nánar',
      step_tools: 'Þú þarft:',
      where_room: 'Hvar: {{room}}',
      celebrate_stars_got_title: 'Þú fékkst {{n}} stjörnur',
      celebrate_stars_got_body: 'Samþykkt og bætt við stjörnurnar þínar',
      tidy_put_all_back: 'Settu alla þessa hluti aftur á sinn stað',
    },
    reward_celebration: {
      congratulations: 'Til hamingju!',
      you_have_a_reward: 'Þú átt verðlaun!',
      check_rewards_prompt: 'Kíktu á verðlaunin þín til að sækja þau!'
    },
    notifications: {
      task_verified_heading: 'Vel gert!',
      task_verified_message: '{{admin}} staðfesti {{task}}. Þú fékkst {{points}} stig!',
      task_verified_message_zero_points: '{{admin}} staðfesti {{task}}.',
      dismiss_ok: 'Í lagi',
      dismiss_aria: 'Loka',
      admin_pending_verification_one: '1 verkefni bíður staðfestingar hjá þér.',
      admin_pending_verification_other: '{{count}} verkefni bíða staðfestingar hjá þér.',
      approved_said_yes: '{{admin}} sagði já!',
      approved_for: 'fyrir {{task}}',
      approved_note: '{{admin}} segir: {{note}}',
      approved_now_you_have: 'Nú áttu {{n}} stjörnur',
      approved_reward_ready: '{{reward}} er tilbúið!',
      approved_more_to_goal: '{{n}} stjörnur í viðbót í {{reward}}',
      approved_see_reward: 'Sjá verðlaunin mín',
      approved_not_now: 'Ekki núna',
      approved_ok: 'Húrra!',
    },
    admin_panel: {
      header_title: 'Stjórnborð',
      header_subtitle: 'Hafðu umsjón með verkefnum, verðlaunum og meðlimum fjölskyldunnar',
      tab_tasks: 'Verkefni',
      tab_rooms: 'Herbergi',
      tab_members: 'Meðlimir',
      tab_pending: 'Í bið',
      tab_rewards: 'Verðlaun',
      tab_tools: 'Áhöld',
      task_management: 'Umsjón verkefna',
      create_new_task: 'Búa til nýtt verkefni',
      active_tasks: 'Virk verkefni',
      active_tasks_help: 'Verkefni sem eru í vinnslu eða í boði',
      edit: 'Breyta',
      remove: 'Fjarlægja',
      mark_as_incomplete: 'Merkja sem ólokið',
      confirm_mark_incomplete_title: 'Ertu viss um að þú viljir merkja þetta verkefni sem ólokið',
      cancel: 'Hætta við',
      confirm_yes_im_sure: 'Já, ég er viss',
      no_active_tasks: 'Engin virk verkefni fundust.',
      completed_tasks: 'Kláruð verkefni',
      completed_tasks_help: 'Verkefni sem er lokið og hafa verið staðfest',
      no_completed_tasks: 'Engin kláruð verkefni fundust.',
      room_management: 'Umsjón herbergja',
      add_new_room: 'Bæta við nýju herbergi',
      member_management: 'Umsjón meðlima',
      add_new_member: 'Bæta við nýjum meðlim',
      pending_verification: 'Verkefni sem bíða staðfestingar',
      verify_this_task: 'Staðfesta þetta verkefni',
      self_verify_not_allowed_hint: 'Annar stjórnandi verður að staðfesta þetta verkefni þegar fleiri en einn stjórnandi er til staðar.',
      points_not_awarded_section: 'Staðfest — stig vantar',
      points_not_awarded_help: 'Þessi verkefni voru merkt staðfest áður en stigin vistuðust rétt. Notaðu „Veita stig“ einu sinni til að skrá stigin á þann sem kláraði verkefnið.',
      award_missing_points: 'Veita stig',
      no_tasks_needing_points: 'Öll staðfest verkefni eru með skráð stig.',
      verify_success: 'Verkefnið var staðfest. Stig hafa verið veitt.',
      verify_repaired_points_success: 'Stigin sem vantaði hafa verið veitt þeim sem kláraði verkefnið.',
      verify_already_done: 'Þetta verkefni er þegar staðfest og stigin eru skráð.',
      verify_misattribution_credit_confirm: 'Kerfið sýnir að þegar hafi verið greitt fyrir þetta verkefni. Ef staða meðlimsins hækkaði aldrei, á að skrá einskiptisleiðréttingu núna? (Ekki nota ef stigin bárust þegar.)',
      verify_misattribution_success: 'Einskiptisleiðrétting var skráð. Biddu meðliminn um að endurhlaða eða skrá sig inn aftur til að sjá uppfærð stig.',
      verify_misattribution_already_done: 'Einskiptisleiðrétting hefur þegar verið skráð fyrir þetta verkefni.',
      verify_already_done_hint: 'Ef meðlimurinn hefur enn engin stig, smelltu aftur á Veita stig og staðfestu einskiptisleiðréttinguna, eða hreinsaðu points_awarded_on_verification fyrir þetta verkefni í gagnagrunninum og reyndu aftur.',
      no_pending_verify: 'Engin verkefni bíða staðfestingar.',
      reward_management: 'Umsjón verðlauna',
      create_new_reward: 'Búa til ný verðlaun',
      claimed: 'Sótt',
      available: 'Í boði',
      tool_management: 'Umsjón áhalda',
      create_new_tool: 'Búa til nýtt áhald',
      no_tools_msg: 'Engin áhöld hafa verið búin til. Búðu til fyrsta áhaldið til að byrja!',
      task_modal_title_create: 'Búa til nýtt verkefni',
      task_modal_title_edit_prefix: 'Breyta verkefni',
      reward_modal_title_create: 'Búa til ný verðlaun',
      reward_modal_title_edit_prefix: 'Breyta verðlaunum',
      tool_modal_title_create: 'Búa til nýtt áhald',
      tool_modal_title_edit_prefix: 'Breyta áhaldi',
      room_modal_title_add: 'Bæta við nýju herbergi',
      room_modal_title_edit: 'Breyta herbergi',
      delete_room_title: 'Eyða herbergi',
      delete_room_btn: 'Eyða',
      delete_btn: 'Eyða',
      delete_tool_title: 'Eyða áhaldi',
      delete_reward_title: 'Eyða verðlaunum',
      delete_confirm_prefix: 'Ertu viss um að þú viljir eyða',
      delete_room_prompt_prefix: 'Ertu viss um að þú viljir eyða varanlega herberginu',
      delete_room_blocked_message: 'Ekki er hægt að eyða þessu herbergi því eitt eða fleiri verkefni nota það. Eyddu þeim verkefnum eða færðu þau annað fyrst.',
      go_to_tasks_btn: 'Skoða verkefni',
      member_modal_title_add: 'Bæta við nýjum fjölskyldumeðlim',
      member_edit_title: 'Breyta fjölskyldumeðlim',
      cancel_btn: 'Hætta við',
      save_changes_btn: 'Vista breytingar',
      create_task_btn: 'Búa til verkefni',
      create_reward_btn: 'Búa til verðlaun',
      create_tool_btn: 'Búa til áhald',
      add_room_btn: 'Bæta við herbergi',
      add_member_btn: 'Bæta við meðlim',
      remove_image_btn: 'Fjarlægja mynd',
      remove_item_btn: 'Fjarlægja atriði',
      remove_task_btn: 'Fjarlægja verkefni',
      task_title_label: 'Titill verkefnis',
      task_title_placeholder: 'Sláðu inn titil verkefnis',
      description_label: 'Lýsing (valfrjálst)',
      description_placeholder: 'Sláðu inn lýsingu á verkefninu',
      room_label: 'Herbergi',
      no_room_option: 'Ekkert herbergi',
      assignment_label: 'Úthlutun verkefnis',
      available_anyone_option: 'Í boði fyrir alla',
      assign_to_member_prefix: 'Úthluta á',
      assignment_hint_assigned: 'Þetta verkefni verður aðeins sýnilegt þeim meðlim sem það er skráð á',
      assignment_hint_anyone: 'Þetta verkefni verður í boði fyrir hvaða fjölskyldumeðlim sem er',
      points_label: 'Stig sem veitt eru',
      points_placeholder: 'Stig',
      due_date_label: 'Skiladagur',
      recurrence_label: 'Endurtaka á X daga fresti (0 = engin endurtekning)',
      recreate_on_validation_label: 'Endurtaka við staðfestingu',
      recreate_on_validation_help: 'Búa verkefnið strax til aftur eftir staðfestingu stjórnanda',
      days_to_complete_label: 'Fjöldi daga til að klára verkefnið',
      days_to_complete_placeholder: 't.d. 7',
      days_to_complete_help: 'Ef þetta er stillt verður skiladagurinn stofndagur + þessi fjöldi daga.',
      auto_available_label: 'Sjálfkrafa í boði',
      header_image_label: 'Haus-mynd (valfrjálst)',
      current_header_image_label: 'Núverandi haus-mynd',
      checklist_label: 'Atriði á gátlista',
      checklist_item_placeholder: 'Sláðu inn texta fyrir atriði á gátlista',
      add_checklist_item_btn: 'Bæta við atriði á gátlista',
      images_optional_label: 'Myndir (valfrjálst):',
      camera_title: 'Myndavél',
      required_tools_label: 'Nauðsynleg áhöld',
      no_tools_available_msg: 'Engin áhöld í boði. Búðu fyrst til áhöld í flipanum Áhöld.',
      remove_task_title: 'Fjarlægja verkefni',
      remove_task_prompt_prefix: 'Ertu viss um að þú viljir fjarlægja verkefnið',
      irreversible_note: 'Þessa aðgerð er ekki hægt að afturkalla.',
      reward_title_label: 'Titill verðlauna',
      reward_title_placeholder: 'Sláðu inn titil verðlauna',
      reward_desc_label: 'Lýsing',
      reward_desc_placeholder: 'Sláðu inn lýsingu á verðlaununum',
      points_required_label: 'Stig sem þarf',
      reward_progress_start_label: 'Stig teljast frá (dagsetning)',
      reward_progress_start_hint: 'Aðeins stig sem safnast frá byrjun þessa dags (á þínum tíma) telja til þessara verðlauna. Heildarstig annars staðar breytast ekki.',
      target_type_label: 'Tegund markhóps',
      target_type_individual: 'Einstaklingur',
      target_type_family: 'Fjölskylda',
      assign_member_label: 'Úthluta á fjölskyldumeðlim',
      select_member_option: 'Veldu fjölskyldumeðlim',
      current_header_image_id_label: 'Auðkenni núverandi haus-myndar',
      tool_name_label: 'Heiti áhalds',
      tool_name_placeholder: 'Sláðu inn heiti áhalds',
      tool_image_label: 'Mynd af áhaldi (valfrjálst)',
      current_tool_image_label: 'Núverandi mynd af áhaldi',
      room_name_label_required: 'Heiti herbergis *',
      room_name_placeholder: 'Sláðu inn heiti herbergis',
      member_name_label_required: 'Nafn *',
      member_name_label: 'Nafn',
      member_name_placeholder: 'Sláðu inn nafn meðlims',
      member_pin_label_optional: 'PIN (valfrjálst)',
      member_pin_hint_optional: 'Skildu eftir autt til að halda núverandi PIN. PIN verður að vera 4–8 tölustafir.',
      member_is_admin_label: 'Stjórnandaaðgangur',
      member_update_success: 'Meðlimur uppfærður',
      member_update_failed: 'Ekki tókst að uppfæra meðlim',
      member_password_label_required: 'Lykilorð *',
      member_password_placeholder: 'Sláðu inn lykilorð',
      member_email_label_optional: 'Netfang (valfrjálst)',
      member_email_placeholder: 'Sláðu inn netfang',
      admin_access_label: 'Stjórnandaaðgangur',
      member_type_label: 'Tegund meðlims',
      member_type_adult: 'Fullorðinn',
      member_type_child: 'Barn',
      err_room_name: 'Gefðu herberginu nafn.',
      err_room_create: 'Ekki tókst að búa til herbergið. Reyndu aftur.',
      err_room_update: 'Ekki tókst að vista herbergið. Reyndu aftur.',
      err_generic_missing: 'Eitthvað vantar. Lokaðu og reyndu aftur.',
      err_task_title_points: 'Gefðu verkefninu titil og fjölda stjarna.',
      err_checklist_empty: 'Bættu við að minnsta kosti einu skrefi með texta.',
      err_task_create: 'Ekki tókst að búa til verkefnið. Reyndu aftur.',
      err_task_update: 'Ekki tókst að vista verkefnið. Reyndu aftur.',
      err_task_remove: 'Ekki tókst að fjarlægja verkefnið. Reyndu aftur.',
      err_template_use: 'Ekki tókst að búa til verkefni úr þessu sniðmáti. Reyndu aftur.',
      err_save_template: 'Ekki tókst að vista sniðmátið. Reyndu aftur.',
      saved_template: 'Vistað sem sniðmát.',
      err_header_image: 'Ekki tókst að vista myndina. Reyndu aftur.',
      err_reward_title: 'Gefðu verðlaununum titil.',
      err_reward_points: 'Sláðu inn hve margar stjörnur verðlaunin þurfa.',
      err_reward_member: 'Veldu fyrir hvern verðlaunin eru.',
      err_reward_create: 'Ekki tókst að búa til verðlaunin. Reyndu aftur.',
      err_reward_update: 'Ekki tókst að vista verðlaunin. Reyndu aftur.',
      reward_updated: 'Verðlaun vistuð.',
      err_tool_name: 'Gefðu áhaldinu nafn.',
      err_tool_create: 'Ekki tókst að búa til áhaldið. Reyndu aftur.',
      err_tool_update: 'Ekki tókst að vista áhaldið. Reyndu aftur.',
      err_member_required: 'Nýr meðlimur þarf nafn, notandanafn og PIN.',
      err_pin_format: 'PIN þarf að vera 4–8 tölustafir (bara tölur).',
      err_username_short: 'Notandanafnið þarf að vera minnst 2 stafir.',
      err_member_create: 'Ekki tókst að bæta meðlimnum við. Reyndu aftur.',
      verify_misattribution_title: 'Gefa stjörnurnar núna?',
      verify_misattribution_yes: 'Já, gefa þær',
      status_assigned: 'Fyrir {{name}}',
      status_open: 'Hver sem er getur tekið það',
      comes_back_on: 'Kemur aftur {{date}}',
      anyone_can_take: 'Hver sem er getur tekið það næsta (annars fer það aftur til sama aðila)',
      repeat_hint: 'Með „endurtaka á“ á það næsta að klárast svo mörgum dögum eftir samþykki og birtist „dagar til að klára“ dögum áður.',
      step_tools_label: 'Áhöld fyrir þetta skref',
      step_tools_hint: 'Veldu fyrst áhöld verkefnisins neðar. Síðan geturðu valið þau sem þetta skref þarf.',
      room_picture_label: 'Mynd af herberginu (valfrjálst)',
      room_picture_hint: 'Mynd hjálpar börnunum að sjá í hvaða herbergi verkefnið er.',
      room_picture_add: 'Bæta við mynd',
      room_picture_change: 'Skipta um mynd',
      room_picture_failed: 'Herbergið var vistað en ekki tókst að hlaða myndinni upp. Reyndu aftur.',
      step_drag: 'Dragðu til að færa skrefið',
      step_move_up: 'Færa skrefið upp',
      step_move_down: 'Færa skrefið niður',
      show_more_completed_tasks: 'Sýna meira',
      show_less_completed_tasks: 'Sýna minna',
      task_saving: 'Vista verkefnið…',
      task_saved: 'Verkefnið er vistað',
      try_again: 'Reyna aftur',
      deadline_question: 'Frestur',
      deadline_none: 'Enginn frestur',
      deadline_within: 'Þarf að klárast innan',
      days_unit: 'daga',
      repeat_question: 'Þegar það er samþykkt…',
      repeat_none: 'er það búið – það kemur ekki aftur',
      repeat_now: 'kemur það strax aftur',
      repeat_after: 'kemur það aftur eftir',
      next_question: 'Hver fær það næsta?',
      next_same: '{{name}} aftur',
      next_anyone: 'Allir geta tekið það',
      summary_title: 'Svona verður það:',
      due_this_time_label: 'Frestur í þetta sinn (valfrjálst)',
      due_this_time_help: 'Láttu það vera autt, þá gildir fresturinn hér að ofan.',
      sum_now_person: '{{name}} fær verkefnið núna.',
      sum_now_anyone: 'Allir í fjölskyldunni geta tekið það núna.',
      sum_within_one: 'Það þarf að klárast innan {{n}} dags.',
      sum_within_other: 'Það þarf að klárast innan {{n}} daga.',
      sum_end: 'Þegar þú samþykkir það er það búið.',
      sum_back_now_same: 'Þegar þú samþykkir það kemur það strax aftur til {{name}}.',
      sum_back_now_anyone: 'Þegar þú samþykkir það kemur það strax aftur og allir geta tekið það.',
      sum_back_after_same_one: 'Þegar þú samþykkir það kemur það aftur til {{name}} eftir {{n}} dag.',
      sum_back_after_same_other: 'Þegar þú samþykkir það kemur það aftur til {{name}} eftir {{n}} daga.',
      sum_back_after_anyone_one: 'Þegar þú samþykkir það kemur það aftur eftir {{n}} dag og allir geta tekið það.',
      sum_back_after_anyone_other: 'Þegar þú samþykkir það kemur það aftur eftir {{n}} daga og allir geta tekið það.',
      sum_then_within_one: 'Þá er {{n}} dagur til að klára það.',
      sum_then_within_other: 'Þá eru {{n}} dagar til að klára það.',
      repeat_next: 'er næsta verkefni',
      next_ready_after: 'Tilbúið eftir',
      hours_unit: 'klukkustundir',
      waits_for: 'Bíður eftir: {{task}}',
      then_next: 'Síðan: {{task}}',
      sum_next: 'Þegar þú samþykkir það kemur "{{task}}" næst.',
      sum_next_wait_one: 'Það er tilbúið {{n}} klukkustund eftir að þetta var klárað.',
      sum_next_wait_other: 'Það er tilbúið {{n}} klukkustundum eftir að þetta var klárað.',
      sum_next_waits: 'Þangað til sjá börnin ekki "{{task}}".',
      reward_saving: 'Vista verðlaunin…',
      reward_saved: 'Verðlaunin eru vistuð',
      assignment_hint_each: 'Hver og einn fær sitt eigið verkefni: í eigin dagatali, samþykkt sér og með eigin stjörnum.',
      err_choose_person: 'Veldu að minnsta kosti einn.',
      names_and: '{{first}} og {{last}}',
      sum_now_each: '{{names}} fá hvert sitt verkefni núna.',
      sum_back_now_each: 'Þegar þú samþykkir verkefni einhvers kemur hans eða hennar strax aftur.',
      sum_back_after_each_one: 'Þegar þú samþykkir verkefni einhvers kemur hans eða hennar aftur eftir {{n}} dag.',
      sum_back_after_each_other: 'Þegar þú samþykkir verkefni einhvers kemur hans eða hennar aftur eftir {{n}} daga.',
      shared_ready: 'Tilbúið',
      remove_shared_note: 'Því er eytt hjá öllum sem fengu það. Það sem þegar er búið helst í sögunni.',
      repeat_plan: 'kemur það aftur eftir föstu plani',
      plan_weekly: 'í hverri viku á',
      plan_every: 'á',
      plan_every_unit: 'daga fresti',
      plan_from: 'frá',
      sum_plan_person: '{{name}} fær verkefnið.',
      sum_plan_anyone: 'Allir í fjölskyldunni geta tekið það.',
      sum_plan_each: '{{names}} fá hvert sitt verkefni.',
      sum_plan_weekly: 'Það birtist hvern {{days}}.',
      sum_plan_every_one: 'Það birtist á hverjum degi frá {{date}}.',
      sum_plan_every_other: 'Það birtist á {{n}} daga fresti frá {{date}}.',
      sum_plan_back: 'Þegar þú samþykkir það kemur það aftur næsta áætlaða dag.',
      plan_week_every_one: 'í hverri viku',
      plan_week_every_other: 'á {{n}} vikna fresti',
      plan_on: 'á',
      plan_monthly: 'í hverjum mánuði',
      plan_month_every_one: 'í hverjum mánuði',
      plan_month_every_other: 'á {{n}} mánaða fresti',
      plan_the: 'á',
      plan_which: 'hvaða',
      plan_weekday: 'vikudagur',
      plan_nth_1: 'fyrsta',
      plan_nth_2: 'annan',
      plan_nth_3: 'þriðja',
      plan_nth_4: 'fjórða',
      plan_nth_last: 'síðasta',
      plan_on_date: 'þann',
      plan_date_unit: '. hvers mánaðar',
      sum_plan_weekly_every: 'Það birtist á {{n}} vikna fresti, á {{days}}.',
      sum_plan_every_open_one: 'Það birtist á hverjum degi.',
      sum_plan_every_open_other: 'Það birtist á {{n}} daga fresti.',
      sum_plan_month_day_one: 'Það birtist þann {{day}}. hvers mánaðar.',
      sum_plan_month_day_other: 'Það birtist þann {{day}}. á {{n}} mánaða fresti.',
      sum_plan_month_nth_one: 'Það birtist {{nth}} {{weekday}} hvers mánaðar.',
      sum_plan_month_nth_other: 'Það birtist {{nth}} {{weekday}} á {{n}} mánaða fresti.',
      step_timer_label: 'Tímamælir (valfrjálst)',
      step_timer_minutes: 'Tímamælir, mínútur',
      step_timer_seconds: 'Tímamælir, sekúndur',
      step_timer_min: 'mín',
      step_timer_sec: 'sek',
      step_timer_clear: 'Enginn tímamælir',
      step_timer_hint: 'T.d. "Planki – 0:45". Sá sem gerir verkefnið ræsir hann og heyrir hljóð þegar tíminn er búinn.',
    },
    parent: {
      to_approve_one: '{{n}} verkefni til að samþykkja',
      to_approve_other: '{{n}} verkefni til að samþykkja',
      finished: '{{name}} kláraði {{task}}',
      review: 'Fara yfir',
      nothing_to_approve: 'Ekkert til að samþykkja núna',
      family: 'Fjölskylda',
      open_one: '{{n}} verkefni eftir',
      open_other: '{{n}} verkefni eftir',
      open_none: 'Engin verkefni eftir',
      set_up: 'Uppsetning',
      tasks: 'Verkefni',
      rewards: 'Verðlaun',
      members: 'Fjölskylda',
      rooms_tools: 'Herbergi og áhöld',
      lock: 'Læsa',
      settings: 'Stillingar',
      more_waiting: '+{{n}} í viðbót',
      your_tasks: 'Þín eigin verkefni',
    },
    approve: {
      title: 'Samþykkja verkefni',
      approve: 'Samþykkja',
      not_done: 'Ekki búið enn',
      note_so_neat: 'Svo snyrtilegt!',
      note_well_done: 'Vel gert!',
      note_thank_you: 'Takk!',
      reason_look_again: 'Næstum — kíktu aftur',
      reason_steps_missing: 'Nokkur skref vantar enn',
      reason_do_together: 'Gerum það saman',
      pick_reason: 'Hvað á {{name}} að heyra?',
      cancel: 'Hætta við',
      steps_done_one: 'Skrefið er búið',
      steps_done_other: 'Öll {{n}} skrefin búin',
      approved_toast: '{{task}} samþykkt. {{name}} fékk {{stars}}.',
      undo: 'Afturkalla',
      sent_back_toast: '{{task}} fór aftur til {{name}}.',
      today_at: 'Í dag {{time}}',
      yesterday_at: 'Í gær {{time}}',
      from_grownup: 'Fullorðinn segir: {{reason}}',
      back: 'Til baka',
      self_blocked: 'Annar fullorðinn þarf að samþykkja þitt eigið verkefni.',
    },
    my_settings: {
      title: 'Mínar stillingar',
      language: 'Tungumál',
      same_as_device: 'Eins og þetta tæki',
      look: 'Ljóst eða dökkt',
      light: 'Ljóst',
      dark: 'Dökkt',
      notifications: 'Tilkynningar',
      on: 'Kveikt',
      off: 'Slökkt',
      notif_turn_on: 'Kveikja á þessu tæki',
      notif_turned_on: 'Kveikt er á tilkynningum.',
      notif_turned_off: 'Slökkt er á tilkynningum.',
      notif_parent_off: 'Fullorðinn hefur slökkt á tilkynningunum þínum.',
      notif_parent_on: 'Fullorðinn vill að kveikt sé á tilkynningunum þínum.',
      notif_unsupported: 'Þetta tæki getur ekki sýnt tilkynningar frá AntNest.',
      notif_blocked: 'Lokað er á tilkynningar í þessum vafra. Biddu fullorðinn að leyfa þær í stillingum vafrans.',
      timer_sound: 'Hljóð tímamælis',
      timer_sound_help: 'Hljóðið þegar tímamælir á skrefi nær núlli. Ýttu til að heyra það.',
    },
    parent_notifications: {
      title: 'Tilkynningar barnanna',
      help: 'Veldu hver ræður. „Alltaf kveikt“ þýðir að barnið getur ekki slökkt á þeim; „Slökkt“ þýðir að ekkert er sent til barnsins.',
      child: 'Barnið ræður',
      on: 'Alltaf kveikt',
      off: 'Slökkt',
      saved: 'Vistað.',
    },
    handover: {
      title: 'Verðlaun til að afhenda',
      help: 'Þessi hafa verið sótt. Gefðu barninu þau og ýttu svo á Afhent.',
      got: '{{name}} fékk {{reward}}',
      handed_over: 'Afhent',
      done_toast: '{{reward}} afhent {{name}}.',
    },
    library: {
      access_denied: 'Aðeins fullorðnir geta opnað þetta.',
      title: 'Verkefnasafn',
      intro: 'Byrjaðu á staðlaðu verkefni eða sniðmáti sem þú vistaðir úr verkefni. Aðeins fjölskyldan þín sér „Sniðmátin mín“.',
      age: 'Aldur',
      age_group: 'Aldurshópur',
      age_all: 'Allur aldur',
      age_2_3: '2–3 ára',
      age_4_5: '4–5 ára',
      age_6_7: '6–7 ára',
      age_8_9: '8–9 ára',
      age_10_12: '10–12 ára',
      age_13_plus: '13+ ára',
      standard_title: 'Stöðluð verkefni',
      none_standard: 'Engin stöðluð verkefni fyrir þetta tungumál og aldur.',
      badge_standard: 'Staðlað',
      my_title: 'Sniðmátin mín',
      my_intro: 'Vistað með „Vista sem sniðmát“ í verkefnaforminu. Aðeins fjölskyldan þín sér þau.',
      none_my: 'Engin vistuð sniðmát enn. Búðu til verkefni og notaðu „Vista sem sniðmát“ eða breyttu aldurssíunni.',
      badge_my: 'Mitt sniðmát',
      use: 'Nota',
      save_as_template: 'Vista sem sniðmát',
      days: 'Dagar',
      stars: 'Stjörnur',
      tool_name: 'Heiti áhalds',
      tool_name_ph: 't.d. Ryksuga',
      username_required: 'Notandanafn (nauðsynlegt)',
      username_ph: 'Veldu notandanafn',
      pin_label: 'PIN (4–8 tölustafir)',
      pin_ph: 'Sláðu inn PIN',
      adults: 'Fullorðnir',
      show: 'Sýna',
      hide: 'Fela',
      added_to_tasks: '„{{title}}“ var bætt við verkefnin ykkar',
      chain_title: '{{n}} verkefni sem fylgja hvert öðru',
      chain_wait_one: 'næsta tilbúið eftir {{n}} klukkustund',
      chain_wait_other: 'næsta tilbúið eftir {{n}} klukkustundir',
      chain_loops: 'Síðan byrjar það upp á nýtt.',
    },
    deletion: {
      delete_member: 'Eyða meðlim',
      delete_member_title: 'Eyða {{name}}?',
      delete_member_text: '{{name}} er strax fjarlægð(ur) úr fjölskyldunni og skráð(ur) út á öllum tækjum.',
      delete_member_lost: 'Allar stjörnur, tölfræði, verkefni og verðlaun {{name}} verða eytt.',
      undo_30: 'Þú getur afturkallað þetta í 30 daga, til {{date}}. Eftir það er öllu eytt endanlega og ekki hægt að endurheimta það.',
      delete_member_yes: 'Já, eyða',
      member_deleted_toast: '{{name}} hefur verið eytt. Þú getur endurheimt í 30 daga.',
      recently_deleted: 'Nýlega eytt',
      recently_deleted_hint: 'Þú getur endurheimt þessa meðlimi fram að dagsetningunni sem sýnd er. Eftir það er þeim og öllum gögnum þeirra eytt endanlega.',
      gone_on: 'Eytt endanlega {{date}}',
      restore: 'Endurheimta',
      member_restored_toast: '{{name}} er aftur í fjölskyldunni. Kveikja þarf aftur á tilkynningum.',
      family_section: 'Eyða fjölskyldu',
      delete_family_row: 'Eyða allri fjölskyldunni',
      delete_family_hint: 'Allir og allt í fjölskyldunni. Hægt að afturkalla í 30 daga.',
      delete_family_title: 'Eyða allri fjölskyldunni?',
      delete_family_text: 'Allir í {{family}} eru skráðir út strax og geta ekki notað AntNest fyrr en fjölskyldan hefur verið endurheimt.',
      delete_family_lost: 'Öllu verður eytt: öllum meðlimum, stjörnum, tölfræði, verkefnum, verðlaunum, viðburðum og myndum.',
      type_family_name: 'Sláðu inn nafn fjölskyldunnar til að staðfesta: {{family}}',
      delete_family_yes: 'Eyða fjölskyldunni',
      pending_title: '{{family}} er á leið í eyðingu',
      pending_text: 'Fjölskyldunni og öllum gögnum hennar verður eytt endanlega {{date}}. Þangað til getur þú endurheimt allt eins og það var.',
      days_left: 'Dagar eftir: {{days}}',
      pending_not_admin: 'Aðeins fullorðinn með stjórnandaaðgang getur endurheimt fjölskylduna.',
      restore_family: 'Endurheimta fjölskylduna',
      family_restored_toast: 'Velkomin aftur! Fjölskyldan hefur verið endurheimt. Kveikja þarf aftur á tilkynningum.',
      log_out: 'Skrá út',
      cancel: 'Hætta við',
    },
    pictures: {
      remove_picture: 'Fjarlægja mynd',
      removed: 'Myndin var fjarlægð. Þú ert aftur með venjulega maurinn.',
      unreadable: 'Ekki tókst að opna myndina. Prófaðu aðra mynd eða taktu nýja.',
      rotate: 'Snúa',
      preparing: 'Undirbý myndina…',
    },
    billing: {
      title: 'Áskrift',
      trial_title: 'Ókeypis prufutími – dagar eftir: {{days}}',
      trial_text: 'Ókeypis prufutíminn þinn endar {{date}}. Veldu áskriftarleið fyrir þann tíma til að halda áfram að nota AntNest.',
      expired_title: 'Ókeypis prufutíminn þinn er liðinn',
      expired_text: 'Veldu áskriftarleið til að halda áfram að nota AntNest. Verkefni, stjörnur og verðlaun fjölskyldunnar eru öll enn hér.',
      active_title_monthly: 'Þú greiðir mánaðarlega',
      active_title_yearly: 'Þú greiðir árlega',
      active_text: 'Áskriftin þín endurnýjast {{date}}.',
      grace_title: 'Greiðsla gekk ekki í gegn',
      grace_text: 'Uppfærðu greiðsluupplýsingarnar þínar í síðasta lagi {{date}} til að halda áfram að nota AntNest.',
      cancelling_title: 'Áskriftinni þinni hefur verið sagt upp',
      cancelling_text: 'Þú getur notað AntNest til {{date}}. Veldu áskriftarleið til að halda áfram eftir það.',
      exempt_title: 'AntNest er ókeypis fyrir fjölskylduna ykkar',
      exempt_text: 'Þú þarft ekki að greiða neitt.',
      plans_title: 'Veldu áskriftarleið',
      plans_intro: 'Ein áskrift nær yfir alla fjölskylduna, í öllum tækjum.',
      plan_monthly: 'Mánaðarlega',
      plan_yearly: 'Árlega',
      price_monthly: '{{price}} á mánuði',
      price_yearly: '{{price}} á ári',
      price_soon: 'Verð kemur fljótlega',
      includes_family: 'Öll fjölskyldan, í öllum tækjum',
      includes_tasks: 'Öll tilbúin verkefni og gátlistar',
      includes_cancel: 'Segðu upp hvenær sem er',
      choose_monthly: 'Velja mánaðarlega',
      choose_yearly: 'Velja árlega',
      coming_soon: 'Greiðslur opna fljótlega. Þangað til er AntNest ókeypis.',
      manage_title: 'Stjórnaðu áskriftinni þinni',
      manage_text: 'Skiptu um leið, uppfærðu kortið, skoðaðu kvittanir eða segðu upp.',
      manage_button: 'Stjórna áskrift',
      banner_trial: 'Dagar þar til ókeypis prufutímanum lýkur: {{days}}.',
      banner_trial_last_day: 'Ókeypis prufutíminn þinn endar í dag.',
      banner_payment: 'Greiðsla gekk ekki í gegn. Uppfærðu kortið í síðasta lagi {{date}}.',
      banner_cancelling: 'Áskriftin þín endar {{date}}.',
      banner_action: 'Sjá áskriftarleiðir',
      paywall_title: 'AntNest er í smá pásu',
      paywall_text: 'Biddu fullorðinn að velja áskriftarleið. Stjörnurnar þínar og verkefnin eru öll örugg og bíða eftir þér.',
      manage_failed: 'Ekki tókst að opna áskriftarsíðuna. Reyndu aftur eftir smá stund.',
    },
    pocket: {
      title: 'Vasapeningar',
      intro: 'Vasapeningar reiknast út frá stjörnunum sem hvert barn fær fyrir samþykkt verkefni í mánuðinum. AntNest færir enga peninga: þið borgið eins og venjulega og merkið sem greitt.',
      no_children: 'Bættu barni við fjölskylduna til að gefa vasapeninga.',
      currency: 'Gjaldmiðill',
      for_child: 'Vasapeningar fyrir {{name}}',
      how_question: 'Hvernig eru þeir unnir?',
      per_ten_label: '{{currency}} fyrir hverjar 10 stjörnur',
      fixed_label: '{{currency}} á mánuði, þegar {{name}} hefur fengið að minnsta kosti',
      threshold_label: 'stjörnur',
      threshold_help: '0 stjörnur = alltaf gefið (fastir vasapeningar).',
      bonus_label: 'Bónus:',
      bonus_every_label: '{{currency}} aukalega fyrir hverjar',
      bonus_stars_label: 'stjörnur í viðbót',
      cap_question: 'Hámark á mánuði — veldu eitt',
      cap_none: 'Ekkert hámark',
      cap_at: 'Í mesta lagi',
      cap_unit: '{{currency}} á mánuði',
      cap_required: 'Veldu hámark eða ekkert hámark áður en þú vistar.',
      example_title: 'Til dæmis í mánuði með:',
      example_line_one: '{{n}} stjarna → {{amount}}',
      example_line_other: '{{n}} stjörnur → {{amount}}',
      capped_short: 'hámarki náð',
      typical_dk: 'Algengt í Danmörku á mánuði: undir 100 DKK 6–10 ára, 50–200 DKK 11–14 ára, 200–500 DKK 15–17 ára.',
      save: 'Vista',
      saved: 'Vasapeningar fyrir {{name}} vistaðir',
      save_failed: 'Ekki tókst að vista. Reyndu aftur.',
      my_month: 'Vasapeningar í {{month}}: {{amount}}',
      my_next_one: '{{n}} stjarna í viðbót gefur {{amount}} meira',
      my_next_other: '{{n}} stjörnur í viðbót gefa {{amount}} meira',
      my_capped: 'Þú hefur náð hámarki mánaðarins. Vel gert!',
      my_last_paid: '{{month}}: {{amount}} — greitt ✓',
      my_last_unpaid: '{{month}}: {{amount}} — bíður greiðslu',
      to_pay_title: 'Vasapeningar til greiðslu',
      to_pay_help: 'Borgið eins og venjulega (reiðufé, banki eða app) og merkið svo sem greitt.',
      mark_paid: 'Merkja sem greitt',
      task_counts: 'Gefur vasapeninga',
      task_counts_help: 'Taktu hakið af fyrir hversdagsverk eins og að búa um rúmið, svo aðeins stærri verk teljist með.',
      typical_intro: 'Algengt í {{country}} á mánuði:',
      typical_band: '{{ages}} ára: {{amount}}',
      typical_under: 'undir {{amount}}',
      typical_about: 'um {{amount}}',
      typical_source: 'heimild: {{source}}',
    },
    getting_started: {
      title: 'Komdu þér af stað',
      progress: '{{done}} af {{all}} búið',
      hide: 'Fela',
      item_approve: 'Samþykktu fyrsta verkefnið sem er lokið',
      item_own_task: 'Breyttu verkefni eða búðu til þitt eigið',
      item_home_screen: 'Settu AntNest á heimaskjáinn',
      item_calendar: 'Skoðaðu verkefnin í dagatalinu',
      home_screen_how: 'iPhone/iPad: Ýttu á Deila í Safari og veldu „Bæta á heimaskjá". Android: Ýttu á ⋮ í Chrome og veldu „Setja upp forrit" eða „Bæta á heimaskjá". Gerðu það líka í tækjum barnanna.',
      home_screen_done: 'Ég er búin(n) að því',
      pocket_title: 'Vasapeningar fyrir stjörnurnar?',
      pocket_text: 'Nú þegar börnin eru komin af stað geta stjörnurnar líka gefið vasapeninga – fasta upphæð, upphæð fyrir hverjar 10 stjörnur og mánaðarlegt hámark sem þið ákveðið sjálf.',
      pocket_setup: 'Setja það upp',
      pocket_no: 'Nei takk',
    },
    editor: {
      close: 'Loka',
      unsaved_title: 'Vista breytingarnar?',
      unsaved_body: 'Þú hefur breytt einhverju. Viltu vista áður en þú lokar?',
      save: 'Vista',
      discard: 'Ekki vista',
      keep_editing: 'Halda áfram að breyta',
    },
    timer: {
      start: 'Ræsa tímamæli',
      pause: 'Gera hlé',
      resume: 'Halda áfram',
      restart: 'Byrja upp á nýtt',
      times_up: 'Tíminn er búinn!',
      sound_bell: 'Bjalla',
      sound_chime: 'Klukkuspil',
      sound_beeps: 'Vekjaraklukka',
      sound_marimba: 'Marimba',
      sound_fanfare: 'Lúðrablástur',
      volume_note: 'Hljóðið fylgir hljóðstyrk símans – hækkaðu svo þú heyrir það.',
    },
    approval_setting: {
      title: 'Samþykkt verkefna fullorðinna',
      label: 'Annar fullorðinn þarf að samþykkja verkefni fullorðinna',
      help_off: 'Af: þegar fullorðinn lýkur verkefni er það samþykkt strax og stjörnurnar eru hans. Verkefni barna samþykkir alltaf fullorðinn.',
      help_on: 'Á: lokið verkefni fullorðins bíður þar til annar fullorðinn samþykkir það (ef fjölskyldan hefur aðeins einn stjórnanda getur hann samþykkt sín eigin).',
      setup_help: 'Ef ekki er hakað við eru verkefni fullorðinna samþykkt um leið og þeim er lokið. Þú getur breytt þessu síðar í Stillingum.',
      saved: 'Vistað.',
    },
  },
  pl: {
    auth: {
      forgot_title: 'Nie pamiętasz hasła?',
      forgot_link: 'Nie pamiętasz hasła?',
      forgot_confirmation: 'Jeśli istnieje konto powiązane z tym adresem e-mail, wysłaliśmy link do resetowania hasła.',
      email_label: 'E-mail',
      send_reset_link: 'Wyślij link resetujący',
      reset_title: 'Ustaw nowe hasło',
      new_password_label: 'Nowe hasło',
      confirm_password_label: 'Potwierdź hasło',
      set_new_password: 'Ustaw nowe hasło',
      password_mismatch: 'Hasła nie są takie same',
      password_policy_msg: 'Co najmniej 12 znaków, w tym wielka litera, mała litera i cyfra.',
      invalid_token: 'Nieprawidłowy lub wygasły link resetujący',
      reset_success: 'Twoje hasło zostało zmienione. Przekierowujemy do logowania...',
      reset_failed: 'Nie udało się zresetować hasła. Spróbuj ponownie.',
      login_failed: 'Logowanie nie powiodło się. Spróbuj ponownie.',
      beta_banner: 'AntNest jest w wersji beta. Masz kod zaproszenia?',
      beta_banner_link: 'Zarejestruj się tutaj',
      or: 'lub',
      login_google: 'Zaloguj się przez Google',
      login_facebook: 'Zaloguj się przez Facebooka',
      no_account: 'Nie masz konta?',
      create_one: 'Załóż je',
      err_code_required: 'Potrzebny jest kod beta.',
      err_agree: 'Zaakceptuj regulamin i politykę prywatności.',
      err_fields: 'Wypełnij wszystkie pola.',
      err_code_invalid: 'Ten kod beta jest nieprawidłowy lub został już użyty.',
      err_signup_failed: 'Nie udało się utworzyć konta. Spróbuj ponownie.',
      signup_title: 'Załóż konto',
      beta_code: 'Kod beta',
      beta_code_ph: 'Wpisz kod zaproszenia',
      full_name: 'Twoje imię',
      full_name_ph: 'np. Anna',
      username: 'Nazwa użytkownika',
      show_password: 'Pokaż hasło',
      hide_password: 'Ukryj hasło',
      rule_length: 'Co najmniej 12 znaków',
      rule_upper: 'Wielka litera',
      rule_lower: 'Mała litera',
      rule_digit: 'Cyfra',
      password_match: 'Hasła są zgodne',
      family_name: 'Nazwa rodziny',
      family_name_ph: 'np. Rodzina Kowalskich',
      family_default: 'Moja rodzina',
      agree_before: 'Akceptuję',
      terms: 'regulamin',
      and: 'i',
      privacy: 'politykę prywatności',
      creating_account: 'Tworzenie konta…',
      create_account: 'Załóż konto',
      signup_google: 'Zarejestruj się przez Google',
      signup_facebook: 'Zarejestruj się przez Facebooka',
      choose_language: 'Wybierz język',
      oauth_failed: 'Logowanie nie zostało dokończone. Spróbuj ponownie.',
      processing: 'Logowanie…',
      complete_setup: 'Prawie gotowe',
      your_name: 'Twoje imię',
      complete_setup_btn: 'Utwórz rodzinę i przejdź dalej',
      login_apple: 'Zaloguj się przez Apple',
      signup_apple: 'Zarejestruj się przez Apple',
    },
    rooms: {
      kitchen: 'Kuchnia',
      living_room: 'Salon',
      dining_room: 'Jadalnia',
      bedroom: 'Sypialnia',
      bathroom: 'Łazienka',
      toilet: 'Toaleta',
      hallway: 'Korytarz',
      entryway: 'Przedpokój',
      laundry_room: 'Pralnia',
      garage: 'Garaż',
      office: 'Gabinet',
      playroom: 'Pokój zabaw',
      nursery: 'Pokój dziecięcy',
      patio: 'Taras/Balkon',
      garden: 'Ogród',
      storage: 'Schowek',
      basement: 'Piwnica',
      attic: 'Strych',
      child_bedroom: 'Pokój dziecka',
    },
    onboarding: {
      title: 'Zaczynajmy!',
      family_name: 'Nazwa rodziny',
      adults: 'Dorośli',
      children: 'Dzieci',
      member_name: 'Imię',
      username: 'Nazwa użytkownika',
      pin_placeholder: 'PIN (4 cyfry)',
      role_admin: 'Administrator',
      role_member: 'Członek rodziny',
      next: 'Dalej',
      back: 'Wstecz',
      confirm: 'Wygląda dobrze',
      processing: 'Zapisywanie…',
      step_rooms_title: 'Urządź swój dom',
      step_rooms_help: 'Kliknij pomieszczenia poniżej, aby dodać je do swojego domu. Jeśli masz pokój lub miejsce, którego nie ma na liście, możesz dodać je ręcznie.\nPomieszczenia zawsze możesz edytować, dodawać i usuwać w panelu administratora.',
      custom_room_placeholder: 'Własna nazwa pomieszczenia',
      add_custom: 'Dodaj własne',
      remove: 'Usuń',
      step_review_title: 'Podsumowanie',
      review_family: 'Rodzina',
      review_members: 'Członkowie',
      review_rooms: 'Pomieszczenia',
      error_generic: 'Coś poszło nie tak podczas zapisywania',
      step_members_help: 'Dodaj tutaj członków swojej rodziny.\nNadaj każdemu imię i nazwę użytkownika.\nKażdy członek rodziny musi mieć kod PIN, aby móc się zalogować.',
      validation_incomplete: 'Niektóre pola nie są wypełnione poprawnie',
      adult_label: 'Dorosły',
      child_label: 'Dziecko',
      privacy_note: 'Te informacje służą wyłącznie do skonfigurowania Waszej rodziny. Nie udostępniamy ani nie sprzedajemy – i nigdy nie będziemy – Waszych danych ani informacji osobowych nikomu.',
      child_age: 'Wiek',
      child_room_name: 'Pokój {{name}}',
      child_room_name_s: 'Pokój {{name}}',
      hint_members: 'Zawsze możesz później dodać lub zmienić członków rodziny w Admin → Członkowie.',
      hint_rooms: 'Każde dziecko dostaje własny pokój, aby jego zadania były we właściwym miejscu. Pokoje możesz zawsze zmienić później w Admin → Pokoje.',
      step_tasks_title: 'Zadania na początek',
      step_tasks_help: 'Wybraliśmy kilka zadań pasujących do Waszych pokoi i wieku dzieci. Odznaczcie te, których nie chcecie – wszystko można później zmienić.',
      starters_unavailable: 'Nie udało się wczytać propozycji. Możesz pominąć ten krok i dodać zadania później.',
      tasks_for_child: 'Zadania: {{name}}',
      tasks_shared: 'Dla wszystkich (każdy może je wziąć)',
      whole_home: 'Cały dom',
      hint_tasks: 'Dużo więcej standardowych zadań znajdziecie w Admin → Zadania; tam możecie też tworzyć własne.',
      skip_step: 'Pomiń ten krok',
      step_rewards_title: 'Nagrody do zdobycia',
      step_rewards_help: 'Dzieci są zmotywowane, gdy pierwsza nagroda jest w zasięgu. Ustawiliśmy je tak, by pierwszą można było zdobyć w kilka dni.',
      rewards_for_child: 'Dla {{name}}',
      rewards_family: 'Dla całej rodziny (liczą się gwiazdki wszystkich)',
      hint_rewards: 'Zawsze możecie dodać własne nagrody lub zmienić te w Admin → Nagrody.',
      review_starters: 'Na początek',
      review_tasks_one: '{{n}} zadanie',
      review_tasks_other: '{{n}} zadań',
      review_rewards_one: '{{n}} nagroda',
      review_rewards_other: '{{n}} nagród',
      create_family: 'Utwórz naszą rodzinę',
      done_title: 'Wasza rodzina jest gotowa!',
      done_made: 'Przygotowaliśmy dla Was {{tasks}} zadań i {{rewards}} nagród.',
      done_try: 'Wypróbujcie pierwsze zadanie: przełączcie się na dziecko przez „{{switch}}" u góry i dotknijcie „{{start}}" przy zadaniu.',
      done_admin: 'Gdy zadanie jest zrobione, zatwierdź je w „{{approve}}" – wtedy dodadzą się gwiazdki.',
      go_home: 'Przejdź do strony głównej rodziny',
      go_admin: 'Przejdź do Admin',
    },
    common: {
      save: 'Zapisz',
      take_photo: 'Zrób zdjęcie',
      cancel: 'Anuluj',
      ok: 'OK',
      loading: 'Ładowanie...',
      all: 'Wszystkie',
      remove_item: 'Usuń element',
      remove_image: 'Usuń zdjęcie',
      error_generic: 'Coś poszło nie tak. Spróbuj ponownie.',
      you: 'Ty',
      stars_one: '{{n}} gwiazdka',
      stars_other: '{{n}} gwiazdek',
      update_ready: 'Nowa wersja AntNest jest gotowa.',
      update_now: 'Aktualizuj',
    },
    header: {
      stats: 'Statystyki',
      app_title: 'AntNest',
      home: 'Start',
      tasks: 'Zadania',
      calendar: 'Kalendarz',
      rewards: 'Nagrody',
      settings: 'Ustawienia',
      profile: 'Profil',
      admin: 'Administracja',
      logout: 'Wyloguj się',
      points: 'punkty',
      settings_tooltip: 'Ustawienia',
      menu_open: 'Otwórz menu',
      menu_close: 'Zamknij menu',
      nav_menu: 'Menu',
      tab_bar_label: 'Główne sekcje',
      switch_user: 'Zmień użytkownika',
      master_admin: 'Główny administrator',
      change_picture: 'Zmień mój obrazek',
      someone_else: 'Kolej kogoś innego',
      for_grownups: 'Dla dorosłych',
      for_grownups_hint: 'Ustawienia, zatwierdzanie, wylogowanie',
      my_page: 'Moja strona',
    },
    stats: {
      title: 'Statystyki',
      tab_me: 'Ja',
      tab_family: 'Rodzina',
      tab_tasks: 'Zadania',
      period_week: 'Tydzień',
      period_month: 'Miesiąc',
      period_year: 'Rok',
      period_all: 'Cały czas',
      prev: 'Poprzedni',
      next: 'Następny',
      all_time: 'Cały czas',
      tasks_done: 'zadań zrobionych',
      points: 'punktów',
      rewards: 'nagród',
      chart_points: 'Punkty w czasie',
      where_helped: 'Moja pomoc według pomieszczeń',
      no_room: 'Bez pomieszczenia',
      my_top_tasks: 'Moje najczęstsze zadania',
      together: 'Razem',
      who_helped: 'Kto pomagał',
      everyone_over_time: 'Wszyscy w czasie',
      repeating_title: 'Zadania',
      per_task_title: 'Każde zadanie',
      times_done_axis: 'Liczba wykonań',
      times: '{n}×',
      last_done: 'ostatnio: {date}',
      empty: 'Na razie nic w tym okresie.',
      family_hidden: 'Wasza rodzina pokazuje to tylko dorosłym.',
      week_short: 'Tydz. {n}',
      enc_start: 'Nowy start — każde zadanie się liczy! 💪',
      enc_first: 'Twoje pierwsze zadanie — brawo! 🎉',
      enc_done: 'Zrobione zadania: {n} — świetnie! ⭐',
      enc_more: 'Więcej punktów niż w poprzednim okresie — tak trzymaj! 🚀',
      visibility_title: 'Statystyki: co widzą dzieci',
      visibility_help: 'Wybierz, jak dzieci widzą kartę Rodzina. Dorośli administratorzy zawsze widzą wszystko.',
      visibility_team: 'Widok zespołu — udział każdego, bez rankingu (zalecane)',
      visibility_full: 'Pełne porównanie — według punktów',
      visibility_admins: 'Ukryte — tylko dorośli widzą porównanie rodziny',
      visibility_saved: 'Zapisano',
      team_view_note: 'Udział każdego w punktach rodziny',
    },
    calendar: {
      title: 'Kalendarz',
      coming_soon: 'Kalendarz jest w budowie. Pojawią się tu podstawowe widoki i wydarzenia.',
      view_day: 'Dzień',
      view_week: 'Tydzień',
      view_month: 'Miesiąc',
      week_label: 'Tydzień',
      no_events_for_day: 'Brak wydarzeń',
      today_label: 'dzisiaj',
      prev_day: 'Poprzedni dzień',
      next_day: 'Następny dzień',
      prev_week: 'Poprzedni tydzień',
      next_week: 'Następny tydzień',
      prev_month: 'Poprzedni miesiąc',
      next_month: 'Następny miesiąc',
      go_to_today: 'Dzisiaj',
      not_allowed_edit_event: 'Możesz zmieniać tylko wydarzenia utworzone przez siebie.',
      filters_title: 'Filtruj według osoby',
      single_view_button: 'Widok pojedynczy',
      family_view_button: 'Widok rodzinny',
      no_events_for_range: 'Brak wydarzeń w tym zakresie',
      no_events: 'Brak wydarzeń w tym okresie.',
      add_event: 'Dodaj wydarzenie',
      edit_event: 'Edytuj wydarzenie',
      event_form_title: 'Tytuł',
      event_form_description: 'Opis (opcjonalnie)',
      event_form_location: 'Miejsce (opcjonalnie)',
      event_form_start: 'Początek',
      event_form_end: 'Koniec',
      event_form_all_day: 'Cały dzień',
      event_form_attendees: 'Uczestnicy',
      event_form_save: 'Zapisz',
      event_form_cancel: 'Anuluj',
      event_form_delete: 'Usuń',
      delete_confirm_title: 'Usuń wydarzenie',
      delete_confirm_message: 'Czy na pewno chcesz usunąć to wydarzenie?',
      success_created: 'Wydarzenie utworzone',
      success_updated: 'Wydarzenie zaktualizowane',
      success_deleted: 'Wydarzenie usunięte',
      error_generic: 'Coś poszło nie tak',
      validation_title_required: 'Tytuł jest wymagany',
      validation_end_after_start: 'Koniec musi być późniejszy niż początek',
      event_form_image: 'Zdjęcie wydarzenia (opcjonalnie)',
      remove_image: 'Usuń zdjęcie',
      create_calendar_event: 'Utwórz wydarzenie w kalendarzu',
      event_modal_title_create: 'Utwórz wydarzenie w kalendarzu',
      event_modal_title_edit_prefix: 'Edytuj',
      reminder_label: 'Przypomnienie',
      reminder_none: 'Bez przypomnienia',
      reminder_10m: '10 minut wcześniej',
      reminder_30m: '30 minut wcześniej',
      reminder_1h: '1 godzinę wcześniej',
      reminder_12h: '12 godzin wcześniej',
      reminder_1d: '1 dzień wcześniej',
      nothing_planned: 'Na razie nic nie zaplanowano',
      nothing_this_month: 'W tym miesiącu nic nie zaplanowano',
      today_day: 'Dzisiaj, {{day}}',
      tomorrow_day: 'Jutro, {{day}}',
      at_time: 'O {{time}}',
      all_day_label: 'Cały dzień',
      with_names: 'z: {{names}}',
      task_label: 'Zadanie',
      everyone: 'Wszyscy',
      back: 'Wstecz',
      due_today: 'Do zrobienia dziś',
      due_today_at: 'Do zrobienia dziś do {{time}}',
      due_tomorrow: 'Do zrobienia do jutra',
      due_tomorrow_at: 'Do zrobienia jutro do {{time}}',
      due_later: 'Do zrobienia do tego dnia',
      due_later_at: 'Do zrobienia do {{time}}',
      overdue_since: 'Zaległe od {{day}}',
      planned: 'Zaplanowane',
    },
    settings: {
      title: 'Ustawienia',
      subtitle: 'Zarządzaj swoim kontem i preferencjami aplikacji',
      dark_mode_section: 'Tryb ciemny',
      current_theme: 'Aktualny motyw',
      following_system: 'Zgodnie z ustawieniami systemu',
      manual_selection: 'Wybór ręczny',
      follow_system_label: 'Używaj ustawień systemowych',
      auto_switch_hint: 'Automatycznie przełączaj między trybem jasnym a ciemnym na podstawie ustawień systemu operacyjnego',
      enable_dark_mode: 'Włącz tryb ciemny',
      manual_toggle_hint: 'Ręcznie przełączaj między jasnym a ciemnym motywem',
      light_mode: 'Tryb jasny',
      dark_mode: 'Tryb ciemny',
      more_settings_title: 'Więcej ustawień już wkrótce',
      more_settings_desc: 'Pojawią się tu dodatkowe opcje personalizacji, w tym preferencje powiadomień, ustawienia prywatności oraz zarządzanie kontem.',
      language_section: 'Język',
      language_hint: 'Wybierz preferowany język interfejsu',
      back: 'Wstecz',
      profile_parent: 'Rodzic. Zmień imię, zdjęcie i hasło',
      profile_member: 'Zmień imię, zdjęcie i PIN',
      look: 'Wygląd',
      auto: 'Auto',
      tell_me_when: 'Powiadom mnie, gdy',
      notif_this_device: 'Powiadomienia na tym urządzeniu',
      notif_blocked_parent: 'Zablokowane w tej przeglądarce. Zezwól na powiadomienia dla tej strony w ustawieniach przeglądarki.',
      n_review_ready: 'Zadanie czeka na zatwierdzenie',
      n_task_approved: 'Moje zadanie zostało zatwierdzone',
      n_reward_ready: 'Nagroda jest gotowa',
      n_due_soon: 'Termin mojego zadania się zbliża',
      n_overdue: 'Moje zadanie jest spóźnione',
      n_new_task: 'Nowe zadanie jest wolne',
      n_event: 'Przypomnienia z kalendarza',
      daily_summary: 'Codzienne podsumowanie',
      daily_summary_time: 'Godzina codziennego podsumowania',
      quiet_hours: 'Godziny ciszy',
      quiet_hours_hint: 'Brak powiadomień między tymi godzinami',
      quiet_from: 'Cisza od',
      quiet_to: 'Cisza do',
      this_device: 'To urządzenie',
      change_pin: 'Zmień mój PIN',
      switch_person: 'Zmień osobę',
      refresh_session: 'Odśwież to urządzenie',
      session_refreshed: 'To urządzenie jest aktualne.',
      locked_until: 'Zablokowane do {{time}}',
      unlock: 'Odblokuj',
      unlocked: '@{{name}} może znów się zalogować.',
      log_out_device: 'Wyloguj z tego urządzenia',
      logout_confirm_title: 'Wylogować z tego urządzenia?',
      logout_confirm_text: 'Cała rodzina {{family}} zostanie tu wylogowana. Dorosły musi zalogować się ponownie, zanim ktokolwiek będzie mógł używać AntNest na tym urządzeniu.',
      logout_confirm_yes: 'Wyloguj',
      cancel: 'Anuluj',
      n_reward_claimed: 'Nagroda zostaje odebrana',
    },
    rewards: {
      title: 'Nagrody',
      subtitle: 'Śledź swoje postępy i odbieraj nagrody',
      your_points: 'Twoje punkty',
      family_points: 'Punkty rodziny',
      claimed_rewards: 'Odebrane nagrody',
      your_coins: 'Twoje monety',
      individual_rewards: 'Nagrody indywidualne',
      no_individual_rewards: 'Nie masz jeszcze żadnych nagród indywidualnych.',
      family_rewards: 'Nagrody rodzinne',
      no_family_rewards: 'Nie ma jeszcze żadnych nagród rodzinnych.',
      goal_reached: 'Cel osiągnięty!',
      complete_suffix: 'ukończone',
      target_whole_family: 'Cała rodzina',
      claim_reward_button: 'Odbierz nagrodę!',
      family_claim_parent_only: 'Nagrody rodzinne może odebrać tylko rodzic',
      claimed_rewards_section: 'Odebrane nagrody',
      no_claimed_rewards: 'Nie odebrano jeszcze żadnych nagród. Działaj dalej ze swoimi zadaniami!',
      claimed_on: 'Odebrano',
      unknown_date: 'Nieznana data',
      unknown_member: 'Nieznany członek rodziny',
      reward_header_alt: 'Nagłówek nagrody',
      show_more: 'Pokaż więcej',
      show_fewer: 'Pokaż mniej',
      points_abbrev: 'pkt',
      need_more_prefix: 'Brakuje',
      rewards_to_claim_heading: 'Nagrody do odebrania',
      more_stars_for_prefix: 'Jeszcze {{count}} gwiazdek do',
      progress_counts_from: 'Liczą się punkty zdobyte od {{date}} włącznie.',
      lifetime_total_hint: 'Suma od początku (niezależna od okresów liczenia nagród).',
      your_stars_label: 'twoje gwiazdki',
      ready_to_get: 'Gotowe do odebrania',
      keep_saving: 'Zbieraj dalej na',
      n_more: 'jeszcze {{n}}',
      get_it: 'Odbierz',
      grownup_gets: 'Tę odbiera dorosły',
      got_one: '{{n}} zdobyta nagroda',
      got_other: '{{n}} zdobytych nagród',
      see_all: 'Zobacz wszystkie',
      counting_since: 'Liczymy gwiazdki od {{date}}',
      nothing_yet: 'Nie ma jeszcze nagród. Poproś dorosłego, żeby jakieś dodał!',
      claim_failed: 'Nie udało się odebrać nagrody. Spróbuj ponownie.',
    },
    dashboard: {
      welcome_back: 'Witaj ponownie',
      subheading: 'Oto co dziś dzieje się w Twojej rodzinie',
      leaderboard_title: 'Ranking rodziny',
      family_member: 'Członek rodziny',
      tasks_title: 'Zadania',
      available_tasks: 'Dostępne zadania',
      no_available_tasks: 'W tej chwili nie ma żadnych zadań!',
      my_active_tasks: 'Moje aktywne zadania',
      no_my_tasks: 'Nie masz żadnych aktywnych zadań!',
      recently_completed_tasks: 'Ostatnio ukończone zadania',
      completed_tasks: 'Ukończone zadania',
      no_completed_tasks: 'Nie ma jeszcze ukończonych zadań!',
      manage_all_tasks: 'Zarządzaj wszystkimi zadaniami',
      my_rewards_title: 'Moje nagrody',
      my_points: 'Moje punkty',
      available_rewards: 'Dostępne nagrody',
      claimed_rewards: 'Odebrane nagrody',
      next_reward_goal: 'Następny cel nagrody',
      all_rewards_claimed: 'Wszystkie nagrody odebrane! Świetna robota!',
      reward_ready: 'Można odebrać!',
      showing_rewards_help: 'Pokazujemy: Twoje nagrody osobiste oraz nagrody dla całej rodziny',
      claim_now: 'Odbierz teraz!',
      locked: 'Zablokowane',
      go_to_rewards: 'Przejdź do nagród',
      my_tasks_primary_title: 'Moje zadania',
      my_tasks_home_hint: 'To tylko zadania przypisane do Ciebie. Aby wybrać nowe, otwórz Zadania.',
      due_overdue: 'Po terminie',
      due_today: 'Na dziś',
      due_no_deadline: 'Bez terminu',
      tasks_heading_possessive: 'Zadania: {{name}}',
      missions_banner: 'Masz dziś {{count}} zadań do zrobienia. Do dzieła!',
      missions_banner_empty: 'Żadne zadanie na Ciebie nie czeka — super robota!',
      available_tasks_banner_one: 'Możesz zdobyć więcej punktów! Dostępne jest 1 zadanie.',
      available_tasks_banner_other: 'Możesz zdobyć więcej punktów! Dostępnych zadań: {{count}}.',
      find_tasks_cta: 'Znajdź zadanie',
      missions_heading: 'Dzisiejsze zadania',
      mission_cta: 'Do dzieła!',
      hi_name: 'Cześć, {{name}}',
      goal_of: '{{have}} z {{need}} gwiazdek',
      goal_get_it: 'Odbierz',
      goal_none: 'Nie ma jeszcze nagród. Poproś dorosłego, żeby jakąś dodał!',
      today: 'Dzisiaj',
      no_tasks_now: 'Na razie brak zadań',
      waiting_one: '{{n}} gwiazdka, gdy dorosły powie tak',
      waiting_other: '{{n}} gwiazdek, gdy dorosły powie tak',
      family_goal: 'Cel rodziny',
      family_goal_more: 'Liczą się gwiazdki wszystkich. Jeszcze {{n}} do: {{reward}}.',
      family_goal_ready: '{{reward}} jest gotowe! Dorosły może je odebrać dla rodziny.',
      due_tomorrow: 'Jutro',
      due_in_days_one: 'Za {{n}} dzień',
      due_in_days_other: 'Za {{n}} dni',
      more_stars_title: 'Chcesz więcej gwiazdek?',
      more_stars_hint: 'Każdy może je wziąć. Wybierz jedno, kiedy masz ochotę!',
      see_all_can_take: 'Zobacz wszystkie ({{n}})',
      take_failed: 'Nie udało się wziąć zadania',
    },
    task_board: {
      title: 'Tablica zadań',
      subtitle: 'Zarządzaj wszystkimi zadaniami rodziny i śledź ich postęp',
      available_tasks: 'Dostępne zadania',
      no_available_tasks: 'W tej chwili nie ma żadnych zadań!',
      my_active_tasks: 'Moje aktywne zadania',
      no_my_tasks: 'Nie masz żadnych aktywnych zadań!',
      view: 'Zobacz',
      complete: 'Zakończ',
      awaiting_verification: 'Czeka na zatwierdzenie',
      awaiting_verification_pill: 'Czeka na zatwierdzenie',
      tasks_assigned_to_others: 'Zadania przypisane innym',
      no_tasks_assigned_to_others: 'Żadne zadania nie są przypisane innym osobom!',
      view_task: 'Zobacz zadanie',
      completed_tasks: 'Ukończone zadania',
      no_completed_tasks: 'Nie ma jeszcze ukończonych zadań!',
      take_task: 'Biorę',
      take_over_task: 'Przejmij zadanie',
      unclaim_task: 'Oddaj',
      your_tasks: 'Twoje zadania',
      can_take: 'Te możesz wziąć',
      waiting_heading: 'Czeka na dorosłego',
      others_tasks: 'Zadania innych',
      done_one: '{{n}} zadanie zrobione',
      done_other: '{{n}} zadań zrobionych',
      moved_to_yours: '{{task}} jest teraz w: Twoje zadania',
      nothing_to_take: 'Na razie nie ma zadań do wzięcia',
      no_your_tasks: 'Nie masz teraz zadań.',
    },
    login: {
      title: 'AntNest',
      email_label: 'E-mail',
      password_label: 'Hasło',
      logging_in: 'Logowanie...',
      login_button: 'Zaloguj się',
      demo_account: 'Konto demo',
      demo_email: 'E-mail: admin@family.com',
      demo_password: 'Hasło: admin'
    },
    member_chooser: {
      title: 'Wybierz osobę',
      sign_out_device: 'Wyloguj się na tym urządzeniu',
      sign_out_device_hint: 'Czyści dane logowania rodziny w tej przeglądarce. Użyj tej opcji, jeśli coś się zacięło lub chcesz ponownie zalogować się jako opiekun przez Google albo e-mail.',
      who_title: 'Kim jesteś?',
      hi_name: 'Cześć, {{name}}!',
      enter_pin: 'Wpisz swój PIN',
      log_in: 'Zaloguj',
      use_password: 'Użyj hasła',
      use_pin: 'Użyj PIN-u',
      password_label: 'Hasło',
      delete_digit: 'Usuń',
      back: 'Wstecz',
      locked_until: 'Za dużo prób. Spróbuj ponownie o {{time}}.',
      locked_later: 'Za dużo prób. Spróbuj trochę później.',
      wrong_pin: 'Nie udało się. Spróbuj ponownie.',
    },
    user_profile: {
      user_not_found: 'Nie znaleziono użytkownika.',
      back_to_home: 'Powrót do strony głównej',
      title: 'Profil użytkownika',
      profile_information: 'Informacje o profilu',
      name: 'Imię',
      email: 'E-mail',
      email_not_set: 'Brak adresu e-mail',
      role: 'Rola',
      admin: 'Administrator',
      family_member: 'Członek rodziny',
      points: 'Punkty',
      coins: 'Monety',
      edit_profile: 'Edytuj profil',
      save_changes: 'Zapisz zmiany',
      cancel: 'Anuluj',
      email_optional_label: 'E-mail (opcjonalnie)',
      new_password_optional_label: 'Nowe hasło (opcjonalnie)',
      name_placeholder: 'Wpisz swoje imię',
      email_enter_placeholder: 'Wpisz adres e-mail',
      email_add_placeholder: 'Dodaj e-mail',
      username_for_member_login: 'Nazwa użytkownika (do logowania członka rodziny)',
      pin_label: 'PIN (4–8 cyfr)',
      avatar: 'Awatar',
      avatar_hint: 'Kliknij gotowy awatar lub wgraj własny.',
      upload: 'Wgraj',
      avatar_updated: 'Awatar zaktualizowany',
      avatar_update_failed: 'Nie udało się zaktualizować awatara',
      linked_accounts: 'Połączone konta społecznościowe',
      no_linked_accounts: 'Brak połączonych kont.',
      unlink: 'Odłącz',
      unlink_failed: 'Nie udało się odłączyć',
      unlink_confirm: 'Odłączyć {{provider}}?',
      account_unlinked: 'Konto zostało odłączone.',
      link_to_google: 'Połącz z Google',
      change_password: 'Zmień hasło',
      current_password: 'Obecne hasło',
      new_password: 'Nowe hasło',
      password_changed: 'Hasło zostało zmienione',
      password_change_failed: 'Nie udało się zmienić hasła',
      save_new_password: 'Zapisz nowe hasło',
      pin_validation: 'PIN musi mieć 4–8 cyfr (tylko cyfry)',
      current_pin_label: 'Obecny PIN (tylko jeśli już go masz)',
      current_pin_required: 'Wpisz obecny PIN, aby go zmienić.',
      current_pin_incorrect: 'Obecny PIN jest nieprawidłowy.',
      email_managed_by_login: 'Twój e-mail pochodzi z metody logowania i nie można go tutaj zmienić.',
      username_validation: 'Nazwa użytkownika musi mieć co najmniej 2 znaki',
      profile_updated: 'Profil zaktualizowany',
      google_link_success: 'Konto Google zostało połączone',
      picture_type_bad: 'Wybierz obrazek PNG, JPG lub WebP.',
      picture_too_big: 'Ten obrazek jest za duży (maks. 1 MB).',
      choose_picture: 'Wybierz ten obrazek',
      or_pick_one: 'Albo wybierz jeden z tych',
      tap_picture: 'Dotknij obrazka, aby wybrać nowy.',
      facebook_link_success: 'Konto Facebook zostało połączone',
      link_to_facebook: 'Połącz z Facebookiem',
      link_to_apple: 'Połącz z Apple',
    },
    admin: {
      header_title: 'Panel administratora',
      header_subtitle: 'Zarządzaj zadaniami, nagrodami i członkami rodziny',
      tab_tasks: 'Zadania',
      tab_rooms: 'Pomieszczenia',
      tab_members: 'Członkowie',
      tab_pending: 'Oczekujące',
      tab_rewards: 'Nagrody',
      tab_tools: 'Narzędzia'
    },
    checklist_editor: {
      back_to_admin: 'Powrót do panelu administratora',
      edit_checklist_for: 'Edytuj listę kroków',
      add_new_item: 'Dodaj nowy krok',
      add_item: 'Dodaj krok',
      current_items: 'Obecne kroki',
      no_items: 'Brak kroków na liście. Dodaj je powyżej!',
      cancel: 'Anuluj',
      save: 'Zapisz'
    },
    task_details: {
      loading: 'Ładowanie...',
      title: 'Szczegóły zadania',
      room: 'Pomieszczenie',
      points: 'Punkty',
      status: 'Status',
      status_completed: 'Ukończone',
      status_active: 'Aktywne',
      assigned_to: 'Przypisane do',
      due_date: 'Termin',
      tools_required: 'Potrzebne narzędzia',
      checklist: 'Lista kroków',
      task_completed: 'Zadanie ukończone',
      completed_by: 'Wykonane przez',
      message: 'Wiadomość',
      submit_for_verification: 'Wyślij do zatwierdzenia',
      close: 'Zamknij',
      preview_title: 'Podgląd zadania',
      preview_room_placeholder: 'Sugerowane przy użyciu tego zadania',
      preview_checklist_hint: 'Kroki (tylko podgląd — odhaczysz je po dodaniu zadania)',
      view: 'Zobacz'
    },
    task_flow: {
      start_task: 'Zaczynamy',
      tools_ready_title: 'Masz wszystko?',
      tools_ready_subtitle: 'Te narzędzia będą Ci potrzebne. Przygotuj je, zanim przejdziesz dalej.',
      ready_to_start_title: 'Gotowy?',
      ready_to_start_body: 'Chcesz zacząć to zadanie?',
      tools_ready_no: 'Jeszcze nie',
      tools_ready_yes: 'Tak, działamy',
      finish_step: 'Zrobione',
      final_put_tools_message: 'Świetnie! Odłóż wszystkie narzędzia na swoje miejsce. Potem kliknij przycisk poniżej, aby zakończyć zadanie.',
      complete_task_button: 'Gotowe',
      no_checklist_warning: 'To zadanie nie ma jeszcze kroków. Poproś rodzica o dodanie listy kroków, zanim zaczniesz.',
      view_checklist_readonly_hint: 'Kroki będziesz mógł odhaczać po kliknięciu „Zacznij zadanie”.',
      progress_label: 'Postęp',
      step_counter_prefix: 'Krok',
      not_assigned: 'Jeszcze nieprzypisane',
      mission_label: 'Misja',
      celebrate_heading: 'Świetna robota!',
      celebrate_subtitle: 'Ukończono: {{title}}',
      celebrate_note: 'Super! Tak trzymaj!',
      celebrate_button: 'Wróć do startu',
      step_of: 'Krok {{n}} z {{total}}',
      back_step: 'Wróć o krok',
      tidy_title: 'Prawie gotowe!',
      tidy_subtitle: 'Odłóż te rzeczy na miejsce',
      tidy_no_tools: 'Wszystko gotowe? Stuknij Gotowe.',
      back_to_steps: 'Wróć do kroków',
      celebrate_stars_title: '{{n}} gwiazdek jest w drodze',
      celebrate_stars_body: 'Dostaniesz je, gdy dorosły powie tak',
      see_details: 'Zobacz szczegóły',
      step_tools: 'Potrzebujesz:',
      where_room: 'Gdzie: {{room}}',
      celebrate_stars_got_title: 'Masz {{n}} gwiazdek',
      celebrate_stars_got_body: 'Zatwierdzone i dodane do twoich gwiazdek',
      tidy_put_all_back: 'Odłóż wszystkie te rzeczy na swoje miejsce',
    },
    reward_celebration: {
      congratulations: 'Gratulacje!',
      you_have_a_reward: 'Masz nagrodę!',
      check_rewards_prompt: 'Zajrzyj do sekcji nagród, żeby ją odebrać!'
    },
    notifications: {
      task_verified_heading: 'Świetna robota!',
      task_verified_message: 'Zadanie {{task}} zostało zatwierdzone przez {{admin}}. Otrzymujesz {{points}} punktów!',
      task_verified_message_zero_points: 'Zadanie {{task}} zostało zatwierdzone przez {{admin}}.',
      dismiss_ok: 'OK',
      dismiss_aria: 'Zamknij',
      admin_pending_verification_one: '1 zadanie czeka na Twoje zatwierdzenie.',
      admin_pending_verification_other: 'Zadania czekające na Twoje zatwierdzenie: {{count}}.',
      approved_said_yes: '{{admin}} mówi: tak!',
      approved_for: 'za {{task}}',
      approved_note: '{{admin}} mówi: {{note}}',
      approved_now_you_have: 'Masz teraz {{n}} gwiazdek',
      approved_reward_ready: '{{reward}} czeka na ciebie!',
      approved_more_to_goal: 'Jeszcze {{n}} gwiazdek do: {{reward}}',
      approved_see_reward: 'Zobacz moją nagrodę',
      approved_not_now: 'Nie teraz',
      approved_ok: 'Hura!',
    },
    admin_panel: {
      header_title: 'Panel administratora',
      header_subtitle: 'Zarządzaj zadaniami, nagrodami i członkami rodziny',
      tab_tasks: 'Zadania',
      tab_rooms: 'Pomieszczenia',
      tab_members: 'Członkowie',
      tab_pending: 'Oczekujące',
      tab_rewards: 'Nagrody',
      tab_tools: 'Narzędzia',
      task_management: 'Zarządzanie zadaniami',
      create_new_task: 'Utwórz nowe zadanie',
      active_tasks: 'Aktywne zadania',
      active_tasks_help: 'Zadania, które są w trakcie realizacji lub dostępne',
      edit: 'Edytuj',
      remove: 'Usuń',
      mark_as_incomplete: 'Oznacz jako nieukończone',
      confirm_mark_incomplete_title: 'Czy na pewno chcesz oznaczyć to zadanie jako nieukończone',
      cancel: 'Anuluj',
      confirm_yes_im_sure: 'Tak, jestem pewien',
      no_active_tasks: 'Nie znaleziono aktywnych zadań.',
      completed_tasks: 'Ukończone zadania',
      completed_tasks_help: 'Zadania, które zostały wykonane i zatwierdzone',
      no_completed_tasks: 'Nie znaleziono ukończonych zadań.',
      room_management: 'Zarządzanie pomieszczeniami',
      add_new_room: 'Dodaj nowe pomieszczenie',
      member_management: 'Zarządzanie członkami rodziny',
      add_new_member: 'Dodaj nowego członka rodziny',
      pending_verification: 'Zadania oczekujące na zatwierdzenie',
      verify_this_task: 'Zatwierdź to zadanie',
      self_verify_not_allowed_hint: 'Gdy administratorów jest więcej niż jeden, to zadanie musi zatwierdzić inny administrator.',
      points_not_awarded_section: 'Zatwierdzone — brak punktów',
      points_not_awarded_help: 'Te zadania zostały zatwierdzone, zanim punkty zapisały się poprawnie. Użyj raz opcji „Przyznaj punkty”, aby zapisać je osobie, która wykonała zadanie.',
      award_missing_points: 'Przyznaj punkty',
      no_tasks_needing_points: 'Wszystkie zatwierdzone zadania mają zapisane punkty.',
      verify_success: 'Zadanie zostało zatwierdzone. Punkty zostały przyznane.',
      verify_repaired_points_success: 'Brakujące punkty zostały przyznane osobie, która wykonała to zadanie.',
      verify_already_done: 'To zadanie jest już zatwierdzone, a punkty są zapisane.',
      verify_misattribution_credit_confirm: 'System pokazuje, że punkty za to zadanie zostały już wypłacone. Jeśli saldo tej osoby nigdy się nie zwiększyło, czy przyznać teraz jednorazowe wyrównanie? (Nie używaj, jeśli punkty zostały już otrzymane.)',
      verify_misattribution_success: 'Jednorazowe wyrównanie zostało przyznane. Poproś tę osobę o odświeżenie strony lub ponowne zalogowanie, aby zobaczyć zaktualizowane punkty.',
      verify_misattribution_already_done: 'Jednorazowe wyrównanie za to zadanie zostało już przyznane.',
      verify_already_done_hint: 'Jeśli dana osoba nadal nie ma punktów, kliknij ponownie „Przyznaj punkty” i potwierdź jednorazowe wyrównanie albo wyczyść pole points_awarded_on_verification dla tego zadania w bazie danych i spróbuj ponownie.',
      no_pending_verify: 'Brak zadań oczekujących na zatwierdzenie.',
      reward_management: 'Zarządzanie nagrodami',
      create_new_reward: 'Utwórz nową nagrodę',
      claimed: 'Odebrana',
      available: 'Dostępna',
      tool_management: 'Zarządzanie narzędziami',
      create_new_tool: 'Utwórz nowe narzędzie',
      no_tools_msg: 'Nie utworzono jeszcze żadnych narzędzi. Zacznij od dodania pierwszego!',
      task_modal_title_create: 'Utwórz nowe zadanie',
      task_modal_title_edit_prefix: 'Edytuj zadanie',
      reward_modal_title_create: 'Utwórz nową nagrodę',
      reward_modal_title_edit_prefix: 'Edytuj nagrodę',
      tool_modal_title_create: 'Utwórz nowe narzędzie',
      tool_modal_title_edit_prefix: 'Edytuj narzędzie',
      room_modal_title_add: 'Dodaj nowe pomieszczenie',
      room_modal_title_edit: 'Edytuj pomieszczenie',
      delete_room_title: 'Usuń pomieszczenie',
      delete_room_btn: 'Usuń',
      delete_btn: 'Usuń',
      delete_tool_title: 'Usuń narzędzie',
      delete_reward_title: 'Usuń nagrodę',
      delete_confirm_prefix: 'Czy na pewno chcesz usunąć',
      delete_room_prompt_prefix: 'Czy na pewno chcesz trwale usunąć pomieszczenie',
      delete_room_blocked_message: 'Nie można usunąć tego pomieszczenia, ponieważ korzysta z niego co najmniej jedno zadanie. Najpierw usuń te zadania lub przypisz je do innego pomieszczenia.',
      go_to_tasks_btn: 'Zobacz zadania',
      member_modal_title_add: 'Dodaj nowego członka rodziny',
      member_edit_title: 'Edytuj członka rodziny',
      cancel_btn: 'Anuluj',
      save_changes_btn: 'Zapisz zmiany',
      create_task_btn: 'Utwórz zadanie',
      create_reward_btn: 'Utwórz nagrodę',
      create_tool_btn: 'Utwórz narzędzie',
      add_room_btn: 'Dodaj pomieszczenie',
      add_member_btn: 'Dodaj członka',
      remove_image_btn: 'Usuń zdjęcie',
      remove_item_btn: 'Usuń element',
      remove_task_btn: 'Usuń zadanie',
      task_title_label: 'Tytuł zadania',
      task_title_placeholder: 'Wpisz tytuł zadania',
      description_label: 'Opis (opcjonalnie)',
      description_placeholder: 'Wpisz opis zadania',
      room_label: 'Pomieszczenie',
      no_room_option: 'Brak pomieszczenia',
      assignment_label: 'Przypisanie zadania',
      available_anyone_option: 'Dostępne dla każdego',
      assign_to_member_prefix: 'Przypisz do',
      assignment_hint_assigned: 'To zadanie będzie widoczne tylko dla przypisanej osoby',
      assignment_hint_anyone: 'To zadanie będzie mógł wziąć dowolny członek rodziny',
      points_label: 'Punkty do zdobycia',
      points_placeholder: 'Punkty',
      due_date_label: 'Termin',
      recurrence_label: 'Powtarzaj co X dni (0 = bez powtarzania)',
      recreate_on_validation_label: 'Twórz ponownie po zatwierdzeniu',
      recreate_on_validation_help: 'Utwórz zadanie od razu po zatwierdzeniu przez administratora',
      days_to_complete_label: 'Liczba dni na wykonanie zadania',
      days_to_complete_placeholder: 'np. 7',
      days_to_complete_help: 'Jeśli podasz wartość, termin to data utworzenia + tyle dni.',
      auto_available_label: 'Automatycznie dostępne',
      header_image_label: 'Zdjęcie nagłówka (opcjonalnie)',
      current_header_image_label: 'Obecne zdjęcie nagłówka',
      checklist_label: 'Kroki na liście',
      checklist_item_placeholder: 'Wpisz treść kroku',
      add_checklist_item_btn: 'Dodaj krok',
      images_optional_label: 'Zdjęcia (opcjonalnie):',
      camera_title: 'Aparat',
      required_tools_label: 'Wymagane narzędzia',
      no_tools_available_msg: 'Brak dostępnych narzędzi. Najpierw utwórz je w zakładce Narzędzia.',
      remove_task_title: 'Usuń zadanie',
      remove_task_prompt_prefix: 'Czy na pewno chcesz usunąć zadanie',
      irreversible_note: 'Tej operacji nie można cofnąć.',
      reward_title_label: 'Tytuł nagrody',
      reward_title_placeholder: 'Wpisz tytuł nagrody',
      reward_desc_label: 'Opis',
      reward_desc_placeholder: 'Wpisz opis nagrody',
      points_required_label: 'Wymagane punkty',
      reward_progress_start_label: 'Punkty liczone od (data)',
      reward_progress_start_hint: 'Do tej nagrody liczą się tylko punkty zdobyte od początku tego dnia (Twojego czasu). Sumy od początku w innych miejscach pozostają bez zmian.',
      target_type_label: 'Typ nagrody',
      target_type_individual: 'Indywidualna',
      target_type_family: 'Rodzinna',
      assign_member_label: 'Przypisz do członka rodziny',
      select_member_option: 'Wybierz członka rodziny',
      current_header_image_id_label: 'ID obecnego zdjęcia nagłówka',
      tool_name_label: 'Nazwa narzędzia',
      tool_name_placeholder: 'Wpisz nazwę narzędzia',
      tool_image_label: 'Zdjęcie narzędzia (opcjonalnie)',
      current_tool_image_label: 'Obecne zdjęcie narzędzia',
      room_name_label_required: 'Nazwa pomieszczenia *',
      room_name_placeholder: 'Wpisz nazwę pomieszczenia',
      member_name_label_required: 'Imię *',
      member_name_label: 'Imię',
      member_name_placeholder: 'Wpisz imię członka rodziny',
      member_pin_label_optional: 'PIN (opcjonalnie)',
      member_pin_hint_optional: 'Zostaw puste, aby zachować obecny PIN. PIN musi mieć 4–8 cyfr.',
      member_is_admin_label: 'Dostęp administratora',
      member_update_success: 'Dane członka rodziny zaktualizowane',
      member_update_failed: 'Nie udało się zaktualizować danych członka rodziny',
      member_password_label_required: 'Hasło *',
      member_password_placeholder: 'Wpisz hasło',
      member_email_label_optional: 'E-mail (opcjonalnie)',
      member_email_placeholder: 'Wpisz adres e-mail',
      admin_access_label: 'Dostęp administratora',
      member_type_label: 'Typ członka rodziny',
      member_type_adult: 'Dorosły',
      member_type_child: 'Dziecko',
      err_room_name: 'Nadaj pokojowi nazwę.',
      err_room_create: 'Nie udało się utworzyć pokoju. Spróbuj ponownie.',
      err_room_update: 'Nie udało się zapisać pokoju. Spróbuj ponownie.',
      err_generic_missing: 'Czegoś brakuje. Zamknij i spróbuj ponownie.',
      err_task_title_points: 'Nadaj zadaniu tytuł i liczbę gwiazdek.',
      err_checklist_empty: 'Dodaj co najmniej jeden krok z tekstem.',
      err_task_create: 'Nie udało się utworzyć zadania. Spróbuj ponownie.',
      err_task_update: 'Nie udało się zapisać zadania. Spróbuj ponownie.',
      err_task_remove: 'Nie udało się usunąć zadania. Spróbuj ponownie.',
      err_template_use: 'Nie udało się utworzyć zadania z tego szablonu. Spróbuj ponownie.',
      err_save_template: 'Nie udało się zapisać szablonu. Spróbuj ponownie.',
      saved_template: 'Zapisano jako szablon.',
      err_header_image: 'Nie udało się zapisać obrazka. Spróbuj ponownie.',
      err_reward_title: 'Nadaj nagrodzie tytuł.',
      err_reward_points: 'Wpisz, ile gwiazdek wymaga nagroda.',
      err_reward_member: 'Wybierz, dla kogo jest nagroda.',
      err_reward_create: 'Nie udało się utworzyć nagrody. Spróbuj ponownie.',
      err_reward_update: 'Nie udało się zapisać nagrody. Spróbuj ponownie.',
      reward_updated: 'Nagroda zapisana.',
      err_tool_name: 'Nadaj narzędziu nazwę.',
      err_tool_create: 'Nie udało się utworzyć narzędzia. Spróbuj ponownie.',
      err_tool_update: 'Nie udało się zapisać narzędzia. Spróbuj ponownie.',
      err_member_required: 'Nowy członek potrzebuje imienia, nazwy użytkownika i PIN-u.',
      err_pin_format: 'PIN musi mieć 4–8 cyfr (tylko cyfry).',
      err_username_short: 'Nazwa użytkownika musi mieć co najmniej 2 znaki.',
      err_member_create: 'Nie udało się dodać członka. Spróbuj ponownie.',
      verify_misattribution_title: 'Dać gwiazdki teraz?',
      verify_misattribution_yes: 'Tak, daj je',
      status_assigned: 'Dla: {{name}}',
      status_open: 'Każdy może je wziąć',
      comes_back_on: 'Wraca {{date}}',
      anyone_can_take: 'Każdy może wziąć następne (inaczej wraca do tej samej osoby)',
      repeat_hint: 'Przy „powtarzaj co” następne ma termin tyle dni po zatwierdzeniu i pojawia się „dni na wykonanie” dni wcześniej.',
      step_tools_label: 'Narzędzia do tego kroku',
      step_tools_hint: 'Najpierw wybierz narzędzia zadania niżej. Potem możesz wybrać te, których potrzebuje ten krok.',
      room_picture_label: 'Zdjęcie pokoju (opcjonalnie)',
      room_picture_hint: 'Zdjęcie pomaga dzieciom zobaczyć, w którym pokoju jest zadanie.',
      room_picture_add: 'Dodaj zdjęcie',
      room_picture_change: 'Zmień zdjęcie',
      room_picture_failed: 'Pokój został zapisany, ale nie udało się wysłać zdjęcia. Spróbuj ponownie.',
      step_drag: 'Przeciągnij, aby przesunąć krok',
      step_move_up: 'Przesuń krok w górę',
      step_move_down: 'Przesuń krok w dół',
      show_more_completed_tasks: 'Pokaż więcej',
      show_less_completed_tasks: 'Pokaż mniej',
      task_saving: 'Zapisywanie zadania…',
      task_saved: 'Zadanie zapisane',
      try_again: 'Spróbuj ponownie',
      deadline_question: 'Termin',
      deadline_none: 'Bez terminu',
      deadline_within: 'Do zrobienia w ciągu',
      days_unit: 'dni',
      repeat_question: 'Gdy zostanie zatwierdzone…',
      repeat_none: 'jest skończone – nie wraca',
      repeat_now: 'wraca od razu',
      repeat_after: 'wraca po',
      next_question: 'Kto dostanie następne?',
      next_same: 'Znowu {{name}}',
      next_anyone: 'Każdy może je wziąć',
      summary_title: 'Co się stanie:',
      due_this_time_label: 'Termin tym razem (opcjonalnie)',
      due_this_time_help: 'Zostaw puste, a zostanie użyty termin powyżej.',
      sum_now_person: '{{name}} dostaje zadanie teraz.',
      sum_now_anyone: 'Każdy w rodzinie może je teraz wziąć.',
      sum_within_one: 'Trzeba je zrobić w ciągu {{n}} dnia.',
      sum_within_other: 'Trzeba je zrobić w ciągu {{n}} dni.',
      sum_end: 'Gdy je zatwierdzisz, jest skończone.',
      sum_back_now_same: 'Gdy je zatwierdzisz, od razu wraca do {{name}}.',
      sum_back_now_anyone: 'Gdy je zatwierdzisz, od razu wraca i każdy może je wziąć.',
      sum_back_after_same_one: 'Gdy je zatwierdzisz, wróci do {{name}} za {{n}} dzień.',
      sum_back_after_same_other: 'Gdy je zatwierdzisz, wróci do {{name}} za {{n}} dni.',
      sum_back_after_anyone_one: 'Gdy je zatwierdzisz, wróci za {{n}} dzień i każdy będzie mógł je wziąć.',
      sum_back_after_anyone_other: 'Gdy je zatwierdzisz, wróci za {{n}} dni i każdy będzie mógł je wziąć.',
      sum_then_within_one: 'Potem jest {{n}} dzień na zrobienie.',
      sum_then_within_other: 'Potem jest {{n}} dni na zrobienie.',
      repeat_next: 'następne zadanie to',
      next_ready_after: 'Gotowe po',
      hours_unit: 'godzinach',
      waits_for: 'Czeka na: {{task}}',
      then_next: 'Potem: {{task}}',
      sum_next: 'Gdy je zatwierdzisz, następne będzie „{{task}}”.',
      sum_next_wait_one: 'Będzie gotowe {{n}} godzinę po zrobieniu tego.',
      sum_next_wait_other: 'Będzie gotowe {{n}} godzin po zrobieniu tego.',
      sum_next_waits: 'Do tego czasu dzieci nie widzą „{{task}}”.',
      reward_saving: 'Zapisywanie nagrody…',
      reward_saved: 'Nagroda zapisana',
      assignment_hint_each: 'Każdy dostaje własne zadanie: we własnym kalendarzu, zatwierdzane osobno, z własnymi gwiazdkami.',
      err_choose_person: 'Wybierz co najmniej jedną osobę.',
      names_and: '{{first}} i {{last}}',
      sum_now_each: '{{names}} dostają teraz każde własne zadanie.',
      sum_back_now_each: 'Gdy zatwierdzisz czyjeś zadanie, jego zadanie od razu wraca.',
      sum_back_after_each_one: 'Gdy zatwierdzisz czyjeś zadanie, jego zadanie wróci za {{n}} dzień.',
      sum_back_after_each_other: 'Gdy zatwierdzisz czyjeś zadanie, jego zadanie wróci za {{n}} dni.',
      shared_ready: 'Gotowe',
      remove_shared_note: 'Zostanie usunięte dla wszystkich, którym je dano. Już zrobione zostają w historii.',
      repeat_plan: 'wraca według stałego planu',
      plan_weekly: 'co tydzień w',
      plan_every: 'co',
      plan_every_unit: 'dni',
      plan_from: 'od',
      sum_plan_person: '{{name}} dostaje zadanie.',
      sum_plan_anyone: 'Każdy w rodzinie może je wziąć.',
      sum_plan_each: '{{names}} dostają każde własne zadanie.',
      sum_plan_weekly: 'Pojawia się w każdy {{days}}.',
      sum_plan_every_one: 'Pojawia się codziennie od {{date}}.',
      sum_plan_every_other: 'Pojawia się co {{n}} dni od {{date}}.',
      sum_plan_back: 'Gdy je zatwierdzisz, wróci w następnym zaplanowanym dniu.',
      plan_week_every_one: 'co tydzień',
      plan_week_every_other: 'co {{n}} tygodnie',
      plan_on: 'w',
      plan_monthly: 'co miesiąc',
      plan_month_every_one: 'co miesiąc',
      plan_month_every_other: 'co {{n}} miesiące',
      plan_the: 'w',
      plan_which: 'który',
      plan_weekday: 'dzień tygodnia',
      plan_nth_1: '1.',
      plan_nth_2: '2.',
      plan_nth_3: '3.',
      plan_nth_4: '4.',
      plan_nth_last: 'ostatni',
      plan_on_date: 'dnia',
      plan_date_unit: 'miesiąca',
      sum_plan_weekly_every: 'Pojawia się co {{n}} tygodnie, w {{days}}.',
      sum_plan_every_open_one: 'Pojawia się codziennie.',
      sum_plan_every_open_other: 'Pojawia się co {{n}} dni.',
      sum_plan_month_day_one: 'Pojawia się {{day}}. dnia każdego miesiąca.',
      sum_plan_month_day_other: 'Pojawia się {{day}}. dnia co {{n}} miesiące.',
      sum_plan_month_nth_one: 'Pojawia się co miesiąc: {{weekday}} ({{nth}} w miesiącu).',
      sum_plan_month_nth_other: 'Pojawia się co {{n}} miesiące: {{weekday}} ({{nth}} w miesiącu).',
      step_timer_label: 'Minutnik (opcjonalnie)',
      step_timer_minutes: 'Minutnik, minuty',
      step_timer_seconds: 'Minutnik, sekundy',
      step_timer_min: 'min',
      step_timer_sec: 's',
      step_timer_clear: 'Bez minutnika',
      step_timer_hint: 'Np. „Deska – 0:45”. Osoba wykonująca zadanie uruchamia go i słyszy dźwięk, gdy czas minie.',
    },
    parent: {
      to_approve_one: '{{n}} zadanie do zatwierdzenia',
      to_approve_other: '{{n}} zadań do zatwierdzenia',
      finished: '{{name}} skończył(a): {{task}}',
      review: 'Przejrzyj',
      nothing_to_approve: 'Na razie nic do zatwierdzenia',
      family: 'Rodzina',
      open_one: '{{n}} zadanie do zrobienia',
      open_other: '{{n}} zadań do zrobienia',
      open_none: 'Brak zadań do zrobienia',
      set_up: 'Ustawienia rodziny',
      tasks: 'Zadania',
      rewards: 'Nagrody',
      members: 'Rodzina',
      rooms_tools: 'Pomieszczenia i narzędzia',
      lock: 'Zablokuj',
      settings: 'Ustawienia',
      more_waiting: '+{{n}} więcej',
      your_tasks: 'Twoje własne zadania',
    },
    approve: {
      title: 'Zatwierdź zadania',
      approve: 'Zatwierdź',
      not_done: 'Jeszcze nie gotowe',
      note_so_neat: 'Ale porządek!',
      note_well_done: 'Dobra robota!',
      note_thank_you: 'Dziękuję!',
      reason_look_again: 'Prawie — zajrzyj jeszcze raz',
      reason_steps_missing: 'Kilka kroków jeszcze brakuje',
      reason_do_together: 'Zróbmy to razem',
      pick_reason: 'Co ma usłyszeć {{name}}?',
      cancel: 'Anuluj',
      steps_done_one: 'Krok zrobiony',
      steps_done_other: 'Wszystkie kroki ({{n}}) zrobione',
      approved_toast: '{{task}} zatwierdzone. {{name}} dostaje {{stars}}.',
      undo: 'Cofnij',
      sent_back_toast: '{{task}} wraca do: {{name}}.',
      today_at: 'Dzisiaj {{time}}',
      yesterday_at: 'Wczoraj {{time}}',
      from_grownup: 'Dorosły mówi: {{reason}}',
      back: 'Wstecz',
      self_blocked: 'Twoje własne zadanie musi zatwierdzić inny dorosły.',
    },
    my_settings: {
      title: 'Moje ustawienia',
      language: 'Język',
      same_as_device: 'Jak to urządzenie',
      look: 'Jasny czy ciemny',
      light: 'Jasny',
      dark: 'Ciemny',
      notifications: 'Powiadomienia',
      on: 'Włączone',
      off: 'Wyłączone',
      notif_turn_on: 'Włącz na tym urządzeniu',
      notif_turned_on: 'Powiadomienia są włączone.',
      notif_turned_off: 'Powiadomienia są wyłączone.',
      notif_parent_off: 'Dorosły wyłączył twoje powiadomienia.',
      notif_parent_on: 'Dorosły chce, żeby twoje powiadomienia były włączone.',
      notif_unsupported: 'To urządzenie nie może pokazywać powiadomień z AntNest.',
      notif_blocked: 'Powiadomienia są zablokowane w tej przeglądarce. Poproś dorosłego, żeby je zezwolił w ustawieniach przeglądarki.',
      timer_sound: 'Dźwięk minutnika',
      timer_sound_help: 'Dźwięk, gdy minutnik kroku dojdzie do zera. Dotknij, aby posłuchać.',
    },
    parent_notifications: {
      title: 'Powiadomienia dzieci',
      help: 'Wybierz, kto decyduje. „Zawsze włączone” oznacza, że dziecko nie może ich wyłączyć; „Wyłączone” oznacza, że nic nie jest wysyłane do dziecka.',
      child: 'Dziecko decyduje',
      on: 'Zawsze włączone',
      off: 'Wyłączone',
      saved: 'Zapisano.',
    },
    handover: {
      title: 'Nagrody do wręczenia',
      help: 'Te zostały odebrane. Daj je dziecku, a potem dotknij Wręczone.',
      got: '{{name}} zdobył(a) {{reward}}',
      handed_over: 'Wręczone',
      done_toast: '{{reward}} wręczono: {{name}}.',
    },
    library: {
      access_denied: 'Tylko dorośli mogą to otworzyć.',
      title: 'Biblioteka zadań',
      intro: 'Zacznij od zadania standardowego albo szablonu zapisanego z zadania. Tylko twoja rodzina widzi „Moje szablony”.',
      age: 'Wiek',
      age_group: 'Grupa wiekowa',
      age_all: 'Każdy wiek',
      age_2_3: '2–3 lata',
      age_4_5: '4–5 lat',
      age_6_7: '6–7 lat',
      age_8_9: '8–9 lat',
      age_10_12: '10–12 lat',
      age_13_plus: '13+ lat',
      standard_title: 'Zadania standardowe',
      none_standard: 'Brak zadań standardowych dla tego języka i wieku.',
      badge_standard: 'Standard',
      my_title: 'Moje szablony',
      my_intro: 'Zapisane przyciskiem „Zapisz jako szablon” w formularzu zadania. Widzi je tylko twoja rodzina.',
      none_my: 'Brak zapisanych szablonów. Utwórz zadanie i użyj „Zapisz jako szablon” albo zmień filtr wieku.',
      badge_my: 'Mój szablon',
      use: 'Użyj',
      save_as_template: 'Zapisz jako szablon',
      days: 'Dni',
      stars: 'Gwiazdki',
      tool_name: 'Nazwa narzędzia',
      tool_name_ph: 'np. Odkurzacz',
      username_required: 'Nazwa użytkownika (wymagana)',
      username_ph: 'Wybierz nazwę użytkownika',
      pin_label: 'PIN (4–8 cyfr)',
      pin_ph: 'Wpisz PIN',
      adults: 'Dorośli',
      show: 'Pokaż',
      hide: 'Ukryj',
      added_to_tasks: '„{{title}}” dodano do waszych zadań',
      chain_title: '{{n}} zadania po kolei',
      chain_wait_one: 'następne gotowe po {{n}} godzinie',
      chain_wait_other: 'następne gotowe po {{n}} godzinach',
      chain_loops: 'Potem od nowa.',
    },
    deletion: {
      delete_member: 'Usuń członka',
      delete_member_title: 'Usunąć: {{name}}?',
      delete_member_text: '{{name}} od razu znika z rodziny i zostaje wylogowany(-a) na wszystkich urządzeniach.',
      delete_member_lost: 'Wszystkie gwiazdki, statystyki, zadania i nagrody osoby {{name}} zostaną usunięte.',
      undo_30: 'Możesz to cofnąć przez 30 dni, do {{date}}. Potem wszystko zostanie trwale usunięte i nie da się tego odzyskać.',
      delete_member_yes: 'Tak, usuń',
      member_deleted_toast: 'Usunięto: {{name}}. Możesz przywrócić przez 30 dni.',
      recently_deleted: 'Ostatnio usunięci',
      recently_deleted_hint: 'Możesz przywrócić tych członków do podanej daty. Potem zostaną trwale usunięci razem ze wszystkimi danymi.',
      gone_on: 'Trwałe usunięcie: {{date}}',
      restore: 'Przywróć',
      member_restored_toast: '{{name}} wraca do rodziny. Powiadomienia trzeba włączyć ponownie.',
      family_section: 'Usuń rodzinę',
      delete_family_row: 'Usuń całą rodzinę',
      delete_family_hint: 'Wszyscy i wszystko w rodzinie. Można cofnąć przez 30 dni.',
      delete_family_title: 'Usunąć całą rodzinę?',
      delete_family_text: 'Wszyscy w rodzinie {{family}} zostają od razu wylogowani i nie mogą używać AntNest, dopóki rodzina nie zostanie przywrócona.',
      delete_family_lost: 'Wszystko zostanie usunięte: wszyscy członkowie, gwiazdki, statystyki, zadania, nagrody, wydarzenia i zdjęcia.',
      type_family_name: 'Aby potwierdzić, wpisz nazwę rodziny: {{family}}',
      delete_family_yes: 'Usuń rodzinę',
      pending_title: 'Rodzina {{family}} jest zaplanowana do usunięcia',
      pending_text: 'Rodzina i wszystkie jej dane zostaną trwale usunięte {{date}}. Do tego czasu możesz przywrócić wszystko tak, jak było.',
      days_left: 'Pozostało dni: {{days}}',
      pending_not_admin: 'Tylko dorosły z uprawnieniami administratora może przywrócić rodzinę.',
      restore_family: 'Przywróć rodzinę',
      family_restored_toast: 'Witaj ponownie! Rodzina została przywrócona. Powiadomienia trzeba włączyć ponownie.',
      log_out: 'Wyloguj',
      cancel: 'Anuluj',
    },
    pictures: {
      remove_picture: 'Usuń zdjęcie',
      removed: 'Zdjęcie usunięte. Masz znowu zwykłą mrówkę.',
      unreadable: 'Nie udało się otworzyć zdjęcia. Spróbuj innego albo zrób nowe.',
      rotate: 'Obróć',
      preparing: 'Przygotowywanie zdjęcia…',
    },
    billing: {
      title: 'Subskrypcja',
      trial_title: 'Darmowy okres próbny – pozostałe dni: {{days}}',
      trial_text: 'Twój darmowy okres próbny kończy się {{date}}. Wybierz plan przed tym dniem, aby dalej korzystać z AntNest.',
      expired_title: 'Twój darmowy okres próbny się skończył',
      expired_text: 'Wybierz plan, aby dalej korzystać z AntNest. Zadania, gwiazdki i nagrody Twojej rodziny wciąż tu są.',
      active_title_monthly: 'Płacisz co miesiąc',
      active_title_yearly: 'Płacisz co rok',
      active_text: 'Twoja subskrypcja odnowi się {{date}}.',
      grace_title: 'Płatność nie przeszła',
      grace_text: 'Zaktualizuj dane płatności do {{date}}, aby dalej korzystać z AntNest.',
      cancelling_title: 'Twoja subskrypcja została anulowana',
      cancelling_text: 'Możesz korzystać z AntNest do {{date}}. Wybierz plan, aby kontynuować później.',
      exempt_title: 'AntNest jest bezpłatny dla Twojej rodziny',
      exempt_text: 'Nie musisz nic płacić.',
      plans_title: 'Wybierz plan',
      plans_intro: 'Jedna subskrypcja obejmuje całą rodzinę na wszystkich urządzeniach.',
      plan_monthly: 'Miesięczny',
      plan_yearly: 'Roczny',
      price_monthly: '{{price}} miesięcznie',
      price_yearly: '{{price}} rocznie',
      price_soon: 'Cena wkrótce',
      includes_family: 'Cała rodzina na wszystkich urządzeniach',
      includes_tasks: 'Wszystkie gotowe zadania i listy kroków',
      includes_cancel: 'Anuluj w dowolnym momencie',
      choose_monthly: 'Wybierz miesięczny',
      choose_yearly: 'Wybierz roczny',
      coming_soon: 'Płatności ruszą wkrótce. Do tego czasu AntNest jest bezpłatny.',
      manage_title: 'Zarządzaj subskrypcją',
      manage_text: 'Zmień plan, zaktualizuj kartę, zobacz potwierdzenia płatności lub anuluj.',
      manage_button: 'Zarządzaj subskrypcją',
      banner_trial: 'Dni do końca darmowego okresu próbnego: {{days}}.',
      banner_trial_last_day: 'Twój darmowy okres próbny kończy się dzisiaj.',
      banner_payment: 'Płatność nie przeszła. Zaktualizuj kartę do {{date}}.',
      banner_cancelling: 'Twoja subskrypcja kończy się {{date}}.',
      banner_action: 'Zobacz plany',
      paywall_title: 'AntNest robi sobie przerwę',
      paywall_text: 'Poproś dorosłego o wybranie planu. Wszystkie twoje gwiazdki i zadania są bezpieczne i na ciebie czekają.',
      manage_failed: 'Nie udało się otworzyć strony subskrypcji. Spróbuj ponownie za chwilę.',
    },
    pocket: {
      title: 'Kieszonkowe',
      intro: 'Kieszonkowe liczy się z gwiazdek, które dziecko zdobywa za zatwierdzone zadania w miesiącu. AntNest nie przelewa pieniędzy: płacicie jak zwykle i oznaczacie jako wypłacone.',
      no_children: 'Dodaj dziecko do rodziny, aby dawać kieszonkowe.',
      currency: 'Waluta',
      for_child: 'Kieszonkowe dla {{name}}',
      how_question: 'Jak się je zdobywa?',
      per_ten_label: '{{currency}} za każde 10 gwiazdek',
      fixed_label: '{{currency}} miesięcznie, gdy {{name}} zdobędzie co najmniej',
      threshold_label: 'gwiazdek',
      threshold_help: '0 gwiazdek = zawsze (stałe kieszonkowe).',
      bonus_label: 'Premia:',
      bonus_every_label: '{{currency}} więcej za każde',
      bonus_stars_label: 'gwiazdek więcej',
      cap_question: 'Limit miesięczny — wybierz',
      cap_none: 'Bez limitu',
      cap_at: 'Najwyżej',
      cap_unit: '{{currency}} miesięcznie',
      cap_required: 'Wybierz limit lub brak limitu przed zapisaniem.',
      example_title: 'Na przykład w miesiącu z:',
      example_line_one: '{{n}} gwiazdka → {{amount}}',
      example_line_other: '{{n}} gwiazdek → {{amount}}',
      capped_short: 'limit osiągnięty',
      typical_dk: 'Typowo w Danii miesięcznie: poniżej 100 DKK w wieku 6–10, 50–200 DKK w wieku 11–14, 200–500 DKK w wieku 15–17.',
      save: 'Zapisz',
      saved: 'Kieszonkowe dla {{name}} zapisane',
      save_failed: 'Nie udało się zapisać. Spróbuj ponownie.',
      my_month: 'Kieszonkowe ({{month}}): {{amount}}',
      my_next_one: '{{n}} gwiazdka więcej da {{amount}} więcej',
      my_next_other: '{{n}} gwiazdek więcej da {{amount}} więcej',
      my_capped: 'Osiągnięto maksimum w tym miesiącu. Brawo!',
      my_last_paid: '{{month}}: {{amount}} — wypłacone ✓',
      my_last_unpaid: '{{month}}: {{amount}} — czeka na wypłatę',
      to_pay_title: 'Kieszonkowe do wypłaty',
      to_pay_help: 'Zapłaćcie jak zwykle (gotówka, przelew lub BLIK), a potem oznaczcie jako wypłacone.',
      mark_paid: 'Oznacz jako wypłacone',
      task_counts: 'Daje kieszonkowe',
      task_counts_help: 'Odznacz przy codziennych zadaniach, jak ścielenie łóżka, aby liczyły się tylko większe prace.',
      typical_intro: 'Typowo ({{country}}) miesięcznie:',
      typical_band: '{{ages}} lat: {{amount}}',
      typical_under: 'poniżej {{amount}}',
      typical_about: 'ok. {{amount}}',
      typical_source: 'źródło: {{source}}',
    },
    getting_started: {
      title: 'Na dobry początek',
      progress: '{{done}} z {{all}} zrobione',
      hide: 'Ukryj',
      item_approve: 'Zatwierdź pierwsze ukończone zadanie',
      item_own_task: 'Zmień zadanie albo utwórz własne',
      item_home_screen: 'Dodaj AntNest do ekranu głównego',
      item_calendar: 'Zobacz zadania w kalendarzu',
      home_screen_how: 'iPhone/iPad: w Safari dotknij Udostępnij i wybierz „Do ekranu początkowego". Android: w Chrome dotknij ⋮ i wybierz „Zainstaluj aplikację" lub „Dodaj do ekranu głównego". Zrób to też na urządzeniach dzieci.',
      home_screen_done: 'Zrobione',
      pocket_title: 'Kieszonkowe za gwiazdki?',
      pocket_text: 'Skoro dzieci już zaczęły, gwiazdki mogą też dawać kieszonkowe – stałą kwotę, kwotę za każde 10 gwiazdek i miesięczny limit, który sami wybierzecie.',
      pocket_setup: 'Skonfiguruj',
      pocket_no: 'Nie, dziękuję',
    },
    editor: {
      close: 'Zamknij',
      unsaved_title: 'Zapisać zmiany?',
      unsaved_body: 'Coś zostało zmienione. Czy zapisać przed zamknięciem?',
      save: 'Zapisz',
      discard: 'Nie zapisuj',
      keep_editing: 'Edytuj dalej',
    },
    timer: {
      start: 'Uruchom minutnik',
      pause: 'Pauza',
      resume: 'Wznów',
      restart: 'Od nowa',
      times_up: 'Czas minął!',
      sound_bell: 'Dzwonek',
      sound_chime: 'Kuranty',
      sound_beeps: 'Budzik',
      sound_marimba: 'Marimba',
      sound_fanfare: 'Fanfara',
      volume_note: 'Dźwięk zależy od głośności telefonu – podgłośnij, żeby go usłyszeć.',
    },
    approval_setting: {
      title: 'Zatwierdzanie zadań dorosłych',
      label: 'Zadania dorosłych musi zatwierdzić inna dorosła osoba',
      help_off: 'Wyłączone: gdy dorosły skończy zadanie, jest ono od razu zatwierdzone, a gwiazdki są jego. Zadania dzieci zawsze zatwierdza dorosły.',
      help_on: 'Włączone: ukończone zadanie dorosłego czeka, aż zatwierdzi je inna dorosła osoba (jeśli w rodzinie jest tylko jeden administrator, może zatwierdzać swoje).',
      setup_help: 'Jeśli nie zaznaczysz, zadania dorosłych zostaną zatwierdzone od razu po wykonaniu. Możesz to później zmienić w Ustawieniach.',
      saved: 'Zapisano.',
    },
  }
};

export const fallback_language: SupportedLanguageCode = 'en';

export const normalizeLanguageCode = (code: string): SupportedLanguageCode => {
  const base = (code || '').toLowerCase().split('-')[0];
  if (base === 'no') return 'nb';
  const valid = supported_languages.map(l => l.code);
  return (valid.includes(base as SupportedLanguageCode) ? base : fallback_language) as SupportedLanguageCode;
};


